import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";

import * as pdfjsLib from "pdfjs-dist";
import presets from "../lib/takeoff/drywallPresets.json";
import { MovableHost } from "./FullscreenHost";
import { createEngine } from "../lib/takeoff/engine/engineClient";
import {
  assignTags,
  chainEdges,
  uncoveredTaggedFaces,
  thicknessGuesser,
} from "../lib/takeoff/engine/tagAssign";
import {
  centerlineChains,
  outsideCorners,
  simplify as simplifyPts,
  filterOpenFaces,
  dropNestedLines,
  simplifyRing,
  applyExterior,
} from "../lib/takeoff/engine/centerlines";
import AssemblyWizard, { descOf } from "./AssemblyWizard";
import TraceSetupPanel, { pagesWithRole, suggestRole } from "./TraceSetupPanel";

import { buildSheetLegends } from "../lib/takeoff/sheetLegend";
import { extractSheet } from "../lib/takeoff/engine/pdfVectors";
import {
  parseLegend,
  legendPanelBoxes,
} from "../lib/takeoff/engine/legendParse";
import { drawingArea } from "../lib/takeoff/engine/sheetPrep";
import { buildWorkingPdf, showPdfWindow } from "../lib/takeoff/workingDrawing";
import {
  openReportWindow,
  reportWindowStatus,
} from "../lib/takeoff/estimateReport";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const API = "https://erp-xe9u.onrender.com/api";
// ---------------------------------------------------------------------------
// Constants + helpers
// ---------------------------------------------------------------------------

const PALETTE = [
  "#f97316",
  "#38bdf8",
  "#4ade80",
  "#f472b6",
  "#facc15",
  "#a78bfa",
  "#fb7185",
  "#2dd4bf",
  "#fbbf24",
  "#818cf8",
];

const PRESET_SCALES = [
  { label: `1/16" = 1'-0"`, ftPerPaperInch: 16 },
  { label: `3/32" = 1'-0"`, ftPerPaperInch: 32 / 3 },
  { label: `1/8" = 1'-0"`, ftPerPaperInch: 8 },
  { label: `3/16" = 1'-0"`, ftPerPaperInch: 16 / 3 },
  { label: `1/4" = 1'-0"`, ftPerPaperInch: 4 },
  { label: `3/8" = 1'-0"`, ftPerPaperInch: 8 / 3 },
  { label: `1/2" = 1'-0"`, ftPerPaperInch: 2 },
  { label: `3/4" = 1'-0"`, ftPerPaperInch: 4 / 3 },
  { label: `1" = 1'-0"`, ftPerPaperInch: 1 },
];

const AREA_TOL_IN = 4; // area outlines drop jogs smaller than this (inches) — fewer corners, easier to reshape
const UNTAGGED = "__untagged"; // wallType of the "Untagged wall" condition ("__untagged:ceil:<grid>" for untagged ceilings)
const isUntagged = (c) =>
  !!c?.wallType && String(c.wallType).startsWith(UNTAGGED);
// washrooms (and other wet rooms) get their own wall assembly — different board, same tag
export const WET_ROOM =
  /\b(WASH ?ROOMS?|TOILETS?|W\.?C\.?|WCS|BATH ?(ROOMS?)?|RESTROOMS?|SHOWERS?|LAV(ATOR(Y|IES))?|ENSUITE|POWDER ROOM|UNIVERSAL W\/?R|U\.?W\.?R)\b/i;
export const isWetRoom = (label, texts = []) =>
  WET_ROOM.test([label || "", ...(texts || [])].join(" \n "));
// count assemblies the trace fills in (one marker per item; drag / add / delete markers like any count)
export const COUNT_KINDS = {
  door: {
    name: "Doors — single · HM frame",
    color: "#e879f9",
    description:
      "Single door openings counted off the plans (sized in the trace wizard, 3'-2\" × 7'-0\" by default)\nHollow metal frame — supply / install per opening",
  },
  door2: {
    name: "Doors — double · HM frame",
    color: "#c026d3",
    description:
      "Double door openings (pairs — 6'-2\" × 7'-0\" by default)\nHollow metal frame for a pair — supply / install per opening",
  },
  window: {
    name: "Windows",
    color: "#22d3ee",
    description:
      "Window openings in traced walls (returns / sills / bead as needed)",
  },
  bead: {
    name: "Corner bead — outside corners",
    color: "#4ade80",
    description:
      "One marker per outside corner · LF = corners × wall height\nAdd markers for corners the trace missed",
  },
};
const uid = () =>
  Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

// ── how an assembly looks on the drawing ────────────────────────────────────────────────────────────────
// Lines are drawn at a real thickness (inches on the sheet, 4" unless the assembly says otherwise) so they lie
// over the architectural wall instead of a hairline. Each assembly picks its pattern; counts pick a symbol.
export const DEFAULT_LINE_IN = 4;
export const LINE_PATTERNS = [
  ["transparent", "Transparent"],
  ["solid", "Solid"],
  ["hatch", "Hatch"],
  ["crosshatch", "Cross-hatch"],
  ["dashed", "Dashed"],
  ["dotted", "Dotted"],
  ["outline", "Outline"],
  ["thin", "Thin line"],
];
// where a hand-drawn line goes: "auto" = clicked on a wall face (or anywhere on the wall) → moved onto the wall's
// centre, so the band covers it face to face; "click" = exactly where clicked; "left" / "right" = the band sits to
// that side of the clicks (looking along the drawing direction)
export const LINE_ALIGNS = [
  ["auto", "Centre on the wall (auto)"],
  ["click", "Exactly where clicked"],
  ["left", "Band to the left of the clicks"],
  ["right", "Band to the right of the clicks"],
];
export const COUNT_SYMBOLS = [
  ["circle", "Circle"],
  ["square", "Square"],
  ["triangle", "Triangle"],
  ["diamond", "Diamond"],
  ["star", "Star"],
  ["hexagon", "Hexagon"],
  ["cross", "Cross ✕"],
  ["plus", "Plus +"],
];
const COUNT_SYMBOL_DEFAULT = {
  "__count:door": "square",
  "__count:door2": "diamond",
  "__count:window": "triangle",
  "__count:bead": "plus",
};
export const HEADER_WT = "__header:"; // wallType of a door-header assembly: "__header:<wall assembly id>"
export const isHeaderCond = (c) =>
  !!c?.wallType && String(c.wallType).startsWith(HEADER_WT);
export const condLineIn = (c) => {
  const v = Number(c?.lineIn);
  return v > 0 ? v : DEFAULT_LINE_IN;
};
// lines default to solid; areas (ceilings, rooms) stay transparent so the drawing shows through the fill
export const condPattern = (c) =>
  c?.linePattern ||
  (isHeaderCond(c) ? "hatch" : c?.type === "linear" ? "solid" : "transparent");
export const condAlign = (c) => c?.lineAlign || "auto";
export const condSymbol = (c) =>
  c?.symbol || COUNT_SYMBOL_DEFAULT[c?.wallType] || "circle";

// count symbols on the canvas renderer: a circle marker with `symbol` draws that shape instead (hit-testing stays a
// circle, so selecting / box-selecting / tooltips all work as before)
export function symbolPath(ctx, sym, x, y, r) {
  const poly = (n, rot, rr = r) => {
    for (let i = 0; i < n; i++) {
      const a = rot + (i * 2 * Math.PI) / n;
      const px = x + rr * Math.cos(a),
        py = y + rr * Math.sin(a);
      i ? ctx.lineTo(px, py) : ctx.moveTo(px, py);
    }
    ctx.closePath();
  };
  switch (sym) {
    case "square":
      ctx.rect(x - r * 0.88, y - r * 0.88, r * 1.76, r * 1.76);
      break;
    case "triangle":
      poly(3, -Math.PI / 2, r * 1.15);
      break;
    case "diamond":
      poly(4, -Math.PI / 2, r * 1.15);
      break;
    case "hexagon":
      poly(6, 0, r * 1.05);
      break;
    case "star":
      for (let i = 0; i < 10; i++) {
        const a = -Math.PI / 2 + (i * Math.PI) / 5,
          rr = i % 2 ? r * 0.5 : r * 1.25;
        const px = x + rr * Math.cos(a),
          py = y + rr * Math.sin(a);
        i ? ctx.lineTo(px, py) : ctx.moveTo(px, py);
      }
      ctx.closePath();
      break;
    case "cross":
    case "plus": {
      const k = sym === "cross" ? Math.PI / 4 : 0,
        w = r * 0.42,
        L0 = r * 1.15;
      const pts = [
        [-w, -L0],
        [w, -L0],
        [w, -w],
        [L0, -w],
        [L0, w],
        [w, w],
        [w, L0],
        [-w, L0],
        [-w, w],
        [-L0, w],
        [-L0, -w],
        [-w, -w],
      ];
      pts.forEach(([px, py], i) => {
        const qx = x + px * Math.cos(k) - py * Math.sin(k),
          qy = y + px * Math.sin(k) + py * Math.cos(k);
        i ? ctx.lineTo(qx, qy) : ctx.moveTo(qx, qy);
      });
      ctx.closePath();
      break;
    }
    default:
      ctx.arc(x, y, r, 0, Math.PI * 2, false);
  }
}
if (L?.Canvas?.prototype && !L.Canvas.prototype._symbolPatched) {
  const orig = L.Canvas.prototype._updateCircle;
  L.Canvas.prototype._updateCircle = function (layer) {
    const sym = layer?.options?.symbol;
    if (!sym || sym === "circle") return orig.call(this, layer);
    if (!this._drawing || layer._empty()) return;
    const p = layer._point,
      r = Math.max(Math.round(layer._radius), 1);
    this._ctx.beginPath();
    symbolPath(this._ctx, sym, p.x, p.y, r);
    this._fillStroke(this._ctx, layer);
  };
  L.Canvas.prototype._symbolPatched = true;
}
// a small SVG of a symbol, for the assembly list / pickers
export function symbolSvg(sym, color = "#f97316", size = 14) {
  const pts = [];
  const ctx = {
    moveTo: (x, y) => pts.push(`M${x.toFixed(2)} ${y.toFixed(2)}`),
    lineTo: (x, y) => pts.push(`L${x.toFixed(2)} ${y.toFixed(2)}`),
    closePath: () => pts.push("Z"),
    rect: (x, y, w, h) =>
      pts.push(
        `M${x.toFixed(2)} ${y.toFixed(2)}h${w.toFixed(2)}v${h.toFixed(2)}h${(-w).toFixed(2)}Z`,
      ),
    arc: (x, y, r) =>
      pts.push(
        `M${x - r} ${y}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0`,
      ),
  };
  symbolPath(ctx, sym, 8, 8, 5.6);
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
      <path
        d={pts.join("")}
        fill={color}
        fillOpacity="0.85"
        stroke="#fff"
        strokeWidth="1"
      />
    </svg>
  );
}
// hatch fills for the canvas renderer (a CanvasPattern is a valid stroke / fill style) — one per colour
const _hatchCache = new Map();
export function hatchPattern(color, kind = "hatch") {
  const key = `${color}|${kind}`;
  if (_hatchCache.has(key)) return _hatchCache.get(key);
  let pat = color;
  try {
    const S = 10;
    const c = document.createElement("canvas");
    c.width = c.height = S;
    const g = c.getContext("2d");
    g.fillStyle = color;
    g.globalAlpha = 0.28;
    g.fillRect(0, 0, S, S);
    g.globalAlpha = 1;
    g.strokeStyle = color;
    g.lineWidth = 2;
    g.beginPath();
    g.moveTo(-1, S + 1);
    g.lineTo(S + 1, -1);
    g.moveTo(-1, 1);
    g.lineTo(1, -1);
    g.moveTo(S - 1, S + 1);
    g.lineTo(S + 1, S - 1);
    if (kind === "crosshatch") {
      g.moveTo(-1, -1);
      g.lineTo(S + 1, S + 1);
      g.moveTo(S - 1, -1);
      g.lineTo(S + 1, 1);
      g.moveTo(-1, S - 1);
      g.lineTo(1, S + 1);
    }
    g.stroke();
    pat = g.createPattern(c, "repeat") || color;
  } catch {
    pat = color;
  }
  _hatchCache.set(key, pat);
  return pat;
}

// ── lines drawn on a wall: moved onto the wall's centre ─────────────────────────────────────────────────
// For each straight piece of a hand-drawn line, the drawing's own lines running alongside it are found; when two of
// them make a wall (2"–16" apart) and the clicks were on one face or between the faces, the piece moves onto the
// centre of that wall. Corners are rebuilt as the meeting point of the moved pieces (mitred), so one line turns the
// corner and the band covers both walls right into the corner.
//   pts: [[lat,lng]…] map units · segsNear(q, r) → [[a,b]…] map units · unitsPerFt: map units per foot
//   fromSeg: only pieces from this index on move (a line being continued keeps what it had)
//   align: "auto" | "left" | "right" | "click" · bandFt: the assembly's line thickness (for left / right)
export function centerOnWalls(
  pts,
  { segsNear, unitsPerFt, fromSeg = 0, align = "auto", bandFt = 4 / 12 },
) {
  if (!pts || pts.length < 2 || !(unitsPerFt > 0) || align === "click")
    return pts;
  const n = pts.length - 1;
  const off = new Array(n).fill(0);
  const U = (inch) => (inch / 12) * unitsPerFt;
  for (let i = fromSeg; i < n; i++) {
    const a = pts[i],
      b = pts[i + 1];
    const dx = b[0] - a[0],
      dy = b[1] - a[1],
      L0 = Math.hypot(dx, dy);
    if (L0 < U(6)) continue;
    const ux = dx / L0,
      uy = dy / L0,
      nx = -uy,
      ny = ux; // a normal in (lat, lng) — on screen it points to the RIGHT of the drawing direction (lat is up)
    if (align === "left" || align === "right") {
      off[i] = (align === "left" ? -1 : 1) * ((bandFt * unitsPerFt) / 2);
      continue;
    }
    if (!segsNear) continue;
    // faces of the wall: drawing lines parallel to this piece, overlapping its middle, within 16"
    const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    const reach = Math.max(U(18), L0 / 2);
    const faces = [];
    for (const [p, q] of segsNear(mid, reach)) {
      const ex = q[0] - p[0],
        ey = q[1] - p[1],
        EL = Math.hypot(ex, ey);
      if (EL < U(3)) continue;
      if (Math.abs((ex * uy - ey * ux) / EL) > 0.07) continue; // not parallel (~4°)
      // overlap along the piece: the face must run past the middle 60 %
      const t0 = ((p[0] - a[0]) * ux + (p[1] - a[1]) * uy) / L0,
        t1 = ((q[0] - a[0]) * ux + (q[1] - a[1]) * uy) / L0;
      if (Math.max(t0, t1) < 0.2 || Math.min(t0, t1) > 0.8) continue;
      const d = (p[0] - a[0]) * nx + (p[1] - a[1]) * ny; // signed offset of the face
      if (Math.abs(d) > U(16)) continue;
      faces.push(d);
    }
    if (faces.length < 2) continue;
    faces.sort((x, y) => x - y);
    // the narrowest pair of faces (a wall: 2"–16") that has the clicks on it or between
    let best = null;
    for (let p = 0; p < faces.length; p++)
      for (let q = p + 1; q < faces.length; q++) {
        const w = faces[q] - faces[p];
        if (w < U(1.75) || w > U(16)) continue;
        if (faces[p] > U(1) || faces[q] < -U(1)) continue;
        if (!best || w < best.w) best = { w, c: (faces[p] + faces[q]) / 2 };
      }
    if (best && Math.abs(best.c) > U(0.25)) off[i] = best.c;
  }
  if (off.every((o) => !o)) return pts;
  // rebuild the points: each one where its two moved pieces meet (a mitre); ends just move with their piece
  const lineOf = (i) => {
    const a = pts[i],
      b = pts[i + 1];
    const L0 = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
    const nx = -(b[1] - a[1]) / L0,
      ny = (b[0] - a[0]) / L0;
    return {
      a: [a[0] + nx * off[i], a[1] + ny * off[i]],
      b: [b[0] + nx * off[i], b[1] + ny * off[i]],
    };
  };
  const meet = (s, t) => {
    const r = [s.b[0] - s.a[0], s.b[1] - s.a[1]],
      v = [t.b[0] - t.a[0], t.b[1] - t.a[1]];
    const den = r[0] * v[1] - r[1] * v[0];
    if (Math.abs(den) < 1e-9) return s.b; // straight on
    const k = ((t.a[0] - s.a[0]) * v[1] - (t.a[1] - s.a[1]) * v[0]) / den;
    return [s.a[0] + r[0] * k, s.a[1] + r[1] * k];
  };
  const ls = Array.from({ length: n }, (_, i) => lineOf(i));
  const out = pts.map((p) => [p[0], p[1]]);
  out[0] = ls[0].a;
  out[n] = ls[n - 1].b;
  for (let i = 1; i < n; i++) {
    const m = meet(ls[i - 1], ls[i]);
    // a mitre that shoots off (very sharp turn) — keep the corner where it was moved to
    out[i] =
      Math.hypot(m[0] - pts[i][0], m[1] - pts[i][1]) <= U(24) ? m : ls[i].a;
  }
  // a closed loop (end back on the start): the start / end corner is a mitre too
  if (
    n >= 3 &&
    Math.hypot(pts[0][0] - pts[n][0], pts[0][1] - pts[n][1]) < U(1)
  ) {
    const m = meet(ls[n - 1], ls[0]);
    out[0] = out[n] = m;
  }
  return out;
}

// ── door deductions shown as door swings (a leaf + its arc), so they never look like a door count ──────
//   seg: [[lat,lng],[lat,lng]] the opening · kind "single" | "double" · flip 0–3 (bit 1 = other side,
//   bit 2 = hinge at the other jamb) → [[lat,lng]…] polylines
export function doorSwingLines(seg, kind = "single", flip = 0) {
  const [A, B] = seg;
  const w = Math.hypot(B[0] - A[0], B[1] - A[1]);
  if (!(w > 0)) return [];
  const ux = (B[0] - A[0]) / w,
    uy = (B[1] - A[1]) / w;
  const side = flip & 1 ? -1 : 1;
  const nx = -uy * side,
    ny = ux * side;
  const swing = (H, dirx, diry, r, steps = 14) => {
    // hinge H, the leaf opens along (n), the arc runs from the open leaf back to the jamb along (dir)
    const leaf = [H, [H[0] + nx * r, H[1] + ny * r]];
    const arc = [];
    for (let i = 0; i <= steps; i++) {
      const t = (i / steps) * (Math.PI / 2);
      arc.push([
        H[0] + r * (Math.cos(t) * nx + Math.sin(t) * dirx),
        H[1] + r * (Math.cos(t) * ny + Math.sin(t) * diry),
      ]);
    }
    return [leaf, arc];
  };
  if (kind === "double") {
    const r = w / 2;
    return [...swing(A, ux, uy, r, 10), ...swing(B, -ux, -uy, r, 10)];
  }
  return flip & 2 ? swing(B, -ux, -uy, w) : swing(A, ux, uy, w);
}

// ── door headers: a line in front of every door deduction, on a header assembly of the wall's own assembly ──
// LF = door width, height = wall height − door height (a 7' door in a 9' wall → a 2' header). They're kept in
// step with the doors automatically (move, resize, delete a door → its header follows).
//   → { shapes, conditions, changed } — the lists with the headers brought up to date
export function planDoorHeaders(shapes, conditions, scales = {}) {
  const condById = new Map(conditions.map((c) => [c.id, c]));
  const r2 = (v) => Math.round(v * 100) / 100;
  const wantShapes = new Map();
  const wantConds = new Map(); // header cond id → parent cond
  for (const s of shapes) {
    if (s.kind !== "polyline" || s.meta?.headerOf) continue;
    const c = condById.get(s.conditionId);
    if (!c || c.type !== "linear" || isHeaderCond(c) || c.doorHeaders === false)
      continue;
    const segs = s.meta?.doorSegs || [];
    if (!segs.length || !(Number(s.meta?.doorLf) > 0)) continue;
    const h = Number(s.meta?.height) || Number(c.height) || 0;
    const dh = Number(s.meta?.doorHeight) || DEFAULT_DOOR_H;
    const hh = h ? Math.round((h - dh) * 100) / 100 : null;
    if (h && !(hh > 0.01)) continue; // door to the underside of the ceiling: no header
    const hcId = `hdr-${c.id}`;
    const f = scales[String(s.page)] ?? scales[s.page];
    // in front of the door: just off the wall band, on the side away from the swing
    const offU = f ? (condLineIn(c) / 2 + 2.5) / 12 / f : 0;
    segs.forEach((seg, i) => {
      if (!seg?.[0] || !seg?.[1]) return;
      const [A, B] = seg;
      const L0 = Math.hypot(B[0] - A[0], B[1] - A[1]);
      if (!(L0 > 0)) return;
      const flip = Number(s.meta?.doorFlips?.[i]) || 0;
      const side = flip & 1 ? 1 : -1; // opposite the swing
      const nx = (-(B[1] - A[1]) / L0) * side * offU,
        ny = ((B[0] - A[0]) / L0) * side * offU;
      const id = `hdr-${s.id}-${i}`;
      wantConds.set(hcId, c);
      wantShapes.set(id, {
        id,
        conditionId: hcId,
        page: s.page,
        kind: "polyline",
        latlngs: [
          [r2(A[0] + nx), r2(A[1] + ny)],
          [r2(B[0] + nx), r2(B[1] + ny)],
        ],
        meta: {
          source: "door-header",
          headerOf: s.id,
          door: i,
          hdrFor: c.id,
          doorKind: s.meta?.doorKinds?.[i] || "single",
          ...(hh ? { height: hh } : {}),
          ...(s.meta?.unitId ? { unitId: s.meta.unitId } : {}),
        },
      });
    });
  }
  let changed = false,
    shapesChanged = false;
  // shapes: drop headers whose door is gone, update / add the rest. A header moved to another header assembly by
  // hand stays there (until its wall changes assembly).
  const nextShapes = [];
  const seen = new Set();
  for (const s of shapes) {
    if (!s.meta?.headerOf) {
      nextShapes.push(s);
      continue;
    }
    const w = wantShapes.get(s.id);
    if (!w) {
      changed = true;
      continue;
    }
    seen.add(s.id);
    const keepCond =
      s.meta?.hdrFor === w.meta.hdrFor &&
      isHeaderCond(condById.get(s.conditionId))
        ? s.conditionId
        : w.conditionId;
    const upd = { ...w, conditionId: keepCond };
    if (JSON.stringify(upd) !== JSON.stringify(s)) {
      changed = true;
      nextShapes.push(upd);
    } else nextShapes.push(s);
  }
  for (const [id, w] of wantShapes)
    if (!seen.has(id)) {
      changed = true;
      nextShapes.push(w);
    }
  shapesChanged = changed;
  changed = false;
  // header assemblies: one per wall assembly that has doors — named after it (until renamed by hand)
  const nextConds = [];
  for (const c of conditions) {
    if (!isHeaderCond(c)) {
      nextConds.push(c);
      continue;
    }
    const parent = condById.get(String(c.wallType).slice(HEADER_WT.length));
    const used =
      wantConds.has(c.id) || nextShapes.some((x) => x.conditionId === c.id);
    if (
      !parent ||
      (!used && c.headerAuto !== false && c.id.startsWith("hdr-"))
    ) {
      changed = true;
      continue;
    }
    const name = `Header — ${parent.name}`;
    if (c.headerAuto !== false && c.name !== name) {
      changed = true;
      nextConds.push({ ...c, name });
    } else nextConds.push(c);
  }
  for (const [id, parent] of wantConds)
    if (!nextConds.some((c) => c.id === id)) {
      changed = true;
      nextConds.push({
        id,
        name: `Header — ${parent.name}`,
        type: "linear",
        wallType: `${HEADER_WT}${parent.id}`,
        height: null,
        color: parent.color || "#f97316",
        description: `Door headers over the doors in ${parent.name}\nLF = door width · height = wall height − door height`,
        headerAuto: true,
        linePattern: "hatch",
      });
    }
  return {
    shapes: shapesChanged ? nextShapes : shapes,
    conditions: changed ? nextConds : conditions,
    shapesChanged,
    condsChanged: changed,
    changed: shapesChanged || changed,
  };
}

// ── sheet revisions: match the pages of an updated set to the takeoff's sheets by sheet number / name ────
// The sheet number is the first token of the sheet's name that looks like one (A101, A-2.1, S3.01, M2A…).
const SHEET_NO_RE = /^[A-Z]{1,3}[-.]?\d{1,4}(?:\.\d{1,3})?[A-Z]?$/;
export function sheetNumberOf(name) {
  for (const tok of String(name || "")
    .toUpperCase()
    .split(/[\s—–,:;|/()]+/)) {
    const t = tok.replace(/[^A-Z0-9.-]/g, "");
    if (t && SHEET_NO_RE.test(t) && /\d/.test(t)) return t;
  }
  return null;
}
const squashNo = (s) =>
  String(s || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
//   newPages: [{ src, tokens:[{s, x, y}] (x,y 0–1 of the page, y down), title }] · sheets: [{ page, name }]
//   → Map(src → { page, score, why })  (each takeoff sheet used once — the best match wins)
export function matchRevisionPages(newPages, sheets) {
  const cands = [];
  for (const np of newPages) {
    const toks = np.tokens || [];
    for (const sh of sheets) {
      const no = sheetNumberOf(sh.name);
      let score = 0,
        why = "";
      if (no) {
        const want = squashNo(no);
        for (const t of toks) {
          if (squashNo(t.s) !== want) continue;
          // the title block is usually bottom-right: a hit there counts more
          const s = 6 + (t.x > 0.6 ? 2 : 0) + (t.y > 0.6 ? 2 : 0);
          if (s > score) {
            score = s;
            why = `sheet number ${no}`;
          }
        }
      }
      // the sheet's title words (FLOOR PLAN, LEVEL 2…) as a tie-break / fallback
      const words = String(sh.name || "")
        .toUpperCase()
        .replace(no || "\u0000", " ")
        .split(/[^A-Z0-9]+/)
        .filter((w) => w.length >= 3);
      if (words.length) {
        const have = new Set(toks.map((t) => squashNo(t.s)));
        const hit = words.filter((w) => have.has(w)).length / words.length;
        if (hit >= 0.6) {
          score += hit * 3;
          if (!why) why = "sheet title";
        }
      }
      if (score >= 3) cands.push({ src: np.src, page: sh.page, score, why });
    }
  }
  cands.sort((a, b) => b.score - a.score);
  const out = new Map(),
    used = new Set();
  for (const c of cands) {
    if (out.has(c.src) || used.has(c.page)) continue;
    out.set(c.src, c);
    used.add(c.page);
  }
  return out;
}

export function parseFeetInput(raw) {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim().toLowerCase().replace(/,/g, "");
  if (!s) return null;
  let m = s.match(
    /^(\d+(?:\.\d+)?)\s*(?:'|ft|feet)\s*-?\s*(?:(\d+(?:\.\d+)?)\s*(?:"|in|inches)?)?$/,
  );
  if (m) return parseFloat(m[1]) + (m[2] ? parseFloat(m[2]) / 12 : 0);
  m = s.match(/^(\d+(?:\.\d+)?)\s*(?:"|in|inches)$/);
  if (m) return parseFloat(m[1]) / 12;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

const fmtQty = (n, d = 1) =>
  n === null || n === undefined
    ? "—"
    : n.toLocaleString("en-US", {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      });

// 12.54 → 12'-6"
export function fmtFeetIn(feet) {
  if (feet === null || feet === undefined) return "—";
  let ft = Math.floor(feet);
  let inch = Math.round((feet - ft) * 12);
  if (inch === 12) {
    ft += 1;
    inch = 0;
  }
  return `${ft}'-${inch}"`;
}

function ringArea(latlngs) {
  let a = 0;
  for (let i = 0; i < latlngs.length; i++) {
    const [y1, x1] = latlngs[i];
    const [y2, x2] = latlngs[(i + 1) % latlngs.length];
    a += x1 * y2 - x2 * y1;
  }
  return Math.abs(a) / 2;
}

// The straight walls a drawn line is made of: it splits only where it turns more than ~15° (a point on a
// straight run doesn't split it). A line that closes back on its start is a loop (a box → 4 walls).
// Returns each wall's length in the same units as latlngs. Studs are counted per wall, each with its own
// start and end stud — so a corner gets a stud from both walls, like it's framed.
export function pathWalls(latlngs, closed = false, closeTol = 0) {
  const pts = [];
  for (const p of latlngs || []) {
    const q = pts[pts.length - 1];
    if (!q || Math.hypot(p[0] - q[0], p[1] - q[1]) > 1e-9) pts.push(p);
  }
  if (pts.length < 2) return [];
  let ring = closed;
  if (!ring && pts.length > 3) {
    const a = pts[0],
      b = pts[pts.length - 1];
    if (Math.hypot(a[0] - b[0], a[1] - b[1]) <= closeTol) {
      ring = true;
      pts.pop();
    }
  }
  const m = pts.length;
  const turns = (a, b, c) => {
    const ux = b[0] - a[0],
      uy = b[1] - a[1],
      vx = c[0] - b[0],
      vy = c[1] - b[1];
    const lu = Math.hypot(ux, uy),
      lv = Math.hypot(vx, vy);
    if (!lu || !lv) return false;
    return (ux * vx + uy * vy) / (lu * lv) < Math.cos((15 * Math.PI) / 180);
  };
  const seg = (i) =>
    Math.hypot(
      pts[(i + 1) % m][0] - pts[i][0],
      pts[(i + 1) % m][1] - pts[i][1],
    );
  if (!ring) {
    const walls = [];
    let cur = 0;
    for (let i = 0; i < m - 1; i++) {
      cur += seg(i);
      if (i + 1 < m - 1 && turns(pts[i], pts[i + 1], pts[i + 2])) {
        walls.push(cur);
        cur = 0;
      }
    }
    walls.push(cur);
    return walls.filter((w) => w > 0);
  }
  // a loop: count from a corner so no wall is split across the start point
  const corner = [];
  for (let i = 0; i < m; i++)
    if (turns(pts[(i - 1 + m) % m], pts[i], pts[(i + 1) % m])) corner.push(i);
  if (corner.length < 2) {
    let t = 0;
    for (let i = 0; i < m; i++) t += seg(i);
    return [t];
  }
  const walls = [];
  for (let k = 0; k < corner.length; k++) {
    const a = corner[k],
      b = corner[(k + 1) % corner.length];
    let t = 0;
    for (let i = a; i !== b; i = (i + 1) % m) t += seg(i);
    walls.push(t);
  }
  return walls.filter((w) => w > 0);
}
function pathLength(latlngs, closed = false) {
  let d = 0;
  const n = closed ? latlngs.length : latlngs.length - 1;
  for (let i = 0; i < n; i++) {
    const [y1, x1] = latlngs[i];
    const [y2, x2] = latlngs[(i + 1) % latlngs.length];
    d += Math.hypot(x2 - x1, y2 - y1);
  }
  return d;
}

function shapePrimaryQty(shape, cond, fpu) {
  if (cond.type === "count") return { qty: 1, unit: "EA" };
  if (!fpu) return { qty: null, unit: cond.type === "area" ? "SF" : "LF" };
  if (cond.type === "area")
    return { qty: ringArea(shape.latlngs) * fpu * fpu, unit: "SF" };
  return {
    qty: pathLength(shape.latlngs, shape.kind === "polygon") * fpu,
    unit: "LF",
  };
}

// A wall run's net quantities: gross LF less its windows and doors; SF = LF × height less window area
// (width × window height, or the full wall height when none is set) and door area (width × door height,
// 7'-0" unless set).
export const DEFAULT_DOOR_H = 7;
export const DEFAULT_DOOR_W = 38 / 12; // 3'-2" (3'-0" door + frame)
export const DEFAULT_DOUBLE_DOOR_W = 74 / 12; // 6'-2" pair
// ── saving / several people on one takeoff ──
// Numbers in shapes to 0.01 (far below a pixel) and empty (null) meta fields dropped: half the size, and the
// same shape always serialises the same way (so "did it change?" is a string compare).
const r2n = (v) => (Number.isInteger(v) ? v : Math.round(v * 100) / 100);
const deepSlim = (v) =>
  typeof v === "number"
    ? r2n(v)
    : Array.isArray(v)
      ? v.map(deepSlim)
      : v && typeof v === "object"
        ? Object.fromEntries(
            Object.entries(v)
              .filter(([, x]) => x != null)
              .map(([k, x]) => [k, deepSlim(x)]),
          )
        : v;
export const slimShape = (sh) => ({
  ...sh,
  latlngs: deepSlim(sh.latlngs),
  ...(sh.meta ? { meta: deepSlim(sh.meta) } : {}),
});
// whole-value fields of a takeoff (the last save wins per field); shapes and conditions merge item by item
export const SYNC_KEYS = [
  "fileName",
  "gcsPath",
  "status",
  "pageCount",
  "scales",
  "pageNames",
  "excludedPages",
  "pageBidAreas",
  "proposals",
  "wallTagSettings",
  "assemblies",
  "traceSettings",
  "units",
  "scaleInfo",
  "coPins",
  "sheetRevisions",
];
const sjson = (v) => JSON.stringify(v ?? null);
// the signed-in person's name, as the app shows it (App.jsx keeps it in localStorage) — sent with saves so the
// change log says who; the server prefers the name it knows from the login token
export const myName = () =>
  localStorage.getItem("userDisplayName") ||
  [localStorage.getItem("givenName"), localStorage.getItem("surname")]
    .filter(Boolean)
    .join(" ") ||
  "";
// what the server has, as this screen last saw it: { rev, shapes: Map(id→json), conditions: Map(id→json), keys }
export function syncSnapshot(payload, rev = 0) {
  return {
    rev,
    shapes: new Map((payload.shapes || []).map((x) => [x.id, sjson(x)])),
    conditions: new Map(
      (payload.conditions || []).map((x) => [x.id, sjson(x)]),
    ),
    keys: Object.fromEntries(SYNC_KEYS.map((k) => [k, sjson(payload[k])])),
    applied: new Set(),
  };
}
// what this screen changed since then → ops for the server (shapes / conditions one by one, fields whole)
export function opsSince(sync, payload) {
  const ops = {
    shapes: { upsert: [], remove: [] },
    conditions: { upsert: [], remove: [] },
    set: {},
  };
  for (const key of ["shapes", "conditions"]) {
    const cur = new Map((payload[key] || []).map((x) => [x.id, x]));
    for (const [id, x] of cur)
      if (sync[key].get(id) !== sjson(x)) ops[key].upsert.push(x);
    for (const id of sync[key].keys())
      if (!cur.has(id)) ops[key].remove.push(id);
  }
  for (const k of SYNC_KEYS)
    if (payload[k] !== undefined && sync.keys[k] !== sjson(payload[k]))
      ops.set[k] = payload[k];
  const empty =
    !ops.shapes.upsert.length &&
    !ops.shapes.remove.length &&
    !ops.conditions.upsert.length &&
    !ops.conditions.remove.length &&
    !Object.keys(ops.set).length;
  return { ops, empty };
}
// a list with other people's changes merged in, item by item; items changed here and not saved yet are kept
export function mergeById(list, changes, keepIds = new Set()) {
  if (!changes.size) return list;
  const out = [];
  const seen = new Set();
  for (const x of list) {
    seen.add(x.id);
    const ch = changes.get(x.id);
    if (!ch || keepIds.has(x.id)) out.push(x);
    else if (!ch.remove) out.push(ch.obj);
  }
  for (const [id, ch] of changes)
    if (!seen.has(id) && !ch.remove) out.push(ch.obj);
  return out;
}

// the door sizes set in the wizard (feet) — every traced or hand-added door is deducted at these
export function doorSizes(settings) {
  const p = (v, d) => {
    const x = parseFeetInput(v ?? "");
    return x && x > 0 ? x : d;
  };
  return {
    single: p(settings?.doorWidth, DEFAULT_DOOR_W),
    double: p(settings?.doubleDoorWidth, DEFAULT_DOUBLE_DOOR_W),
    height: p(settings?.doorHeight, DEFAULT_DOOR_H),
  };
}
// a traced line's doors at the set sizes (single / double kept from the drawing)
export const sizedDoors = (ch, ds) =>
  (ch.doors || []).map((_, i) =>
    (ch.doorKinds?.[i] || "single") === "double" ? ds.double : ds.single,
  );

// Wall tags, checked wall by wall. The engine types a whole traced line from any tag on its straight line;
// here every wall PIECE of a line is typed from the tags that actually TOUCH it — the touching tag wins:
//   • a line is cut where it turns AND where another wall meets it (a tee / demising wall): a wall can change type
//     there (MS.12b on one side, MS.18b round the corner or past the tee), so the type is never carried through;
//   • a tag touches a piece when its arrow / leader lands on it, or its symbol sits against it (within the wall's
//     half-thickness + 4"); one tag printed in several places counts once per place, an arrow counts triple;
//   • at a corner a tag belongs to the wall it is closest to (exactly on the corner point: neither);
//   • one tag touching (or ≥ 60 % of the touching weight) → that type, solid; two tags close → stronger one, dashed
//     ("check"), the other offered as the alternative;
//   • nothing touching → the nearest touching type along the SAME straight wall (solid within 10', dashed beyond);
//     else tags printed near it (within 2'); else the line's own tag, dashed. Never across a turn.
// Neighbouring pieces that come out the same are joined back; doors / windows go with the piece they sit on.
// Wall thickness is NOT used here (tags decide); it only types walls with no tag at all.
//   lines: traced lines [{tag, pts, lf, run, windows, windowSegs, doors, doorSegs, doorKinds, …}] sheet units
//   → the same lines, split where needed, each with .tag and .tagEv = { src: "tag" | "check", reasons, alt }
const FAR_TAG_FT = 10;
export function retagLinesByEvidence(
  lines,
  { tagCands = [], runs = [], ftPerUnit, has },
) {
  const U = (inch) => inch / 12 / ftPerUnit;
  const cands = tagCands.filter((t) => has(t.text));
  const segInfo = (a, b, p) => {
    const dx = b[0] - a[0],
      dy = b[1] - a[1],
      L2 = dx * dx + dy * dy || 1e-12,
      L = Math.sqrt(L2);
    const k = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2,
      kc = Math.max(0, Math.min(1, k));
    return {
      d: Math.hypot(p[0] - a[0] - kc * dx, p[1] - a[1] - kc * dy),
      along: k * L,
      L,
    };
  };
  const turn = (a, b, c) => {
    const ux = b[0] - a[0],
      uy = b[1] - a[1],
      vx = c[0] - b[0],
      vy = c[1] - b[1];
    const cos =
      (ux * vx + uy * vy) / (Math.hypot(ux, uy) * Math.hypot(vx, vy) || 1);
    return cos < Math.cos((15 * Math.PI) / 180);
  };
  const dist = (p, q) => Math.hypot(p[0] - q[0], p[1] - q[1]);
  const lerp = (a, b, k) => [
    a[0] + (b[0] - a[0]) * k,
    a[1] + (b[1] - a[1]) * k,
  ];
  const mid = ([a, b]) => lerp(a, b, 0.5);
  const lenOf = (ps) =>
    ps.reduce((s, p, i) => (i ? s + dist(p, ps[i - 1]) : 0), 0);

  // tags: grid index (sheet units) so each wall only looks at the tags around it
  const CB = U(48);
  const grid = new Map();
  for (const t of cands) {
    const k = `${Math.floor(t.x / CB)},${Math.floor(t.y / CB)}`;
    if (!grid.has(k)) grid.set(k, []);
    grid.get(k).push(t);
  }
  const candsNear = (a, b, r) => {
    const x0 = Math.floor((Math.min(a[0], b[0]) - r) / CB),
      x1 = Math.floor((Math.max(a[0], b[0]) + r) / CB),
      y0 = Math.floor((Math.min(a[1], b[1]) - r) / CB),
      y1 = Math.floor((Math.max(a[1], b[1]) + r) / CB);
    const out = [];
    for (let i = x0; i <= x1; i++)
      for (let j = y0; j <= y1; j++)
        for (const t of grid.get(`${i},${j}`) || []) out.push(t);
    return out;
  };
  // every line end: where another wall meets a line (a corner or a tee)
  const ENDS = [];
  for (const l of lines) {
    const p = l.pts || [];
    if (p.length >= 2) ENDS.push({ p: p[0], l }, { p: p[p.length - 1], l });
  }

  const out = [];
  for (const ch of lines) {
    const pts0 = ch.pts || [];
    if (pts0.length < 2 || ch.loose) {
      out.push(ch);
      continue;
    }
    const th = runs[ch.run]?.th || U(5);
    const touchD = th / 2 + U(4); // a tag against the wall's face (or its arrow landing on it)
    const nearD = Math.max(U(24), th * 1.5); // printed near the wall
    const edgeM = U(12); // a tee closer than this to a corner / end isn't a cut

    // ── cut points: turns, plus tees (another line's end on this line's straight run)
    const pts = [pts0[0]];
    const cutAt = [0]; // indices into pts that are cuts
    const turnCut = new Set([0]); // cuts that are corners (vs tees)
    for (let i = 1; i < pts0.length; i++) {
      const a = pts0[i - 1],
        b = pts0[i],
        L = dist(a, b);
      // tees on this segment
      const tees = [];
      for (const e of ENDS) {
        if (e.l === ch) continue;
        const g = segInfo(a, b, e.p);
        if (g.d > th / 2 + U(3) || g.along < edgeM || g.along > L - edgeM)
          continue;
        if (!tees.some((t) => Math.abs(t - g.along) < edgeM))
          tees.push(g.along);
      }
      tees.sort((x, y) => x - y);
      for (const t of tees) {
        pts.push(lerp(a, b, t / L));
        cutAt.push(pts.length - 1);
      }
      pts.push(b);
      if (i === pts0.length - 1) {
        cutAt.push(pts.length - 1);
        turnCut.add(pts.length - 1);
      } else if (turn(a, b, pts0[i + 1])) {
        cutAt.push(pts.length - 1);
        turnCut.add(pts.length - 1);
      }
    }
    // the polyline between two cuts is one straight piece (no turn inside it)
    const pieces = [];
    for (let w = 0; w + 1 < cutAt.length; w++) {
      const i0 = cutAt[w],
        i1 = cutAt[w + 1];
      pieces.push({
        i0,
        i1,
        a: pts[i0],
        b: pts[i1],
        // which ends are corners of this line (a tag there could mean the other wall)
        cornerA: i0 > 0 && turnCut.has(i0),
        cornerB: i1 < pts.length - 1 && turnCut.has(i1),
        // a straight-on neighbour (same wall, cut only by a tee) — evidence may carry along it, never round a turn
        straightPrev: w > 0 && !turnCut.has(i0),
        straightNext: w + 2 < cutAt.length && !turnCut.has(i1),
      });
    }
    // ── what touches each piece
    for (let w = 0; w < pieces.length; w++) {
      const pc = pieces[w];
      const L = dist(pc.a, pc.b);
      const touch = new Map(),
        near = new Map();
      const placesT = new Map(); // text → [[x,y]] counted already (one printed tag = one vote)
      for (const t of candsNear(pc.a, pc.b, nearD)) {
        const g = segInfo(pc.a, pc.b, [t.x, t.y]);
        if (g.d > nearD || g.along < -U(3) || g.along > L + U(3)) continue;
        // at a corner: the tag goes with the wall it is closest to; exactly on the corner point, with neither
        const adj =
          g.along < U(12) && pc.cornerA
            ? pieces[w - 1]
            : g.along > L - U(12) && pc.cornerB
              ? pieces[w + 1]
              : null;
        if (adj) {
          const d2 = segInfo(adj.a, adj.b, [t.x, t.y]).d;
          if (d2 < g.d - U(1) || Math.abs(d2 - g.d) <= U(1)) continue;
        }
        if (g.d <= touchD && (t.touch || t.arrow || t.touch === undefined)) {
          const seen = placesT.get(t.text) || [];
          if (seen.some((p) => dist(p, [t.x, t.y]) < U(18))) continue;
          seen.push([t.x, t.y]);
          placesT.set(t.text, seen);
          touch.set(t.text, (touch.get(t.text) || 0) + (t.arrow ? 3 : 1));
        } else
          near.set(t.text, (near.get(t.text) || 0) + 1 / (1 + g.d * ftPerUnit));
      }
      pc.touch = [...touch.entries()].sort((x, y) => y[1] - x[1]);
      pc.near = [...near.entries()].sort((x, y) => y[1] - x[1]);
      pc.L = L;
    }
    const decide = (ranked, minShare) => {
      const tot = ranked.reduce((s, [, v]) => s + v, 0);
      const share = ranked[0][1] / tot;
      return share >= minShare
        ? {
            tag: ranked[0][0],
            ev: { src: "tag", reasons: [], alt: ranked[1]?.[0] || null },
          }
        : {
            tag: ranked[0][0],
            ev: {
              src: "check",
              reasons: [
                `tags ${ranked
                  .slice(0, 3)
                  .map(([t]) => t)
                  .join(" and ")} both touch this wall`,
              ],
              alt: ranked[1][0],
            },
          };
    };
    for (const pc of pieces)
      if (pc.touch.length) Object.assign(pc, decide(pc.touch, 0.6));
    // no tag touching: the nearest touching type on the same straight wall (across tees only, not turns)
    for (let w = 0; w < pieces.length; w++) {
      const pc = pieces[w];
      if (pc.tag) continue;
      let best = null;
      for (const dir of [-1, 1]) {
        let k = w,
          gap = 0;
        while (true) {
          const cur = pieces[k];
          if (dir < 0 ? !cur.straightPrev : !cur.straightNext) break;
          k += dir;
          const nb = pieces[k];
          if (nb.touch.length) {
            if (!best || gap < best.gap) best = { gap, nb };
            break;
          }
          gap += nb.L;
        }
      }
      const nearSameOf = (tag) => {
        let d = Infinity;
        for (const t of candsNear(pc.a, pc.b, nearD + U(FAR_TAG_FT * 12)))
          if (t.text === tag)
            d = Math.min(d, segInfo(pc.a, pc.b, [t.x, t.y]).d);
        return d;
      };
      if (best) {
        const far =
          best.gap > U(FAR_TAG_FT * 12) &&
          nearSameOf(best.nb.tag) > nearD + U(FAR_TAG_FT * 12);
        pc.tag = best.nb.tag;
        pc.ev = far
          ? {
              src: "check",
              reasons: [
                `no tag on this wall — ${pc.tag} carried from ${Math.round(best.gap * ftPerUnit)}' along it`,
              ],
              alt: null,
            }
          : {
              src: best.nb.ev.src,
              reasons: [...best.nb.ev.reasons],
              alt: best.nb.ev.alt,
            };
        pc.carried = true;
      } else if (pc.near.length) Object.assign(pc, decide(pc.near, 0.75));
      else if (ch.tag && has(ch.tag)) {
        // the line's own tag: solid when that tag is printed within 10' of this wall, dashed beyond
        const nearSame = nearSameOf(ch.tag);
        pc.tag = ch.tag;
        pc.ev =
          nearSame <= nearD + U(FAR_TAG_FT * 12)
            ? { src: "tag", reasons: [], alt: null }
            : {
                src: "check",
                reasons: [
                  Number.isFinite(nearSame)
                    ? `no tag on this wall — ${ch.tag} carried from ${Math.round(nearSame * ftPerUnit)}' away`
                    : `no tag on this wall — ${ch.tag} carried along the line`,
                ],
                alt: null,
              };
      } else pc.tag = null;
    }
    // join neighbouring pieces that came out the same
    const groups = [];
    for (const pc of pieces) {
      const g = groups[groups.length - 1];
      const src = pc.ev?.src || null;
      if (g && g.tag === pc.tag && g.src === src) {
        g.i1 = pc.i1;
        if (pc.ev) {
          g.ev.reasons = [...new Set([...g.ev.reasons, ...pc.ev.reasons])];
          g.ev.alt = g.ev.alt || pc.ev.alt;
        }
      } else
        groups.push({
          i0: pc.i0,
          i1: pc.i1,
          tag: pc.tag,
          src,
          ev: pc.ev ? { ...pc.ev, reasons: [...pc.ev.reasons] } : null,
        });
    }
    if (groups.length === 1) {
      const g = groups[0];
      out.push(
        g.ev
          ? { ...ch, tag: g.tag, tagEv: g.ev }
          : { ...ch, tag: g.tag ?? ch.tag },
      );
      continue;
    }
    // split: doors / windows go with the piece their middle sits on
    const groupOf = (seg) => {
      const m = mid(seg);
      let best = 0,
        bd = Infinity;
      groups.forEach((g, gi) => {
        for (let i = g.i0; i < g.i1; i++) {
          const d = segInfo(pts[i], pts[i + 1], m).d;
          if (d < bd) {
            bd = d;
            best = gi;
          }
        }
      });
      return best;
    };
    const total = lenOf(pts) || 1;
    groups.forEach((g, gi) => {
      const gp = pts.slice(g.i0, g.i1 + 1);
      const pick = (segs, vals, extra) => {
        const S = [],
          V = [],
          X = [];
        (segs || []).forEach((sg, k) => {
          if (groupOf(sg) === gi) {
            S.push(sg);
            V.push(vals?.[k]);
            if (extra) X.push(extra[k]);
          }
        });
        return [S, V, X];
      };
      const [ws, wv] = pick(ch.windowSegs, ch.windows);
      const [dsg, dv, dk] = pick(ch.doorSegs, ch.doors, ch.doorKinds || []);
      const [, dns] = pick(ch.doorSegs, ch.doorNoSwing || []);
      const f = lenOf(gp) / total;
      out.push({
        ...ch,
        tag: g.tag,
        ...(g.ev ? { tagEv: g.ev } : {}),
        pts: gp,
        lf: lenOf(gp) * ftPerUnit,
        runLf: Object.fromEntries(
          Object.entries(ch.runLf || {}).map(([k, v]) => [k, v * f]),
        ),
        windowSegs: ws,
        windows: ch.windowSegs
          ? wv
          : (ch.windows || []).length && gi === 0
            ? ch.windows
            : [],
        doorSegs: dsg,
        doors: dv,
        doorKinds: dk,
        doorNoSwing: dns,
      });
    });
  }
  return out;
}

export function wallNet(shape, cond, runLf) {
  const m = shape.meta || {};
  const h = Number(m.height) || Number(cond?.height) || 0;
  const winLf = Math.min(runLf, Number(m.deductLf) || 0);
  const doorLf = Math.min(Math.max(0, runLf - winLf), Number(m.doorLf) || 0);
  const wh = m.windowHeight ? Number(m.windowHeight) : h;
  const dh = m.doorHeight ? Number(m.doorHeight) : DEFAULT_DOOR_H;
  const winSf =
    m.deductLf != null
      ? winLf * Math.min(wh, h || wh)
      : Number(m.deductSf) || 0; // (older shapes stored deductSf)
  const doorSf = doorLf * Math.min(dh, h || dh);
  const dSf = winSf + doorSf;
  return {
    h,
    lf: runLf - winLf - doorLf,
    sf: Math.max(0, runLf * h - dSf),
    dLf: winLf + doorLf,
    dSf,
    winLf,
    winSf,
    doorLf,
    doorSf,
  };
}

// typical units: a shape that belongs to a unit counts once per unit (unit qty, default 1)
export function unitQty(shape, units) {
  const id = shape?.meta?.unitId;
  if (!id || !units?.length) return 1;
  const u = units.find((x) => x.id === id);
  return u ? Math.max(0, Number(u.qty) || 0) : 1;
}

// Full metrics for one condition. Shapes on excluded pages are ignored; shapes in a typical unit count
// unit-qty times.
// Full metrics for one condition. Shapes on excluded pages are ignored; shapes in a typical unit count
// unit-qty times.  Also reports the exact wall height(s) the runs were traced with.
export function computeCondMetrics(
  cond,
  shapes,
  scales,
  excludedPages = [],
  units = [],
) {
  const excluded = new Set(excludedPages);
  const condShapes = shapes.filter(
    (s) => s.conditionId === cond.id && !excluded.has(s.page),
  );
  const count = condShapes.reduce((n, s) => n + unitQty(s, units), 0);
  if (cond.type === "count") {
    // a count with a height (corner bead: one per outside corner) also gives LF = Σ height
    let lfc = 0,
      any = false;
    for (const s of condShapes) {
      const h = Number(s.meta?.height) || Number(cond.height) || 0;
      if (h) any = true;
      lfc += h * unitQty(s, units);
    }
    return {
      count,
      lf: any ? lfc : null,
      sf: null,
      shapeLfs: [],
      shapeWalls: [],
      uncal: false,
      shapes: condShapes.length,
    };
  }
  let lf = 0;
  let areaSf = 0;
  let wallSf = 0;
  let missingHeight = false;
  const shapeLfs = [];
  const shapeWalls = []; // per run (same order as shapeLfs): its straight walls in LF — studs are counted per wall
  let uncal = false;
  for (const s of condShapes) {
    const fpu = scales[String(s.page)] ?? scales[s.page];
    if (!fpu) {
      uncal = true;
      continue;
    }
    const q = unitQty(s, units);
    const runLf = pathLength(s.latlngs, s.kind === "polygon") * fpu;
    const walls = pathWalls(s.latlngs, s.kind === "polygon", 0.5 / fpu).map(
      (w) => w * fpu,
    ); // closed if the end is within 6"
    for (let i = 0; i < Math.round(q); i++) {
      shapeLfs.push(runLf);
      shapeWalls.push(walls);
    }
    if (s.kind === "polygon") {
      lf += runLf * q;
      areaSf += ringArea(s.latlngs) * fpu * fpu * q;
      continue;
    }
    // each wall run at its own height (set per floor when traced, adjustable per wall), less its windows and doors
    const n = wallNet(s, cond, runLf);
    lf += n.lf * q;
    if (!n.h) missingHeight = true;
    wallSf += n.sf * q;
  }
  if (uncal)
    return {
      count,
      lf: null,
      sf: null,
      shapeLfs: [],
      shapeWalls: [],
      uncal: true,
      shapes: condShapes.length,
    };
  let sf = null;
  if (cond.type === "area") sf = areaSf;
  else if (cond.type === "linear")
    sf = condShapes.length
      ? missingHeight
        ? null
        : wallSf
      : cond.height
        ? 0
        : null;
  return {
    count,
    lf,
    sf,
    shapeLfs,
    shapeWalls,
    uncal: false,
    shapes: condShapes.length,
  };
}

// the point half-way along a line ([lat,lng])
export function lineMidpoint(ll) {
  let tot = 0;
  for (let i = 0; i + 1 < ll.length; i++)
    tot += Math.hypot(ll[i + 1][0] - ll[i][0], ll[i + 1][1] - ll[i][1]);
  let half = tot / 2;
  for (let i = 0; i + 1 < ll.length; i++) {
    const d = Math.hypot(ll[i + 1][0] - ll[i][0], ll[i + 1][1] - ll[i][1]);
    if (half <= d && d > 0) {
      const k = half / d;
      return [
        ll[i][0] + (ll[i + 1][0] - ll[i][0]) * k,
        ll[i][1] + (ll[i + 1][1] - ll[i][1]) * k,
      ];
    }
    half -= d;
  }
  return ll[0];
}

// the nearest point of a line to p → { i (segment), t (0..1 along it), p: [lat,lng], d (distance) }
export function projectOnLine(ll, p) {
  let best = null;
  for (let i = 0; i + 1 < ll.length; i++) {
    const a = ll[i],
      b = ll[i + 1];
    const vx = b[0] - a[0],
      vy = b[1] - a[1];
    const L2 = vx * vx + vy * vy;
    const t =
      L2 > 0
        ? Math.max(
            0,
            Math.min(1, ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / L2),
          )
        : 0;
    const q = [a[0] + vx * t, a[1] + vy * t];
    const d = Math.hypot(p[0] - q[0], p[1] - q[1]);
    if (!best || d < best.d) best = { i, t, p: q, d };
  }
  return best;
}
// distance along a line (from its first point) of a projection {i, t}
export function alongLine(ll, proj) {
  let s = 0;
  for (let i = 0; i < proj.i; i++)
    s += Math.hypot(ll[i + 1][0] - ll[i][0], ll[i + 1][1] - ll[i][1]);
  const a = ll[proj.i],
    b = ll[proj.i + 1] || a;
  return s + proj.t * Math.hypot(b[0] - a[0], b[1] - a[1]);
}
// the piece of a line between two distances along it, its own points kept in between
export function lineBetween(ll, s0, s1) {
  const out = [];
  let acc = 0;
  const at = (i, d) => {
    const a = ll[i],
      b = ll[i + 1];
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
    const k = Math.max(0, Math.min(1, (d - acc) / L));
    return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k];
  };
  for (let i = 0; i + 1 < ll.length; i++) {
    const L = Math.hypot(ll[i + 1][0] - ll[i][0], ll[i + 1][1] - ll[i][1]);
    const e = acc + L;
    if (e >= s0 && acc <= s1) {
      if (!out.length) out.push(at(i, Math.max(s0, acc)));
      if (e <= s1) out.push([ll[i + 1][0], ll[i + 1][1]]);
      else out.push(at(i, s1));
    }
    acc = e;
    if (acc > s1) break;
  }
  // (no zero-length steps)
  return out.filter(
    (p, i) =>
      !i || Math.hypot(p[0] - out[i - 1][0], p[1] - out[i - 1][1]) > 1e-9,
  );
}
// Cut a wall line into pieces at distances along it. Each piece keeps the line's meta (height, tag, unit…); its
// windows and doors go to the piece their middle sits on, with the LF deducted re-added per piece.
//   cuts: distances along the line (map units) · f: feet per map unit (for window / door lengths with no LF stored)
export function splitLineMeta(shape, cuts, f) {
  const ll = shape.latlngs;
  let total = 0;
  for (let i = 0; i + 1 < ll.length; i++)
    total += Math.hypot(ll[i + 1][0] - ll[i][0], ll[i + 1][1] - ll[i][1]);
  const stops = [
    0,
    ...cuts.filter((c) => c > 0 && c < total).sort((a, b) => a - b),
    total,
  ];
  const m = shape.meta || {};
  const midS = (sg) =>
    alongLine(
      ll,
      projectOnLine(ll, [(sg[0][0] + sg[1][0]) / 2, (sg[0][1] + sg[1][1]) / 2]),
    );
  const segLf = (sg) =>
    f ? Math.hypot(sg[1][0] - sg[0][0], sg[1][1] - sg[0][1]) * f : 0;
  const out = [];
  for (let k = 0; k + 1 < stops.length; k++) {
    const a = stops[k],
      b = stops[k + 1];
    if (b - a <= 1e-9) continue;
    const pts = lineBetween(ll, a, b);
    if (pts.length < 2) continue;
    const inside = (sg) => {
      const sm = midS(sg);
      return sm >= a && (sm < b || (k + 2 === stops.length && sm <= b));
    };
    const meta = { ...m };
    if (m.windowSegs) {
      const idx = m.windowSegs
        .map((sg, i) => (inside(sg) ? i : -1))
        .filter((i) => i >= 0);
      meta.windowSegs = idx.map((i) => m.windowSegs[i]);
      meta.windows = idx.map((i) => m.windows?.[i] ?? segLf(m.windowSegs[i]));
      if (m.deductLf != null)
        meta.deductLf =
          Math.round(
            meta.windows.reduce((t, v) => t + (Number(v) || 0), 0) * 100,
          ) / 100;
    }
    if (m.doorSegs) {
      const idx = m.doorSegs
        .map((sg, i) => (inside(sg) ? i : -1))
        .filter((i) => i >= 0);
      meta.doorSegs = idx.map((i) => m.doorSegs[i]);
      meta.doors = idx.map((i) => m.doors?.[i] ?? segLf(m.doorSegs[i]));
      if (m.doorKinds)
        meta.doorKinds = idx.map((i) => m.doorKinds[i] || "single");
      if (m.doorFlips) meta.doorFlips = idx.map((i) => m.doorFlips[i] || 0);
      if (m.doorLf != null)
        meta.doorLf =
          Math.round(
            meta.doors.reduce((t, v) => t + (Number(v) || 0), 0) * 100,
          ) / 100;
    }
    out.push({ s0: a, s1: b, latlngs: pts, meta });
  }
  return out;
}
// a deduction `len` long (map units) centred on a point of the line, kept on that straight wall
export function segOnLine(ll, proj, len) {
  const a = ll[proj.i],
    b = ll[proj.i + 1];
  const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (!(L > 0)) return [proj.p, proj.p];
  const ux = (b[0] - a[0]) / L,
    uy = (b[1] - a[1]) / L;
  const half = Math.min(len, L) / 2;
  let c = proj.t * L;
  c = Math.max(half, Math.min(L - half, c)); // the whole opening stays on this wall
  return [
    [a[0] + ux * (c - half), a[1] + uy * (c - half)],
    [a[0] + ux * (c + half), a[1] + uy * (c + half)],
  ];
}

// markers for the count assemblies: the middle of every door (single / double by width) and window on the
// wall lines, and every outside corner of the rooms. Points in sheet units.
export function countMarks({
  chains,
  rooms,
  ftPerUnit,
  corners = true,
  doors = [],
  cornerLines = null,
}) {
  const out = [];
  const mid = ([a, b]) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  for (const ch of chains || []) {
    (ch.doorSegs || []).forEach((seg, i) =>
      out.push({
        key: (ch.doorKinds?.[i] || "single") === "double" ? "door2" : "door",
        p: mid(seg),
        lf: ch.doors?.[i],
        noSwing: !!ch.doorNoSwing?.[i],
      }),
    );
    (ch.windowSegs || []).forEach((seg, i) =>
      out.push({ key: "window", p: mid(seg), lf: ch.windows?.[i] }),
    );
  }
  if (corners)
    for (const p of outsideCorners(rooms, {
      ftPerUnit,
      doors,
      doorSegs: (chains || []).flatMap((c) => c.doorSegs || []),
      lines:
        cornerLines || (chains || []).map((c) => ({ pts: c.pts, th: c.th })),
    }))
      out.push({ key: "bead", p });
  return out;
}

// Doors / windows the trace may have missed (points in sheet units):
//   • a door swing drawn on the plan with no counted door at it,
//   • a door tag (D1, D-12, …) or window tag (W1, …) with no counted door / window near it.
// Tags that are wall types being traced (a "W1" wall) are skipped.
//   arcs: engine door swings [{hx,hy,cx,cy,r}] · marks: counted [{key, p}] · texts: sheet text [{s,x0,y0,x1,y1}]
export const DOOR_TAG_RE = /^D[-.]?\d{1,3}[A-Z]?$/i;
export const WINDOW_TAG_RE = /^(?:W|WIN|WDW|CW)[-.]?\d{1,3}[A-Z]{0,2}$/i; // W1, W-2, W12C, WIN3, CW4
export function openingSuspects({
  arcs = [],
  marks = [],
  texts = [],
  ftPerUnit,
  wallTags = new Set(),
  gaps = [],
}) {
  const U = (ft) => ft / ftPerUnit;
  const doorsAt = marks
    .filter((m) => m.key === "door" || m.key === "door2")
    .map((m) => m.p);
  const winsAt = marks.filter((m) => m.key === "window").map((m) => m.p);
  const near = (list, p, d) =>
    list.some((q) => Math.hypot(q[0] - p[0], q[1] - p[1]) <= d);
  const out = [];
  const add = (x) => {
    if (
      !out.some(
        (o) =>
          o.kind === x.kind &&
          Math.hypot(o.p[0] - x.p[0], o.p[1] - x.p[1]) < U(3),
      )
    )
      out.push(x);
  };
  // door-sized gaps the trace left out (beside another door, no swing of their own read)
  for (const g of gaps) {
    const mid = [(g.a[0] + g.b[0]) / 2, (g.a[1] + g.b[1]) / 2];
    if (!near(doorsAt, mid, U(2)))
      add({
        kind: "door",
        p: mid,
        hint: "door-sized gap in a wall, no door swing read there",
      });
  }
  for (const a of arcs) {
    const rFt = a.r * ftPerUnit;
    if (rFt < 1.5 || rFt > 4.5) continue; // a door leaf is 1½'–4½'
    const mid = [(a.hx + a.cx) / 2, (a.hy + a.cy) / 2]; // middle of the opening (hinge → closed leaf end)
    if (!near(doorsAt, mid, a.r + U(1.5)))
      add({
        kind: "door",
        p: mid,
        hint: "door swing drawn here but not counted",
      });
  }
  // a tag with a line of text right after it on the same row is a schedule / legend entry ("D1  3'-2" FRAME…")
  const isLegend = (t) => {
    const h = t.y1 - t.y0 || 1;
    return texts.some(
      (o) =>
        o !== t &&
        String(o.s || "").trim().length >= 3 &&
        o.x0 >= t.x1 - h * 0.2 &&
        o.x0 - t.x1 < h * 3 &&
        Math.min(o.y1, t.y1) - Math.max(o.y0, t.y0) > h * 0.4,
    );
  };
  for (const t of texts) {
    const s = String(t.s || "").replace(/\s+/g, "");
    if (!s || wallTags.has(s.toUpperCase())) continue;
    if ((DOOR_TAG_RE.test(s) || WINDOW_TAG_RE.test(s)) && isLegend(t)) continue;
    const c = [(t.x0 + t.x1) / 2, (t.y0 + t.y1) / 2];
    if (DOOR_TAG_RE.test(s) && !near(doorsAt, c, U(4)))
      add({
        kind: "door",
        p: c,
        hint: `door tag ${s} with no door counted near it`,
      });
    else if (WINDOW_TAG_RE.test(s) && !near(winsAt, c, U(6)))
      add({
        kind: "window",
        p: c,
        hint: `window tag ${s} with no window counted near it`,
      });
  }
  // one door, one suspect: door clues within 5' of each other (a swing and its tag, two tags on a pair) are one
  const merged = [];
  for (const x of out) {
    const hit =
      x.kind === "door" &&
      merged.find(
        (m) =>
          m.kind === "door" &&
          Math.hypot(m.p[0] - x.p[0], m.p[1] - x.p[1]) < U(5),
      );
    if (hit)
      hit.hint = `${hit.hint}; ${x.hint.replace(/ with no door counted near it| drawn here but not counted/, "")}`;
    else merged.push({ ...x });
  }
  return merged;
}

// one line made of two: deductions (windows, doors) add up; the first line's height and type stay
export function mergeWallMeta(a = {}, b = {}) {
  const m = { ...(b || {}), ...(a || {}) };
  const sum = (k) =>
    Math.round(((Number(a?.[k]) || 0) + (Number(b?.[k]) || 0)) * 100) / 100;
  const cat = (k) => [...(a?.[k] || []), ...(b?.[k] || [])];
  if (a?.deductLf != null || b?.deductLf != null) m.deductLf = sum("deductLf");
  if (a?.doorLf != null || b?.doorLf != null) m.doorLf = sum("doorLf");
  for (const k of ["windows", "windowSegs", "doors", "doorKinds", "doorSegs"])
    if (a?.[k] || b?.[k]) m[k] = cat(k);
  // each door keeps the way its swing is drawn
  if (a?.doorFlips || b?.doorFlips) {
    const pad = (x) =>
      Array.from(
        { length: (x?.doorSegs || x?.doors || []).length },
        (_, i) => Number(x?.doorFlips?.[i]) || 0,
      );
    m.doorFlips = [...pad(a), ...pad(b)];
  }
  return m;
}

// join lines whose ends touch (within tol) into as few polylines as possible; points are [lat,lng]
export function joinPolylines(lines, tol) {
  const pool = lines
    .map((l) => l.map((p) => [p[0], p[1]]))
    .filter((l) => l.length >= 2);
  const near = (p, q) => Math.hypot(p[0] - q[0], p[1] - q[1]) <= tol;
  const out = [];
  while (pool.length) {
    let cur = pool.shift(),
      grew = true;
    while (grew) {
      grew = false;
      for (let i = 0; i < pool.length; i++) {
        const o = pool[i],
          s = cur[0],
          e = cur[cur.length - 1],
          os = o[0],
          oe = o[o.length - 1];
        let next = null;
        if (near(e, os)) next = [...cur, ...o.slice(1)];
        else if (near(e, oe)) next = [...cur, ...o.slice(0, -1).reverse()];
        else if (near(s, oe)) next = [...o, ...cur.slice(1)];
        else if (near(s, os)) next = [...o.slice(1).reverse(), ...cur];
        if (next) {
          cur = next;
          pool.splice(i, 1);
          grew = true;
          break;
        }
      }
    }
    out.push(cur);
  }
  return out;
}

function serializeLatLngs(layer, kind) {
  if (kind === "marker") {
    const p = layer.getLatLng();
    return [[p.lat, p.lng]];
  }
  const raw = kind === "polygon" ? layer.getLatLngs()[0] : layer.getLatLngs();
  return raw.map((p) => [p.lat, p.lng]);
}

// ---------------------------------------------------------------------------
// Small UI atoms (added)
// ---------------------------------------------------------------------------
// Line icons (24px grid, stroke = currentColor) — one consistent set across the workspace
const ICONS = {
  flag: "M4 22V4M4 4h12l-2 4 2 4H4",
  legend: "M4 6h2M9 6h11M4 12h2M9 12h11M4 18h2M9 18h11",
  printer:
    "M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6z",
  scissors:
    "M6 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM20 4 8.1 15.9M14.5 14.5 20 20M8.1 8.1 12 12",
  pin: "M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12zM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z",
  panelLeft:
    "M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM9 3v18",
  panelRight:
    "M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM15 3v18",
  hand: "M18 11V6a2 2 0 0 0-4 0v5M14 10V4a2 2 0 0 0-4 0v6M10 10.5V6a2 2 0 0 0-4 0v8a8 8 0 0 0 16 0v-2a2 2 0 0 0-4 0",
  ruler:
    "M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.4 2.4 0 0 1 0-3.4l2.6-2.6a2.4 2.4 0 0 1 3.4 0zM14.5 12.5l2-2M11.5 9.5l2-2M8.5 6.5l2-2M17.5 15.5l2-2",
  measure: "M2 12h20M6 8l-4 4 4 4M18 8l4 4-4 4",
  wand: "M15 4V2M15 16v-2M8 9h2M20 9h2M17.8 11.8 19 13M17.8 6.2 19 5M3 21l9-9M12.2 6.2 11 5",
  scan: "M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2M7 8h10v8H7z",
  layers: "M12 2 2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5",
  tag: "M12.6 2.6A2 2 0 0 0 11.2 2H4a2 2 0 0 0-2 2v7.2a2 2 0 0 0 .6 1.4l8.7 8.7a2.4 2.4 0 0 0 3.4 0l6.6-6.6a2.4 2.4 0 0 0 0-3.4zM7.5 7.5h0",
  pencil: "M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z",
  trash:
    "M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6",
  shield: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10zM9 12l2 2 4-4",
  save: "M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2zM17 21v-8H7v8M7 3v5h8",
  download: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3",
  maximize:
    "M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3",
  minimize:
    "M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3",
  chevL: "M15 18l-6-6 6-6",
  chevR: "M9 18l6-6-6-6",
  chevD: "M6 9l6 6 6-6",
  x: "M18 6 6 18M6 6l12 12",
  file: "M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5zM14 2v6h6",
  plus: "M12 5v14M5 12h14",
  arrowR: "M5 12h14M12 5l7 7-7 7",
  draw: "M12 19l7-7 3 3-7 7-3-3zM18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5zM2 2l7.6 7.6M11 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4z",
  cursor: "M4 4l7.07 17 2.51-7.39L21 11.07z",
  undo: "M9 14 4 9l5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11",
  redo: "m15 14 5-5-5-5M20 9H9.5a5.5 5.5 0 0 0 0 11H13",
  merge:
    "M8 6v6a4 4 0 0 0 4 4h4M16 12l4 4-4 4M8 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4z",
  search: "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.35-4.35",
  collapseAll: "M7 20l5-5 5 5M7 4l5 5 5-5",
  expandAll: "M7 15l5 5 5-5M7 9l5-5 5 5",
  copy: "M8 8h12v12H8zM4 16V4h12",
  duplicate: "M9 9h11v11H9zM5 15H4V4h11v1M14.5 12.5v5M12 15h5",
  settings:
    "M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0M14 4v4M8 10v4M16 16v4",
  grip: "M9 5h.01M9 12h.01M9 19h.01M15 5h.01M15 12h.01M15 19h.01",
  door: "M4 21h16M6 21V4l9-1v18M12 12h.01",
  eye: "M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  eyeOff:
    "M3 3l18 18M10.6 5.1A10.9 10.9 0 0 1 12 5c6.5 0 10 7 10 7a17.6 17.6 0 0 1-3.2 4.2M6.6 6.6C3.8 8.4 2 12 2 12s3.5 7 10 7c1.6 0 3-.4 4.3-1M9.9 9.9a3 3 0 0 0 4.2 4.2",
};
export function Icon({ name, size = 16, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d={ICONS[name] || ""} />
    </svg>
  );
}
// feet & inches typed any usual way: 9 · 9' · 9'6" · 9' 6 · 9-6 · 9.5 · 114" → feet
export function parseFeetLoose(raw) {
  const t = String(raw ?? "").trim();
  // "9-6", "9 6", "9-6\"" = 9'-6" (checked first: a plain number parse would read "9-6" as 9)
  const m = t.match(/^(\d+)\s*(?:-|\s)\s*(\d+(?:\.\d+)?)\s*"?$/);
  if (m) return Number(m[1]) + Number(m[2]) / 12;
  const v = parseFeetInput(t);
  return v != null && Number.isFinite(v) ? v : null;
}
// a feet-and-inches field: shows 9'-0", takes any of the above; saves on Enter or leaving the field (Esc undoes)
export function FtInInput({
  value,
  onCommit,
  placeholder = "—",
  className = "",
  title,
  autoFocus,
}) {
  const show = (v) => (v ? fmtFeetIn(Number(v)) : "");
  const [txt, setTxt] = useState(show(value));
  const [bad, setBad] = useState(false);
  useEffect(() => {
    setTxt(show(value));
    setBad(false);
  }, [value]);
  const commit = () => {
    const t = txt.trim();
    if (!t) {
      if (value != null) onCommit(null);
      return;
    }
    const v = parseFeetLoose(t);
    if (v && v > 0) {
      setBad(false);
      if (Math.abs(v - (Number(value) || 0)) > 1e-6)
        onCommit(Math.round(v * 10000) / 10000);
      setTxt(show(v));
    } else setBad(true);
  };
  return (
    <input
      value={txt}
      autoFocus={autoFocus}
      title={title}
      placeholder={placeholder}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        setTxt(e.target.value);
        setBad(false);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setTxt(show(value));
          setBad(false);
          e.currentTarget.blur();
        }
      }}
      className={`rounded-md border bg-gray-950 px-2 text-[12px] tabular-nums text-gray-100 placeholder-gray-600 focus:outline-none ${bad ? "border-red-500/70" : "border-white/10 focus:border-orange-500"} ${className}`}
    />
  );
}
// colour picker: the palette as swatches, plus any colour
// how an assembly is drawn: line thickness (real inches), pattern, where a hand-drawn line sits; count symbol;
// door headers on / off for a wall assembly
function LookOnDrawing({ cond, onChange, headerCount }) {
  const lab =
    "mb-1 block text-[10px] font-medium uppercase tracking-wider text-gray-500";
  const sel =
    "h-8 w-full rounded-md border border-white/10 bg-gray-950 px-1.5 text-[12px] text-gray-100 focus:border-orange-500 focus:outline-none";
  if (cond.type === "count")
    return (
      <div>
        <span className={lab}>Symbol on the drawing</span>
        <div className="flex flex-wrap gap-1">
          {COUNT_SYMBOLS.map(([k, l]) => (
            <button
              key={k}
              title={l}
              onClick={() => onChange({ symbol: k })}
              className={`grid h-8 w-8 place-items-center rounded-md ring-1 ${condSymbol(cond) === k ? "bg-orange-500/20 ring-orange-500" : "bg-white/[0.04] ring-white/10 hover:bg-white/10"}`}
            >
              {symbolSvg(k, cond.color, 16)}
            </button>
          ))}
        </div>
      </div>
    );
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {cond.type === "linear" && (
          <label className="block">
            <span className={lab}>Line thickness</span>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="0.5"
                step="0.5"
                defaultValue={condLineIn(cond)}
                key={`li-${cond.id}-${cond.lineIn}`}
                onBlur={(e) => {
                  const v = Number(e.target.value);
                  if (v > 0 && v !== condLineIn(cond))
                    onChange({ lineIn: Math.round(v * 100) / 100 });
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
                className={`${sel} text-right`}
              />
              <span className="text-[11px] text-gray-500">in</span>
            </div>
          </label>
        )}
        <label
          className={`block ${cond.type === "linear" ? "" : "col-span-2"}`}
        >
          <span className={lab}>Pattern</span>
          <select
            value={condPattern(cond)}
            onChange={(e) => onChange({ linePattern: e.target.value })}
            className={sel}
          >
            {LINE_PATTERNS.map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </label>
      </div>
      {cond.type === "linear" && (
        <label className="block">
          <span className={lab}>Drawing a line</span>
          <select
            value={condAlign(cond)}
            onChange={(e) => onChange({ lineAlign: e.target.value })}
            className={sel}
          >
            {LINE_ALIGNS.map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-[10px] leading-snug text-gray-500">
            {condAlign(cond) === "auto"
              ? "Click along a wall face (or anywhere on the wall) — the line moves onto the wall's centre so the band covers it face to face. C while drawing turns it off for that line."
              : condAlign(cond) === "click"
                ? "The line goes exactly where you click."
                : `Click along a wall face — the band lies to the ${condAlign(cond)} of your clicks.`}
          </span>
        </label>
      )}
      {headerCount != null && (
        <label className="flex cursor-pointer items-start gap-2 rounded-md bg-white/[0.03] px-2 py-1.5">
          <input
            type="checkbox"
            checked={cond.doorHeaders !== false}
            onChange={(e) => onChange({ doorHeaders: e.target.checked })}
            className="mt-0.5 accent-orange-500"
          />
          <span className="text-[11px] leading-snug text-gray-300">
            Door headers
            <span className="block text-[10px] text-gray-500">
              A line in front of every door in this wall, on “Header —{" "}
              {cond.name}” · door width × (wall height − door height)
              {headerCount ? ` · ${headerCount} now` : ""}
            </span>
          </span>
        </label>
      )}
    </div>
  );
}

function Swatches({ value, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {PALETTE.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          title={c}
          className={`h-5 w-5 rounded-full transition-transform hover:scale-110 ${value === c ? "ring-2 ring-white ring-offset-2 ring-offset-gray-900" : ""}`}
          style={{ background: c }}
        />
      ))}
      <label
        className="relative h-5 w-5 cursor-pointer overflow-hidden rounded-full border border-dashed border-white/30 text-center text-[11px] leading-[18px] text-gray-400 hover:border-white/60"
        title="Any colour"
      >
        +
        <input
          type="color"
          value={value || "#f97316"}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
      </label>
    </div>
  );
}
const TYPE_META = {
  linear: {
    label: "Wall · LF",
    short: "LF",
    tone: "bg-orange-500/15 text-orange-300",
  },
  area: { label: "Area · SF", short: "SF", tone: "bg-sky-500/15 text-sky-300" },
  count: {
    label: "Count · EA",
    short: "EA",
    tone: "bg-fuchsia-500/15 text-fuchsia-300",
  },
};

// ── assembly picker: a searchable list instead of a long native dropdown. Type to filter by number, tag or name
// (#56, ms.12, "fire rated"…), arrow keys + Enter to pick. Suggested / recently used types sit on top.
//   options: conditions [{id, no, name, color, description}] · value: id ("" = none / mixed)
//   suggested: ids to pin on top (the tag's alternative, the AI's read…) · recent: ids used lately
const splitName = (name) => {
  const s = String(name || "");
  const i = s.indexOf(" — ");
  return i > 0 && i <= 16 ? [s.slice(0, i), s.slice(i + 3)] : ["", s];
};
function AssemblySelect({
  options = [],
  value = "",
  onChange,
  placeholder = "Pick an assembly…",
  suggested = [],
  recent = [],
  size = "md",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const listRef = useRef(null);
  const cur = options.find((c) => c.id === value) || null;
  const norm = (x) =>
    String(x || "")
      .toLowerCase()
      .replace(/[\s.\-_]/g, "");
  const rows = useMemo(() => {
    const nq = norm(q);
    const hit = (c) =>
      !nq ||
      norm(`${c.no || ""}`) === nq.replace(/^#/, "") ||
      norm(`#${c.no || ""} ${c.name} ${c.description || ""}`).includes(nq);
    const list = options.filter(hit);
    const pinIds = [...new Set([...suggested, ...recent])].filter((id) =>
      list.some((c) => c.id === id),
    );
    const pinned = pinIds.map((id) => list.find((c) => c.id === id));
    const rest = list
      .filter((c) => !pinIds.includes(c.id))
      .sort(
        (a, b) =>
          (a.no || 1e9) - (b.no || 1e9) ||
          String(a.name).localeCompare(String(b.name), undefined, {
            numeric: true,
          }),
      );
    return [
      ...(pinned.length && !nq
        ? [{ head: suggested.length ? "Suggested & recent" : "Recently used" }]
        : []),
      ...(nq ? [] : pinned),
      ...(pinned.length && !nq ? [{ head: "All assemblies" }] : []),
      ...(nq ? list : rest),
    ];
  }, [options, q, suggested, recent]);
  const pickable = rows.filter((r) => !r.head);
  const show = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const w = Math.max(r.width, 340);
    const below = window.innerHeight - r.bottom;
    const h = Math.min(380, Math.max(220, below - 16));
    setPos(
      below >= 240
        ? {
            left: Math.min(r.left, window.innerWidth - w - 8),
            top: r.bottom + 4,
            width: w,
            maxH: h,
          }
        : {
            left: Math.min(r.left, window.innerWidth - w - 8),
            bottom: window.innerHeight - r.top + 4,
            width: w,
            maxH: Math.min(380, r.top - 16),
          },
    );
    setQ("");
    setHi(
      Math.max(
        0,
        pickable.findIndex((c) => c.id === value),
      ),
    );
    setOpen(true);
  };
  const pick = (c) => {
    setOpen(false);
    if (c && c.id !== value) onChange?.(c.id);
  };
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-i="${hi}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [hi, open]);
  const [tag, rest] = splitName(cur?.name);
  let k = -1;
  return (
    <div className="relative min-w-0">
      <button
        ref={btnRef}
        type="button"
        onClick={() => (open ? setOpen(false) : show())}
        className={`flex w-full min-w-0 items-center gap-2 rounded-lg border bg-gray-950/60 px-2.5 text-left transition-colors ${open ? "border-orange-500" : "border-gray-700 hover:border-gray-600"} ${size === "sm" ? "h-8" : "h-10"}`}
      >
        {cur ? (
          <>
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: cur.color }}
            />
            {cur.no ? (
              <span className="shrink-0 font-mono text-[11px] text-orange-300/90">
                #{cur.no}
              </span>
            ) : null}
            {tag && (
              <span className="shrink-0 text-[12px] font-semibold text-white">
                {tag}
              </span>
            )}
            <span className="min-w-0 flex-1 truncate text-[12px] text-gray-300">
              {rest}
            </span>
          </>
        ) : (
          <span className="min-w-0 flex-1 truncate text-[12px] text-gray-500">
            {placeholder}
          </span>
        )}
        <Icon name="chevD" size={14} className="shrink-0 text-gray-500" />
      </button>
      {open && pos && (
        <>
          <div
            className="fixed inset-0 z-[1390]"
            onClick={() => setOpen(false)}
          />
          <div
            className="fixed z-[1400] flex flex-col overflow-hidden rounded-xl border border-white/10 bg-gray-900 shadow-2xl"
            style={{
              left: pos.left,
              top: pos.top,
              bottom: pos.bottom,
              width: pos.width,
              maxHeight: pos.maxH,
            }}
          >
            <div className="border-b border-white/5 p-2">
              <input
                autoFocus
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setHi(0);
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setHi((h) => Math.min(pickable.length - 1, h + 1));
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setHi((h) => Math.max(0, h - 1));
                  } else if (e.key === "Enter") {
                    e.preventDefault();
                    pick(pickable[hi]);
                  } else if (e.key === "Escape") {
                    e.stopPropagation();
                    setOpen(false);
                  }
                  e.stopPropagation(); // the drawing's shortcuts don't fire while typing here
                }}
                placeholder="Search # number, tag or name…"
                style={{ fontSize: 12 }}
                className="h-8 w-full rounded-lg border border-white/10 bg-gray-950/70 px-2.5 text-gray-100 placeholder:text-gray-600 focus:border-orange-500 focus:outline-none"
              />
            </div>
            <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto p-1">
              {rows.map((r, i) => {
                if (r.head)
                  return (
                    <div
                      key={`h${i}`}
                      className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500"
                    >
                      {r.head}
                    </div>
                  );
                k++;
                const idx = k;
                const [t2, n2] = splitName(r.name);
                const on = r.id === value;
                return (
                  <button
                    key={r.id}
                    type="button"
                    data-i={idx}
                    onMouseEnter={() => setHi(idx)}
                    onClick={() => pick(r)}
                    className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left ${idx === hi ? "bg-white/10" : ""}`}
                  >
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ background: r.color }}
                    />
                    <span className="w-9 shrink-0 font-mono text-[11px] text-orange-300/80">
                      {r.no ? `#${r.no}` : ""}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        {t2 && (
                          <span className="shrink-0 text-[12px] font-semibold text-white">
                            {t2}
                          </span>
                        )}
                        <span
                          className={`truncate text-[12px] ${t2 ? "text-gray-400" : "text-gray-100"}`}
                        >
                          {n2}
                        </span>
                      </span>
                    </span>
                    {on && (
                      <svg
                        viewBox="0 0 16 16"
                        className="h-3.5 w-3.5 shrink-0 text-orange-400"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <path d="M3.5 8.5l3 3 6-7" />
                      </svg>
                    )}
                  </button>
                );
              })}
              {!pickable.length && (
                <div className="px-3 py-6 text-center text-[12px] text-gray-500">
                  No assembly matches “{q}”.
                </div>
              )}
            </div>
            <div className="border-t border-white/5 px-3 py-1.5 text-[10px] text-gray-500">
              ↑ ↓ to move · Enter to apply · Esc to close
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function IconBtn({
  title,
  onClick,
  active,
  danger,
  disabled,
  children,
  className = "",
}) {
  return (
    <button
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${
        active
          ? "bg-orange-500 text-white shadow-sm shadow-orange-900/40"
          : danger
            ? "text-gray-400 hover:bg-red-500/15 hover:text-red-300"
            : "text-gray-400 hover:bg-white/5 hover:text-white"
      } ${className}`}
    >
      {children}
    </button>
  );
}
// a tool on the floating rail: icon, label on hover, shortcut
// measured as: wall (LF) / area (SF) / count (EA) — changeable until something is drawn with it
function TypeSwitch({ cond, used, onChange }) {
  return (
    <div
      className="inline-flex rounded-md bg-gray-950 p-0.5"
      title={
        used
          ? "Something is drawn with this assembly — delete it first to change the type"
          : "Change how it's measured"
      }
    >
      {Object.entries(TYPE_META).map(([k, m]) => (
        <button
          key={k}
          disabled={used && cond.type !== k}
          onClick={() => onChange(k)}
          className={`h-7 rounded px-2 text-[11px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-30 ${cond.type === k ? m.tone : "text-gray-400 hover:bg-white/5 hover:text-gray-200"}`}
        >
          {m.short}
        </button>
      ))}
    </div>
  );
}

// pick the change order a pin is for
function CoPicker({ list, err, pinned, onPick, onClose }) {
  const [q, setQ] = useState("");
  const [note, setNote] = useState("");
  const shown = (list || []).filter(
    (c) =>
      !q.trim() ||
      `${c.number ?? ""} ${c.title || ""} ${c.status || ""}`
        .toLowerCase()
        .includes(q.trim().toLowerCase()),
  );
  return (
    <div
      className="w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-gray-900 shadow-2xl"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="border-b border-white/10 px-4 py-3">
        <div className="text-sm font-semibold text-white">
          Pin a change order here
        </div>
        <div className="text-[11px] text-gray-500">
          The pin stays on this spot of the drawing and opens the change order
        </div>
      </div>
      <div className="space-y-2 p-3">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Find a change order — number, title…"
          className="w-full rounded-lg border border-gray-700 bg-gray-950 px-2.5 py-1.5 text-[13px] text-gray-100 placeholder-gray-600 focus:border-teal-500 focus:outline-none"
        />
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note on the pin (optional)"
          className="w-full rounded-lg border border-gray-700 bg-gray-950 px-2.5 py-1.5 text-[12px] text-gray-100 placeholder-gray-600 focus:border-teal-500 focus:outline-none"
        />
        <div className="max-h-[45vh] overflow-y-auto rounded-lg border border-white/10">
          {list == null && (
            <p className="p-4 text-center text-xs text-gray-500">Loading…</p>
          )}
          {err && <p className="p-4 text-center text-xs text-red-300">{err}</p>}
          {list && !shown.length && !err && (
            <p className="p-4 text-center text-xs text-gray-500">
              No change orders on this project.
            </p>
          )}
          {shown.map((c) => (
            <button
              key={c._id}
              onClick={() => onPick(c, note.trim())}
              className="flex w-full items-center gap-2 border-b border-white/5 px-3 py-2 text-left hover:bg-teal-500/10"
            >
              <span className="rounded bg-teal-600/20 px-1.5 py-0.5 font-mono text-[11px] text-teal-200">
                CO {c.number ?? "?"}
              </span>
              <span className="min-w-0 flex-1 truncate text-[12.5px] text-gray-100">
                {c.title || "Untitled"}
              </span>
              {c.status && (
                <span className="text-[10.5px] text-gray-500">{c.status}</span>
              )}
              {pinned.has(String(c._id)) && (
                <span className="text-[10px] text-teal-300">pinned</span>
              )}
            </button>
          ))}
        </div>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── first open of a drawing set: every sheet's scale is read before the drawing unlocks. Flipping through sheets
// while the set is still being read loads every page at once and can run the browser out of memory.
const SCALE_LOAD_CSS = `
@keyframes sl-scan { 0% { transform: translateY(0); opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { transform: translateY(104px); opacity: 0; } }
@keyframes sl-draw { from { stroke-dashoffset: 120; } to { stroke-dashoffset: 0; } }
@keyframes sl-tick { 0%, 100% { opacity: .25; } 50% { opacity: 1; } }
@keyframes sl-stripes { from { background-position: 0 0; } to { background-position: 28px 0; } }
.sl-line { stroke-dasharray: 120; animation: sl-draw 1.6s ease-in-out infinite alternate; }
`;
function ScaleLoadOverlay({ run, label, onSkip }) {
  const pct = run.total
    ? Math.min(100, Math.round((run.done / run.total) * 100))
    : 0;
  return (
    <div className="absolute inset-0 z-[1400] flex items-center justify-center bg-gray-950/90 backdrop-blur-sm">
      <style>{SCALE_LOAD_CSS}</style>
      <div className="w-[26rem] max-w-[calc(100%-2rem)] rounded-2xl border border-white/10 bg-gray-900 p-6 shadow-2xl">
        <div className="relative mx-auto mb-5 h-[120px] w-[160px] overflow-hidden rounded-lg border border-white/10 bg-gray-950">
          <svg viewBox="0 0 160 120" className="absolute inset-0 h-full w-full">
            {[
              [14, 14, 146, 14],
              [146, 14, 146, 106],
              [146, 106, 14, 106],
              [14, 106, 14, 14],
              [70, 14, 70, 60],
              [70, 60, 146, 60],
              [104, 60, 104, 106],
            ].map(([x1, y1, x2, y2], i) => (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                className="sl-line"
                stroke="#9ca3af"
                strokeWidth="2"
                style={{ animationDelay: `${i * 0.12}s` }}
              />
            ))}
            {/* a dimension string being checked */}
            <line
              x1="14"
              y1="114"
              x2="146"
              y2="114"
              stroke="#f97316"
              strokeWidth="1"
            />
            {[14, 47, 80, 113, 146].map((x, i) => (
              <line
                key={x}
                x1={x}
                y1="110"
                x2={x}
                y2="118"
                stroke="#f97316"
                strokeWidth="1.5"
                style={{ animation: `sl-tick 1.2s ${i * 0.15}s infinite` }}
              />
            ))}
          </svg>
          <div
            className="absolute inset-x-0 top-2 h-0.5 bg-orange-500 shadow-[0_0_12px_2px_rgba(249,115,22,0.6)]"
            style={{ animation: "sl-scan 1.8s ease-in-out infinite" }}
          />
        </div>
        <h3 className="text-center text-sm font-semibold text-white">
          Reading the scale on every sheet
        </h3>
        <p className="mt-1 text-center text-xs text-gray-400">
          {run.total ? `${run.done} of ${run.total} sheets` : "Starting…"}
          {label ? <span className="text-gray-500"> · {label}</span> : null}
        </p>
        <div className="mt-4 h-2 overflow-hidden rounded-full bg-gray-800">
          <div
            className="h-full rounded-full bg-orange-500 transition-[width] duration-300"
            style={{
              width: `${Math.max(4, pct)}%`,
              backgroundImage:
                "repeating-linear-gradient(45deg, rgba(255,255,255,.18) 0 7px, transparent 7px 14px)",
              animation: "sl-stripes .8s linear infinite",
            }}
          />
        </div>
        <p className="mt-4 text-center text-[11px] leading-relaxed text-gray-500">
          This runs once for a new drawing set. The drawing unlocks when it's
          done — opening sheets while they're still being read can run the
          browser out of memory.
        </p>
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={onSkip}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-400 hover:bg-white/5 hover:text-gray-200"
          >
            Skip for now — set scales later
          </button>
        </div>
      </div>
    </div>
  );
}

function RailBtn({
  icon,
  label,
  kbd,
  active,
  danger,
  disabled,
  busy,
  onClick,
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`group relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all disabled:cursor-not-allowed disabled:opacity-35 ${
        active
          ? danger
            ? "bg-red-500 text-white"
            : "bg-orange-500 text-white shadow-lg shadow-orange-900/40"
          : "text-gray-300 hover:bg-white/10 hover:text-white"
      }`}
    >
      <Icon name={icon} size={18} className={busy ? "animate-pulse" : ""} />
      <span className="pointer-events-none absolute left-1/2 top-12 z-10 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-950 px-2 py-1 text-[11px] font-medium text-gray-100 shadow-lg ring-1 ring-white/10 group-hover:block">
        {label}
        {kbd && (
          <kbd className="ml-2 rounded bg-white/10 px-1 text-[10px] text-gray-400">
            {kbd}
          </kbd>
        )}
      </span>
    </button>
  );
}
function HeaderBtn({
  icon,
  children,
  onClick,
  disabled,
  active,
  title,
  primary,
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-[12px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        primary
          ? "bg-orange-500 text-white shadow-sm shadow-orange-900/40 hover:bg-orange-400"
          : active
            ? "bg-white/10 text-white"
            : "text-gray-300 hover:bg-white/5 hover:text-white"
      }`}
    >
      {icon && <Icon name={icon} size={15} />}
      {children}
    </button>
  );
}
// The assembly you're drawing with — always on the toolbar, so the Assemblies list can be collapsed or
// hidden. Pick one to start drawing with it.
// The assembly you're drawing with — always on the toolbar, so the Assemblies list can be collapsed or
// hidden. Pick one to start drawing with it.
function AssemblyPicker({
  conditions,
  activeId,
  drawing,
  disabled,
  condMetrics,
  onPick,
  onStop,
  hidden = null, // Set of assembly ids hidden on the drawing
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const act = conditions.find((c) => c.id === activeId);
  const groups = [
    ["linear", "Walls · LF"],
    ["area", "Areas · SF"],
    ["count", "Counts · EA"],
  ];
  const match = (c) =>
    !q ||
    `${c.name} ${c.description || ""}`.toLowerCase().includes(q.toLowerCase());
  return (
    <div ref={ref} className="relative ">
      <div className="flex items-center">
        <button
          onClick={() => setOpen((v) => !v)}
          disabled={disabled && !conditions.length}
          aria-label={
            act ? `Drawing with ${act.name}` : "Pick the assembly to draw with"
          }
          className={`flex h-10 max-w-[10rem] items-center gap-2 rounded-xl px-2.5 text-left transition-colors xl:max-w-[15rem] ${drawing ? "bg-orange-500/20 ring-1 ring-orange-500/60" : "hover:bg-white/10"}`}
        >
          <span
            className="h-3 w-3 shrink-0 rounded-full ring-2 ring-white/10"
            style={{
              background: act?.color || "transparent",
              borderStyle: act ? "none" : "dashed",
            }}
          />
          <span className="min-w-0">
            <span className="block text-[9.5px] font-medium uppercase leading-none tracking-wider text-gray-500">
              {drawing ? "Drawing" : "Assembly"}
            </span>
            <span className="block truncate text-[12px] font-semibold leading-tight text-gray-100">
              {act ? act.name : "Pick one to draw"}
            </span>
          </span>
          <Icon name="chevD" size={14} className="shrink-0 text-gray-400" />
        </button>
        {act &&
          (drawing ? (
            <button
              onClick={onStop}
              aria-label="Stop drawing"
              className="ml-0.5 h-8 rounded-lg bg-orange-500 px-2 text-[11px] font-semibold text-white hover:bg-orange-400"
            >
              Stop
            </button>
          ) : (
            <button
              onClick={() => onPick(act)}
              disabled={disabled}
              aria-label={`Draw with ${act.name}`}
              className="ml-0.5 h-8 rounded-lg bg-white/5 px-2 text-[11px] font-semibold text-gray-100 hover:bg-white/10 disabled:opacity-40"
            >
              <Icon name="draw" size={14} />
            </button>
          ))}
      </div>
      {open && (
        <div className="absolute left-0 top-12 z-[1300] w-80 rounded-xl border border-white/10 bg-gray-900 p-2 shadow-2xl">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find an assembly…"
            onKeyDown={(e) => {
              if (e.key === "Escape") setOpen(false);
            }}
            className="mb-2 h-8 w-full rounded-lg !border !border-white/10 !bg-gray-950 !px-2 !py-0 text-[12px] text-gray-100 placeholder-gray-600 !shadow-none !outline-none !ring-0 focus:!border-orange-500/60 focus:!ring-0"
          />
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {groups.map(([type, title]) => {
              const list = conditions.filter(
                (c) => c.type === type && match(c),
              );
              if (!list.length) return null;
              return (
                <div key={type}>
                  <div className="mb-0.5 px-1 text-[10px] font-medium uppercase tracking-wider text-gray-500">
                    {title}
                  </div>
                  {list.map((c) => {
                    const m = condMetrics?.[c.id];
                    const qty =
                      type === "area"
                        ? m?.sf
                        : type === "linear"
                          ? m?.lf
                          : m?.count;
                    return (
                      <button
                        key={c.id}
                        onClick={() => {
                          setOpen(false);
                          setQ("");
                          onPick(c);
                        }}
                        className={`flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left ${c.id === activeId ? "bg-orange-500/15" : "hover:bg-white/5"}`}
                      >
                        <span
                          className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ background: c.color }}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            <span className="truncate text-[12px] font-medium text-gray-100">
                              {c.no ? (
                                <span className="mr-1 font-mono text-[10px] text-orange-300/80">
                                  #{c.no}
                                </span>
                              ) : null}
                              {c.name}
                            </span>
                            {hidden?.has(c.id) && (
                              <span
                                className="inline-flex shrink-0 items-center gap-0.5 rounded bg-amber-500/15 px-1 text-[9.5px] font-semibold text-amber-300"
                                title="Hidden on the drawing — picking it shows it again"
                              >
                                <Icon name="eyeOff" size={10} />
                                hidden
                              </span>
                            )}
                          </span>
                          {c.description && (
                            <span className="block truncate text-[10.5px] text-gray-500">
                              {c.description.split("\n").join(" · ")}
                            </span>
                          )}
                        </span>
                        {qty != null && (
                          <span className="shrink-0 text-[10.5px] tabular-nums text-gray-400">
                            {Math.round(qty).toLocaleString()}{" "}
                            {type === "area"
                              ? "SF"
                              : type === "linear"
                                ? "LF"
                                : "EA"}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
            {!conditions.length && (
              <p className="p-2 text-[11px] text-gray-500">
                No assemblies yet — read them with Assemblies &amp; trace, or
                add one in the left panel.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// "What should these rooms make?" — asked by Detect rooms / Fill room so only the wanted lines get drawn
function RoomMakePrompt({ action, current, onPick, onCancel }) {
  const [dontAsk, setDontAsk] = useState(false);
  const opts = [
    {
      k: "both",
      title: "Walls + ceilings",
      sub: "Perimeter lines and the room area",
      walls: true,
      fill: true,
    },
    {
      k: "walls",
      title: "Walls only",
      sub: "Perimeter lines (LF × height) — no areas",
      walls: true,
      fill: false,
    },
    {
      k: "ceilings",
      title: "Ceilings only",
      sub: "Room areas (SF) — no wall lines",
      walls: false,
      fill: true,
    },
  ];
  return (
    <div
      className="w-[34rem] max-w-[calc(100%-2rem)] rounded-2xl border border-white/10 bg-gray-900/95 p-4 shadow-2xl backdrop-blur"
      role="dialog"
      aria-label="What should the rooms make?"
    >
      <div className="mb-1 text-[13px] font-semibold text-white">
        {action === "detect" ? "Detect rooms" : "Fill room"} — what should it
        make?
      </div>
      <div className="mb-3 text-[11px] text-gray-400">
        Pick what you're taking off, so you don't end up with lines or areas
        you'll have to delete.
      </div>
      <div className="grid grid-cols-3 gap-2">
        {opts.map((o, i) => (
          <button
            key={o.k}
            autoFocus={o.k === current || (current === "none" && i === 0)}
            onClick={() => onPick(o.k, dontAsk)}
            className={`group flex flex-col items-center gap-2 rounded-xl border px-2 py-3 text-center transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 ${o.k === current ? "border-orange-500/70 bg-orange-500/10" : "border-white/10 bg-white/[0.03] hover:border-white/25 hover:bg-white/[0.06]"}`}
          >
            <svg width="64" height="48" viewBox="0 0 64 48" aria-hidden="true">
              <path
                d="M8 8h30v12h18v20H8z"
                fill={o.fill ? "rgba(56,189,248,0.35)" : "none"}
                stroke={o.walls ? "#f97316" : "rgba(148,163,184,0.5)"}
                strokeWidth={o.walls ? 3 : 1}
                strokeDasharray={o.walls ? "" : "3 3"}
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-[12px] font-semibold text-gray-100">
              {o.title}
            </span>
            <span className="text-[10.5px] leading-snug text-gray-400">
              {o.sub}
            </span>
            <kbd className="rounded bg-white/10 px-1.5 text-[10px] text-gray-400">
              {i + 1}
            </kbd>
          </button>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2 text-[11px] text-gray-400">
        <label className="flex cursor-pointer items-center gap-1.5">
          <input
            type="checkbox"
            checked={dontAsk}
            onChange={(e) => setDontAsk(e.target.checked)}
            className="accent-orange-600"
          />
          Don't ask again on this takeoff{" "}
          <span className="text-gray-600">(change it in Settings)</span>
        </label>
        <button
          onClick={onCancel}
          className="ml-auto rounded-md px-2 py-1 text-gray-400 hover:bg-white/10 hover:text-white"
        >
          Cancel{" "}
          <kbd className="ml-1 rounded bg-white/10 px-1 text-[10px]">Esc</kbd>
        </button>
      </div>
      <KeyPick onKey={(n) => onPick(opts[n].k, dontAsk)} />
    </div>
  );
}
// 1 / 2 / 3 pick an option while the prompt is up
function KeyPick({ onKey }) {
  const ref = useRef(onKey);
  ref.current = onKey;
  useEffect(() => {
    const h = (e) => {
      if (["1", "2", "3"].includes(e.key) && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        ref.current(Number(e.key) - 1);
      }
    };
    document.addEventListener("keydown", h, true);
    return () => document.removeEventListener("keydown", h, true);
  }, []);
  return null;
}

const confColor = (c) =>
  c == null
    ? "bg-gray-700 text-gray-300"
    : c >= 0.9
      ? "bg-green-900/60 text-green-300"
      : c >= 0.75
        ? "bg-yellow-900/50 text-yellow-300"
        : "bg-red-900/50 text-red-300";
const sevStyle = {
  high: "border-red-700/60 bg-red-950/40 text-red-200",
  med: "border-yellow-700/50 bg-yellow-950/30 text-yellow-100",
  info: "border-gray-700 bg-gray-800/60 text-gray-300",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// ── Bid area picker: type anything, or open the list and pick one already used on this takeoff ──
const BID_AREA_PRESETS = [
  "Basement",
  "Level 1",
  "Level 2",
  "Level 3",
  "Level 4",
  "Roof",
];

function BidAreaCombobox({
  value,
  onChange,
  options,
  placeholder = "Bid area",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState(""); // only filters while typing; the ▾ shows everything
  const [pos, setPos] = useState({ left: 0, top: 0, width: 180 });
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  const openList = (withFilter) => {
    const r = boxRef.current?.getBoundingClientRect();
    if (r)
      setPos({
        left: Math.min(r.left, window.innerWidth - 200),
        top: r.bottom + 4,
        width: Math.max(r.width, 180),
      });
    setFilter(withFilter ? String(value || "") : "");
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (
        !boxRef.current?.contains(e.target) &&
        !e.target.closest?.("[data-bid-area-list]")
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const f = filter.trim().toLowerCase();
  const shown = f
    ? options.filter((o) => o.toLowerCase().includes(f))
    : options;
  const typed = String(value || "").trim();
  const isNew =
    typed && !options.some((o) => o.toLowerCase() === typed.toLowerCase());

  const pick = (v) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className={`relative flex items-center ${className}`}>
      <input
        ref={inputRef}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setFilter(e.target.value);
          if (!open) openList(true);
        }}
        onFocus={() => !open && openList(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          if (e.key === "Enter") {
            if (shown.length === 1 && f) pick(shown[0]);
            else setOpen(false);
          }
        }}
        className="min-w-0 flex-1 !border-0 !bg-transparent !p-0 !shadow-none !outline-none !ring-0 text-[12px] text-orange-100 placeholder-gray-500 focus:!border-0 focus:!ring-0"
      />
      {typed && (
        <button
          type="button"
          onClick={() => {
            onChange("");
            inputRef.current?.focus();
          }}
          className="px-0.5 text-[11px] text-gray-500 hover:text-white"
          title="Clear"
        >
          ✕
        </button>
      )}
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openList(false))}
        className="px-0.5 text-[10px] text-gray-400 hover:text-white"
        title="Choose a bid area"
      >
        ▾
      </button>
      {open && (
        <div
          data-bid-area-list
          style={{
            position: "fixed",
            left: pos.left,
            top: pos.top,
            width: pos.width,
          }}
          className="z-[2000] max-h-64 overflow-y-auto rounded-lg border border-white/10 bg-gray-900 p-1 shadow-2xl"
        >
          {shown.map((o) => (
            <button
              key={o}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(o)}
              className={`block w-full truncate rounded-md px-2.5 py-1.5 text-left text-[12px] ${
                o.toLowerCase() === typed.toLowerCase()
                  ? "bg-orange-500/20 text-orange-200"
                  : "text-gray-200 hover:bg-white/10"
              }`}
            >
              {o}
            </button>
          ))}
          {isNew && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(typed)}
              className="block w-full truncate rounded-md px-2.5 py-1.5 text-left text-[12px] text-orange-300 hover:bg-white/10"
            >
              + Use "{typed}"
            </button>
          )}
          {!shown.length && !isNew && (
            <div className="px-2.5 py-2 text-[11px] text-gray-500">
              Type a name to add one
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── people & the change log ──
const PEER_COLORS = [
  "#f97316",
  "#38bdf8",
  "#a78bfa",
  "#34d399",
  "#f472b6",
  "#facc15",
  "#60a5fa",
  "#fb7185",
];
const peerColor = (name) => {
  let h = 0;
  for (const ch of String(name || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PEER_COLORS[h % PEER_COLORS.length];
};
const initials = (name) =>
  String(name || "?")
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";
function Avatar({ name, size = 20, ring = false }) {
  return (
    <span
      title={name}
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-gray-900 ${ring ? "ring-2 ring-gray-900" : ""}`}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.42,
        background: peerColor(name),
      }}
    >
      {initials(name)}
    </span>
  );
}
const ago = (d) => {
  const s = Math.round((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(d).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};
// The change log: every save, who made it and what changed (newest first). Click a sheet to go there,
// "Show" to select what that change added / changed (if it's still there).
function ChangeLog({
  api,
  takeoffId,
  authHeaders,
  pageLabel,
  onGoToPage,
  onShow,
  rev,
}) {
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [more, setMore] = useState(true);
  const load = async (before = 0) => {
    if (!takeoffId) return;
    setBusy(true);
    try {
      const r = await fetch(
        `${api}/takeoffs3/${takeoffId}/log?limit=50${before ? `&before=${before}` : ""}`,
        { headers: authHeaders() },
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      const list = data.entries || [];
      setEntries((prev) => {
        const all = before
          ? [...prev, ...list]
          : [
              ...list,
              ...prev.filter((e) => !list.some((x) => x.rev === e.rev)),
            ];
        return all.sort((a, b) => b.rev - a.rev);
      });
      if (before || !entries.length) setMore(list.length === 50);
      setErr("");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  // newest on open and whenever the takeoff's revision moves (my saves, others' changes)
  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takeoffId, rev]);
  if (!takeoffId)
    return (
      <p className="p-3 text-gray-500">
        The change log starts once the takeoff is saved.
      </p>
    );
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-[11px] text-gray-500">
        <span>Every save — who, when and what changed</span>
        <button
          onClick={() => load(0)}
          className="rounded px-1.5 py-0.5 hover:bg-white/10 hover:text-gray-200"
        >
          {busy ? "…" : "Refresh"}
        </button>
      </div>
      {err && <p className="text-red-400">{err}</p>}
      {entries.map((e) => (
        <div
          key={e.rev}
          className="rounded-lg border border-white/5 bg-gray-800/50 p-2"
        >
          <div className="flex items-center gap-2">
            <Avatar name={e.user?.name} />
            <span className="min-w-0 flex-1 truncate font-medium text-gray-200">
              {e.user?.name || "Someone"}
            </span>
            <span
              className="shrink-0 text-[10px] text-gray-500"
              title={new Date(e.at).toLocaleString()}
            >
              {ago(e.at)}
            </span>
          </div>
          <ul className="mt-1 space-y-0.5 pl-7 text-[11px] text-gray-300">
            {(e.summary?.length ? e.summary : ["Saved"]).map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
          {(e.pages?.length > 0 || e.shapeIds?.length > 0) && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1 pl-7">
              {(e.pages || []).slice(0, 6).map((p) => (
                <button
                  key={p}
                  onClick={() => onGoToPage(p)}
                  className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-gray-300 hover:bg-white/10"
                >
                  {pageLabel(p)}
                </button>
              ))}
              {e.shapeIds?.length > 0 && (
                <button
                  onClick={() => onShow(e.shapeIds, e.pages?.[0])}
                  className="ml-auto rounded bg-orange-500/15 px-1.5 py-0.5 text-[10px] text-orange-300 hover:bg-orange-500/25"
                >
                  Show
                </button>
              )}
            </div>
          )}
        </div>
      ))}
      {!entries.length && !busy && !err && (
        <p className="py-4 text-center text-gray-500">No changes logged yet.</p>
      )}
      {more && entries.length > 0 && (
        <button
          onClick={() => load(entries[entries.length - 1].rev)}
          disabled={busy}
          className="w-full rounded-lg bg-white/5 py-1.5 text-gray-300 hover:bg-white/10 disabled:opacity-40"
        >
          Older changes
        </button>
      )}
    </div>
  );
}

export default function TakeoffViewer({
  projectId,
  opportunityId, // bid-stage takeoff: pass this instead of projectId
  onRenameDrawing, // async (takeoffId, newName) => savedName  (PATCH /takeoffs3/:id/rename)
  conditionRenames, // { [condId]: newName } — set by the estimate page
  fileUrl, // optional override; by default the drawing streams from MongoDB via gcsPath ("gridfs:<id>")
  initialData,
  onSave, // async (payload) => saved doc
  onPushToBudget, // async (takeoffId) => push result
  onUploadDrawing, // async (file) => { gcsPath } — uploads the PDF into MongoDB GridFS
  getCatalog, // async () => ItemDatabase catalog
  onGoToEstimate, // () => void — switch the launcher to the Estimate tab
  onRemoteChanges, // (entries) => void — other people's saved changes, for the estimate (mappings / settings)
  // RFIs on the drawing
  rfiPins = [], // [{ id, rfiId, number, title, status, page, lat, lng, question }]
  pinMode = false, // the "RFI pin" tool is on: a click on the drawing calls onRfiPinPlace
  onPinModeChange, // (on) => void
  onRfiPinPlace, // (page, [lat, lng]) => void
  onRfiPinClick, // (pin) => void
  focusPin = null, // { page, lat, lng, t } — go there
  getPayloadRef, // ref the host can call for the drawing's current data (RFI suggestions)
  onImmersiveChange, // (on) => void — the workspace went full screen (the host shows the estimate full screen too)
  snipMode = false, // "cut a picture" tool: drag a box on the drawing → onSnip(dataURL, { page })
  onSnipModeChange, // (on) => void
  onSnip,
  onDuplicateCondition, // (fromId, toId) => void — the host copies the estimate items (mappings) too
  listTakeoffs, // async () => [{ _id, fileName, pageCount, updatedAt, conditions, gcsPath }] — saved takeoffs on this project
  onOpenTakeoff, // (id) => void — open another saved takeoff
  className = "",
  inShell = false, // true when rendered by COTakeoffLauncher (already full-window, outside the app tabs)
}) {
  // --- document state
  const [pdfDoc, setPdfDoc] = useState(null);
  const pdfDocRef = useRef(null);
  pdfDocRef.current = pdfDoc;
  const [fileName, setFileName] = useState(initialData?.fileName || "");
  const [gcsPath, setGcsPath] = useState(initialData?.gcsPath || "");
  // the newest file name / stored drawing, readable from callbacks made before the re-render
  const gcsRef = useRef(initialData?.gcsPath || "");
  const fileNameRef = useRef(initialData?.fileName || "");
  const saveRef = useRef(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageImage, setPageImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [autoNaming, setAutoNaming] = useState(false);
  const [autoNameProgress, setAutoNameProgress] = useState({
    done: 0,
    total: 0,
  });
  // add this ref up with the others
  const resizeObsRef = useRef(null);
  const fitRef = useRef(null); // holds the current page's fit function
  const [mapReady, setMapReady] = useState(false);
  // --- takeoff state
  const drawCondIdRef = useRef(null); // condition locked in when Draw was clicked
  const [conditions, setConditions] = useState(initialData?.conditions || []);
  const [shapes, setShapes] = useState(initialData?.shapes || []);
  const [scales, setScales] = useState(initialData?.scales || {});
  const [editCondId, setEditCondId] = useState(null); // assembly whose name is being edited in the left panel
  const [editAsmId, setEditAsmId] = useState(null); // assembly open in the edit panel (name, tag, height, colour, items)
  const [units, setUnits] = useState(initialData?.units || []); // typical units: [{id, name, qty}]
  const [unitDraft, setUnitDraft] = useState(null); // { ids, name, qty } while naming a new unit
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      return JSON.parse(
        localStorage.getItem("takeoff.collapsedGroups") || "[]",
      );
    } catch {
      return [];
    }
  });
  const presentGroups = ["linear", "area", "count"].filter((t) =>
    conditions.some((c) => c.type === t),
  );
  const allGroupsShut =
    presentGroups.length > 0 &&
    presentGroups.every((t) => collapsedGroups.includes(t));
  useEffect(() => {
    try {
      localStorage.setItem(
        "takeoff.collapsedGroups",
        JSON.stringify(collapsedGroups),
      );
    } catch {
      /* private mode */
    }
  }, [collapsedGroups]);
  // undo / redo: snapshots of the takeoff (shapes, conditions, scales). A burst of changes (a vertex drag,
  // a trace) is one step. Undo/redo themselves don't record.
  const histRef = useRef({
    past: [],
    future: [],
    last: null,
    skip: false,
    t: 0,
  });
  const [histN, setHistN] = useState({ past: 0, future: 0 });
  useEffect(() => {
    const h = histRef.current;
    const cur = { shapes, conditions, scales, units };
    if (
      h.last &&
      !h.skip &&
      (h.last.shapes !== shapes ||
        h.last.conditions !== conditions ||
        h.last.scales !== scales ||
        h.last.units !== units)
    ) {
      const now = Date.now();
      if (now - h.t > 350 || !h.past.length) {
        h.past.push(h.last);
        if (h.past.length > 150) h.past.shift();
      }
      h.t = now;
      h.future = [];
    }
    h.skip = false;
    h.last = cur;
    setHistN((n) =>
      n.past === h.past.length && n.future === h.future.length
        ? n
        : { past: h.past.length, future: h.future.length },
    );
  }, [shapes, conditions, scales, units]);

  const [pageNames, setPageNames] = useState(initialData?.pageNames || {});
  // ── sheet revisions: an updated sheet is shown instead of the original; the takeoff stays as it is.
  //    { [page]: [{ no, gcsPath, srcPage, fileName, at, by, w, h }] } oldest first. Which one a screen shows is
  //    that screen's own choice (the newest unless picked otherwise in the sheet's revision menu).
  const [sheetRevisions, setSheetRevisions] = useState(
    initialData?.sheetRevisions || {},
  );
  const [revView, setRevView] = useState({}); // page → revision no. shown (0 = the original)
  const revsOf = (n) => sheetRevisions[n] || sheetRevisions[String(n)] || [];

  // the revision shown for page n (from any callback, newest state) → { no, gcsPath, srcPage … } | null

  const shownRevNo = (n) => {
    const l = revsOf(n);
    if (!l.length) return 0;
    const v = revView[n];
    return v === undefined ||
      (v && !l.some((r) => r.no === v)) ||
      (v === 0 && l[0].added)
      ? l[l.length - 1].no
      : v;
  };
  // a sheet that only exists in an added drawing (no original page to go back to)
  const isAddedSheet = (n) => !!revsOf(n)[0]?.added;
  const revStateRef = useRef({});
  revStateRef.current = { sheetRevisions, revView };
  // the revision shown for page n (from any callback, newest state) → { no, gcsPath, srcPage … } | null
  const shownRev = (n) => {
    const { sheetRevisions: sr, revView: rv } = revStateRef.current;
    const l = sr[n] || sr[String(n)] || [];
    if (!l.length) return null;
    const v = rv[n];
    if (v === 0 && !l[0].added) return null;
    return l.find((r) => r.no === v) || l[l.length - 1];
  };

  const revKeyOf = (n) => {
    const r = shownRev(n);
    return r ? `${r.gcsPath}#${r.srcPage || 1}` : "orig";
  };
  const [pageBidAreas, setPageBidAreas] = useState(
    initialData?.pageBidAreas || {},
  );
  // change orders pinned on the drawing (projects): [{ id, coId, number, title, status, page, lat, lng, note }]
  const [coPins, setCoPins] = useState(initialData?.coPins || []);
  const bidAreaOptions = useMemo(
    () => [
      ...new Set([
        ...Object.values(pageBidAreas)
          .map((v) => String(v || "").trim())
          .filter(Boolean),
        ...BID_AREA_PRESETS,
      ]),
    ],
    [pageBidAreas],
  );
  const [excludedPages, setExcludedPages] = useState(
    initialData?.excludedPages || [],
  );
  const [status, setStatus] = useState(initialData?.status || "draft");
  const [takeoffId, setTakeoffId] = useState(initialData?._id || null);
  const takeoffIdRef = useRef(initialData?._id || null);
  const [saveError, setSaveError] = useState("");
  const [activeCondId, setActiveCondId] = useState(null);
  const [editingShapeId, setEditingShapeId] = useState(null);
  // pan | draw | calibrate | measure | edit | delete | extend
  const [mode, setMode] = useState("select");
  const [calibDialog, setCalibDialog] = useState(null); // { pixels }
  const [calibInput, setCalibInput] = useState("");
  const [scaleAllPages, setScaleAllPages] = useState(false);
  const [measureUnits, setMeasureUnits] = useState(0);
  const [measurePts, setMeasurePts] = useState(0);
  // how each sheet got its scale (auto-check on upload, preset, calibrated) — and which ones need one
  const [scaleInfo, setScaleInfo] = useState(initialData?.scaleInfo || {});
  const [scaleRun, setScaleRun] = useState(null); // { done, total } while the set is being checked
  const [scaleMenu, setScaleMenu] = useState(false);
  const [drawingMenu, setDrawingMenu] = useState(false);
  const [openConds, setOpenConds] = useState([]); // assemblies whose items are expanded
  const [editingDescId, setEditingDescId] = useState(null);
  const toggleOpenCond = (id) =>
    setOpenConds((v) =>
      v.includes(id) ? v.filter((x) => x !== id) : [...v, id],
    );
  // left panel: width (drag its edge), the drawing / scale card folded or open, assembly search
  const lsGet = (k, d) => {
    try {
      const v = localStorage.getItem(k);
      return v == null ? d : JSON.parse(v);
    } catch {
      return d;
    }
  };
  const lsSet = (k, v) => {
    try {
      localStorage.setItem(k, JSON.stringify(v));
    } catch {
      /* private mode */
    }
  };
  const [leftW, setLeftW] = useState(() => lsGet("takeoff.leftW", 320));
  const [asmQuery, setAsmQuery] = useState("");
  const [savedList, setSavedList] = useState(null);
  useEffect(() => {
    if (pdfDoc || !listTakeoffs) return;
    let dead = false;
    listTakeoffs()
      .then((l) => {
        if (!dead) setSavedList(Array.isArray(l) ? l : []);
      })
      .catch(() => {});
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc]);
  // the Assemblies list shows THIS sheet (assemblies drawn here, with this sheet's quantities) by default;
  // "All sheets" shows the whole takeoff with a per-sheet breakdown
  const [asmScope, setAsmScope] = useState(() =>
    lsGet("takeoff.asmScope", "sheet"),
  );
  useEffect(() => lsSet("takeoff.asmScope", asmScope), [asmScope]);
  useEffect(() => lsSet("takeoff.leftW", leftW), [leftW]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  // --- add-condition form
  const [showAddCond, setShowAddCond] = useState(false);
  const [newCond, setNewCond] = useState({
    name: "",
    description: "",
    type: "area",
    wallType: "",
    height: "",
  });

  // --- refs
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const layerMapRef = useRef(new Map());
  const pageCacheRef = useRef(new Map());
  const renderingRef = useRef(new Map()); // "page|revision" → render under way
  const pendingFocusRef = useRef(null);

  // calibrate / measure dimension line
  const tempPtsRef = useRef([]); // max 2 points
  const dimGroupRef = useRef(null);

  // extend-line mode
  const extendShapeIdRef = useRef(null);
  const lastShapeClickRef = useRef(0);
  const boxRef = useRef({ start: null, rect: null, endedAt: 0 });
  const drawRef = useRef(null); // live drawing: { condId, kind, pts:[[lat,lng]], extendId, layers }
  // a length typed while drawing ("10", "10'6", "126\"") — Enter places the next point exactly that far
  const lenTypedRef = useRef("");
  const [lenTyped, setLenTypedState] = useState("");
  const setLenTyped = (v) => {
    lenTypedRef.current = v;
    setLenTypedState(v);
  };
  const mouseLLRef = useRef(null); // last pointer position (lat,lng)
  const extendPreviewRef = useRef(null);

  const handlersRef = useRef({});
  const stateRef = useRef({});
  // selection: one or many shapes (click, Shift+click, or drag a box with the Select tool)
  const selRef = useRef(new Set());
  const [selIds, setSelIdsState] = useState([]);
  const setSel = (ids) => {
    const u = [...new Set(ids)];
    selRef.current = new Set(u);
    setSelIdsState(u);
  };
  const selShapeId = selIds.length === 1 ? selIds[0] : null;
  const setSelShapeId = (id) => setSel(id ? [id] : []);
  const baseToolRef = useRef("select"); // what Esc returns to: "select" (box-select) or "pan"
  const isNeutral = (m) => m === "select" || m === "pan";
  stateRef.current = {
    conditions,
    shapes,
    scales,
    activeCondId,
    mode,
    pageNum,
    pageImage,
    excludedPages,
    units,
  };

  const fpu = scales[pageNum] ?? scales[String(pageNum)] ?? null;
  const activeCond = conditions.find((c) => c.id === activeCondId) || null;

  function setMapCursor(cursor) {
    const el = mapRef.current?.getContainer();
    if (el) el.style.cursor = cursor;
  }

  // -------------------------------------------------------------------------
  // Dimension line — standard estimation style:
  //   perpendicular tick at each end, straight line between, arrowheads at the
  //   ends pointing OUTWARD into the ticks, measurement label centered above.
  // -------------------------------------------------------------------------
  function clearDim() {
    const map = mapRef.current;
    if (dimGroupRef.current && map) map.removeLayer(dimGroupRef.current);
    dimGroupRef.current = null;
  }

  function renderDim(
    p1,
    p2,
    { live = false, keep = false, color = "#f97316", n = null } = {},
  ) {
    const map = mapRef.current;
    if (!map) return null;
    if (!keep) clearDim();
    const group = L.layerGroup();
    const [y1, x1] = p1;
    const [y2, x2] = p2;
    const len = Math.hypot(x2 - x1, y2 - y1);
    const z = Math.pow(2, map.getZoom()); // screen px per map unit
    const ah = 14 / z; // arrowhead length
    const tick = 10 / z; // half tick length
    const opts = {
      color,
      weight: 2,
      opacity: live ? 0.85 : 1,
      interactive: false,
    };

    // main line
    group.addLayer(L.polyline([p1, p2], opts));

    if (len > 0) {
      const ang = Math.atan2(y2 - y1, x2 - x1);
      const nx = -Math.sin(ang); // unit normal
      const ny = Math.cos(ang);

      // perpendicular end ticks
      for (const [ty, tx] of [
        [y1, x1],
        [y2, x2],
      ]) {
        group.addLayer(
          L.polyline(
            [
              [ty + ny * tick, tx + nx * tick],
              [ty - ny * tick, tx - nx * tick],
            ],
            opts,
          ),
        );
      }

      // arrowheads INSIDE the line, tips at the ticks, wings toward center
      const wings = (tipY, tipX, inwardAng) => {
        for (const da of [Math.PI / 8, -Math.PI / 8]) {
          const a = inwardAng + da;
          group.addLayer(
            L.polyline(
              [
                [tipY, tipX],
                [tipY + Math.sin(a) * ah, tipX + Math.cos(a) * ah],
              ],
              opts,
            ),
          );
        }
      };
      wings(y1, x1, ang); // tip at p1, wings toward p2
      wings(y2, x2, ang + Math.PI); // tip at p2, wings toward p1

      // measurement label — offset above the line, well padded
      const st = stateRef.current;
      const pageFpu =
        st.scales[st.pageNum] ?? st.scales[String(st.pageNum)] ?? null;
      const label =
        (n ? `<span style="opacity:.55;font-weight:500">#${n}</span> ` : "") +
        (pageFpu != null ? fmtFeetIn(len * pageFpu) : `${fmtQty(len, 0)} px`);
      const off = 18 / z;
      const mid = [(y1 + y2) / 2 + ny * off, (x1 + x2) / 2 + nx * off];
      group.addLayer(
        L.marker(mid, {
          interactive: false,
          icon: L.divIcon({
            className: "",
            html: `<div style="transform:translate(-50%,-50%);width:max-content;background:#111827;border:1.5px solid ${color};color:#ffedd5;font:600 13px/1.3 ui-sans-serif,system-ui;padding:4px 10px;border-radius:7px;white-space:nowrap;letter-spacing:.02em;box-shadow:0 2px 8px rgba(0,0,0,.55)">${label}</div>`,
            iconSize: [0, 0],
          }),
        }),
      );
    }

    group.addTo(map);
    if (!keep) dimGroupRef.current = group;
    return group;
  }
  // finished measurements stay on the sheet while you take the next: [{id, p1, p2, page}]
  const measuresRef = useRef([]);
  const measureLayersRef = useRef(new Map());
  const measureHoverRef = useRef(null);
  const [measures, setMeasuresState] = useState([]);
  const [measureChain, setMeasureChain] = useState(false);
  const measureChainRef = useRef(false);
  measureChainRef.current = measureChain;
  const setMeasures = (list) => {
    measuresRef.current = list;
    setMeasuresState(list);
  };
  function drawMeasures() {
    const map = mapRef.current;
    if (!map) return;
    measureLayersRef.current.forEach((g) => map.removeLayer(g));
    measureLayersRef.current.clear();
    const pg = stateRef.current.pageNum;
    measuresRef.current.forEach((m, i) => {
      if (m.page !== pg) return;
      const g = renderDim(m.p1, m.p2, {
        keep: true,
        color: "#fb923c",
        n: measuresRef.current.length > 1 ? i + 1 : null,
      });
      if (g) measureLayersRef.current.set(m.id, g);
    });
  }
  handlersRef.current.cancelMeasurePoint = () => {
    tempPtsRef.current = [];
    clearDim();
    setMeasurePts(measuresRef.current.length ? 2 : 0);
  };
  // the sheet changed: show that sheet's measurements
  useEffect(() => {
    if (measuresRef.current.length) drawMeasures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum]);
  function clearMeasures() {
    setMeasures([]);
    drawMeasures();
    tempPtsRef.current = [];
    clearDim();
    setMeasurePts(0);
    setMeasureUnits(0);
  }
  function hideMeasureHover() {
    if (measureHoverRef.current && mapRef.current)
      mapRef.current.removeLayer(measureHoverRef.current);
    measureHoverRef.current = null;
  }
  // where a measuring click lands: a corner or line of the drawing nearby; from the first point, locked to the
  // usual directions unless Shift
  function measureSnap(raw, free) {
    const from = tempPtsRef.current.length === 1 ? tempPtsRef.current[0] : null;
    return constrainPoint(raw, free, from);
  }

  // assembly renames made on the estimate page → the drawing's conditions
  useEffect(() => {
    if (!conditionRenames) return;
    setConditions((prev) =>
      prev.map((c) =>
        conditionRenames[c.id] != null && conditionRenames[c.id] !== c.name
          ? { ...c, name: conditionRenames[c.id] }
          : c,
      ),
    );
  }, [conditionRenames]);

  // -------------------------------------------------------------------------
  // Map lifecycle
  // -------------------------------------------------------------------------
  useEffect(() => {
    const map = L.map(mapElRef.current, {
      crs: L.CRS.Simple,
      minZoom: -5,
      maxZoom: 6,
      zoomSnap: 0,
      zoomDelta: 0.75,
      wheelPxPerZoomLevel: 60,
      wheelDebounceTime: 15,
      zoomControl: false,
      attributionControl: false,
      doubleClickZoom: false,
      preferCanvas: true,
    });
    L.control.zoom({ position: "bottomright" }).addTo(map);
    map.pm.setGlobalOptions({
      allowSelfIntersection: false,
      snappable: false, // corner handles snap to the drawing's lines on release (snapEditedVertex), not to other shapes
      continueDrawing: true,
      hideMiddleMarkers: false,
    });

    map.on("pm:create", (e) => handlersRef.current.onCreate?.(e));
    map.on("pm:remove", (e) => handlersRef.current.onRemove?.(e));
    map.on("click", (e) => handlersRef.current.onMapClick?.(e));
    map.on("dblclick", (e) => handlersRef.current.onMapDblClick?.(e));
    map.on("mousemove", (e) => handlersRef.current.onMouseMove?.(e));
    map.on("zoomend", () => handlersRef.current.onZoomEnd?.());

    const el = map.getContainer();
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      e.stopPropagation();
      const step = e.deltaY < 0 ? 1.5 : -1.5;
      const pt = map.mouseEventToContainerPoint(e);
      map.setZoomAround(pt, map.getZoom() + step);
    };
    el.addEventListener("wheel", onWheel, { passive: false });

    // Select tool: drag on the drawing = box select (every line / area the box touches).
    // Right- or middle-button drag pans with any tool. A right-click without dragging while drawing
    // finishes the line / area (like Enter).
    let mid = null;
    const onCtx = (ev) => ev.preventDefault();
    const onDown = (ev) => {
      // right- or middle-drag pans; so does a left-drag with Ctrl (Cmd on a Mac) held, with any tool
      const modPan =
        ev.button === 0 &&
        (ev.ctrlKey || ev.metaKey) &&
        handlersRef.current.mode?.() !== "pan";
      if (ev.button === 1 || ev.button === 2 || modPan) {
        if (ev.target.closest?.(".leaflet-control")) return;
        ev.preventDefault();
        ev.stopPropagation();
        mid = {
          x: ev.clientX,
          y: ev.clientY,
          x0: ev.clientX,
          y0: ev.clientY,
          btn: modPan ? "mod" : ev.button,
        };
        el.style.cursor = "grabbing";
        return;
      }
      if (ev.button !== 0 || handlersRef.current.mode?.() !== "select") return;
      if (snipModeRef.current || pinModeRef.current) return; // the RFI tools own the left button
      if (ev.target.closest?.(".leaflet-control, .leaflet-marker-icon")) return;
      boxRef.current.start = map.mouseEventToLatLng(ev);
      boxRef.current.moved = false;
      boxRef.current.shift = ev.shiftKey;
    };
    const onMove = (ev) => {
      if (mid) {
        map.panBy([mid.x - ev.clientX, mid.y - ev.clientY], { animate: false });
        mid = { x: ev.clientX, y: ev.clientY };
        return;
      }
      const b = boxRef.current;
      if (!b.start) return;
      const cur = map.mouseEventToLatLng(ev);
      const a = map.latLngToContainerPoint(b.start),
        c = map.latLngToContainerPoint(cur);
      if (!b.moved && Math.hypot(a.x - c.x, a.y - c.y) < 5) return;
      b.moved = true;
      const bounds = L.latLngBounds(b.start, cur);
      if (b.rect) b.rect.setBounds(bounds);
      else
        b.rect = L.rectangle(bounds, {
          color: "#fb923c",
          weight: 1,
          dashArray: "4 3",
          fillColor: "#fb923c",
          fillOpacity: 0.08,
          interactive: false,
        }).addTo(map);
    };
    const onUp = () => {
      if (mid) {
        const clickOnly = Math.hypot(mid.x - mid.x0, mid.y - mid.y0) < 4;
        if (mid.btn === 2 && clickOnly) handlersRef.current.rightClick?.();
        if (mid.btn === "mod") handlersRef.current.panEndedAt = Date.now();
        mid = null;
        el.style.cursor = modDown ? "grab" : "";
        return;
      }
      const b = boxRef.current;
      if (!b.start) return;
      if (b.moved && b.rect) {
        handlersRef.current.boxSelect?.(b.rect.getBounds(), b.shift);
        b.endedAt = Date.now();
      }
      if (b.rect) map.removeLayer(b.rect);
      b.start = null;
      b.rect = null;
      b.moved = false;
    };
    // holding Ctrl / Cmd shows the hand: drag to pan
    let modDown = false;
    const onModKey = (e) => {
      const down = e.ctrlKey || e.metaKey;
      if (down === modDown) return;
      modDown = down;
      if (!mid) el.style.cursor = down ? "grab" : "";
    };
    const onBlur = () => {
      modDown = false;
      if (!mid) el.style.cursor = "";
    };
    window.addEventListener("keydown", onModKey);
    window.addEventListener("keyup", onModKey);
    window.addEventListener("blur", onBlur);
    el.addEventListener("mousedown", onDown, true);
    el.addEventListener("contextmenu", onCtx);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);

    mapRef.current = map;
    map.dragging.disable(); // Select is the default tool: drag = box select (right- or middle-drag pans)

    // Single source of truth for "container changed size": whenever the map
    // element gets a real, non-zero box, recalc size and re-run whatever fit
    // the current page registered. This fires on mount, on tab show, on window
    // resize, on sidebar collapse — every case the old RAF loop tried to guess.
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      const w = entry?.contentRect?.width ?? el.clientWidth;
      const h = entry?.contentRect?.height ?? el.clientHeight;
      if (w > 0 && h > 0) {
        map.invalidateSize({ animate: false });
        // run the active page's fit (if one is waiting on real size)
        fitRef.current?.();
      }
    });
    ro.observe(el);
    resizeObsRef.current = ro;

    return () => {
      ro.disconnect();
      resizeObsRef.current = null;
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("mousedown", onDown, true);
      el.removeEventListener("contextmenu", onCtx);
      window.removeEventListener("keydown", onModKey);
      window.removeEventListener("keyup", onModKey);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      // Geoman turns a layer's editing off when the layer is removed, and on the canvas renderer that touches an
      // SVG path the line doesn't have (→ "reading 'classList'" on unmount / hot reload). Switch Geoman off and
      // drop those listeners first, then tear the map down — a failure here never takes the page with it.
      try {
        map.pm?.disableDraw?.();
        map.pm?.disableGlobalEditMode?.();
        map.pm?.disableGlobalDragMode?.();
        map.pm?.disableGlobalRemovalMode?.();
      } catch {
        /* already off */
      }
      map.eachLayer((l) => {
        try {
          l.off("remove");
          l.off("pm:disable");
          if (l.pm) l.pm._enabled = false;
        } catch {
          /* ignore */
        }
      });
      try {
        map.remove();
      } catch (e) {
        console.warn("[takeoff] map teardown", e);
      }
      mapRef.current = null;
    };
  }, []);
  useEffect(() => {
    const onKey = (e) => {
      const st = stateRef.current;
      const t = e.target;
      const typing =
        t?.tagName === "INPUT" ||
        t?.tagName === "TEXTAREA" ||
        t?.tagName === "SELECT";
      const drawing =
        (st.mode === "draw" || st.mode === "extend") && drawRef.current;
      if (e.key === "Escape" && handlersRef.current.roomAsk?.()) {
        handlersRef.current.closeRoomAsk?.();
        return;
      }
      if (e.key === "Escape" && pinModeRef.current) {
        pinCbRef.current?.(false);
        return;
      }
      if (e.key === "Escape" && coModeRef.current) {
        handlersRef.current.setCoMode?.(false);
        return;
      }
      if (e.key === "Escape" && placeDoorRef.current) {
        handlersRef.current.cancelPlaceDoor?.();
        return;
      }
      if (e.key === "Escape" && spliceRef.current) {
        handlersRef.current.cancelSplice?.();
        return;
      }
      if (e.key === "Escape" && snipModeRef.current) {
        snipCbRef.current?.(false);
        return;
      }
      // measuring: Esc first drops a half-done measurement, then leaves the tool
      if (
        e.key === "Escape" &&
        st.mode === "measure" &&
        tempPtsRef.current.length === 1
      ) {
        handlersRef.current.cancelMeasurePoint?.();
        return;
      }
      if (e.key === "Escape" && drawing && lenTypedRef.current) {
        setLenTyped(""); // first Esc only clears a typed length
        handlersRef.current.redrawDraft?.();
        return;
      }
      if (e.key === "Escape") {
        // Esc keeps what you've drawn (a line needs 2 points, an area 3) and leaves the tool.
        // To throw points away, use Backspace first.
        if (drawing) {
          handlersRef.current.finishDraft?.();
          if (stateRef.current.mode === "draw") setPanMode();
          return;
        }
        setPanMode();
        return;
      }
      if (typing) return;
      // typing a length while drawing: digits, ' " . - and a space between feet and inches
      if (
        drawing &&
        drawRef.current.kind !== "marker" &&
        drawRef.current.pts.length &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey
      ) {
        const buf = lenTypedRef.current;
        if (
          /^[0-9.'"]$/.test(e.key) ||
          (buf && (e.key === "-" || e.key === " "))
        ) {
          e.preventDefault();
          setLenTyped((buf + e.key).slice(0, 14));
          handlersRef.current.redrawDraft?.();
          return;
        }
        if (buf && e.key === "Backspace") {
          e.preventDefault();
          setLenTyped(buf.slice(0, -1));
          handlersRef.current.redrawDraft?.();
          return;
        }
        if (buf && e.key === "Escape") {
          e.preventDefault();
          setLenTyped("");
          handlersRef.current.redrawDraft?.();
          return;
        }
        if (buf && (e.key === "Enter" || e.key === "Tab")) {
          e.preventDefault();
          handlersRef.current.placeTypedLength?.(buf);
          return;
        }
      }
      if (e.key === "Enter" && drawing) {
        e.preventDefault();
        (handlersRef.current.finishDraft || finishDraft)();
        return;
      }
      // C while drawing a wall line: centre it on the wall ⇄ keep it exactly where clicked (this line only)
      if (
        drawing &&
        drawRef.current.kind === "polyline" &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        e.key.toLowerCase() === "c"
      ) {
        e.preventDefault();
        e.stopImmediatePropagation(); // (C is also Calibrate — not while drawing a line)
        const d = drawRef.current;
        d.center = d.center === false;
        d._cKey = null;
        handlersRef.current.redrawDraft?.();
        return;
      }
      if (e.key === "Backspace" && drawing) {
        e.preventDefault();
        handlersRef.current.popPoint?.();
        return;
      }
      if (e.code === "Space") {
        // Space = draw with the selected line's / area's assembly (pick a line, Space, draw).
        // Shift+Space on one selected line = carry on drawing that same line.
        e.preventDefault();
        if (drawing) return;
        const ids = [...selRef.current];
        const sel = ids
          .map((id) => st.shapes.find((x) => x.id === id))
          .filter(Boolean);
        if (e.shiftKey && sel.length === 1 && sel[0].kind === "polyline") {
          handlersRef.current.extend?.(sel[0]);
          return;
        }
        const condId =
          sel.length && sel.every((x) => x.conditionId === sel[0].conditionId)
            ? sel[0].conditionId
            : !sel.length
              ? st.activeCondId
              : null;
        if (condId) handlersRef.current.drawWith?.(condId);
        else
          flash(
            sel.length
              ? "These are different assemblies — select one line, then Space."
              : "Click a line (or pick an assembly), then Space to draw with it.",
            "info",
          );
        return;
      }
      if (
        (e.metaKey || e.ctrlKey) &&
        !e.altKey &&
        (e.key.toLowerCase() === "z" || e.key.toLowerCase() === "y")
      ) {
        e.preventDefault();
        const redo = e.key.toLowerCase() === "y" || e.shiftKey;
        // while drawing, Ctrl+Z takes the last point back off; with nothing drawn yet it undoes the last change
        if (!redo) {
          handlersRef.current.smartUndo?.();
          return;
        }
        if (drawing && drawRef.current?.pts?.length) return;
        handlersRef.current.redo?.();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // V flips Select ⇄ Pan (from any other tool it goes to Select); H is always Pan
      if (!drawing && e.key.toLowerCase() === "v")
        setTool(
          st.mode === "select"
            ? "pan"
            : st.mode === "pan"
              ? "select"
              : baseToolRef.current === "pan"
                ? "pan"
                : "select",
        );
      if (!drawing && e.key.toLowerCase() === "h") setTool("pan");
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // the title block corner only (right 32 % × bottom 50 % of the sheet), ~1000 px, JPEG — a few hundred KB at most
  async function titleBlockImage(page) {
    const base = page.getViewport({ scale: 1 });
    const cw = base.width * 0.32,
      ch = base.height * 0.5;
    const x = base.width - cw,
      y = base.height - ch;
    const sc = Math.min(3, 1000 / Math.max(cw, ch));
    const vp = page.getViewport({
      scale: sc,
      offsetX: -x * sc,
      offsetY: -y * sc,
    });
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(cw * sc));
    canvas.height = Math.max(1, Math.round(ch * sc));
    const ctx = canvas.getContext("2d", { alpha: false });
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    const b64 = canvas.toDataURL("image/jpeg", 0.7).split(",")[1];
    canvas.width = canvas.height = 0;
    return b64;
  }

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "X-Tenant-ID": localStorage.getItem("tenantId"),
  });

  // ── sheet names: read the title block from the PDF's own text first (free), crop-and-AI only if that fails ──
  const TB_LABELS =
    /^(SHEET|TITLE|NAME|NO\.?|NUMBER|DRAWN|CHECKED|DATE|PROJECT|SCALE|REVISION|REV|ISSUED?|JOB|DWG|BY|OF|FOR|CLIENT|ARCHITECT|ENGINEER|SEAL|STAMP)\b/i;

  // text of a PDF page (text layer + mark-up / text-box annotations, which often hold legends)
  async function pageItems(page) {
    const vp = page.getViewport({ scale: 1 });
    const tc = await page.getTextContent();
    const items = [];
    for (const it of tc.items || []) {
      const str = (it.str || "").trim();
      if (!str) continue;
      const t = pdfjsLib.Util.transform(vp.transform, it.transform);
      const h = Math.hypot(t[2], t[3]) || it.height || 8;
      items.push({
        s: str,
        x0: t[4],
        y0: t[5] - h,
        x1: t[4] + (it.width || str.length * h * 0.55) * vp.scale,
        y1: t[5],
      });
    }
    try {
      for (const a of await page.getAnnotations()) {
        const str = (a.contentsObj?.str || a.richText?.str || "")
          .replace(/\s+/g, " ")
          .trim();
        if (!str || !a.rect) continue;
        const [x0, y0, x1, y1] = vp.convertToViewportRectangle(a.rect);
        items.push({
          s: str,
          x0: Math.min(x0, x1),
          y0: Math.min(y0, y1),
          x1: Math.max(x0, x1),
          y1: Math.max(y0, y1),
          para: str.length > 20,
        });
      }
    } catch {
      /* optional */
    }
    return items;
  }
  async function sheetItems(n) {
    return pageItems(await docPage(n));
  }

  // sheet number + title from the title block's text (bottom-right block or right-edge strip)
  async function titleBlockFromPage(page) {
    const vp = page.getViewport({ scale: 1 });
    const W = vp.width,
      H = vp.height;
    const items = (await pageItems(page))
      .map((i) => ({ ...i, s: i.s.trim(), h: i.y1 - i.y0 }))
      .filter(
        (i) =>
          i.s &&
          i.h > 0 &&
          i.x1 >= W * 0.7 &&
          (i.y0 >= H * 0.55 || i.x0 >= W * 0.85),
      );
    if (!items.length) return null;

    const nums = items
      .filter((i) => SHEET_NO_RE.test(i.s.toUpperCase()) && /\d/.test(i.s))
      .sort((a, b) => b.h - a.h || b.y0 - a.y0);
    if (!nums.length) return null;
    const num = nums[0];

    const words = items.filter(
      (i) =>
        i !== num &&
        i.s.length >= 4 &&
        /[A-Za-z]{3,}/.test(i.s) &&
        !TB_LABELS.test(i.s) &&
        !/\d{1,2}[/-]\d{1,2}[/-]\d{2,4}/.test(i.s),
    );
    let title = null;
    if (words.length) {
      const top = words.sort((a, b) => b.h - a.h)[0];
      const lines = words
        .filter(
          (i) =>
            i.h >= top.h * 0.85 &&
            i.h <= top.h * 1.15 &&
            Math.abs(i.y0 - top.y0) <= top.h * 3.2 &&
            Math.abs(i.x0 - top.x0) <= W * 0.2,
        )
        .sort((a, b) => a.y0 - b.y0);
      title = lines.map((l) => l.s).join(" ");
    }
    return {
      sheetNumber: num.s.toUpperCase(),
      sheetTitle: title ? title.toUpperCase().slice(0, 120) : null,
    };
  }
  const titleBlockFromText = async (n) => titleBlockFromPage(await docPage(n));

  // the title block corner only (right 32 % × bottom 50 % of the sheet), ~1000 px, JPEG
  async function titleBlockImage(page) {
    const base = page.getViewport({ scale: 1 });
    const cw = base.width * 0.32,
      ch = base.height * 0.5;
    const x = base.width - cw,
      y = base.height - ch;
    const sc = Math.min(3, 1000 / Math.max(cw, ch));
    const vp = page.getViewport({
      scale: sc,
      offsetX: -x * sc,
      offsetY: -y * sc,
    });
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(cw * sc));
    canvas.height = Math.max(1, Math.round(ch * sc));
    const ctx = canvas.getContext("2d", { alpha: false });
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    const b64 = canvas.toDataURL("image/jpeg", 0.7).split(",")[1];
    canvas.width = canvas.height = 0;
    return b64;
  }

  async function autoNameSheets(opts) {
    if (!pdfDoc || !pageCount) return;
    const force = opts?.force === true; // (the button passes a click event — ignored)
    const named = (n) => !!(pageNames[n] ?? pageNames[String(n)]);
    const pages = Array.from({ length: pageCount }, (_, i) => i + 1).filter(
      (n) => !excludedPages.includes(n) && (force || !named(n)),
    );
    if (!pages.length) return flash("Every sheet already has a name.", "ok");

    setAutoNaming(true);
    setAutoNameProgress({ done: 0, total: pages.length });
    const results = {};
    const needAi = [];

    try {
      // 1) free: the PDF's own text
      for (let i = 0; i < pages.length; i++) {
        const n = pages[i];
        try {
          const t = await titleBlockFromText(n);
          if (t)
            results[n] = [t.sheetNumber, t.sheetTitle]
              .filter(Boolean)
              .join(" — ");
          else needAi.push(n);
        } catch {
          needAi.push(n);
        }
        setAutoNameProgress({ done: i + 1, total: pages.length });
        if (i % 10 === 9) await new Promise((r) => setTimeout(r, 0));
      }

      // 2) AI only for the rest, on the title-block corner only, and only if the estimator agrees
      let aiRead = 0;
      if (
        needAi.length &&
        window.confirm(
          `${Object.keys(results).length} sheet(s) named from the drawing's text.\n\n${needAi.length} sheet(s) have no readable title-block text (scanned or flattened). Read those with AI? (title block corner only)`,
        )
      ) {
        setAutoNameProgress({ done: 0, total: needAi.length });
        let cursor = 0;
        const worker = async () => {
          while (cursor < needAi.length) {
            const n = needAi[cursor++];
            try {
              const imageBase64 = await titleBlockImage(await docPage(n));
              const res = await fetch(`${API}/takeoffs3/name-sheet`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  ...authHeaders(),
                },
                body: JSON.stringify({ imageBase64 }),
              });
              const data = await res.json();
              if (data.success && (data.sheetNumber || data.sheetTitle)) {
                results[n] = [data.sheetNumber, data.sheetTitle]
                  .filter(Boolean)
                  .join(" — ");
                aiRead++;
              }
            } catch (err) {
              console.error(`Auto-name failed for page ${n}`, err);
            } finally {
              setAutoNameProgress((p) => ({ ...p, done: p.done + 1 }));
            }
          }
        };
        await Promise.all(Array.from({ length: 2 }, worker));
      }

      setPageNames((prev) => ({ ...prev, ...results }));
      const total = Object.keys(results).length;
      flash(
        total
          ? `Named ${total} of ${pages.length} sheets (${total - aiRead} from the drawing text${aiRead ? `, ${aiRead} by AI` : ""}).`
          : "Couldn't read any sheet names — type them in the Sheets list.",
        total ? "ok" : "warn",
      );
    } finally {
      setAutoNaming(false);
    }
  }

  // -------------------------------------------------------------------------
  // PDF loading + cached page rendering + preload
  // -------------------------------------------------------------------------
  const loadPdf = useCallback(async (src, name) => {
    setLoading(true);
    setError("");
    try {
      pageCacheRef.current.forEach((img) => URL.revokeObjectURL(img.url));
      pageCacheRef.current.clear();
      renderingRef.current.clear();
      overlayPageRef.current = null; // a new PDF: its first sheet is a new picture, fitted again
      engineRef.current?.reset();
      setSheetGeo({});

      // src: bytes, one URL, or several URLs to try in order (stored copy first, then the parent's link).
      // Our own drawing route needs the auth headers; anything else (a signed link) must not get them.
      const tries =
        typeof src === "string" ? [src] : Array.isArray(src) ? src : [null];
      let doc = null,
        lastErr = null;
      for (const u of tries) {
        try {
          const params =
            u === null
              ? { data: src }
              : {
                  url: u,
                  ...(/\/takeoffs3\/drawing/.test(u)
                    ? { httpHeaders: authHeaders() }
                    : {}),
                };
          doc = await pdfjsLib.getDocument(params).promise;
          break;
        } catch (e) {
          lastErr = e;
          console.warn(
            "Drawing source failed:",
            u,
            e?.status || "",
            e?.message,
          );
        }
      }
      if (!doc) throw lastErr || new Error("no drawing source");
      setPdfDoc(doc);
      // sheets added from later drawing sets sit after the original's last page
      setPageCount(
        Math.max(
          doc.numPages,
          ...Object.keys(revStateRef.current.sheetRevisions || {}).map(
            (k) => Number(k) || 0,
          ),
        ),
      );
      setPageNum(1);
      if (name) {
        setFileName(name);
        fileNameRef.current = name;
      }
    } catch (err) {
      console.error("PDF load failed", err);
      // say why — the stored drawing route answers 401/403 (auth), 404 (file gone), 410 (old Google storage)
      const st = err?.status || (err?.name === "MissingPDFException" ? 404 : 0);
      setError(
        st === 401 || st === 403
          ? `Could not load the drawing — the server refused access (HTTP ${st}). Sign in again.`
          : st === 404
            ? "Could not load the drawing — it isn't in storage any more. Open the PDF again to re-attach it."
            : st === 410
              ? "This drawing is still in the old Google storage — open the PDF again to move it over."
              : st
                ? `Could not load the drawing (HTTP ${st}).`
                : `Could not load the drawing${err?.message ? ` — ${err.message}` : ""}.`,
      );
    } finally {
      setLoading(false);
    }
  }, []);
  // ── rename the drawing (the takeoff keeps its _id, so nothing forks) ──────────
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameVal, setRenameVal] = useState("");
  const [renameErr, setRenameErr] = useState("");
  async function renameDrawing() {
    let name = renameVal.trim().replace(/[\\/]/g, " ").replace(/\s+/g, " ");
    if (!name || name === fileNameRef.current) {
      setRenameOpen(false);
      setRenameErr("");
      return;
    }
    if (!/\.pdf$/i.test(name)) name += ".pdf";
    setRenameErr("");
    try {
      const id = takeoffIdRef.current || takeoffId;
      const serverRenamed = !!(id && onRenameDrawing);
      if (serverRenamed) name = (await onRenameDrawing(id, name)) || name;
      fileNameRef.current = name;
      setFileName(name);
      if (!serverRenamed) saveRef.current?.({ quiet: true }).catch(() => {});
      setRenameOpen(false);
    } catch (err) {
      setRenameErr(
        err?.response?.data?.error || err?.message || "Rename failed",
      );
    }
  }

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const buf = await file.arrayBuffer();
    // a new drawing: its stored copy comes from the upload below
    gcsRef.current = "";
    setGcsPath("");
    // the name is known now — don't wait for pdf.js (the upload can land first and save without it)
    fileNameRef.current = file.name;
    setFileName(file.name);
    autoLoadedRef.current = true; // it's open already — don't reload it from the server when the upload lands
    loadPdf(new Uint8Array(buf), file.name);
    e.target.value = "";
    // file the takeoff now — if the upload is slow or fails, it's still in the project's list
    setTimeout(() => {
      saveRef.current?.({ quiet: true }).catch(() => {});
    }, 0);
    // upload to the database in the background so it reloads automatically
    if (onUploadDrawing) {
      setUploadingPdf(true);
      try {
        const result = await onUploadDrawing(file);
        if (result?.gcsPath) {
          setGcsPath(result.gcsPath);
          // save straight away, so the stored drawing and its file name are linked to the takeoff
          gcsRef.current = result.gcsPath;
          setTimeout(() => {
            saveRef.current?.({ quiet: true }).catch(() => {});
          }, 0);
        }
      } catch (err) {
        console.error("Drawing upload failed", err);
        setError("Drawing upload failed — the takeoff will save without it.");
      } finally {
        setUploadingPdf(false);
      }
    }
  };

  // a sheet's picture — of the revision this screen shows for it (cached per sheet + revision; a render already
  // under way is shared, not started twice)
  const renderPage = useCallback(async (doc, n) => {
    const key = `${n}|${revKeyOf(n)}`;
    if (pageCacheRef.current.has(key)) return pageCacheRef.current.get(key);
    if (renderingRef.current.has(key)) return renderingRef.current.get(key);
    const job = renderPageNow(n, key);
    renderingRef.current.set(key, job);
    try {
      return await job;
    } finally {
      renderingRef.current.delete(key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function renderPageNow(n, key) {
    {
      const page = await docPageRef.current(n);
      const renderScale = await sheetScaleRef.current(n, page);
      const viewport = page.getViewport({ scale: renderScale });
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      await page.render({ canvasContext: canvas.getContext("2d"), viewport })
        .promise;
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      if (!blob) return null;
      const img = {
        url: URL.createObjectURL(blob),
        w: canvas.width,
        h: canvas.height,
        renderScale,
        page: n,
        rev: key,
      };
      pageCacheRef.current.set(key, img);
      return img;
    }
  }
  // ── the PDF page behind sheet n: the revision this screen shows, else the original ──
  const revDocsRef = useRef(new Map()); // stored path → Promise<pdf.js document>
  function revDoc(path) {
    if (!revDocsRef.current.has(path)) {
      const job = pdfjsLib
        .getDocument({
          url: `${API}/takeoffs3/drawing?path=${encodeURIComponent(path)}`,
          httpHeaders: authHeaders(),
        })
        .promise.catch((e) => {
          revDocsRef.current.delete(path);
          throw e;
        });
      revDocsRef.current.set(path, job);
    }
    return revDocsRef.current.get(path);
  }
  async function docPage(n) {
    const r = shownRev(n);
    if (r?.gcsPath) {
      try {
        const d = await revDoc(r.gcsPath);
        return await d.getPage(Math.min(d.numPages, r.srcPage || 1));
      } catch (e) {
        console.warn("Revision drawing failed — showing the original", e);
      }
    }
    return pdfDocRef.current.getPage(n);
  }
  // map units per PDF point for sheet n. A revision is drawn at the original's size on the map (its width
  // matches the original sheet), so the takeoff lies where it was even if the new sheet was plotted at
  // another paper size.
  async function sheetScale(n, page) {
    const base = page.getViewport({ scale: 1 });
    const own = Math.min(3, 4500 / Math.max(base.width, base.height));
    if (!shownRev(n) || !pdfDocRef.current) return own;
    try {
      const o = (await pdfDocRef.current.getPage(n)).getViewport({ scale: 1 });
      const ors = Math.min(3, 4500 / Math.max(o.width, o.height));
      return Math.abs(o.width - base.width) / o.width < 0.005
        ? own
        : (ors * o.width) / base.width;
    } catch {
      return own;
    }
  }
  const docPageRef = useRef(docPage);
  docPageRef.current = docPage;
  const sheetScaleRef = useRef(sheetScale);
  sheetScaleRef.current = sheetScale;

  // ── sheet revisions: upload an updated sheet (or the whole updated set) ──────────────────────────────
  // One sheet: "Add revision" on the sheet → the PDF (if it has several pages, the page that is this sheet is
  // found by its sheet number). The whole set: every page is matched to a sheet of the takeoff by sheet number /
  // title BEFORE anything changes — the sheets must be named (Auto-name or type the names) — and the estimator
  // checks the list first. Each matched sheet gets the next revision number and shows it; the takeoff carries on
  // unchanged on top of it. The original (and every earlier revision) stays a click away in the sheet's menu.
  const [revMenu, setRevMenu] = useState(false);
  const [revModal, setRevModal] = useState(null); // { mode, file, doc, rows:[{src, target, why}], page? }
  const [revBusy, setRevBusy] = useState("");
  const revFileRef = useRef(null);
  const revBulkRef = useRef(null);
  // the words on a page of the new set, with where they sit (0–1 of the page, y down) — title blocks
  async function pageTokens(doc, i) {
    try {
      const pg = await doc.getPage(i);
      const vp = pg.getViewport({ scale: 1 });
      const tc = await pg.getTextContent();
      const out = [];
      for (const it of tc.items || []) {
        const str = String(it.str || "").trim();
        if (!str) continue;
        const x = (it.transform?.[4] || 0) / vp.width,
          y = 1 - (it.transform?.[5] || 0) / vp.height;
        for (const w of str.split(/\s+/)) if (w) out.push({ s: w, x, y });
        out.push({ s: str.replace(/\s+/g, ""), x, y });
      }
      return out;
    } catch {
      return [];
    }
  }
  function namedSheets() {
    return Array.from({ length: pageCount }, (_, k) => k + 1).map((n) => ({
      page: n,
      name: pageNames[n] || pageNames[String(n)] || "",
    }));
  }
  async function openRevisionFile(file, { page = null } = {}) {
    if (!file) return;
    if (!onUploadDrawing)
      return flash("Uploading drawings isn't available here.", "warn");
    setRevBusy("Reading the new drawing…");
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const doc = await pdfjsLib.getDocument({ data: bytes }).promise;
      const N = Math.min(doc.numPages, 400);
      const newPages = [];
      for (let i = 1; i <= N; i++) {
        setRevBusy(`Reading sheet names… ${i}/${N}`);
        newPages.push({ src: i, tokens: await pageTokens(doc, i) });
      }
      const sheets = namedSheets().filter((x) => x.name);
      let rows;
      if (page != null) {
        // one sheet: which page of this file is it?
        let src = 1,
          why = doc.numPages === 1 ? "single-page file" : "";
        if (doc.numPages > 1) {
          const m = matchRevisionPages(
            newPages,
            sheets.filter((x) => x.page === page),
          );
          const hit = [...m.values()][0];
          if (hit) {
            src = hit.src;
            why = hit.why;
          } else why = "not found by name — pick the page";
        }
        rows = [{ src, target: page, why }];
      } else {
        const m = matchRevisionPages(newPages, sheets);
        const canAdd = sheets.length > 0; // unnamed takeoff: nothing can match, so don't add everything blindly
        rows = newPages.map((np) => {
          const hit = m.get(np.src);
          return {
            src: np.src,
            target: hit ? hit.page : canAdd ? "new" : null,
            why: hit
              ? hit.why
              : canAdd
                ? "no matching sheet — will be added as a new sheet"
                : "no sheet names to match against — pick the sheet or add as new",
          };
        });
      }
      setRevModal({
        mode: page != null ? "one" : "bulk",
        page,
        file,
        doc,
        numPages: doc.numPages,
        rows,
        unnamed: namedSheets().filter((x) => !x.name).length,
      });
    } catch (e) {
      console.error("Revision read failed", e);
      flash(
        `Couldn't read that PDF — ${e?.message || "unknown error"}`,
        "error",
      );
    } finally {
      setRevBusy("");
    }
  }
  // pages the text couldn't place (title block drawn as linework): read their title blocks with the AI — only
  // when asked, only those pages
  // pages the text couldn't place (title block drawn as linework): read their title blocks with the AI — only
  // when asked, only those pages, only the title-block corner
  async function aiNameRevisionPages() {
    const m = revModal;
    if (!m) return;
    const todo = m.rows.filter((r) => r.target == null);
    if (!todo.length) return;
    const sheets = namedSheets().filter((x) => x.name);
    const taken = new Set(
      m.rows
        .filter((r) => r.target != null && r.target !== "new")
        .map((r) => r.target),
    );
    const next = m.rows.map((r) => ({ ...r }));
    let k = 0;
    for (const r of todo) {
      setRevBusy(`Reading title blocks… ${++k}/${todo.length}`);
      try {
        const pg = await m.doc.getPage(r.src);
        const res = await fetch(`${API}/takeoffs3/ai/name-sheet`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({ imageBase64: await titleBlockImage(pg) }),
        });
        const data = await res.json();
        const label = [data.sheetNumber, data.sheetTitle]
          .filter(Boolean)
          .join(" ");
        if (!label) continue;
        const hit = [
          ...matchRevisionPages(
            [
              {
                src: r.src,
                tokens: label.split(/\s+/).map((w) => ({ s: w, x: 1, y: 1 })),
              },
            ],
            sheets.filter((x) => !taken.has(x.page)),
          ).values(),
        ][0];
        const row = next.find((x) => x.src === r.src);
        if (hit && row) {
          row.target = hit.page;
          row.why = `title block “${label}” (AI — check it)`;
          taken.add(hit.page);
        } else if (row)
          row.why = `title block “${label}” — no sheet with that name`;
      } catch (e) {
        console.warn("title block read failed", e);
      }
    }
    setRevBusy("");
    setRevModal((x) => (x ? { ...x, rows: next } : x));
  }

  async function applyRevisions() {
    const m = revModal;
    if (!m) return;
    const rows = m.rows.filter((r) => r.target != null && r.src);
    if (!rows.length) return flash("Nothing matched to a sheet yet.", "warn");
    const existing = rows.filter((r) => r.target !== "new");
    const dup = existing.find(
      (r, i) => existing.findIndex((x) => x.target === r.target) !== i,
    );
    if (dup)
      return flash(
        `Two pages are going to ${pageLabel(dup.target)} — pick one.`,
        "warn",
      );
    setRevBusy(`Uploading ${m.file.name}…`);
    try {
      const res = await onUploadDrawing(m.file);
      if (!res?.gcsPath) throw new Error("the upload returned no file");
      revDocsRef.current.set(res.gcsPath, Promise.resolve(m.doc));
      const at = Date.now(),
        by = myName();
      const sizes = {};
      for (const r of rows) {
        try {
          const v = (await m.doc.getPage(r.src)).getViewport({ scale: 1 });
          sizes[r.src] = { w: Math.round(v.width), h: Math.round(v.height) };
        } catch {
          /* size is only for the warning below */
        }
      }
      let sizeWarn = 0;
      for (const r of existing) {
        try {
          const o = (await pdfDocRef.current.getPage(r.target)).getViewport({
            scale: 1,
          });
          const z = sizes[r.src];
          if (z && Math.abs(z.w / z.h - o.width / o.height) > 0.02) sizeWarn++;
        } catch {
          /* fine */
        }
      }
      // new sheets go after the last one (numbers decided here, once)
      let nextN = Math.max(
        pageCount,
        0,
        ...Object.keys(revStateRef.current.sheetRevisions || {}).map(
          (k) => Number(k) || 0,
        ),
      );
      const placed = rows.map((r) =>
        r.target === "new" ? { ...r, target: ++nextN, added: true } : r,
      );
      const newNames = {};
      for (const r of placed.filter((x) => x.added)) {
        try {
          const t = await titleBlockFromPage(await m.doc.getPage(r.src));
          if (t)
            newNames[r.target] = [t.sheetNumber, t.sheetTitle]
              .filter(Boolean)
              .join(" — ");
        } catch {
          /* unnamed — shows as "Sheet N" */
        }
      }
      const addedCount = placed.filter((x) => x.added).length;
      setSheetRevisions((prev) => {
        const next = { ...prev };
        for (const r of placed) {
          const k = String(r.target);
          const list = [...(next[k] || next[r.target] || [])];
          const no =
            list.reduce((mx, x) => Math.max(mx, Number(x.no) || 0), 0) + 1;
          list.push({
            no,
            gcsPath: res.gcsPath,
            srcPage: r.src,
            fileName: m.file.name,
            at,
            by,
            ...(sizes[r.src] || {}),
            ...(r.added ? { added: true } : {}),
          });
          next[k] = list;
          if (next[r.target] && k !== String(r.target)) delete next[r.target];
        }
        return next;
      });
      if (addedCount) {
        setPageCount(nextN);
        if (Object.keys(newNames).length)
          setPageNames((prev) => ({ ...prev, ...newNames }));
      }
      setRevView((v) => {
        const c = { ...v };
        for (const r of placed) delete c[r.target];
        return c;
      });
      setRevModal(null);
      const upd = placed.length - addedCount;
      flash(
        `${upd ? `${upd} sheet${upd === 1 ? "" : "s"} updated` : ""}${upd && addedCount ? " · " : ""}${addedCount ? `${addedCount} new sheet${addedCount === 1 ? "" : "s"} added` : ""}.${sizeWarn ? ` ${sizeWarn} updated sheet${sizeWarn === 1 ? " is" : "s are"} a different shape than the original — check the takeoff lines up.` : ""}`,
        sizeWarn ? "warn" : "ok",
      );
      if (addedCount) setTimeout(() => autoScaleSheets({ quiet: true }), 800);
    } catch (e) {
      console.error("Revision upload failed", e);
      flash(`Revision not added — ${e?.message || "upload failed"}`, "error");
    } finally {
      setRevBusy("");
    }
  }

  function removeRevision(n, no) {
    const r = revsOf(n).find((x) => x.no === no);
    if (!r) return;
    if (revsOf(n)[0]?.added && revsOf(n).length === 1)
      return flash(
        "This sheet only exists in the added drawing — use Exclude on it in the Sheets list instead.",
        "warn",
      );
    if (
      !window.confirm(
        `Remove revision ${no} of ${pageLabel(n)}? The takeoff isn't touched — the sheet goes back to showing the revision before it.`,
      )
    )
      return;
    setSheetRevisions((prev) => {
      const next = { ...prev };
      const k = String(n);
      const list = (next[k] || next[n] || []).filter((x) => x.no !== no);
      delete next[n];
      if (list.length) next[k] = list;
      else delete next[k];
      return next;
    });
    setRevView((v) => {
      const c = { ...v };
      delete c[n];
      return c;
    });
  }
  // a revision shown / hidden somewhere: what was read off that sheet (its lines for snapping, its text, its
  // pictures) is read again from the page now shown
  const revSig = JSON.stringify(
    Object.keys(sheetRevisions)
      .map((k) => [k, revKeyOf(Number(k))])
      .sort(),
  );
  const prevRevKeysRef = useRef(null);
  useEffect(() => {
    const now = Object.fromEntries(JSON.parse(revSig));
    const prev = prevRevKeysRef.current;
    prevRevKeysRef.current = now;
    if (!prev) return;
    for (const k of new Set([...Object.keys(now), ...Object.keys(prev)])) {
      if ((now[k] || "orig") === (prev[k] || "orig")) continue;
      const n = Number(k);
      drawingSegsRef.current.delete(n);
      geomCache.current.delete(n);
      for (const key of [...sheetImgCache.current.keys()])
        if (String(key).startsWith(`${n}|`)) sheetImgCache.current.delete(key);
      setSheetGeo((g) => {
        if (!g[n]) return g;
        const c = { ...g };
        delete c[n];
        return c;
      });
      engineRef.current?.drop?.(n);
      ownPtsRef.current = { key: null, pts: [] };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revSig]);

  const nextIncluded = useCallback(
    (from, dir) => {
      let p = from + dir;
      while (p >= 1 && p <= pageCount && excludedPages.includes(p)) p += dir;
      return p >= 1 && p <= pageCount ? p : null;
    },
    [pageCount, excludedPages],
  );

  const revKeyNow = `${shownRevNo(pageNum)}|${revsOf(pageNum).length}`;
  useEffect(() => {
    if (!pdfDoc) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      // switching a sheet's revision keeps the drawing where it is (no blank map while it renders)
      if (stateRef.current.pageImage?.page !== pageNum) setMapReady(false);
      try {
        const img = await renderPage(pdfDoc, pageNum);
        if (!cancelled && img) setPageImage(img);

        // Preload neighbors
        const nxt = nextIncluded(pageNum, 1);
        const prv = nextIncluded(pageNum, -1);
        if (nxt) renderPage(pdfDoc, nxt).catch(() => {});
        if (prv) renderPage(pdfDoc, prv).catch(() => {});
      } catch (err) {
        console.error("Page render failed", err);
        if (!cancelled) {
          setError("Could not render that page.");
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfDoc, pageNum, renderPage, nextIncluded, revKeyNow]);

  useEffect(() => {
    if (!excludedPages.includes(pageNum)) return;
    const target = nextIncluded(pageNum, 1) ?? nextIncluded(pageNum, -1);
    if (target) setPageNum(target);
  }, [excludedPages, pageNum, nextIncluded]);

  // -------------------------------------------------------------------------
  // Page image → overlay + fit. Fitting is driven by the ResizeObserver via
  // fitRef, so we never guess at layout timing. Readiness is NOT gated on the
  // fit succeeding — the overlay is on the map, so we reveal it right away and
  // let the observer center it whenever the box is real.
  // -------------------------------------------------------------------------
  const overlayPageRef = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !pageImage) return;

    // the same sheet, another revision of it: swap the picture only — the view, the tool and the takeoff stay
    if (
      overlayRef.current &&
      overlayPageRef.current === pageImage.page &&
      map.hasLayer(overlayRef.current)
    ) {
      overlayRef.current.setBounds(
        L.latLngBounds([0, 0], [pageImage.h, pageImage.w]),
      );
      overlayRef.current.setUrl(pageImage.url);
      setMapReady(true);
      setLoading(false);
      return;
    }
    overlayPageRef.current = pageImage.page ?? null;

    setPanMode();
    if (overlayRef.current) map.removeLayer(overlayRef.current);
    clearShapeLayers();

    const bounds = [
      [0, 0],
      [pageImage.h, pageImage.w],
    ];
    overlayRef.current = L.imageOverlay(pageImage.url, bounds).addTo(map);

    // Restore shapes for the current page
    stateRef.current.shapes
      .filter((s) => s.page === stateRef.current.pageNum)
      .forEach(addLayerForShape);

    let fitted = false;

    const fit = () => {
      const m = mapRef.current;
      if (!m) return;
      const el = m.getContainer();
      if (el.clientWidth <= 0 || el.clientHeight <= 0) return; // wait for RO

      try {
        m.invalidateSize({ animate: false });
        const focusId = pendingFocusRef.current;
        const layer = focusId ? layerMapRef.current.get(focusId) : null;
        if (layer) {
          zoomToLayer(layer);
        } else {
          m.fitBounds(bounds, { padding: [20, 20], animate: false });
        }
      } catch (err) {
        console.error("Map fit failed", err);
      }
      pendingFocusRef.current = null;
      fitted = true;
    };

    // Register this page's fit so the ResizeObserver can (re)run it.
    fitRef.current = fit;

    // Try once now. If the box isn't ready, the observer will fire fit() later.
    fit();

    // Reveal immediately — the overlay exists; centering can catch up.
    setMapReady(true);
    setLoading(false);

    // If for some reason the box was 0 at mount and no resize ever fires
    // (rare, but e.g. display:none→block without a size change), nudge once.
    if (!fitted) {
      requestAnimationFrame(() => fitRef.current === fit && fit());
    }

    return () => {
      if (fitRef.current === fit) fitRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageImage]);

  // Restyle layers + refresh tooltips — and follow geometry changes (undo / redo, a door moved, someone
  // else's edit): a layer whose shape got new points is redrawn with them
  useEffect(() => {
    const byId = new Map(shapes.map((s) => [s.id, s]));
    const condById = new Map(conditions.map((c) => [c.id, c]));
    layerMapRef.current.forEach((layer, shapeId) => {
      const shape = byId.get(shapeId);
      if (!shape) return;
      if (layer._ll !== shape.latlngs && !layer.pm?.enabled?.()) {
        if (layer.setLatLngs) layer.setLatLngs(shape.latlngs);
        else if (layer.setLatLng && shape.latlngs?.[0])
          layer.setLatLng(shape.latlngs[0]);
        layer._ll = shape.latlngs;
      }
      const cond = condById.get(shape.conditionId);
      if (cond && layer.setStyle) layer.setStyle(shapeStyle(shape, cond));
      layer.setTooltipContent(tooltipHtml(shape));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scales, conditions, shapes, selIds]);

  // -------------------------------------------------------------------------
  // Layers
  // -------------------------------------------------------------------------
  function clearShapeLayers() {
    const map = mapRef.current;
    layerMapRef.current.forEach((layer) => {
      // a line still in edit mode: Geoman's own clean-up can throw on the canvas renderer — never let it stop the rest
      try {
        if (layer.pm?.enabled?.()) layer.pm.disable();
      } catch {
        if (layer.pm) layer.pm._enabled = false;
      }
      try {
        if (map) map.removeLayer(layer);
      } catch (e) {
        console.warn("[takeoff] layer removal", e);
      }
    });
    layerMapRef.current.clear();
    setEditingShapeId(null);
  }

  function condStyle(cond) {
    return {
      color: cond?.color || "#f97316",
      weight: 2,
      fillColor: cond?.color || "#f97316",
      fillOpacity: 0.22,
    };
  }

  // screen pixels for a real thickness (inches) on a sheet at the current zoom (null: sheet not scaled yet)
  function inchesToPx(inches, page) {
    const map = mapRef.current;
    const sc = stateRef.current.scales || {};
    const f = sc[String(page)] ?? sc[page];
    if (!map || !f) return null;
    return (inches / 12 / f) * Math.pow(2, map.getZoom());
  }
  // a shape's look: its assembly's colour, thickness (4" by default, real size on the sheet) and pattern;
  // counts draw their assembly's symbol; guessed / untagged walls dashed; the selected one orange
  function shapeStyle(shape, cond) {
    const st = condStyle(cond);
    const color = cond?.color || "#f97316";
    const sel = !!(shape && selRef.current.has(shape.id));
    if (shape?.kind === "marker") {
      // a door / window the AI or the cross-check wants looked at: hollow, dashed outline
      const chk = !!shape.meta?.needsCheck;
      st.symbol = condSymbol(cond);
      st.fillOpacity = chk ? 0.12 : 0.9;
      st.dashArray = chk ? "3 3" : null;
      st.weight = chk ? 2.5 : 1.5;
      st.color = chk ? color : "#ffffff";
      st.fillColor = color;
      if (sel) {
        st.weight = 3.5;
        st.color = "#fb923c";
      }
      return st;
    }
    const pattern = condPattern(cond);
    const unsure =
      isUntagged(cond) ||
      (["thickness", "check"].includes(shape?.meta?.tagSource) &&
        !shape?.meta?.tagApproved);
    if (shape?.kind === "polyline") {
      const px = inchesToPx(condLineIn(cond), shape.page);
      const w = pattern === "thin" ? 2.5 : px == null ? 3.5 : Math.max(2.5, px);
      st.weight = w;
      st.lineCap = "butt";
      st.lineJoin = "miter";
      st.opacity = 0.55;
      st.dashArray = null;
      if (pattern === "solid") st.opacity = 1;
      else if (pattern === "hatch" || pattern === "crosshatch") {
        st.color = hatchPattern(color, pattern);
        st.opacity = 1;
      } else if (pattern === "dashed")
        st.dashArray = `${Math.round(Math.max(6, w * 2.2))} ${Math.round(Math.max(4, w * 1.1))}`;
      else if (pattern === "dotted") {
        st.lineCap = "round";
        st.dashArray = `1 ${Math.round(Math.max(5, w * 1.6))}`;
        st.opacity = 0.9;
      } else if (pattern === "outline") {
        st.opacity = 0.95;
        st.weight = Math.max(2, Math.min(3, w * 0.25));
      } else if (pattern === "thin") st.opacity = 0.95;
      // a wall the trace isn't sure of: dashed (over whatever pattern it has)
      if (unsure && pattern !== "dotted")
        st.dashArray = `${Math.round(Math.max(8, w * 2))} ${Math.round(Math.max(6, w * 1.4))}`;
      if (sel) {
        st.color = "#fb923c";
        st.opacity = 0.9;
        st.weight = Math.max(w, 6);
      }
      return st;
    }
    // areas: the pattern is the fill
    st.dashArray = unsure ? "8 6" : null;
    if (pattern === "solid") st.fillOpacity = 0.6;
    else if (pattern === "hatch" || pattern === "crosshatch") {
      st.fillColor = hatchPattern(color, pattern);
      st.fillOpacity = 1;
    } else if (pattern === "outline" || pattern === "thin") st.fillOpacity = 0;
    else if (pattern === "dashed") st.dashArray = "10 6";
    else if (pattern === "dotted") st.dashArray = "2 6";
    if (sel) {
      st.weight = 4;
      st.fillOpacity = Math.max(0.35, st.fillOpacity === 1 ? 1 : 0.35);
      st.color = "#fb923c";
    }
    return st;
  }
  // every layer on the sheet restyled (zoom changes the pixel size of a real thickness)
  function restyleLayers() {
    const st = stateRef.current;
    const condById = new Map(st.conditions.map((c) => [c.id, c]));
    const byId = new Map(st.shapes.map((s) => [s.id, s]));
    layerMapRef.current.forEach((layer, id) => {
      const s = byId.get(id);
      const c = s && condById.get(s.conditionId);
      if (c && layer.setStyle && s.kind === "polyline")
        layer.setStyle(shapeStyle(s, c));
    });
    // the door / window bands over the walls follow too
    for (const l of winLayersRef.current || []) {
      if (!l._band?.inches) continue;
      const px = inchesToPx(l._band.inches, l._band.page);
      if (px)
        l.setStyle({ weight: Math.max(l._band.min, Math.min(60, px + 2)) });
    }
  }

  function tooltipHtml(shape) {
    const st = stateRef.current;
    const cond = st.conditions.find((c) => c.id === shape.conditionId);
    if (!cond) return "";
    const pageFpu =
      st.scales[String(shape.page)] ?? st.scales[shape.page] ?? null;
    const { qty, unit } = shapePrimaryQty(shape, cond, pageFpu);
    const qtyStr = qty === null ? "set scale" : `${fmtQty(qty)} ${unit}`;
    let wall = "";
    if (cond.type === "linear" && qty !== null) {
      const n = wallNet(shape, cond, qty);
      const what = [n.winLf > 0 && "windows", n.doorLf > 0 && "doors"]
        .filter(Boolean)
        .join(" + ");
      wall =
        n.dLf > 0
          ? ` · <b>${fmtQty(n.lf)} LF net</b> (−${fmtQty(n.dLf)} LF ${what})`
          : "";
      if (n.h)
        wall += ` · ${fmtFeetIn(n.h)} high = ${fmtQty(n.sf)} SF${n.dSf ? ` (−${fmtQty(n.dSf)} SF)` : ""}`;
    }
    const ch = shape.meta?.ceilingHeight
      ? ` · clg ${fmtFeetIn(shape.meta.ceilingHeight)}`
      : "";
    const u =
      shape.meta?.unitId &&
      (st.units || []).find((x) => x.id === shape.meta.unitId);
    const unit2 = u
      ? `<br/><span style="color:#fdba74">Typical unit “${u.name}” × ${u.qty}</span>`
      : "";
    return `<span style="font-weight:600">${cond.no ? `#${cond.no} · ` : ""}${cond.name}</span> — ${qtyStr}${wall}${ch}${shape.label ? `<br/><span style="opacity:.7">${shape.label}</span>` : ""}${unit2}`;
  }

  // a corner handle let go near a line of the drawing lands on it (a corner of the drawing first) — Alt keeps
  // it exactly where it was dropped
  function snapEditedVertex(layer, shape, e) {
    if (e?.markerEvent?.originalEvent?.altKey) return;
    const path = e?.indexPath;
    if (!path || !path.length || !layer.getLatLngs) return;
    const ll = layer.getLatLngs();
    let arr = ll;
    for (let k = 0; k < path.length - 1; k++) arr = arr?.[path[k]];
    const i = path[path.length - 1],
      v = arr?.[i];
    if (!v) return;
    const q = [v.lat, v.lng],
      tol = pxTol(10);
    const near = drawingNear(q, tol * 2);
    if (!near) return;
    let best = null,
      bd = tol;
    for (const w of near.verts) {
      const d = Math.hypot(w[0] - q[0], w[1] - q[1]);
      if (d < bd) {
        bd = d;
        best = w;
      }
    }
    if (!best) {
      bd = tol * 0.7;
      for (const [a, b] of near.segs) {
        const vx = b[0] - a[0],
          vy = b[1] - a[1],
          L2 = vx * vx + vy * vy;
        if (!L2) continue;
        const k = Math.max(
          0,
          Math.min(1, ((q[0] - a[0]) * vx + (q[1] - a[1]) * vy) / L2),
        );
        const p = [a[0] + vx * k, a[1] + vy * k],
          d = Math.hypot(p[0] - q[0], p[1] - q[1]);
        if (d < bd) {
          bd = d;
          best = p;
        }
      }
    }
    if (!best) return;
    arr[i] = L.latLng(best[0], best[1]);
    layer.setLatLngs(ll);
    // refresh the handles at the snapped spot
    if (layer.pm?.enabled()) {
      const opts = layer.pm.options;
      layer.pm.disable();
      layer.pm.enable(opts);
    }
    const latlngs = serializeLatLngs(layer, shape.kind);
    setShapes((prev) =>
      prev.map((s) => (s.id === shape.id ? { ...s, latlngs } : s)),
    );
  }

  function attachShapeHandlers(layer, shape) {
    layer._takeoffId = shape.id;
    // no hover popup over the drawing — a shape's details show in the right panel when it's selected
    layer.on("pm:markerdragend", (e) => snapEditedVertex(layer, shape, e));
    layer.on("pm:edit", () => {
      const latlngs = serializeLatLngs(layer, shape.kind);
      setShapes((prev) =>
        prev.map((s) => (s.id === shape.id ? { ...s, latlngs } : s)),
      );
    });
    layer.on("click", (ev) => {
      // Only select-by-click with Select / Pan — while drawing, calibrating or extending,
      // clicking over an existing shape must NOT change anything.
      if (!isNeutral(stateRef.current.mode)) return;
      if (spliceRef.current || placeDoorRef.current) return; // the click is placing a cut / a door
      if (
        ev.originalEvent?.ctrlKey ||
        ev.originalEvent?.metaKey ||
        Date.now() - (handlersRef.current.panEndedAt || 0) < 300
      )
        return; // Ctrl / Cmd drag = pan
      lastShapeClickRef.current = Date.now();
      const cur =
        stateRef.current.shapes.find((s) => s.id === shape.id) || shape;
      if (ev?.originalEvent?.shiftKey) {
        const next = new Set(selRef.current);
        next.has(cur.id) ? next.delete(cur.id) : next.add(cur.id);
        setSel([...next]);
      } else setSel([cur.id]);
      setActiveCondId(cur.conditionId);
    });
  }

  function addLayerForShape(shape) {
    const map = mapRef.current;
    const cond = stateRef.current.conditions.find(
      (c) => c.id === shape.conditionId,
    );
    let layer;
    // door headers are kept in step with their doors — never edited by hand
    const opts = {
      ...shapeStyle(shape, cond),
      ...(shape.meta?.headerOf ? { pmIgnore: true } : {}),
    };
    if (shape.kind === "polygon") layer = L.polygon(shape.latlngs, opts);
    else if (shape.kind === "polyline") layer = L.polyline(shape.latlngs, opts);
    else layer = L.circleMarker(shape.latlngs[0], { ...opts, radius: 7 });
    attachShapeHandlers(layer, shape);
    layer._ll = shape.latlngs; // (so a later change of the points redraws it)
    // a hidden assembly's shape is kept (edits still reach it) but not put on the drawing
    if (!hiddenRef.current?.has(shape.conditionId)) layer.addTo(map);
    layerMapRef.current.set(shape.id, layer);
  }

  function zoomToLayer(layer) {
    const map = mapRef.current;
    if (!map) return;
    const containerEl = map.getContainer();
    if (containerEl.clientWidth === 0 || containerEl.clientHeight === 0) return;
    if (layer.getBounds) {
      const b = layer.getBounds();
      map.fitBounds(b.pad(0.6), { maxZoom: 2 });
    } else if (layer.getLatLng) {
      map.setView(layer.getLatLng(), Math.max(map.getZoom(), 0.5));
    }
    layer.openTooltip?.();
  }
  // -------------------------------------------------------------------------
  // Temp layers
  // -------------------------------------------------------------------------
  function clearTempLayers() {
    clearDim();
    hideMeasureHover();
    tempPtsRef.current = [];
    const map = mapRef.current;
    if (extendPreviewRef.current && map)
      map.removeLayer(extendPreviewRef.current);
    extendPreviewRef.current = null;
    extendShapeIdRef.current = null;
  }

  // -------------------------------------------------------------------------
  // Geoman / map event handlers
  // -------------------------------------------------------------------------
  handlersRef.current.onCreate = (e) => {
    const st = stateRef.current;
    const condId = drawCondIdRef.current; // ← source of truth
    const cond = st.conditions.find((c) => c.id === condId);
    if (st.mode !== "draw" || !cond) {
      mapRef.current?.removeLayer(e.layer);
      return;
    }
    const kind =
      e.shape === "Polygon"
        ? "polygon"
        : e.shape === "Line"
          ? "polyline"
          : "marker";
    const shape = {
      id: uid(),
      conditionId: cond.id, // ← always the Draw'd cond
      page: st.pageNum,
      kind,
      latlngs: serializeLatLngs(e.layer, kind),
    };
    e.layer.setStyle?.(shapeStyle(shape, cond));
    attachShapeHandlers(e.layer, shape);
    layerMapRef.current.set(shape.id, e.layer);
    setShapes((prev) => [...prev, shape]);
  };
  handlersRef.current.onRemove = (e) => {
    const id = e.layer?._takeoffId;
    if (!id) return;
    layerMapRef.current.delete(id);
    setShapes((prev) => prev.filter((s) => s.id !== id));
  };

  handlersRef.current.onMapClick = (e) => {
    const st = stateRef.current;
    if (snipModeRef.current) return; // the cut tool uses press / drag / release
    if (placeDoorRef.current) {
      placeDoorAt([e.latlng.lat, e.latlng.lng]);
      return;
    }
    if (spliceRef.current) {
      spliceClick([e.latlng.lat, e.latlng.lng]);
      return;
    }
    if (coModeRef.current) {
      setCoPick({ page: st.pageNum, lat: e.latlng.lat, lng: e.latlng.lng });
      return;
    }
    // RFI pin tool: the click marks the spot
    if (pinModeRef.current && onRfiPinPlaceRef.current) {
      onRfiPinPlaceRef.current(st.pageNum, [e.latlng.lat, e.latlng.lng]);
      return;
    }

    // a Ctrl / Cmd drag pans — its click never places a point or changes the selection
    if (
      e.originalEvent?.ctrlKey ||
      e.originalEvent?.metaKey ||
      Date.now() - (handlersRef.current.panEndedAt || 0) < 300
    )
      return;
    // ── draw / extend (our own engine)
    if ((st.mode === "draw" || st.mode === "extend") && drawRef.current) {
      const d = drawRef.current;
      const c = constrainPoint(
        [e.latlng.lat, e.latlng.lng],
        e.originalEvent?.shiftKey,
      );
      if (d.kind === "marker") {
        const cond = st.conditions.find((x) => x.id === d.condId);
        // a corner-bead marker takes the floor's wall height (bead LF = corners × height)
        const h =
          cond?.wallType === "__count:bead" ? floorHeightOf(st.pageNum) : null;
        if (cond)
          setShapes((prev) => [
            ...prev,
            {
              id: uid(),
              conditionId: cond.id,
              page: st.pageNum,
              kind: "marker",
              latlngs: [c.p],
              meta: { source: "manual", ...(h ? { height: h } : {}) },
            },
          ]);
        return;
      }
      const last = d.pts[d.pts.length - 1];
      if (last && Math.hypot(c.p[0] - last[0], c.p[1] - last[1]) < pxTol(4))
        return; // 2nd click of a double-click
      // clicking the first point closes an area
      if (
        d.kind === "polygon" &&
        d.pts.length >= 3 &&
        Math.hypot(c.p[0] - d.pts[0][0], c.p[1] - d.pts[0][1]) < pxTol(10)
      ) {
        finishDraft();
        return;
      }
      // going back along the last segment moves the end point back (no doubled-back line); carrying straight
      // on moves it forward (no extra point in a straight run)
      const n = d.pts.length;
      let placed = false;
      if (d.kind === "polyline" && n >= 2) {
        const a0 = d.pts[n - 2],
          b0 = d.pts[n - 1];
        const vx = b0[0] - a0[0],
          vy = b0[1] - a0[1],
          L2 = vx * vx + vy * vy;
        if (L2 > 0) {
          const t = ((c.p[0] - a0[0]) * vx + (c.p[1] - a0[1]) * vy) / L2;
          const off =
            Math.abs((c.p[0] - a0[0]) * vy - (c.p[1] - a0[1]) * vx) /
            Math.sqrt(L2);
          if (off <= pxTol(5)) {
            if (t < 1) {
              if (t * Math.sqrt(L2) <= pxTol(5)) {
                // back onto the previous point: drop the last one
                d.pts.pop();
              } else d.pts[n - 1] = [a0[0] + vx * t, a0[1] + vy * t];
              placed = true;
              if (d.extendId) d.startLen = Math.min(d.startLen, d.pts.length);
            } else {
              // straight on
              d.pts[n - 1] = c.p;
              placed = true;
              if (d.extendId) d.startLen = Math.min(d.startLen, n - 1);
            }
          }
        }
      }
      if (!placed) d.pts.push(c.p);
      if (d.extendId) {
        // continuing a line: every click is saved straight away
        const pts = d.pts.map((q) => [q[0], q[1]]),
          id = d.extendId;
        setShapes((prev) =>
          prev.map((x) => (x.id === id ? { ...x, latlngs: pts } : x)),
        );
        layerMapRef.current.get(id)?.setLatLngs(pts);
      }
      renderDraft(null);
      return;
    }

    // ── select tool: a click on empty drawing clears the selection
    if (isNeutral(st.mode)) {
      if (
        Date.now() - lastShapeClickRef.current < 300 ||
        Date.now() - boxRef.current.endedAt < 300
      )
        return;
      if (!e.originalEvent?.shiftKey) setSel([]);
      return;
    }

    // ── fill room — flood the clicked space with the geometry engine
    if (st.mode === "fill") {
      handlersRef.current.fillAt?.(e.latlng);
      return;
    }

    // ── calibrate / measure — two points ────────────────────────────────────
    if (st.mode === "calibrate" || st.mode === "measure") {
      const pt = measureSnap(
        [e.latlng.lat, e.latlng.lng],
        e.originalEvent?.shiftKey,
      ).p;
      if (st.mode === "calibrate") {
        if (tempPtsRef.current.length >= 2) return;
        tempPtsRef.current.push(pt);
        if (tempPtsRef.current.length === 2) {
          const [p1, p2] = tempPtsRef.current;
          renderDim(p1, p2);
          setCalibDialog({ pixels: Math.hypot(p2[1] - p1[1], p2[0] - p1[0]) });
        }
        return;
      }
      // measure: first click starts, second finishes — it stays on the sheet and the next click starts another
      // (chain: the next one starts where this one ended)
      if (tempPtsRef.current.length === 0) {
        tempPtsRef.current = [pt];
        setMeasurePts(1);
        return;
      }
      const p1 = tempPtsRef.current[0];
      if (Math.hypot(pt[0] - p1[0], pt[1] - p1[1]) < pxTol(3)) return;
      const m = { id: uid(), p1, p2: pt, page: st.pageNum };
      setMeasures([...measuresRef.current, m]);
      clearDim();
      drawMeasures();
      setMeasureUnits(Math.hypot(pt[1] - p1[1], pt[0] - p1[0]));
      if (measureChainRef.current) {
        tempPtsRef.current = [pt];
        setMeasurePts(1);
      } else {
        tempPtsRef.current = [];
        setMeasurePts(2);
      }
      return;
    }
  };

  handlersRef.current.onMapDblClick = () => {
    const st = stateRef.current;
    if ((st.mode === "draw" || st.mode === "extend") && drawRef.current)
      finishDraft();
  };

  handlersRef.current.onMouseMove = (e) => {
    const st = stateRef.current;
    mouseLLRef.current = [e.latlng.lat, e.latlng.lng];
    if (spliceRef.current) {
      handlersRef.current.spliceMove?.([e.latlng.lat, e.latlng.lng]);
      return;
    }
    if ((st.mode === "draw" || st.mode === "extend") && drawRef.current) {
      if (drawRef.current.kind === "marker") return;
      handlersRef.current.lastShift = !!e.originalEvent?.shiftKey;
      // one redraw per frame, however fast the mouse moves
      if (!handlersRef.current.draftRaf)
        handlersRef.current.draftRaf = requestAnimationFrame(() => {
          handlersRef.current.draftRaf = 0;
          if (!drawRef.current || !mouseLLRef.current) return;
          renderDraft(
            constrainPoint(mouseLLRef.current, handlersRef.current.lastShift),
          );
        });
      return;
    }

    if (st.mode === "calibrate" || st.mode === "measure") {
      const c = measureSnap(
        [e.latlng.lat, e.latlng.lng],
        e.originalEvent?.shiftKey,
      );
      // where the click would land (cyan when it's on the drawing or locked straight)
      const map = mapRef.current;
      if (map) {
        const snapped =
          c.locked ||
          Math.hypot(c.p[0] - e.latlng.lat, c.p[1] - e.latlng.lng) > 1e-9;
        if (!measureHoverRef.current)
          measureHoverRef.current = L.circleMarker(c.p, {
            radius: 5,
            weight: 2,
            fillOpacity: 0,
            interactive: false,
          }).addTo(map);
        measureHoverRef.current.setLatLng(c.p);
        measureHoverRef.current.setStyle({
          color: snapped ? "#38bdf8" : "#fff",
        });
      }
      if (tempPtsRef.current.length === 1)
        renderDim(tempPtsRef.current[0], c.p, { live: true });
      return;
    }
  };

  handlersRef.current.onZoomEnd = () => {
    restyleLayers(); // line thickness is real size: its pixel width follows the zoom
    if (drawRef.current) handlersRef.current.redrawDraft?.();
    if (measuresRef.current.length) drawMeasures();
    if (tempPtsRef.current.length === 2)
      renderDim(tempPtsRef.current[0], tempPtsRef.current[1]);
  };

  // -------------------------------------------------------------------------
  // Modes
  // -------------------------------------------------------------------------
  function disableAllPm() {
    const map = mapRef.current;
    if (!map) return;
    map.pm.disableDraw();
    map.pm.disableGlobalEditMode();
    map.pm.disableGlobalRemovalMode();
    if (autoEditRef.current) {
      layerMapRef.current.get(autoEditRef.current)?.pm?.disable();
      autoEditRef.current = null;
    }
    if (editingShapeId) {
      const layer = layerMapRef.current.get(editingShapeId);
      layer?.pm?.disable();
    }
  }

  // back to the neutral tool (Select or Pan — whichever the estimator picked last)
  function setPanMode() {
    disableAllPm();
    clearTempLayers();
    if (measuresRef.current.length) clearMeasures();
    endDraw();
    drawCondIdRef.current = null; // ← clear the lock
    setCalibDialog(null);
    setMeasureUnits(0);
    setMeasurePts(0);
    setEditingShapeId(null);
    const tool = baseToolRef.current;
    const map = mapRef.current;
    if (map) {
      if (tool === "pan") map.dragging.enable();
      else map.dragging.disable();
    }
    setMapCursor(tool === "pan" ? "grab" : "default");
    setMode(tool);
  }
  function setTool(tool) {
    baseToolRef.current = tool;
    setPanMode();
  }

  function applySnapshot(snap) {
    histRef.current.skip = true;
    setShapes(snap.shapes);
    setConditions(snap.conditions);
    setScales(snap.scales);
    setUnits(snap.units || []);
    setSel([]);
  }
  // Undo, step by step:
  //  • while drawing / continuing a line: the last point comes off first (the line isn't thrown away)
  //  • a line you made longer (continued, or drawn on from its end) goes back to the length it had
  //  • the draw tool stays on — undo doesn't drop you out of drawing
  function smartUndo() {
    const st = stateRef.current;
    const d = drawRef.current;
    const drawing = (st.mode === "draw" || st.mode === "extend") && d;
    if (drawing && d.pts.length > (d.extendId ? d.startLen : 0))
      return popPoint();
    if (drawing && d.extendId) {
      // continuing a line with nothing added yet: stop continuing, then undo the last change
      endDraw();
      setPanMode();
    }
    undo({ keepDrawing: true });
  }
  function undo({ keepDrawing = false } = {}) {
    const h = histRef.current;
    if (
      !keepDrawing &&
      (stateRef.current.mode === "draw" || stateRef.current.mode === "extend")
    )
      setPanMode();
    if (!h.past.length) {
      flash("Nothing to undo.", "info");
      return;
    }
    const prev = h.past.pop();
    h.future.push(h.last);
    h.t = 0;
    applySnapshot(prev);
  }
  function redo() {
    const h = histRef.current;
    if (!h.future.length) {
      flash("Nothing to redo.", "info");
      return;
    }
    const next = h.future.pop();
    h.past.push(h.last);
    h.t = 0;
    applySnapshot(next);
  }
  // take the last drawn point back off (Backspace / Ctrl+Z while drawing)
  function popPoint() {
    const d = drawRef.current;
    if (!d) return;
    if (d.pts.length > (d.extendId ? d.startLen : 0)) {
      d.pts.pop();
      if (d.extendId) {
        const pts = d.pts.map((q) => [q[0], q[1]]),
          id = d.extendId;
        setShapes((prev) =>
          prev.map((x) => (x.id === id ? { ...x, latlngs: pts } : x)),
        );
        layerMapRef.current.get(id)?.setLatLngs(pts);
      }
      renderDraft(null);
    }
  }

  function startDraw(cond) {
    if (!pageImage || !cond) return;
    // drawing with a hidden assembly: show it again first — its existing lines must be in view
    if (hiddenRef.current?.has(cond.id)) {
      setHidden([cond.id], false);
      flash(`${cond.name} was hidden — it's showing again.`, "info");
    }
    disableAllPm();
    clearTempLayers();
    endDraw();
    drawCondIdRef.current = cond.id; // ← lock it here
    setActiveCondId(cond.id);
    setSel([]);
    mapRef.current?.dragging.enable(); // drag still pans while drawing
    drawRef.current = {
      condId: cond.id,
      kind:
        cond.type === "area"
          ? "polygon"
          : cond.type === "linear"
            ? "polyline"
            : "marker",
      pts: [],
      extendId: null,
      layers: [],
    };
    setMapCursor("crosshair");
    setMode("draw");
  }

  function startCalibrate() {
    if (!pageImage) return;
    disableAllPm();
    clearTempLayers();
    setCalibInput("");
    setMapCursor("crosshair");
    setMode("calibrate");
  }

  function startMeasure() {
    if (!pageImage) return;
    disableAllPm();
    clearTempLayers();
    clearMeasures();
    setMeasureUnits(0);
    setMeasurePts(0);
    setMapCursor("crosshair");
    setMode("measure");
  }

  function remeasure() {
    clearTempLayers();
    setMeasureUnits(0);
    setMeasurePts(0);
    setMapCursor("crosshair");
  }

  function calibrateFromMeasure() {
    const last = measuresRef.current[measuresRef.current.length - 1];
    if (!last) return;
    const [[y1, x1], [y2, x2]] = [last.p1, last.p2];
    const units = Math.hypot(x2 - x1, y2 - y1);
    if (units > 0) {
      setCalibInput("");
      setCalibDialog({ pixels: units });
    }
  }

  function toggleEditMode() {
    const map = mapRef.current;
    if (!map) return;
    if (mode === "edit") return setPanMode();
    disableAllPm();
    clearTempLayers();
    setMode("edit");
    map.pm.enableGlobalEditMode({
      allowSelfIntersection: false,
      hideMiddleMarkers: false,
      snappable: false,
    });
  }

  function toggleDeleteMode() {
    const map = mapRef.current;
    if (!map) return;
    if (mode === "delete") return setPanMode();
    disableAllPm();
    clearTempLayers();
    setMode("delete");
    map.pm.enableGlobalRemovalMode();
  }

  function setScaleValue(
    feetPerImageUnit,
    info = { source: "manual" },
    onlyPage = null,
  ) {
    const pages =
      onlyPage != null || !scaleAllPages
        ? [onlyPage ?? pageNum]
        : Array.from({ length: pageCount }, (_, i) => i + 1).filter(
            (p) => !excludedPages.includes(p),
          );
    setScales((prev) => {
      const next = { ...prev };
      for (const p of pages) next[p] = feetPerImageUnit;
      return next;
    });
    setScaleInfo((prev) => {
      const next = { ...prev };
      for (const p of pages) next[p] = { ...info, at: Date.now() };
      return next;
    });
  }

  function applyCalibration() {
    const feet = parseFeetInput(calibInput);
    if (!feet || !calibDialog?.pixels) return;
    setScaleValue(feet / calibDialog.pixels, {
      source: "calibrated",
      label: `calibrated (${calibInput})`,
    });
    setPanMode();
  }

  function applyPresetScale(ftPerPaperInch) {
    if (!pageImage) return;
    const feetPerImageUnit = ftPerPaperInch / (72 * pageImage.renderScale);
    const pre = PRESET_SCALES.find(
      (x) => Math.abs(x.ftPerPaperInch - ftPerPaperInch) < 1e-6,
    );
    setScaleValue(feetPerImageUnit, {
      source: "preset",
      label: pre?.label || `1" = ${ftPerPaperInch.toFixed(2)}'`,
    });
  }

  // -------------------------------------------------------------------------
  // Conditions + shapes
  // -------------------------------------------------------------------------
  function addCondition() {
    const name = newCond.name.trim();
    if (!name) return;
    const cond = {
      id: uid(),
      name,
      description: (newCond.description || "").trim(),
      type: newCond.type,
      wallType: newCond.wallType.trim(),
      height:
        newCond.type === "linear" && newCond.height
          ? Number(newCond.height)
          : null,
      color: newCond.color || PALETTE[conditions.length % PALETTE.length],
    };
    setConditions((prev) => [...prev, cond]);
    setNewCond({
      name: "",
      description: "",
      type: newCond.type,
      wallType: "",
      height: newCond.height, // (the next one is usually the same height)
      color: "",
    });
    setShowAddCond(false);
    setActiveCondId(cond.id);
    startDraw(cond);
  }

  // a copy of an assembly (same type, tag, height, items; no lines) right under it, ready to rename
  function duplicateCondition(cond) {
    const used = new Set(conditions.map((c) => c.name));
    let name = `${cond.name} (copy)`;
    for (let i = 2; used.has(name); i++) name = `${cond.name} (copy ${i})`;
    const ci = PALETTE.indexOf(cond.color);
    const copy = {
      ...cond,
      id: uid(),
      name,
      assemblyId: "", // (its own assembly now: the trace keeps typing walls onto the original)
      color: PALETTE[(ci >= 0 ? ci + 1 : conditions.length) % PALETTE.length],
    };
    setConditions((prev) => {
      const i = prev.findIndex((c) => c.id === cond.id);
      const next = [...prev];
      next.splice(i < 0 ? next.length : i + 1, 0, copy);
      return next;
    });
    onDuplicateCondition?.(cond.id, copy.id);
    setActiveCondId(copy.id);
    setEditAsmId(copy.id);
    flash(
      `Duplicated “${cond.name}” — rename it and set what's different.`,
      "ok",
    );
  }
  // a new default height for an assembly's walls: optionally every line already drawn with it too
  // a new default height for an assembly's walls: optionally every line already drawn with it too
  function applyHeightToLines(cond, h) {
    let n = 0;
    setShapes((prev) =>
      prev.map((s) => {
        if (s.conditionId !== cond.id || s.kind !== "polyline") return s;
        n++;
        return {
          ...s,
          meta: { ...(s.meta || {}), height: h, heightManual: false },
        };
      }),
    );
    flash(`Height ${fmtFeetIn(h)} set on every ${cond.name} line.`, "ok");
    return n;
  }

  // the assembly's own height field (left panel): sets the new default AND every wall already drawn with it,
  // hand-set ones included, so the left and right panels always agree. The flag is cleared so later floor-height
  // changes treat these walls like any other.
  function setConditionHeight(cond, h) {
    updateCondition(cond.id, { height: h });
    if (!h) return; // cleared: the walls keep their own heights
    const ids = new Set(
      stateRef.current.shapes
        .filter((s) => s.conditionId === cond.id && s.kind === "polyline")
        .map((s) => s.id),
    );
    if (!ids.size) return;
    setShapes((prev) =>
      prev.map((s) =>
        ids.has(s.id)
          ? {
              ...s,
              meta: { ...(s.meta || {}), height: h, heightManual: false },
            }
          : s,
      ),
    );
    flash(
      `${fmtFeetIn(h)} on ${ids.size} ${cond.name} wall${ids.size === 1 ? "" : "s"}.`,
      "ok",
    );
  }

  // wall ⇄ area ⇄ count — only while nothing is drawn with it (a drawn line can't become an area)
  function changeConditionType(cond, type) {
    if (cond.type === type) return;
    if (stateRef.current.shapes.some((x) => x.conditionId === cond.id))
      return flash(
        "Delete what's drawn with this assembly first — then its type can change.",
        "warn",
      );
    if (drawRef.current?.condId === cond.id) setPanMode();
    updateCondition(cond.id, { type });
    flash(`${cond.name} is now measured as ${TYPE_META[type].label}.`, "ok");
  }
  function updateCondition(condId, patch) {
    setConditions((prev) =>
      prev.map((c) => (c.id === condId ? { ...c, ...patch } : c)),
    );
  }

  function deleteCondition(condId) {
    setPanMode();
    shapes
      .filter((s) => s.conditionId === condId)
      .forEach((s) => {
        const layer = layerMapRef.current.get(s.id);
        if (layer && mapRef.current) mapRef.current.removeLayer(layer);
        layerMapRef.current.delete(s.id);
      });
    setShapes((prev) => prev.filter((s) => s.conditionId !== condId));
    setConditions((prev) => prev.filter((c) => c.id !== condId));
    if (activeCondId === condId) setActiveCondId(null);
  }

  function deleteShape(shapeId) {
    const layer = layerMapRef.current.get(shapeId);
    if (layer && mapRef.current) mapRef.current.removeLayer(layer);
    layerMapRef.current.delete(shapeId);
    if (editingShapeId === shapeId) setEditingShapeId(null);
    setShapes((prev) => prev.filter((s) => s.id !== shapeId));
  }

  function focusShape(shape) {
    if (shape.page !== pageNum) {
      pendingFocusRef.current = shape.id;
      setPageNum(shape.page);
      return;
    }
    const layer = layerMapRef.current.get(shape.id);
    if (layer) zoomToLayer(layer);
  }

  function toggleEditShape(shape) {
    const layer = layerMapRef.current.get(shape.id);
    if (!layer?.pm) return;
    if (editingShapeId === shape.id) {
      layer.pm.disable();
      setEditingShapeId(null);
      return;
    }
    if (shape.page !== pageNum) {
      focusShape(shape);
      return;
    }
    disableAllPm();
    clearTempLayers();
    setMode(baseToolRef.current);
    layer.pm.enable({
      allowSelfIntersection: false,
      hideMiddleMarkers: false, // grab a midpoint square to ADD a vertex
      preventMarkerRemoval: false, // right-click a vertex to remove it
      snappable: false,
    });
    setEditingShapeId(shape.id);
    zoomToLayer(layer);
  }

  // Continue an existing line (Space on a selected line) — from whichever end is nearer the pointer
  function startExtendShape(shape, fromLL = null) {
    if (shape.kind !== "polyline") return;
    if (shape.page !== stateRef.current.pageNum) {
      focusShape(shape);
      return;
    }
    disableAllPm();
    clearTempLayers();
    endDraw();
    let pts = shape.latlngs.map((p) => [p[0], p[1]]);
    const m = fromLL || mouseLLRef.current;
    if (m) {
      const d0 = Math.hypot(pts[0][0] - m[0], pts[0][1] - m[1]),
        d1 = Math.hypot(
          pts[pts.length - 1][0] - m[0],
          pts[pts.length - 1][1] - m[1],
        );
      if (d0 < d1) pts = pts.reverse();
    }
    drawCondIdRef.current = shape.conditionId;
    setActiveCondId(shape.conditionId);
    drawRef.current = {
      condId: shape.conditionId,
      kind: "polyline",
      pts,
      extendId: shape.id,
      startLen: pts.length,
      layers: [],
    };
    mapRef.current?.dragging.enable();
    setMapCursor("crosshair");
    setMode("extend");
    renderDraft(null);
  }

  // ═══════════════ drawing engine ═══════════════
  // Clicks place points. Each new segment locks to the sheet's axes (and the drawing's own wall
  // directions) within ±10° — hold Shift to draw at any angle. Along a locked direction the point
  // stops on lines it would meet and lines up with nearby corners, so a parallel wall is drawn
  // straight and exactly as long as the one beside it. Enter / double-click finishes, Backspace
  // removes the last point, Esc cancels.
  const pxTol = (px) => px / Math.pow(2, mapRef.current?.getZoom() ?? 0);
  // The drawing's own straight lines, per sheet, in map units + a grid index — the snap targets while drawing
  // and editing. (Lines already drawn only attract at their corners, and only up close: a ceiling or wall is
  // meant to land on a line of the drawing, not on the shape beside it.)
  const drawingSegsRef = useRef(new Map()); // page → { segs: Float64Array [lat,lng,lat,lng…], grid, cell }
  useEffect(() => {
    if (!pdfDoc || !pageImage) return;
    const n = pageNum;
    if (drawingSegsRef.current.has(n)) return;
    let dead = false;
    ensureEngineSheet(n)
      .then((eng) => eng.segs(n, 0.5))
      .then((raw) => {
        if (dead || !raw) return;
        const rs = pageImage.renderScale || 1,
          H = pageImage.h || 0;
        const m = raw.length >> 2,
          segs = new Float64Array(m * 4);
        for (let i = 0; i < m; i++) {
          segs[i * 4] = H - raw[i * 4 + 1] * rs;
          segs[i * 4 + 1] = raw[i * 4] * rs;
          segs[i * 4 + 2] = H - raw[i * 4 + 3] * rs;
          segs[i * 4 + 3] = raw[i * 4 + 2] * rs;
        }
        const cell = 60,
          grid = new Map();
        for (let i = 0; i < m; i++) {
          const a0 = Math.floor(Math.min(segs[i * 4], segs[i * 4 + 2]) / cell),
            a1 = Math.floor(Math.max(segs[i * 4], segs[i * 4 + 2]) / cell);
          const b0 = Math.floor(
              Math.min(segs[i * 4 + 1], segs[i * 4 + 3]) / cell,
            ),
            b1 = Math.floor(Math.max(segs[i * 4 + 1], segs[i * 4 + 3]) / cell);
          if ((a1 - a0 + 1) * (b1 - b0 + 1) > 2500) continue; // page borders / huge frames
          for (let a = a0; a <= a1; a++)
            for (let b = b0; b <= b1; b++) {
              const k = a * 100003 + b;
              let arr = grid.get(k);
              if (!arr) grid.set(k, (arr = []));
              arr.push(i);
            }
        }
        drawingSegsRef.current.set(n, { segs, grid, cell });
      })
      .catch(() => {});
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc, pageImage, pageNum]);
  useEffect(() => {
    drawingSegsRef.current = new Map();
  }, [pdfDoc]);
  function drawingNear(q, r) {
    const D = drawingSegsRef.current.get(stateRef.current.pageNum);
    if (!D || !q) return null;
    const { segs, grid, cell } = D,
      seen = new Set(),
      outS = [],
      outV = [];
    for (
      let a = Math.floor((q[0] - r) / cell);
      a <= Math.floor((q[0] + r) / cell);
      a++
    )
      for (
        let b = Math.floor((q[1] - r) / cell);
        b <= Math.floor((q[1] + r) / cell);
        b++
      )
        for (const i of grid.get(a * 100003 + b) || []) {
          if (seen.has(i)) continue;
          seen.add(i);
          const A = [segs[i * 4], segs[i * 4 + 1]],
            B = [segs[i * 4 + 2], segs[i * 4 + 3]];
          outS.push([A, B]);
          outV.push(A, B);
        }
    return { segs: outS, verts: outV };
  }
  // snap targets near q: the drawing's lines and their ends (`verts`, `segs`), plus the corners of what's
  // been drawn (`own` — corners only, never their edges)
  const ownPtsRef = useRef({ key: null, pts: [] });
  function snapGeometry(q, r) {
    const st = stateRef.current;
    // corners of what's drawn on this sheet: gathered once per change, not on every mouse move
    const ext = drawRef.current?.extendId || "";
    const oc = ownPtsRef.current;
    if (oc.shapes !== st.shapes || oc.page !== st.pageNum || oc.ext !== ext) {
      const list = [];
      for (const sh of st.shapes) {
        if (sh.page !== st.pageNum || sh.id === ext || sh.meta?.headerOf)
          continue;
        for (const p of sh.latlngs) list.push(p);
      }
      ownPtsRef.current = {
        shapes: st.shapes,
        page: st.pageNum,
        ext,
        pts: list,
      };
    }
    const base = ownPtsRef.current.pts;
    const d = drawRef.current;
    const own = d?.pts?.length ? base.concat(d.pts) : base;
    const near = drawingNear(q, r);
    if (near) return { verts: near.verts, segs: near.segs, own };
    // drawing lines not loaded yet: the wall faces the engine found
    const verts = [],
      segs = [];
    const g = sheetGeo[st.pageNum];
    if (g?.runs)
      for (const R of g.runs) {
        if (R.t0 === undefined) continue;
        for (const side of [-1, 1]) {
          const off = R.c + (side * (R.th || 0)) / 2;
          const a = toLL([R.ux * R.t0 + R.nx * off, R.uy * R.t0 + R.ny * off]),
            b = toLL([R.ux * R.t1 + R.nx * off, R.uy * R.t1 + R.ny * off]);
          segs.push([a, b]);
          verts.push(a, b);
        }
      }
    return { verts, segs, own };
  }
  function directions() {
    // unit vectors in (lat,lng): sheet axes + the drawing's main wall directions + the last segment's
    const out = [
      [0, 1],
      [1, 0],
    ];
    const g = sheetGeo[stateRef.current.pageNum];
    if (g?.runs?.length) {
      const by = new Map();
      for (const R of g.runs) {
        const a =
          Math.round(
            (((Math.atan2(-R.uy, R.ux) + Math.PI) % Math.PI) * 180) / Math.PI,
          ) % 180;
        by.set(a, (by.get(a) || 0) + ((R.t1 ?? 1) - (R.t0 ?? 0)));
      }
      [...by.entries()]
        .sort((p, q) => q[1] - p[1])
        .slice(0, 4)
        .forEach(([deg]) => {
          const r = (deg * Math.PI) / 180;
          out.push([Math.sin(r), Math.cos(r)], [Math.cos(r), -Math.sin(r)]);
        });
    }
    const d = drawRef.current;
    if (d && d.pts.length >= 2) {
      const [a, b] = [d.pts[d.pts.length - 2], d.pts[d.pts.length - 1]];
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1,
        u = [(b[0] - a[0]) / L, (b[1] - a[1]) / L];
      out.push(u, [u[1], -u[0]]);
    }
    return out;
  }
  // where the next point goes, given the raw pointer position → { p, guide:[a,b]|null, locked }
  function constrainPoint(raw, free, fromPt) {
    const d = drawRef.current;
    const from = fromPt !== undefined ? fromPt : d?.pts?.[d.pts.length - 1];
    const tol = pxTol(10);
    const { verts, segs, own } = snapGeometry(raw, tol * 4);
    // a corner of the drawing first; a corner of something already drawn only when you're right on it
    const nearestVertex = (q, t) => {
      let best = null,
        bd = t;
      for (const v of verts) {
        const dd = Math.hypot(v[0] - q[0], v[1] - q[1]);
        if (dd < bd) {
          bd = dd;
          best = v;
        }
      }
      if (!best)
        for (const v of own) {
          const dd = Math.hypot(v[0] - q[0], v[1] - q[1]);
          if (dd < t * 0.5 && dd < bd) {
            bd = dd;
            best = v;
          }
        }
      return best;
    };
    const nearestOnSeg = (q, t) => {
      let best = null,
        bd = t;
      for (const [a, b] of segs) {
        const vx = b[0] - a[0],
          vy = b[1] - a[1],
          L2 = vx * vx + vy * vy;
        if (!L2) continue;
        const k = Math.max(
          0,
          Math.min(1, ((q[0] - a[0]) * vx + (q[1] - a[1]) * vy) / L2),
        );
        const p = [a[0] + vx * k, a[1] + vy * k],
          dd = Math.hypot(p[0] - q[0], p[1] - q[1]);
        if (dd < bd) {
          bd = dd;
          best = p;
        }
      }
      return best;
    };
    if (!from || free) {
      const v = nearestVertex(raw, tol) || nearestOnSeg(raw, tol * 0.7);
      return { p: v || raw, guide: null, locked: false };
    }
    const dx = raw[0] - from[0],
      dy = raw[1] - from[1],
      len = Math.hypot(dx, dy);
    if (len < 1e-9) return { p: raw, guide: null, locked: false };
    let best = null;
    for (const u of directions()) {
      const cos = Math.abs((dx * u[0] + dy * u[1]) / len);
      const ang = Math.acos(Math.min(1, cos));
      if (ang < (10 * Math.PI) / 180 && (!best || ang < best.ang))
        best = { u: dx * u[0] + dy * u[1] >= 0 ? u : [-u[0], -u[1]], ang };
    }
    if (!best) {
      // off-axis on purpose: corners only
      const v = nearestVertex(raw, tol);
      return { p: v || raw, guide: null, locked: false };
    }
    const u = best.u;
    let t = dx * u[0] + dy * u[1];
    let guide = null;
    // 1. stop on a line this ray meets (the parallel wall, the far face)
    let hit = null;
    for (const [a, b] of segs) {
      const ex = b[0] - a[0],
        ey = b[1] - a[1],
        den = u[0] * ey - u[1] * ex;
      if (Math.abs(den) < 1e-12) continue;
      const wx = a[0] - from[0],
        wy = a[1] - from[1];
      const tt = (wx * ey - wy * ex) / den,
        k = (wx * u[1] - wy * u[0]) / den;
      if (k < -0.001 || k > 1.001 || tt <= tol) continue;
      if (
        Math.abs(tt - t) < tol &&
        (!hit || Math.abs(tt - t) < Math.abs(hit - t))
      )
        hit = tt;
    }
    if (hit !== null) t = hit;
    else {
      // 2. line up with a nearby corner (same x or same y along the locked direction)
      let al = null;
      for (const v of [...verts, ...(drawRef.current?.pts || [])]) {
        const tv = (v[0] - from[0]) * u[0] + (v[1] - from[1]) * u[1];
        if (tv <= tol || Math.abs(tv - t) >= tol) continue;
        if (!al || Math.abs(tv - t) < Math.abs(al.tv - t)) al = { tv, v };
      }
      if (al) {
        t = al.tv;
        guide = [al.v, [from[0] + u[0] * t, from[1] + u[1] * t]];
      }
    }
    return { p: [from[0] + u[0] * t, from[1] + u[1] * t], guide, locked: true };
  }
  // The live line while drawing has its own small SVG layer: moving the mouse redraws only the line being
  // drawn, never the canvas with every shape on the sheet. Its pieces are kept and moved, not rebuilt.
  const draftRendererRef = useRef(null);
  const draftRenderer = () =>
    (draftRendererRef.current ||= L.svg({ padding: 0.25 }));
  // a hand-drawn wall line, moved onto the wall's centre (what will be saved) — cached per point list
  function draftCentered(d, cond) {
    if (d.kind !== "polyline" || !cond || d.pts.length < 2) return null;
    const key = `${d.pts.length}|${d.pts[d.pts.length - 1]}|${d.center !== false}|${condAlign(cond)}`;
    if (d._cKey === key) return d._cPts;
    d._cKey = key;
    d._cPts = centeredLine(
      d.pts,
      cond,
      d.extendId ? (d.startLen || 1) - 1 : 0,
      d,
    );
    return d._cPts;
  }
  function renderDraft(cursor) {
    const map = mapRef.current,
      d = drawRef.current;
    if (!map || !d) return;
    const R = draftRenderer();
    const K = (d.L ||= {});
    const used = new Set();
    const put = (name, make, update) => {
      let l = K[name];
      if (l && !map.hasLayer(l)) l = null;
      if (!l) {
        l = K[name] = make().addTo(map);
        d.layers.push(l);
      } else update?.(l);
      used.add(name);
      return l;
    };
    const cond = stateRef.current.conditions.find((c) => c.id === d.condId);
    const color = cond?.color || "#f97316";
    const pts = cursor ? [...d.pts, cursor.p] : d.pts;
    // the band at the assembly's real thickness, where the line will be saved (centred on the wall)
    if (d.kind === "polyline" && cond) {
      const cp = draftCentered(d, cond);
      const bandPts = cp ? (cursor ? [...cp, cursor.p] : cp) : pts;
      const px = inchesToPx(condLineIn(cond), stateRef.current.pageNum);
      if (bandPts.length >= 2 && px)
        put(
          "band",
          () =>
            L.polyline(bandPts, {
              renderer: R,
              color,
              weight: Math.max(3, px),
              opacity: 0.32,
              lineCap: "butt",
              lineJoin: "miter",
              interactive: false,
            }),
          (l) => {
            l.setLatLngs(bandPts);
            l.setStyle({ weight: Math.max(3, px), color });
          },
        );
    }
    if (pts.length >= 2)
      put(
        "line",
        () =>
          L.polyline(pts, {
            renderer: R,
            color,
            weight: 2,
            opacity: 0.95,
            interactive: false,
          }),
        (l) => l.setLatLngs(pts),
      );
    if (d.kind === "polygon" && pts.length >= 3)
      put(
        "fill",
        () =>
          L.polygon(pts, {
            renderer: R,
            color,
            weight: 0,
            fillColor: color,
            fillOpacity: 0.15,
            interactive: false,
          }),
        (l) => l.setLatLngs(pts),
      );
    // the clicked points (only re-made when one is added / taken off)
    const vpts = d.pts.slice(
      d.extendId ? Math.max(0, (d.startLen || 1) - 1) : 0,
    );
    const vkey = vpts.map((q) => q.join(",")).join(";");
    if (K._vkey !== vkey || (K._verts || []).some((l) => !map.hasLayer(l))) {
      (K._verts || []).forEach((l) => map.removeLayer(l));
      K._verts = vpts.map((q) => {
        const l = L.circleMarker(q, {
          renderer: R,
          radius: 3.5,
          color: "#fff",
          weight: 1.5,
          fillColor: color,
          fillOpacity: 1,
          interactive: false,
        }).addTo(map);
        d.layers.push(l);
        return l;
      });
      K._vkey = vkey;
    }
    if (cursor?.guide)
      put(
        "guide",
        () =>
          L.polyline(cursor.guide, {
            renderer: R,
            color: "#38bdf8",
            weight: 1,
            dashArray: "4 4",
            interactive: false,
          }),
        (l) => l.setLatLngs(cursor.guide),
      );
    if (cursor) {
      put(
        "cursor",
        () =>
          L.circleMarker(cursor.p, {
            renderer: R,
            radius: 4,
            color: cursor.locked ? "#38bdf8" : "#fff",
            weight: 2,
            fillOpacity: 0,
            interactive: false,
          }),
        (l) => {
          l.setLatLng(cursor.p);
          l.setStyle({ color: cursor.locked ? "#38bdf8" : "#fff" });
        },
      );
      const f =
        stateRef.current.scales[stateRef.current.pageNum] ??
        stateRef.current.scales[String(stateRef.current.pageNum)];
      const from = d.pts[d.pts.length - 1];
      // a length being typed: show where it will land (dashed), and the number in the label
      const typed = lenTypedRef.current;
      const typedFt = typed ? parseFeetLoose(typed) : null;
      if (f && from && typedFt > 0) {
        const vx = cursor.p[0] - from[0],
          vy = cursor.p[1] - from[1],
          L0 = Math.hypot(vx, vy) || 1;
        const q = [
          from[0] + (vx / L0) * (typedFt / f),
          from[1] + (vy / L0) * (typedFt / f),
        ];
        put(
          "typedLine",
          () =>
            L.polyline([from, q], {
              renderer: R,
              color: "#fbbf24",
              weight: 3,
              dashArray: "6 5",
              interactive: false,
            }),
          (l) => l.setLatLngs([from, q]),
        );
        put(
          "typedPt",
          () =>
            L.circleMarker(q, {
              renderer: R,
              radius: 5,
              color: "#fbbf24",
              weight: 2,
              fillOpacity: 0,
              interactive: false,
            }),
          (l) => l.setLatLng(q),
        );
      }
      if (f && from) {
        const seg =
          Math.hypot(cursor.p[0] - from[0], cursor.p[1] - from[1]) * f;
        let tot = 0;
        for (
          let i = d.extendId ? (d.startLen || 1) - 1 : 0;
          i + 1 < pts.length;
          i++
        )
          tot +=
            Math.hypot(pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1]) *
            f;
        const centring =
          d.kind === "polyline" && cond && condAlign(cond) === "auto"
            ? d.center === false
              ? `<span style="opacity:.45;font-weight:500"> · C: centre on wall</span>`
              : `<span style="color:#86efac;font-weight:500"> · centres on the wall (C = off)</span>`
            : "";
        const html = typed
          ? `<div style="transform:translate(14px,-30px);width:max-content;white-space:nowrap;background:#111827;color:#fde68a;font:600 12px system-ui;padding:4px 8px;border-radius:7px;border:1.5px solid #fbbf24;box-shadow:0 2px 10px rgba(0,0,0,.5)">⌨ ${typed.replace(/</g, "&lt;")}<span style="opacity:.55;font-weight:500">${typedFt > 0 ? ` = ${fmtFeetIn(typedFt)}` : ""} · Enter</span></div>`
          : `<div style="transform:translate(14px,-28px);width:max-content;white-space:nowrap;background:rgba(17,24,39,.92);color:#fff;font:600 11px system-ui;padding:3px 7px;border-radius:6px;border:1px solid rgba(255,255,255,.12)">${fmtFeetIn(seg)}${tot > seg + 0.01 ? `<span style="opacity:.6;font-weight:500"> · ${fmtFeetIn(tot)} total</span>` : ""}${cursor.locked ? "" : `<span style="color:#fbbf24;font-weight:500"> · free</span>`}<span style="opacity:.45;font-weight:500"> · type a length</span>${centring}</div>`;
        const lab = put(
          "label",
          () =>
            L.marker(cursor.p, {
              interactive: false,
              icon: L.divIcon({ className: "", iconSize: [0, 0], html }),
            }),
          (l) => {
            l.setLatLng(cursor.p);
            const el = l.getElement?.();
            if (el && el._html !== html) {
              el.innerHTML = html;
              el._html = html;
            }
          },
        );
        void lab;
      }
    }
    // anything not drawn this time comes off
    for (const name of Object.keys(K)) {
      if (name.startsWith("_") || used.has(name)) continue;
      map.removeLayer(K[name]);
      delete K[name];
    }
  }
  // a wall line as it will be saved: on the wall's centre (assembly set to "auto"), or the band to one side
  // (left / right), or as clicked. fromSeg: pieces before it stay as they are.
  function centeredLine(pts, cond, fromSeg = 0, d = null) {
    const st = stateRef.current;
    const f = st.scales[st.pageNum] ?? st.scales[String(st.pageNum)];
    const align = condAlign(cond);
    if (!f || pts.length < 2 || align === "click") return pts;
    if (align === "auto" && d?.center === false) return pts;
    return centerOnWalls(pts, {
      segsNear: (q, r) => snapGeometry(q, r).segs,
      unitsPerFt: 1 / f,
      fromSeg: Math.max(0, fromSeg),
      align,
      bandFt: condLineIn(cond) / 12,
    });
  }
  // the next point exactly `text` (feet & inches) from the last one, in the direction the pointer is (locked to the
  // usual directions unless Shift was held at the last move)
  handlersRef.current.redrawDraft = () => {
    const d = drawRef.current;
    if (!d || !mouseLLRef.current) return;
    renderDraft(
      constrainPoint(mouseLLRef.current, handlersRef.current.lastShift),
    );
  };
  handlersRef.current.placeTypedLength = (text) => {
    const d = drawRef.current;
    const st = stateRef.current;
    const f = st.scales[st.pageNum] ?? st.scales[String(st.pageNum)];
    const ft = parseFeetLoose(text);
    setLenTyped("");
    if (!d || !d.pts.length) return;
    if (!f)
      return flash(
        "Set this sheet's scale first — a typed length needs it.",
        "warn",
      );
    if (!(ft > 0)) {
      flash(`“${text}” isn't a length — try 10, 10'6 or 126"`, "warn");
      handlersRef.current.redrawDraft?.();
      return;
    }
    const from = d.pts[d.pts.length - 1];
    const aim = mouseLLRef.current
      ? constrainPoint(mouseLLRef.current, handlersRef.current.lastShift).p
      : null;
    let ux = aim ? aim[0] - from[0] : 0,
      uy = aim ? aim[1] - from[1] : 0;
    let L0 = Math.hypot(ux, uy);
    if (L0 < 1e-9) {
      // pointer on the last point: carry on in the last segment's direction, else to the right
      const prev = d.pts[d.pts.length - 2];
      if (prev) {
        ux = from[0] - prev[0];
        uy = from[1] - prev[1];
      } else {
        ux = 0;
        uy = 1;
      }
      L0 = Math.hypot(ux, uy) || 1;
    }
    const units = ft / f;
    const p = [from[0] + (ux / L0) * units, from[1] + (uy / L0) * units];
    d.pts.push(p);
    if (d.extendId) {
      const pts = d.pts.map((q) => [q[0], q[1]]),
        id = d.extendId;
      setShapes((prev) =>
        prev.map((x) => (x.id === id ? { ...x, latlngs: pts } : x)),
      );
      layerMapRef.current.get(id)?.setLatLngs(pts);
    }
    renderDraft(
      mouseLLRef.current
        ? constrainPoint(mouseLLRef.current, handlersRef.current.lastShift)
        : null,
    );
    flash(`${fmtFeetIn(ft)} placed — keep typing lengths, or click`, "ok");
  };
  function endDraw() {
    const map = mapRef.current,
      d = drawRef.current;
    if (map && d) d.layers.forEach((l) => map.removeLayer(l));
    drawRef.current = null;
    if (lenTypedRef.current) setLenTyped("");
  }
  function floorHeightOf(page) {
    const ts = stateRef.current.traceSettings || {};
    return (
      parseFeetInput(
        ts.floor?.[page]?.height ||
          ts.floor?.[String(page)]?.height ||
          ts.defaultHeight ||
          "",
      ) || null
    );
  }
  // join a line (pts; existing shape `id`, or a new `shape`) onto another line of the same assembly whose end
  // it starts or stops on. Returns true when it was merged (the other line keeps its id, meta and height).
  function joinTouching(id, condId, page, pts, newShape = null) {
    const tol = pxTol(6);
    const st = stateRef.current;
    const others = st.shapes.filter(
      (x) =>
        x.kind === "polyline" &&
        x.conditionId === condId &&
        x.page === page &&
        x.id !== id &&
        x.latlngs.length >= 2,
    );
    const near = (p, q) => Math.hypot(p[0] - q[0], p[1] - q[1]) <= tol;
    const s0 = pts[0],
      e0 = pts[pts.length - 1];
    const o = others.find((x) => {
      const a = x.latlngs[0],
        b = x.latlngs[x.latlngs.length - 1];
      return near(s0, a) || near(s0, b) || near(e0, a) || near(e0, b);
    });
    if (!o) return false;
    const [merged] = joinPolylines([o.latlngs, pts], tol);
    const ll = simplifyPts(merged);
    const from = id ? st.shapes.find((x) => x.id === id) : newShape;
    setShapes((prev) =>
      prev
        .filter((x) => x.id !== id)
        .map((x) =>
          x.id === o.id
            ? { ...x, latlngs: ll, meta: mergeWallMeta(x.meta, from?.meta) }
            : x,
        ),
    );
    layerMapRef.current.get(o.id)?.setLatLngs(ll);
    return o.id;
  }
  // Merge the selected lines (one assembly) into as few lines as possible: ends that meet — or come within
  // 1'-6" of each other — are joined, in-line points dropped. Lines that don't touch anything stay separate.
  function mergeSelectedLines(ids) {
    const st = stateRef.current;
    const list = st.shapes.filter(
      (x) => ids.includes(x.id) && x.kind === "polyline",
    );
    if (list.length < 2) return;
    const f = st.scales[list[0].page] ?? st.scales[String(list[0].page)];
    const tol = f ? 1.5 / f : pxTol(10);
    const pieces = joinPolylines(
      list.map((x) => x.latlngs),
      tol,
    ).map((pts) => simplifyPts(pts));
    // each merged piece keeps the first original line it contains (id, height, type) and every deduction
    const used = new Set();
    const keep = pieces.map((pts) => {
      const owners = list.filter(
        (x) =>
          !used.has(x.id) &&
          pts.some(
            (q) =>
              Math.hypot(q[0] - x.latlngs[0][0], q[1] - x.latlngs[0][1]) <= tol,
          ),
      );
      const base = owners[0] || list.find((x) => !used.has(x.id)) || list[0];
      owners.forEach((o) => used.add(o.id));
      used.add(base.id);
      const meta = owners
        .slice(1)
        .reduce((m, o) => mergeWallMeta(m, o.meta), base.meta || {});
      return { ...base, latlngs: pts, meta };
    });
    const drop = new Set(list.map((x) => x.id));
    keep.forEach((k) => drop.delete(k.id));
    setShapes((prev) => [
      ...prev
        .filter((x) => !drop.has(x.id))
        .map((x) => keep.find((k) => k.id === x.id) || x),
    ]);
    keep.forEach((k) => layerMapRef.current.get(k.id)?.setLatLngs(k.latlngs));
    setSel(keep.map((k) => k.id));
    const before = list.reduce((n, x) => n + x.latlngs.length, 0),
      after = keep.reduce((n, x) => n + x.latlngs.length, 0);
    flash(
      keep.length === 1
        ? `Merged ${list.length} lines into one (${before} → ${after} points).`
        : `${list.length} lines → ${keep.length} (the rest don't touch) · ${before} → ${after} points.`,
      "ok",
    );
  }
  // drop repeated / in-line points from lines
  // an area's outline in map units with as few corners as it needs (jogs under AREA_TOL_IN dropped)
  function slimArea(latlngs, page) {
    const f =
      stateRef.current.scales[page] ?? stateRef.current.scales[String(page)];
    return f
      ? simplifyRing(latlngs, AREA_TOL_IN / 12 / f)
      : simplifyRing(latlngs, 0.5);
  }
  function simplifyShapes(ids) {
    let saved = 0;
    setShapes((prev) =>
      prev.map((x) => {
        if (
          !ids.includes(x.id) ||
          (x.kind !== "polyline" && x.kind !== "polygon")
        )
          return x;
        const pts =
          x.kind === "polygon"
            ? slimArea(x.latlngs, x.page)
            : simplifyPts(x.latlngs);
        saved += x.latlngs.length - pts.length;
        layerMapRef.current.get(x.id)?.setLatLngs(pts);
        return pts.length === x.latlngs.length ? x : { ...x, latlngs: pts };
      }),
    );
    setTimeout(
      () =>
        flash(
          saved
            ? `Removed ${saved} extra point${saved === 1 ? "" : "s"}.`
            : "Already as simple as it gets.",
          saved ? "ok" : "info",
        ),
      0,
    );
  }
  // finish the line / area being drawn (stays in Draw for the next one)
  function finishDraft() {
    const d = drawRef.current;
    if (!d) return;
    const st = stateRef.current;
    const cond = st.conditions.find((c) => c.id === d.condId);
    if (d.extendId) {
      const id = d.extendId;
      // the new part moves onto the wall's centre (the part the line already had stays)
      const pts = simplifyPts(
        centeredLine(
          d.pts.map((q) => [q[0], q[1]]),
          cond,
          (d.startLen || 1) - 1,
          d,
        ),
      );
      const joined = joinTouching(id, d.condId, st.pageNum, pts);
      if (joined) {
        setPanMode();
        setSel([joined]);
        flash(
          "Line extended and joined to the line it meets — one line now.",
          "ok",
        );
        return;
      }
      setShapes((prev) =>
        prev.map((x) => (x.id === id ? { ...x, latlngs: pts } : x)),
      );
      setPanMode();
      setSel([id]);
      if (d.pts.length > (d.startLen || 0)) flash("Line extended.", "ok");
      return;
    }
    const need = d.kind === "polygon" ? 3 : 2;
    if (cond && d.pts.length >= need) {
      const h = cond.type === "linear" ? floorHeightOf(st.pageNum) : null;
      // a wall line lands on the wall's centre: clicked along a face (or anywhere on the wall), the band then
      // covers the wall face to face, and its corners are one mitred line
      const pts =
        d.kind === "polyline"
          ? simplifyPts(
              centeredLine(
                d.pts.map((q) => [q[0], q[1]]),
                cond,
                0,
                d,
              ),
            )
          : d.pts.map((q) => [q[0], q[1]]);
      const shape = {
        id: uid(),
        conditionId: cond.id,
        page: st.pageNum,
        kind: d.kind,
        latlngs: pts,
        meta: { source: "manual", ...(h ? { height: h } : {}) },
      };
      // a new line that starts or ends on another line's end (same assembly) becomes part of that line
      if (
        !(
          d.kind === "polyline" &&
          joinTouching(null, cond.id, st.pageNum, pts, shape)
        )
      )
        setShapes((prev) => [...prev, shape]);
    }
    d.layers.forEach((l) => mapRef.current?.removeLayer(l));
    d.layers = [];
    d.L = {};
    d.pts = [];
  }

  // -------------------------------------------------------------------------
  // Metrics / totals / save / export
  // -------------------------------------------------------------------------
  const condMetrics = useMemo(() => {
    const out = {};
    conditions.forEach((cond) => {
      out[cond.id] = computeCondMetrics(
        cond,
        shapes,
        scales,
        excludedPages,
        units,
      );
    });
    return out;
  }, [conditions, shapes, scales, excludedPages, units]);

  const condMetricsByArea = useMemo(() => {
    const areaOf = (pg) =>
      String(pageBidAreas[pg] ?? pageBidAreas[String(pg)] ?? "").trim();
    const names = new Set(shapes.map((sh) => areaOf(sh.page)));
    if (![...names].some(Boolean)) return null;
    const out = {};
    names.forEach((name) => {
      const sub = shapes.filter((sh) => areaOf(sh.page) === name);
      conditions.forEach((cond) => {
        if (!out[cond.id]) out[cond.id] = {};
        out[cond.id][name] = computeCondMetrics(
          cond,
          sub,
          scales,
          excludedPages,
          units,
        );
      });
    });
    return out;
  }, [conditions, shapes, scales, excludedPages, units, pageBidAreas]);
  // quantities on the sheet on screen (the Assemblies list's default view)

  // legend on the drawing (this sheet) + the print-working-drawings dialog
  const [showLegend, setShowLegend] = useState(false);
  const [wd, setWd] = useState({
    open: false,
    scope: "this",
    legend: "tl",
    details: true,
    dpi: 0,
  });
  const wdBusyRef = useRef(false);
  const legendRows = useMemo(
    () =>
      showLegend
        ? buildSheetLegends({
            conditions,
            shapes: shapes.filter((s) => s.page === pageNum),
            pageNames: {},
            excludedPages: [],
            pageBidAreas: {},
            mappings: [],
            metricsFor: (cond, ps) =>
              computeCondMetrics(cond, ps, scales, excludedPages, units),
          })[0]?.rows || []
        : [],
    [showLegend, conditions, shapes, pageNum, scales, excludedPages, units],
  );

  const pageMetrics = useMemo(() => {
    const onPage = shapes.filter((s) => s.page === pageNum);
    const out = {};
    conditions.forEach((cond) => {
      out[cond.id] = computeCondMetrics(
        cond,
        onPage,
        scales,
        excludedPages,
        units,
      );
    });
    return out;
  }, [conditions, shapes, scales, excludedPages, units, pageNum]);
  function buildPayload() {
    // Smaller request: every number in a shape (outline, window / door segments, …) to 0.01 — far below a
    // pixel; they came out with 15 digits — and empty (null) meta fields dropped. About half the size.
    const slim = shapes.map(slimShape);
    const id = takeoffIdRef.current || takeoffId;
    return {
      ...(id ? { _id: id } : {}),
      ...(projectId ? { projectId } : { opportunityId }),
      fileName: fileNameRef.current || fileName,
      gcsPath: gcsRef.current,
      status,
      pageCount,
      conditions,
      shapes: slim,
      scales,
      pageNames,
      excludedPages,
      pageBidAreas,
      coPins,
      proposals, // unaccepted room proposals
      wallTagSettings: {
        enabled: enabledTags,
        sideAware: tagSideAware,
        map: tagCondMap,
      },
      assemblies,
      traceSettings,
      units,
      scaleInfo,
      sheetRevisions,
    };
  }

  // Saves run one at a time (two first saves racing would both try to create the takeoff), and each
  // one builds its payload when it actually runs, so it always carries the newest state.
  const saveChainRef = useRef(Promise.resolve());
  const doSaveRef = useRef(null);
  doSaveRef.current = doSave;
  function handleSave(opts) {
    const p = saveChainRef.current
      .catch(() => {})
      .then(() => doSaveRef.current(opts));
    saveChainRef.current = p;
    return p;
  }
  saveRef.current = handleSave; // async callers (upload, leaving) always save the latest state
  async function doSave({ quiet = false } = {}) {
    if (!onSave) return null;
    const payload = buildPayload();
    // nothing to save until a drawing is open (an empty takeoff has no name to file it under)
    if (!payload.fileName && !payload.gcsPath && !payload._id) {
      if (!quiet)
        flash(
          "Open a drawing first — the takeoff is saved under the drawing's file name.",
          "info",
        );
      return null;
    }
    // An existing takeoff on a server that keeps revisions: send only what changed since the revision this
    // screen has (other people's work in the meantime is merged on the server, not overwritten)
    const sync = syncRef.current;
    let sent = null;
    let body = payload;
    if (payload._id && sync && supportsOpsRef.current) {
      const { ops, empty } = opsSince(sync, payload);
      if (empty) {
        dirtyRef.current = false;
        return null;
      }
      sent = ops;
      body = {
        _id: payload._id,
        ...(projectId ? { projectId } : { opportunityId }),
        baseRev: sync.rev,
        clientId: clientIdRef.current,
        userName: myName(),
        ops,
      };
    }
    dirtyRef.current = false;
    setSaving(true);
    try {
      const saved = await onSave(
        body === payload
          ? { ...payload, clientId: clientIdRef.current, userName: myName() }
          : body,
      );
      const doc = saved?.data?._id ? saved.data : saved;
      if (doc?._id) {
        takeoffIdRef.current = doc._id;
        setTakeoffId(doc._id);
      }
      if (typeof doc?.rev === "number") {
        supportsOpsRef.current = true;
        if (sent && syncRef.current) {
          // what was sent is now what the server has; then whatever others changed meanwhile
          const s2 = syncRef.current;
          for (const key of ["shapes", "conditions"]) {
            for (const x of sent[key].upsert) s2[key].set(x.id, sjson(x));
            for (const id of sent[key].remove) s2[key].delete(id);
          }
          for (const [k, v] of Object.entries(sent.set)) s2.keys[k] = sjson(v);
          s2.applied.add(doc.rev);
          applyRemote(doc.remote || []);
          s2.rev = Math.max(s2.rev, doc.rev);
          if (doc.reload) setRemoteReload(true); // others saved more than a save brings back
        } else syncRef.current = syncSnapshot(payload, doc.rev);
        setSyncRev(doc.rev);
      }
      // the server may file a new takeoff as "name (2).pdf" when the name is taken — show what it was saved as
      if (doc?.fileName && doc.fileName !== fileNameRef.current) {
        fileNameRef.current = doc.fileName;
        setFileName(doc.fileName);
      }
      setSavedAt(new Date());
      setSaveError("");
      setError("");
      return doc;
    } catch (err) {
      dirtyRef.current = true; // still unsaved — the next change / Retry / leaving tries again
      console.error("Takeoff save failed", err);
      const st = err?.response?.status;
      const msg =
        st === 413
          ? "the takeoff is bigger than the server accepts (HTTP 413 — raise the JSON body limit for /api/takeoffs3)"
          : err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            "unknown error";
      setSaveError(msg);
      if (!quiet) flash(`Not saved — ${msg}`, "error");
      throw err;
    } finally {
      setSaving(false);
    }
  }

  function exportCsv() {
    const rows = [
      ["Condition", "Wall Type", "Type", "Shapes", "LF", "SF", "EA"],
    ];
    conditions.forEach((cond) => {
      const m = condMetrics[cond.id] || {};
      rows.push([
        cond.name,
        cond.wallType || "",
        cond.type,
        String(m.count ?? 0),
        m.lf != null ? m.lf.toFixed(1) : "",
        m.sf != null ? m.sf.toFixed(1) : "",
        cond.type === "count" ? String(m.count ?? 0) : "",
      ]);
    });
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(fileName || "takeoff").replace(/\.pdf$/i, "")}-takeoff.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const measureFeet = fpu ? measureUnits * fpu : null;
  const pageLabel = (n) => pageNames[n] || pageNames[String(n)] || `Sheet ${n}`;

  const modeLabel = {
    pan: null,
    select: null,
    draw: activeCond
      ? `${activeCond.name} — click points, or type a length (10, 10'6") + Enter · locks straight (Shift = any angle) · Enter, double-click or Esc keeps it · Backspace takes a point back`
      : null,
    calibrate:
      "Calibrate: click both ends of a known dimension — Esc to cancel",
    measure: null,
    fill: "Fill room — click inside a room to trace it from the drawing linework (proposal). Esc to stop",
    edit: "Editing shapes — drag vertices, drag a midpoint square to add one, Esc to finish",
    delete: "Delete mode — click a shape to remove it, Esc to finish",
    extend:
      "Continuing the line — every click is saved · Backspace / Ctrl+Z takes a point back · Enter, right-click or Esc when done",
  }[mode];

  // =========================================================================
  // IMMERSIVE WORKSPACE + ENGINE + AI (added)
  // =========================================================================
  const rootRef = useRef(null);
  const proposalLayersRef = useRef(new Map());
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [rightTab, setRightTab] = useState("takeoff");
  const [isFullscreen, setIsFullscreen] = useState(false); // browser fullscreen (hides browser chrome)
  const [immersive, setImmersive] = useState(false); // lifted out of the app tabs/sidebar
  const [toast, setToast] = useState(null); // {msg, kind}
  const [proposals, setProposals] = useState(initialData?.proposals || []);
  const [assemblies, setAssemblies] = useState(initialData?.assemblies || []); // types read off the assembly sheets
  // a condition made from an assembly carries its description (edit the assembly → the condition follows)
  useEffect(() => {
    const byId = new Map(assemblies.map((a) => [a.id, a]));
    const byCond = new Map();
    for (const a of assemblies) if (a.conditionId) byCond.set(a.conditionId, a);
    setConditions((prev) => {
      let changed = false;
      const next = prev.map((c) => {
        const a = byCond.get(c.id) || (c.assemblyId && byId.get(c.assemblyId));
        if (!a) return c;
        const d = descOf(a);
        if ((c.description || "") === d && c.assemblyId === a.id) return c;
        changed = true;
        return { ...c, description: d, assemblyId: a.id };
      });
      return changed ? next : prev;
    });
  }, [assemblies]);
  const [traceSettings, setTraceSettings] = useState(
    initialData?.traceSettings || {},
  ); // sheets, heights, options, batches
  const [showWizard, setShowWizard] = useState(false);
  const [sheetEngInfo, setSheetEngInfo] = useState({}); // page → { tagStyle, colorLegend, colors, area }
  const asmRef = useRef([]);
  asmRef.current = assemblies;
  // ── every assembly has its own number (#1, #2 …) to refer to it by. Numbers are handed out once and never
  //    reused (traceSettings.numberSeq keeps the last one given, so a deleted #7 never comes back as another
  //    assembly). An assembly card made from a legend type shares that type's number; a duplicate number
  //    (copied card, merged takeoffs) gets a fresh one.
  useEffect(() => {
    let seq = Number(traceSettings?.numberSeq) || 0;
    const startSeq = seq;
    for (const x of [...assemblies, ...conditions])
      if (Number(x?.no) > seq) seq = Number(x.no);
    const taken = new Map(); // no → owner
    const asmNo = new Map();
    let aChanged = false,
      cChanged = false;
    const nextA = assemblies.map((a) => {
      if (!a) return a;
      let n = Number(a.no);
      if (!(n > 0) || taken.has(n)) {
        n = ++seq;
        aChanged = true;
      }
      taken.set(n, `a:${a.id}`);
      asmNo.set(a.id, n);
      return n === a.no ? a : { ...a, no: n };
    });
    const asmShared = new Set();
    const nextC = conditions.map((c) => {
      let n = Number(c.no);
      // the main card of a legend type carries the type's number (its washroom variant gets its own)
      if (
        c.assemblyId &&
        asmNo.has(c.assemblyId) &&
        !c.wet &&
        !asmShared.has(c.assemblyId)
      ) {
        asmShared.add(c.assemblyId);
        n = asmNo.get(c.assemblyId);
      } else {
        const owner = taken.get(n);
        if (!(n > 0) || (owner && owner !== `c:${c.id}`)) n = ++seq;
        taken.set(n, `c:${c.id}`);
      }
      if (n !== c.no) {
        cChanged = true;
        return { ...c, no: n };
      }
      return c;
    });
    if (aChanged) setAssemblies(nextA);
    if (cChanged) setConditions(nextC);
    if (seq !== startSeq)
      setTraceSettings((prev) => ({ ...(prev || {}), numberSeq: seq }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assemblies, conditions]);
  const [sheetMenu, setSheetMenu] = useState(false);
  const [sheetListFilter, setSheetListFilter] = useState("all"); // sheet menu: all | done | todo
  // assemblies picked lately in the right panel (pinned on top of the picker)
  const [recentCondIds, setRecentCondIds] = useState([]);
  // ── hidden assemblies (review aid): their lines / areas / markers are taken off the drawing — never deleted.
  //    Remembered per takeoff in this browser; the drawing, the Assemblies list and the picker all say what's hidden,
  //    and drawing with a hidden assembly shows it again (so nothing gets drawn twice thinking it's missing).
  const hideKey = `takeoff-hidden:${initialData?._id || initialData?.id || projectId || opportunityId || "draft"}`;
  const [hiddenConds, setHiddenConds] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem(hideKey) || "[]");
      return new Set(Array.isArray(v) ? v : []);
    } catch {
      return new Set();
    }
  });
  const hiddenRef = useRef(hiddenConds);
  hiddenRef.current = hiddenConds;
  useEffect(() => {
    try {
      localStorage.setItem(hideKey, JSON.stringify([...hiddenConds]));
    } catch {
      /* private window — kept for this session only */
    }
  }, [hiddenConds, hideKey]);
  const setHidden = (ids, hide) =>
    setHiddenConds((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (hide) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  const toggleHidden = (id) => setHidden([id], !hiddenRef.current.has(id));
  const showAllConds = () => setHiddenConds(new Set());
  // review one assembly: everything else that's drawn is hidden
  const isolateCond = (id) =>
    setHiddenConds(
      new Set(
        conditions
          .filter(
            (c) => c.id !== id && shapes.some((x) => x.conditionId === c.id),
          )
          .map((c) => c.id),
      ),
    );
  const noteRecentCond = (id) =>
    setRecentCondIds((prev) =>
      [id, ...prev.filter((x) => x !== id)].slice(0, 5),
    );
  const [sheetQuery, setSheetQuery] = useState("");
  // takeoff items per sheet — for the sheet menu's badges and its "with / without takeoff" filter
  const shapesPerPage = useMemo(() => {
    const m = new Map();
    for (const sh of shapes || []) {
      const pg = Number(sh?.page);
      if (pg) m.set(pg, (m.get(pg) || 0) + 1);
    }
    return m;
  }, [shapes]);
  const [selProposal, setSelProposal] = useState(null);
  const [adjustingId, setAdjustingId] = useState(null);
  const [scaleHint, setScaleHint] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [detectInfo, setDetectInfo] = useState(null);
  const [aiBusy, setAiBusy] = useState("");
  const [checks, setChecks] = useState(null);
  // what Detect rooms / Fill room make: walls (perimeter lines), ceilings (room areas) or both — asked each
  // time unless the estimator turned the question off
  const [acceptPerimeter, setAcceptPerimeter] = useState(
    initialData?.traceSettings?.roomMake !== "ceilings",
  );
  const [acceptArea, setAcceptArea] = useState(
    initialData?.traceSettings?.roomMake !== "walls",
  );
  const [roomAsk, setRoomAsk] = useState(null); // "detect" | "fill" while the question is up
  const [perimCondId, setPerimCondId] = useState("auto");
  const [areaCondId, setAreaCondId] = useState("auto");
  stateRef.current.proposals = proposals;
  stateRef.current.traceSettings = traceSettings;
  const dirtyRef = useRef(false);
  const firstRenderRef = useRef(true);

  const flash = (msg, kind = "info") => {
    setToast({ msg, kind });
    clearTimeout(flash._t);
    flash._t = setTimeout(() => setToast(null), 4500);
  };

  // ---- drawing URL: GridFS-backed (no Google). Auth rides in pdf.js httpHeaders.
  // The takeoff's own stored copy always comes first. A fileUrl from the parent is only a fallback, and only
  // when it's a full URL — "/api/…" from /drawing-url resolves against the web app's host, not the API's.
  const storedSrc =
    gcsPath && gcsPath.startsWith("gridfs:")
      ? `${API}/takeoffs3/drawing?path=${encodeURIComponent(gcsPath)}`
      : null;
  const parentSrc =
    fileUrl && /^(https?:|blob:|data:)/i.test(fileUrl) ? fileUrl : null;
  const drawingSrc = storedSrc || parentSrc;
  const autoLoadedRef = useRef(false);
  useEffect(() => {
    if (autoLoadedRef.current || !drawingSrc) return;
    autoLoadedRef.current = true;
    loadPdf([storedSrc, parentSrc].filter(Boolean), initialData?.fileName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawingSrc]);

  // ---- drywall takeoff engine (our own; walls → rooms → perimeter by wall tag). Runs in a Web Worker.
  const engineRef = useRef(null);
  const getEngine = () => (engineRef.current ||= createEngine(pdfjsLib));
  useEffect(() => () => engineRef.current?.terminate(), []);
  const [sheetGeo, setSheetGeo] = useState({}); // page → { runs, tagCands, tags }
  const [enabledTags, setEnabledTags] = useState(
    initialData?.wallTagSettings?.enabled || null,
  ); // null = auto
  const [tagSideAware, setTagSideAware] = useState(
    !!initialData?.wallTagSettings?.sideAware,
  );
  const [tagCondMap, setTagCondMap] = useState(
    initialData?.wallTagSettings?.map || {},
  ); // tag → conditionId
  const [showTagPanel, setShowTagPanel] = useState(false);

  // ── several people on one takeoff ─────────────────────────────────────────────────────────────────
  // Each open screen saves only its own changes and pulls in everyone else's every few seconds.
  const clientIdRef = useRef(
    Math.random().toString(36).slice(2) + Date.now().toString(36),
  );
  // a server that keeps revisions (else the old whole-takeoff save is used)
  const supportsOpsRef = useRef(typeof initialData?.rev === "number");
  // ── RFI pins ──
  const pinModeRef = useRef(pinMode);
  pinModeRef.current = pinMode;
  const onRfiPinPlaceRef = useRef(onRfiPinPlace);
  onRfiPinPlaceRef.current = onRfiPinPlace;
  const pinCbRef = useRef(onPinModeChange);
  pinCbRef.current = onPinModeChange;
  const pinClickRef = useRef(onRfiPinClick);
  pinClickRef.current = onRfiPinClick;
  const pinLayerRef = useRef(null);
  // the sheet's trace area(s) (Trace settings → Trace area) as a dashed outline on the drawing
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !pageImage) return;
    const rects =
      traceSettings?.drawAreas?.[pageNum] ||
      traceSettings?.drawAreas?.[String(pageNum)] ||
      [];
    if (!rects.length) return;
    const g = L.layerGroup();
    for (const [x0, y0, x1, y1] of rects)
      g.addLayer(
        L.rectangle([toLL([x0, y1]), toLL([x1, y0])], {
          color: "#f97316",
          weight: 1.5,
          dashArray: "8 6",
          fill: false,
          interactive: false,
          pmIgnore: true,
        }),
      );
    g.addTo(map);
    return () => g.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traceSettings?.drawAreas, pageNum, pageImage]);
  const pinFocusRef = useRef(null);
  if (getPayloadRef)
    getPayloadRef.current = () => ({ ...buildPayload(), pageNum });
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    pinLayerRef.current?.remove();
    const g = L.layerGroup();
    const STATUS_BG = {
      draft: "#6b7280",
      sent: "#f97316",
      answered: "#10b981",
      closed: "#374151",
    };
    for (const p of rfiPins || []) {
      if (Number(p.page) !== Number(pageNum)) continue;
      const m = L.marker([p.lat, p.lng], {
        pmIgnore: true,
        zIndexOffset: 2000,
        icon: L.divIcon({
          className: "",
          iconSize: [0, 0],
          html: `<div title="${String(p.title || "").replace(/"/g, "&quot;")}" style="transform:translate(-50%,-100%);display:flex;flex-direction:column;align-items:center;pointer-events:auto;cursor:pointer">
            <div style="background:${STATUS_BG[p.status] || "#f97316"};color:#fff;font:700 11px/1 system-ui,sans-serif;padding:4px 6px;border-radius:6px;box-shadow:0 2px 8px rgba(0,0,0,.5);white-space:nowrap;border:1.5px solid #fff">RFI ${p.number}${p.q ? `·${p.q}` : ""}</div>
            <div style="width:2px;height:10px;background:#fff"></div></div>`,
        }),
      });
      m.on("click", (ev) => {
        L.DomEvent.stopPropagation(ev);
        pinClickRef.current?.(p);
      });
      g.addLayer(m);
    }
    g.addTo(map);
    pinLayerRef.current = g;
    const f = pinFocusRef.current;
    if (f && Number(f.page) === Number(pageNum)) {
      // after the page has fitted itself
      setTimeout(() => {
        try {
          mapRef.current?.setView(
            [f.lat, f.lng],
            Math.max(mapRef.current.getZoom(), 0.5),
          );
        } catch {
          /* map not ready */
        }
      }, 400);
      pinFocusRef.current = null;
    }
    return () => g.remove();
  }, [rfiPins, pageNum, pageImage]); // eslint-disable-line
  useEffect(() => {
    if (!focusPin) return;
    pinFocusRef.current = focusPin;
    if (Number(focusPin.page) !== Number(pageNum))
      setPageNum(Number(focusPin.page));
    else
      setTimeout(() => {
        try {
          mapRef.current?.invalidateSize({ animate: false });
          mapRef.current?.setView(
            [focusPin.lat, focusPin.lng],
            Math.max(mapRef.current.getZoom(), 0.5),
          );
        } catch {
          /* map not ready */
        }
        pinFocusRef.current = null;
      }, 400);
  }, [focusPin]); // eslint-disable-line
  useEffect(() => {
    const el = mapRef.current?.getContainer();
    if (el) el.style.cursor = pinMode || snipMode ? "crosshair" : "";
  }, [pinMode, snipMode]);

  // ── change orders pinned on the drawing (projects) ──
  const [coMode, setCoModeState] = useState(false);
  const coModeRef = useRef(false);
  const setCoMode = (on) => {
    coModeRef.current = on;
    setCoModeState(on);
    const el = mapRef.current?.getContainer();
    if (el) el.style.cursor = on ? "crosshair" : "";
  };
  handlersRef.current.setCoMode = setCoMode;
  const [coPick, setCoPick] = useState(null); // { page, lat, lng } — where a pin is going
  const [coOpen, setCoOpen] = useState(null); // the pin whose card is open
  const [coList, setCoList] = useState(null); // the project's change orders
  const [coErr, setCoErr] = useState("");
  const loadCos = async () => {
    if (!projectId) return;
    setCoErr("");
    try {
      const r = await fetch(
        `${API}/takeoffs3/project/${projectId}/change-orders`,
        { headers: authHeaders() },
      );
      if (!r.ok) throw new Error(`${r.status}`);
      setCoList(await r.json());
    } catch (e) {
      setCoErr("Couldn't load the change orders.");
      setCoList([]);
    }
  };
  useEffect(() => {
    if ((coPick || coMode) && coList == null) loadCos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coPick, coMode]);
  const coLayerRef = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    coLayerRef.current?.remove();
    const g = L.layerGroup();
    for (const p of coPins) {
      if (Number(p.page) !== Number(pageNum)) continue;
      const m = L.marker([p.lat, p.lng], {
        pmIgnore: true,
        zIndexOffset: 1900,
        icon: L.divIcon({
          className: "",
          iconSize: [0, 0],
          html: `<div style="transform:translate(-50%,-100%);display:flex;flex-direction:column;align-items:center;cursor:pointer">
            <div style="background:#0d9488;color:#fff;font:700 11px/1 system-ui,sans-serif;padding:4px 6px;border-radius:6px;box-shadow:0 2px 8px rgba(0,0,0,.5);white-space:nowrap;border:1.5px solid #fff">CO ${String(p.number ?? "").replace(/</g, "&lt;") || "?"}</div>
            <div style="width:2px;height:10px;background:#fff"></div></div>`,
        }),
      });
      m.on("click", (ev) => {
        L.DomEvent.stopPropagation(ev);
        setCoOpen(p);
      });
      g.addLayer(m);
    }
    g.addTo(map);
    coLayerRef.current = g;
    return () => g.remove();
  }, [coPins, pageNum, pageImage]); // eslint-disable-line
  const addCoPin = (co, note) => {
    setCoPins((prev) => [
      ...prev,
      {
        id: uid(),
        coId: String(co._id),
        number: co.number ?? "",
        title: co.title || "",
        status: co.status || "",
        page: coPick.page,
        lat: coPick.lat,
        lng: coPick.lng,
        note: note || "",
        at: new Date().toISOString(),
      },
    ]);
    setCoPick(null);
    setCoMode(false);
    flash(`Change order ${co.number ?? ""} pinned on the drawing.`, "ok");
  };

  // ── cut a picture of the drawing (for an RFI): drag a box, the page image under it is cropped ──
  const snipModeRef = useRef(snipMode);
  snipModeRef.current = snipMode;
  const snipCbRef = useRef(onSnipModeChange);
  snipCbRef.current = onSnipModeChange;
  const onSnipRef = useRef(onSnip);
  onSnipRef.current = onSnip;
  const pageImageRef = useRef(pageImage);
  pageImageRef.current = pageImage;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !snipMode) return;
    map.dragging.disable();
    let start = null;
    let rect = null;
    const down = (e) => {
      if (e.originalEvent?.button !== 0) return;
      start = e.latlng;
      rect?.remove();
      rect = L.rectangle([start, start], {
        color: "#f97316",
        weight: 2,
        dashArray: "6 4",
        fillOpacity: 0.08,
        pmIgnore: true,
        interactive: false,
      }).addTo(map);
    };
    const move = (e) => start && rect?.setBounds([start, e.latlng]);
    const up = async (e) => {
      if (!start) return;
      const a = start;
      const b = e.latlng;
      start = null;
      const img = pageImageRef.current;
      setTimeout(() => rect?.remove(), 250);
      if (!img) return;
      // image pixels: x = lng, y = height − lat (the page sits at [0,0]–[h,w])
      const x0 = Math.max(0, Math.min(a.lng, b.lng));
      const x1 = Math.min(img.w, Math.max(a.lng, b.lng));
      const y0 = Math.max(0, img.h - Math.max(a.lat, b.lat));
      const y1 = Math.min(img.h, img.h - Math.min(a.lat, b.lat));
      if (x1 - x0 < 20 || y1 - y0 < 20) return; // a click, not a box
      try {
        const im = await new Promise((ok, bad) => {
          const i = new Image();
          i.onload = () => ok(i);
          i.onerror = bad;
          i.src = img.url;
        });
        const c = document.createElement("canvas");
        c.width = Math.round(x1 - x0);
        c.height = Math.round(y1 - y0);
        const g = c.getContext("2d");
        g.fillStyle = "#fff";
        g.fillRect(0, 0, c.width, c.height);
        g.drawImage(im, x0, y0, c.width, c.height, 0, 0, c.width, c.height);
        onSnipRef.current?.(c.toDataURL("image/jpeg", 0.92), {
          page: stateRef.current.pageNum,
          box: [x0, y0, x1, y1],
        });
      } catch (err) {
        console.error("Cut failed", err);
      }
    };
    map.on("mousedown", down);
    map.on("mousemove", move);
    map.on("mouseup", up);
    return () => {
      map.off("mousedown", down);
      map.off("mousemove", move);
      map.off("mouseup", up);
      rect?.remove();
      map.dragging.enable();
    };
  }, [snipMode]); // eslint-disable-line
  const remoteCbRef = useRef(onRemoteChanges);
  remoteCbRef.current = onRemoteChanges;
  // (taken from this screen's state right after it loaded — see the effect below — so nothing that only
  //  looks different after loading, like an empty field filled with {}, counts as a change)
  const syncRef = useRef(null);
  const [peers, setPeers] = useState([]); // others with this takeoff open: [{clientId, user:{name}, page}]
  const [syncRev, setSyncRev] = useState(initialData?.rev || 0); // (refreshes the History tab)
  const [remoteReload, setRemoteReload] = useState(false);
  const liveRef = useRef({});
  liveRef.current = {
    shapes,
    conditions,
    scales,
    pageNames,
    excludedPages,
    pageBidAreas,
    proposals,
    assemblies,
    traceSettings,
    units,
    scaleInfo,
    status,
    pageCount,
    fileName,
    gcsPath,
    coPins,
    sheetRevisions,
    wallTagSettings: {
      enabled: enabledTags,
      sideAware: tagSideAware,
      map: tagCondMap,
    },
  };
  const KEY_SETTERS = {
    fileName: (v) => {
      fileNameRef.current = v || "";
      setFileName(v || "");
    },
    status: (v) => setStatus(v || "draft"),
    scales: (v) => setScales(v || {}),
    pageNames: (v) => setPageNames(v || {}),
    excludedPages: (v) => setExcludedPages(v || []),
    pageBidAreas: (v) => setPageBidAreas(v || {}),
    coPins: (v) => setCoPins(v || []),
    proposals: (v) => setProposals(v || []),
    assemblies: (v) => setAssemblies(v || []),
    traceSettings: (v) => setTraceSettings(v || {}),
    units: (v) => setUnits(v || []),
    scaleInfo: (v) => setScaleInfo(v || {}),
    sheetRevisions: (v) => setSheetRevisions(v || {}),
    wallTagSettings: (v) => {
      setEnabledTags(v?.enabled || null);
      setTagSideAware(!!v?.sideAware);
      setTagCondMap(v?.map || {});
    },
  };
  // other people's saved changes → this screen. Anything changed here and not saved yet stays as it is here
  // (it's saved next, and the later save wins on that one item).
  function applyRemote(entries) {
    // the host merges the estimate part (it keeps the estimate's items and settings)
    if (entries?.length) remoteCbRef.current?.(entries);
    const s = syncRef.current;
    if (!s || !entries?.length) return;
    const fresh = entries
      .filter((e) => !s.applied.has(e.rev))
      .sort((a, b) => a.rev - b.rev);
    if (!fresh.length) return;
    const live = liveRef.current;
    const changes = { shapes: new Map(), conditions: new Map() };
    const keys = {};
    const notes = [];
    for (const e of fresh) {
      s.applied.add(e.rev);
      if (e.clientId === clientIdRef.current || !e.ops) continue;
      for (const key of ["shapes", "conditions"]) {
        for (const x of e.ops[key]?.upsert || [])
          changes[key].set(x.id, { obj: x });
        for (const id of e.ops[key]?.remove || [])
          changes[key].set(id, { remove: true });
      }
      Object.assign(keys, e.ops.set || {});
      if (e.summary?.length)
        notes.push(`${e.user?.name || "Someone"}: ${e.summary[0]}`);
    }
    // items changed here but not saved yet: keep ours
    const keep = { shapes: new Set(), conditions: new Set() };
    for (const key of ["shapes", "conditions"]) {
      const cur = new Map((live[key] || []).map((x) => [x.id, x]));
      for (const [id, ch] of changes[key]) {
        const mine = cur.get(id);
        const minejson = mine
          ? sjson(key === "shapes" ? slimShape(mine) : mine)
          : null;
        const synced = s[key].get(id) ?? null;
        if (minejson !== synced) keep[key].add(id);
        if (ch.remove) s[key].delete(id);
        else s[key].set(id, sjson(ch.obj));
      }
    }
    const h = histRef.current;
    if (
      changes.shapes.size ||
      changes.conditions.size ||
      keys.scales ||
      keys.units
    ) {
      h.skip = true;
      // undo / redo never takes other people's work back out
      for (const snap of [...h.past, ...h.future, h.last].filter(Boolean)) {
        snap.shapes = mergeById(snap.shapes || [], changes.shapes);
        snap.conditions = mergeById(snap.conditions || [], changes.conditions);
        if (keys.scales) snap.scales = keys.scales;
        if (keys.units) snap.units = keys.units;
      }
    }
    if (changes.shapes.size)
      setShapes((prev) => mergeById(prev, changes.shapes, keep.shapes));
    if (changes.conditions.size)
      setConditions((prev) =>
        mergeById(prev, changes.conditions, keep.conditions),
      );
    for (const [k, v] of Object.entries(keys)) {
      const setter = KEY_SETTERS[k];
      const mine = sjson(live[k]);
      if (setter && mine === s.keys[k]) setter(v); // unchanged here → take theirs
      s.keys[k] = sjson(v);
    }
    if (notes.length)
      flash(
        notes.length === 1
          ? notes[0]
          : `${notes[notes.length - 1]} (+${notes.length - 1} more changes)`,
        "info",
      );
  }
  // who this is, for the change log: the app normally keeps it; if not, ask the server once
  useEffect(() => {
    if (myName()) return;
    fetch(`${API}/auth/me`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const u = d?.user || d;
        const n =
          u?.displayName ||
          [u?.givenName, u?.surname].filter(Boolean).join(" ") ||
          u?.email;
        if (n) localStorage.setItem("userDisplayName", n);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (
      syncRef.current ||
      !initialData?._id ||
      typeof initialData?.rev !== "number"
    )
      return;
    syncRef.current = syncSnapshot(buildPayload(), initialData.rev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // every 4 s while the tab is visible: other people's changes, and who else has the takeoff open
  useEffect(() => {
    if (!takeoffId || !onSave) return;
    let dead = false,
      busy = false;
    const tick = async () => {
      if (
        busy ||
        document.visibilityState === "hidden" ||
        !syncRef.current ||
        !supportsOpsRef.current
      )
        return;
      busy = true;
      try {
        const q = new URLSearchParams({
          since: String(syncRef.current.rev),
          clientId: clientIdRef.current,
          page: String(stateRef.current.pageNum || ""),
          name: myName(),
        });
        const r = await fetch(`${API}/takeoffs3/${takeoffId}/changes?${q}`, {
          headers: authHeaders(),
        });
        if (!r.ok || dead) return;
        const data = await r.json();
        if (dead) return;
        setPeers(data.presence || []);
        if (data.reload) return setRemoteReload(true);
        applyRemote(data.entries || []);
        if (syncRef.current) {
          syncRef.current.rev = Math.max(syncRef.current.rev, data.rev || 0);
          setSyncRev(syncRef.current.rev);
        }
      } catch {
        /* offline for a moment — next tick */
      } finally {
        busy = false;
      }
    };
    const t = setInterval(tick, 4000);
    tick();
    return () => {
      dead = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takeoffId]);

  async function ensureEngineSheet(n) {
    const eng = getEngine();
    if (eng.isLoaded(n)) return eng;
    const page = await docPage(n);
    await eng.load(n, page);
    return eng;
  }
  const Rv = () => stateRef.current.pageImage?.renderScale || 1;
  const Hv = () => stateRef.current.pageImage?.h || 0;
  const toLL = ([x, y]) => [Hv() - y * Rv(), x * Rv()];
  const fromLL = ([lat, lng]) => [lng / Rv(), (Hv() - lat) / Rv()];
  const ftPerUnitNow = () => {
    const f =
      stateRef.current.scales[stateRef.current.pageNum] ??
      stateRef.current.scales[String(stateRef.current.pageNum)];
    return f ? f * Rv() : null; // feet per sheet unit
  };

  // The printed scale is only ever a SUGGESTION — the estimator confirms it.
  useEffect(() => {
    setScaleHint(null);
    if (!pdfDoc || !pageImage || fpu) return;
    let dead = false;
    ensureEngineSheet(pageNum)
      .then((eng) => {
        const s = eng.scaleOf(pageNum);
        if (dead || !s) return;
        setScaleHint({
          page: pageNum,
          label: s.label,
          multi: s.multi,
          all: s.all,
          fpu: s.ftPerUnit / pageImage.renderScale,
        });
      })
      .catch(() => {});
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc, pageImage, fpu]);

  // tags the estimator counts as wall types. Default: tags that sit in a tag symbol on a wall.
  const allTagList = useMemo(() => {
    const m = new Map();
    Object.values(sheetGeo).forEach((g) =>
      (g.tags || []).forEach((t) => {
        const e = m.get(t.text) || { text: t.text, count: 0, enclosed: 0 };
        e.count += t.count;
        e.enclosed += t.enclosed;
        m.set(t.text, e);
      }),
    );
    return [...m.values()].sort((a, b) => b.count - a.count);
  }, [sheetGeo]);
  const enabledSet = useMemo(() => {
    if (enabledTags) return new Set(enabledTags);
    const known = new Set([
      ...presets.wallTypes.map((w) => w.key),
      ...conditions
        .map((c) => (c.wallType || "").toUpperCase())
        .filter(Boolean),
    ]);
    return new Set(
      allTagList
        .filter(
          (t) =>
            known.has(t.text) ||
            (t.enclosed >= 2 &&
              /^[A-Z]{0,2}-?\d{1,2}[A-Z]?$/.test(t.text) &&
              /[A-Z]/.test(t.text)),
        )
        .map((t) => t.text),
    );
  }, [enabledTags, allTagList, conditions]);
  const tagColor = (tag) => {
    let h = 2166136261;
    for (const ch of tag) {
      h ^= ch.charCodeAt(0);
      h = Math.imul(h, 16777619) >>> 0;
    }
    return `hsl(${Math.round((h % 1000) * 137.508) % 360} 85% 58%)`;
  };

  const edgesWithTags = (p) => {
    const g = sheetGeo[p.page];
    if (!g || p.edited)
      return (p.edges || []).map((e) => ({ ...e, tag: null }));
    return assignTags({
      edges: p.edges || [],
      runs: g.runs,
      tagCands: g.tagCands,
      enabled: enabledSet,
      sideAware: tagSideAware,
    });
  };
  const perimSummary = (p) => {
    const by = {};
    for (const e of edgesWithTags(p)) {
      const k = e.kind === "opening" ? "__open" : e.tag || "__none";
      by[k] = (by[k] || 0) + e.lf;
    }
    return by;
  };

  const mkProposal = (r, source) => ({
    id: uid(),
    page: stateRef.current.pageNum,
    source,
    ring: r.ring,
    latlngs: r.ring.map(toLL),
    edges: r.edges,
    label: r.label || "",
    texts: r.texts || [],
    area_sf: r.areaSf,
    perimeter_lf: r.perimLf,
    wall_lf: r.wallLf,
    opening_lf: r.openingLf,
    confidence: r.confidence,
    snapped: r.snappedFrac,
    tags: null,
  });
  const rememberGeo = (n, res) => {
    setSheetGeo((prev) => ({
      ...prev,
      [n]: {
        runs: res.runs,
        tagCands: res.tagCands,
        tags: res.tags,
        doors: res.doors || prev[n]?.doors || [],
      },
    }));
    // what the engine made of the sheet: tag symbol, colour legend, the area it traced
    if (res.tagStyle || res.colorLegend || res.area || res.stats) {
      const colors = new Map();
      for (const r of res.runs || [])
        if (r.colorFamily && !colors.has(r.colorFamily))
          colors.set(r.colorFamily, { family: r.colorFamily, hex: r.color });
      setSheetEngInfo((prev) => ({
        ...prev,
        [n]: {
          tagStyle: res.tagStyle || null,
          colorLegend: res.colorLegend || [],
          colors: [...colors.values()],
          engine: res.stats?.engine || null,
          area: res.area
            ? {
                source: res.area.source,
                titleBlock: !!res.area.titleBlock,
                excluded: (res.area.excluded || []).length,
              }
            : null,
        },
      }));
    }
  };
  // what the engine needs to know about this job / sheet: the trace area, the tag symbol, the job's wall tags
  const engineOpts = (n, extra = {}) => {
    const ts = stateRef.current.traceSettings || {};
    const tagList = [
      ...new Set(
        (asmRef.current || [])
          .filter((a) => a && a.kind === "wall" && a.tag)
          .map((a) => String(a.tag).toUpperCase()),
      ),
    ].sort();
    const area = ts.drawAreas?.[n] || ts.drawAreas?.[String(n)];
    return {
      tagSymbol: ts.tagSymbol || "auto",
      ...(tagList.length && ts.tagListOnly !== false ? { tagList } : {}),
      ...(area?.length ? { areas: area } : {}),
      ...(ts.gridLines === false ? { gridLines: false } : {}),
      ...(ts.windowTags === false ? { windowTags: false } : {}),
      ...extra,
    };
  };
  // the part of the sheet in view, in sheet units — null when the whole sheet shows
  const viewRect = () => {
    const map = mapRef.current;
    const pi = stateRef.current.pageImage;
    if (!map || !pi) return null;
    const b = map.getBounds();
    const [x0, y1] = fromLL([b.getSouth(), b.getWest()]),
      [x1, y0] = fromLL([b.getNorth(), b.getEast()]);
    const full = x0 <= 0 && y0 <= 0 && x1 >= pi.w / Rv() && y1 >= pi.h / Rv();
    return full
      ? null
      : [
          Math.max(0, x0),
          Math.max(0, y0),
          Math.min(pi.w / Rv(), x1),
          Math.min(pi.h / Rv(), y1),
        ];
  };
  // "Use this view" as the sheet's trace area (or add it — a second plan on the same sheet)
  const useViewAsArea = (add) => {
    const r = viewRect();
    if (!r)
      return flash(
        "Zoom in to the plan first — the whole sheet is in view.",
        "warn",
      );
    const n = stateRef.current.pageNum;
    setTraceSettings((prev) => {
      const all = { ...((prev || {}).drawAreas || {}) };
      const cur = add ? all[n] || all[String(n)] || [] : [];
      delete all[String(n)];
      all[n] = [...cur, r.map((v) => Math.round(v * 100) / 100)];
      return { ...(prev || {}), drawAreas: all };
    });
    flash(
      add
        ? "Added this view to the sheet's trace area."
        : "This sheet now traces only what's in this view.",
      "ok",
    );
  };

  // click-to-fill (one room) ---------------------------------------------------
  handlersRef.current.fillAt = async (latlng) => {
    const f = ftPerUnitNow();
    if (!f)
      return flash(
        "Set the sheet scale first — wall thickness and areas depend on it.",
        "warn",
      );
    const n = stateRef.current.pageNum;
    try {
      const eng = await ensureEngineSheet(n);
      const [x, y] = fromLL([latlng.lat, latlng.lng]);
      setDetecting(true);
      const res = await eng.roomAt(n, f, x, y, engineOpts(n));
      rememberGeo(n, res);
      const r = res.room;
      if (!r || r.error)
        return flash(
          `Couldn't close that room: ${r?.error || "no room here"}. Trace it by hand, or draw the missing wall.`,
          "warn",
        );
      const dup = stateRef.current.proposals.find(
        (q) =>
          q.page === n &&
          Math.abs((q.area_sf || 0) - r.areaSf) < 0.05 &&
          Math.abs((q.perimeter_lf || 0) - r.perimLf) < 0.05,
      );
      if (dup) {
        setSelProposal(dup.id);
        return flash("That room is already proposed.", "info");
      }
      const p = mkProposal(r, "fill");
      setProposals((prev) => [...prev, p]);
      setSelProposal(p.id);
      setRightOpen(true);
      setRightTab("takeoff");
    } catch (e) {
      console.error(e);
      flash("Room fill failed on this sheet.", "warn");
    } finally {
      setDetecting(false);
    }
  };

  const roomMake =
    acceptPerimeter && acceptArea
      ? "both"
      : acceptPerimeter
        ? "walls"
        : acceptArea
          ? "ceilings"
          : "none";
  const ROOM_MAKE_LABEL = {
    both: "walls + ceilings",
    walls: "walls only",
    ceilings: "ceilings only",
    none: "nothing",
  };
  // Detect rooms / Fill room ask first what to make, so no unwanted lines or areas get drawn
  function askRooms(action) {
    if (!pageImage) return;
    if (action === "fill" && stateRef.current.mode === "fill")
      return setPanMode();
    if (!fpu)
      return flash(
        "Set the sheet scale first (Calibrate, preset, or accept the printed scale).",
        "warn",
      );
    if (traceSettings?.roomAskOff && roomMake !== "none")
      return action === "detect" ? detectRooms() : startFill();
    setRoomAsk(action);
  }
  function chooseRoomMake(make, dontAsk) {
    setAcceptPerimeter(make !== "ceilings");
    setAcceptArea(make !== "walls");
    setTraceSettings((prev) => ({
      ...(prev || {}),
      roomMake: make,
      ...(dontAsk ? { roomAskOff: true } : {}),
    }));
    const action = roomAsk;
    setRoomAsk(null);
    if (action === "detect") detectRooms();
    else if (action === "fill") startFill();
  }
  handlersRef.current.roomAsk = () => roomAsk;
  handlersRef.current.closeRoomAsk = () => setRoomAsk(null);

  function startFill() {
    if (!pageImage) return;
    if (!fpu)
      return flash(
        "Set the sheet scale first (Calibrate, preset, or accept the printed scale).",
        "warn",
      );
    disableAllPm();
    clearTempLayers();
    setMapCursor("crosshair");
    setMode("fill");
  }

  // every enclosed room — in the current view when zoomed in (a sheet often holds several plans)
  async function detectRooms() {
    const f = ftPerUnitNow();
    if (!f) return flash("Set the sheet scale first.", "warn");
    setDetecting(true);
    setDetectInfo(null);
    try {
      const eng = await ensureEngineSheet(pageNum);
      const region = viewRect();
      const t0 = performance.now();
      const res = await eng.rooms(pageNum, f, region, engineOpts(pageNum));
      rememberGeo(pageNum, res);
      const existing = stateRef.current.proposals.filter(
        (p) => p.page === pageNum && p.source !== "detect",
      );
      const mine = res.rooms
        .filter(
          (r) =>
            !existing.some(
              (q) =>
                Math.abs((q.area_sf || 0) - r.areaSf) < 0.05 &&
                Math.abs((q.perimeter_lf || 0) - r.perimLf) < 0.05,
            ),
        )
        .map((r) => mkProposal(r, "detect"));
      setProposals((prev) => [
        ...prev.filter((p) => !(p.page === pageNum && p.source === "detect")),
        ...mine,
      ]);
      setDetectInfo({
        found: mine.length,
        inView: !!region,
        ms: Math.round(performance.now() - t0),
      });
      setRightOpen(true);
      setRightTab("takeoff");
      if (!mine.length)
        flash(
          "No enclosed rooms found here. Check the scale, or use Fill room / trace by hand.",
          "info",
        );
    } catch (e) {
      console.error(e);
      flash("Room detection failed on this sheet.", "warn");
    } finally {
      setDetecting(false);
    }
  }

  // proposal layers: ceiling (dashed fill) + perimeter coloured by wall tag, openings dashed grey -------
  const propKey =
    proposals
      .filter((p) => p.page === pageNum)
      .map((p) => p.id + (p.edited ? "e" : ""))
      .join(",") +
    "|" +
    selProposal +
    "|" +
    pageNum +
    "|" +
    [...enabledSet].join(",") +
    "|" +
    tagSideAware +
    "|" +
    !!sheetGeo[pageNum] +
    "|" +
    acceptPerimeter +
    acceptArea;
  const proposalTip = (p) =>
    `<b>${p.label || "Room"}</b> — ${[acceptArea && `${fmtQty(p.area_sf)} SF ceiling`, acceptPerimeter && `${fmtQty(p.wall_lf ?? p.perimeter_lf)} LF wall`].filter(Boolean).join(" · ") || "nothing to make"} <i>(proposal)</i>`;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !pageImage || !mapReady) return;
    proposalLayersRef.current.forEach((l) => map.removeLayer(l));
    proposalLayersRef.current.clear();
    stateRef.current.proposals
      .filter((p) => p.page === pageNum)
      .forEach((p) => {
        const sel = p.id === selProposal;
        const group = L.layerGroup();
        // walls only: the room stays clickable but shows no ceiling fill
        const poly = L.polygon(p.latlngs, {
          color: "#facc15",
          weight: 1,
          dashArray: "5,5",
          fillColor: "#facc15",
          fillOpacity: acceptArea ? (sel ? 0.28 : 0.12) : sel ? 0.08 : 0.02,
        });
        poly.bindTooltip(proposalTip(p), { sticky: true });
        poly.on("click", () => {
          if (isNeutral(stateRef.current.mode)) {
            lastShapeClickRef.current = Date.now();
            setSelProposal(p.id);
            setRightOpen(true);
            setRightTab("takeoff");
          }
        });
        poly.on("pm:edit", () => {
          const ll = serializeLatLngs(poly, "polygon");
          const f =
            stateRef.current.scales[stateRef.current.pageNum] ??
            stateRef.current.scales[String(stateRef.current.pageNum)];
          const ring = ll.map(fromLL);
          const fpuS = f ? f * Rv() : 0;
          const edges = ring.map((a, i) => {
            const b = ring[(i + 1) % ring.length];
            return {
              a,
              b,
              kind: "wall",
              run: -1,
              lf: Math.hypot(b[0] - a[0], b[1] - a[1]) * fpuS,
            };
          });
          setProposals((prev) =>
            prev.map((x) =>
              x.id === p.id
                ? {
                    ...x,
                    latlngs: ll,
                    ring,
                    edges,
                    edited: true,
                    area_sf: f ? ringArea(ll) * f * f : null,
                    perimeter_lf: f ? pathLength(ll, true) * f : null,
                    wall_lf: f ? pathLength(ll, true) * f : null,
                    opening_lf: 0,
                  }
                : x,
            ),
          );
        });
        group.addLayer(poly);
        group._poly = poly;
        for (const e of acceptPerimeter ? edgesWithTags(p) : []) {
          const opening = e.kind === "opening";
          const ln = L.polyline([toLL(e.a), toLL(e.b)], {
            color: opening ? "#9ca3af" : e.tag ? tagColor(e.tag) : "#f97316",
            weight: sel ? 5 : 3.5,
            opacity: 0.95,
            dashArray: opening ? "3,6" : null,
            interactive: true,
          });
          ln.bindTooltip(
            opening
              ? `Opening ${fmtQty(e.lf)} LF (not counted as wall)`
              : `${e.tag ? "Wall tag " + e.tag : "Untagged wall"} · ${fmtQty(e.lf)} LF`,
            { sticky: true },
          );
          group.addLayer(ln);
        }
        group.addTo(map);
        proposalLayersRef.current.set(p.id, group);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propKey, pageImage, mapReady]);
  useEffect(() => {
    proposalLayersRef.current.forEach((g, id) => {
      const p = proposals.find((x) => x.id === id);
      if (p) g._poly?.setTooltipContent(proposalTip(p));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposals]);

  // keep shape layers in sync when shapes are added programmatically (accepted proposals)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const ids = new Set(shapes.map((s) => s.id));
    layerMapRef.current.forEach((layer, id) => {
      if (!ids.has(id)) {
        map.removeLayer(layer);
        layerMapRef.current.delete(id);
      }
    });
    shapes
      .filter((s) => s.page === pageNum && !layerMapRef.current.has(s.id))
      .forEach(addLayerForShape);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapes, mapReady, pageNum]);

  // hidden assemblies: their layers come off the drawing (and out of the selection); shown again → back on
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const byId = new Map(shapes.map((x) => [x.id, x]));
    const off = [];
    layerMapRef.current.forEach((layer, id) => {
      const sh = byId.get(id);
      if (!sh) return;
      const hide = hiddenConds.has(sh.conditionId);
      if (hide && map.hasLayer(layer)) {
        try {
          if (layer.pm?.enabled?.()) layer.pm.disable();
        } catch {
          if (layer.pm) layer.pm._enabled = false;
        }
        try {
          map.removeLayer(layer);
        } catch {
          /* already off */
        }
        off.push(id);
      } else if (!hide && !map.hasLayer(layer)) layer.addTo(map);
    });
    if (off.length && selRef.current.size) {
      const keep = [...selRef.current].filter((id) => !off.includes(id));
      if (keep.length !== selRef.current.size) setSel(keep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenConds, shapes, mapReady, pageNum]);

  function toggleAdjust(p) {
    const layer = proposalLayersRef.current.get(p.id)?._poly;
    if (!layer?.pm) return;
    if (adjustingId === p.id) {
      layer.pm.disable();
      setAdjustingId(null);
      return;
    }
    disableAllPm();
    setMode(baseToolRef.current);
    layer.pm.enable({
      allowSelfIntersection: false,
      snappable: false,
      hideMiddleMarkers: false,
    });
    setAdjustingId(p.id);
    zoomToLayer(layer);
  }

  // drywall presets ---------------------------------------------------------------------
  const wallPreset = (k) => presets.wallTypes.find((w) => w.key === k);
  const ceilPreset = (k) => presets.ceilingTypes.find((c) => c.key === k);
  const presetDesc = (p) =>
    [
      p.board &&
        `Board: ${p.board}${p.layers > 1 ? ` × ${p.layers} layers` : ""}${p.boardSides ? `, ${p.boardSides} side${p.boardSides > 1 ? "s" : ""}` : ""}`,
      p.studs && `Framing: ${p.studs}`,
      p.rated && "Fire rated",
    ]
      .filter(Boolean)
      .join("\n");
  function condFromWallPreset(w) {
    return {
      name: `${w.key} — ${w.name}`,
      type: "linear",
      wallType: w.key,
      height: w.defaultHeight,
      color: w.color,
      description: presetDesc(w),
    };
  }
  function condFromCeilPreset(c) {
    return {
      name: `${c.key} — ${c.name}`,
      type: "area",
      wallType: "",
      height: null,
      color: c.color,
      description: presetDesc(c),
    };
  }
  function addPresetCondition(kind, key, { draw = true } = {}) {
    const spec =
      kind === "wall"
        ? condFromWallPreset(wallPreset(key))
        : condFromCeilPreset(ceilPreset(key));
    const ex = conditions.find((c) => c.name === spec.name);
    if (ex) {
      setActiveCondId(ex.id);
      if (draw) startDraw(ex);
      return ex;
    }
    const cond = { id: uid(), ...spec };
    setConditions((prev) => [...prev, cond]);
    setActiveCondId(cond.id);
    if (draw) startDraw(cond);
    return cond;
  }

  // accept proposals → real shapes (the human gate) -----------------------------------------
  //   ceiling → one polygon on an AREA condition (SF)
  //   perimeter → one polyline per wall tag run on a LINEAR condition (LF × height = wall SF)
  //   openings → not drawn, not counted as wall
  function acceptProposals(list) {
    if (!list.length) return;
    const added = [];
    const have = () => [...stateRef.current.conditions, ...added];
    const ensure = (spec) => {
      const ex = have().find(
        (c) =>
          c.name === spec.name ||
          (spec.wallType &&
            spec.type === "linear" &&
            c.type === "linear" &&
            (c.wallType || "").toUpperCase() === spec.wallType.toUpperCase()),
      );
      if (ex) return ex;
      const c = { id: uid(), ...spec };
      added.push(c);
      return c;
    };
    const pick = (id, fallback) =>
      id === "auto"
        ? fallback()
        : have().find((c) => c.id === id) || fallback();
    const condForTag = (tag) => {
      const mapped =
        tagCondMap[tag] && have().find((c) => c.id === tagCondMap[tag]);
      if (mapped) return mapped;
      const pre = wallPreset(tag);
      return ensure(
        pre
          ? condFromWallPreset(pre)
          : {
              name: `Wall type ${tag}`,
              type: "linear",
              wallType: tag,
              height: 9,
              color: tagColor(tag),
            },
      );
    };
    const newShapes = [];
    let openLf = 0;
    for (const p of list) {
      const wt = wallPreset(p.tags?.wallType) || wallPreset("P1");
      const ct = ceilPreset(p.tags?.ceiling) || ceilPreset("C-GWB");
      const meta = {
        source: `drywall-engine:${p.source}`,
        confidence: p.confidence,
        roomType: p.tags?.roomType || null,
        finish: p.tags?.finish || null,
      };
      if (acceptArea) {
        const ac = pick(areaCondId, () => ensure(condFromCeilPreset(ct)));
        newShapes.push({
          id: uid(),
          conditionId: ac.id,
          page: p.page,
          kind: "polygon",
          latlngs: slimArea(p.latlngs, p.page),
          label: p.label || undefined,
          meta: { ...meta, role: "ceiling" },
        });
      }
      if (acceptPerimeter && traceSettings.wallLines === "faces") {
        for (const ch of chainEdges(edgesWithTags(p), {
          windowsBreak: traceSettings.windowMode === "gap",
        })) {
          const wc = ch.tag
            ? condForTag(ch.tag)
            : pick(perimCondId, () => ensure(condFromWallPreset(wt)));
          const fh = floorHeightOf(p.page);
          const wl = ch.windows.reduce((x, y) => x + y, 0);
          const wh = parseFeetInput(traceSettings.windowHeight || "") || null;
          newShapes.push({
            id: uid(),
            conditionId: wc.id,
            page: p.page,
            kind: "polyline",
            latlngs: ch.pts.map(toLL),
            label: p.label || undefined,
            meta: {
              ...meta,
              role: "perimeter",
              wallTag: ch.tag || null,
              ...(fh ? { height: fh } : {}),
              ...(wl > 0 && traceSettings.windowMode !== "gap"
                ? {
                    windows: ch.windows,
                    windowSegs: ch.windowSegs.map(([a, b]) => [
                      toLL(a),
                      toLL(b),
                    ]),
                    deductLf: Math.round(wl * 100) / 100,
                    windowHeight: wh,
                  }
                : {}),
            },
          });
        }
        openLf += p.opening_lf || 0;
      }
    }
    // one line per wall: every accepted room's walls together, shared walls once, through doors and windows
    if (acceptPerimeter && traceSettings.wallLines !== "faces") {
      const f = ftPerUnitNow();
      const wh = parseFeetInput(traceSettings.windowHeight || "") || null;
      for (const pg of [...new Set(list.map((p) => p.page))]) {
        const geo = sheetGeo[pg];
        const ps = list.filter((p) => p.page === pg);
        const wetSplit = traceSettings.wetSeparate !== false;
        const edges = ps
          .flatMap((p) => {
            const wet = wetSplit && isWetRoom(p.label, p.texts);
            return edgesWithTags(p).map((e) => (wet ? { ...e, wet } : e));
          })
          .filter(
            (e) => !(traceSettings.windowMode === "gap" && e.kind === "window"),
          );
        const chains = f
          ? dropNestedLines(
              centerlineChains({
                edges,
                runs: geo?.runs || [],
                ftPerUnit: f,
                doorMode: traceSettings.doorMode === "gap" ? "gap" : "deduct",
                swings: geo?.doors || [],
              }),
              { ftPerUnit: f },
            )
          : [];
        const fh = floorHeightOf(pg);
        const wt = wallPreset(ps[0]?.tags?.wallType) || wallPreset("P1");
        // washroom walls: the same type as their own assembly
        const wetOf = (c) =>
          ensure({
            name: `${c.name} · Washroom`.slice(0, 120),
            type: "linear",
            wallType: `${c.wallType || c.name}·WR`,
            height: c.height,
            color: tagColor(`wet${c.name}`),
            wet: true,
            description: `${c.description || ""}${c.description ? "\n" : ""}Washroom walls — moisture-resistant board / wet-area finishes`,
            ...(c.assemblyId ? { assemblyId: c.assemblyId } : {}),
          });
        for (const ch of chains) {
          const dry = ch.tag
            ? condForTag(ch.tag)
            : pick(perimCondId, () => ensure(condFromWallPreset(wt)));
          const wc = ch.wet ? wetOf(dry) : dry;
          // doors at the set sizes (3'-2" / 6'-2" unless changed in the wizard)
          if (ch.doors?.length)
            ch.doors = sizedDoors(ch, doorSizes(traceSettings));
          const wl = ch.windows.reduce((x, y) => x + y, 0),
            dl = ch.doors.reduce((x, y) => x + y, 0);
          newShapes.push({
            id: uid(),
            conditionId: wc.id,
            page: pg,
            kind: "polyline",
            latlngs: ch.pts.map(toLL),
            meta: {
              source: "drywall-engine:rooms",
              role: "perimeter",
              line: "centre",
              wallTag: ch.tag || null,
              ...(fh ? { height: fh } : {}),
              ...(wl > 0 && traceSettings.windowMode !== "gap"
                ? {
                    windows: ch.windows,
                    windowSegs: ch.windowSegs.map(([a, b]) => [
                      toLL(a),
                      toLL(b),
                    ]),
                    deductLf: Math.round(wl * 100) / 100,
                    windowHeight: wh,
                  }
                : {}),
              ...(ch.wet ? { wet: true } : {}),
              ...(dl > 0
                ? {
                    doors: ch.doors,
                    doorKinds: ch.doorKinds || [],
                    doorSegs: ch.doorSegs.map(([a, b]) => [toLL(a), toLL(b)]),
                    doorLf: Math.round(dl * 100) / 100,
                    doorHeight: doorSizes(traceSettings).height,
                  }
                : {}),
            },
          });
        }
        // counts: doors (single / double), windows, outside corners
        if (traceSettings.counts !== false && f) {
          const marks = countMarks({
            chains,
            rooms: ps.map((p) => ({ ring: p.ring, edges: p.edges })),
            ftPerUnit: f,
            corners: traceSettings.cornerBead !== false,
            doors: geo?.doors || [],
          });
          for (const m of marks) {
            const spec = COUNT_KINDS[m.key];
            const c = ensure({
              name: spec.name,
              type: "count",
              wallType: `__count:${m.key}`,
              height: null,
              color: spec.color,
              description: spec.description,
            });
            newShapes.push({
              id: uid(),
              conditionId: c.id,
              page: pg,
              kind: "marker",
              latlngs: [toLL(m.p)],
              meta: {
                source: "drywall-engine:rooms",
                role: m.key,
                ...(m.lf ? { width: Math.round(m.lf * 100) / 100 } : {}),
                ...(m.key === "bead" && fh ? { height: fh } : {}),
              },
            });
          }
        }
      }
    }
    if (!newShapes.length)
      return flash("Turn on Ceiling and/or Perimeter to accept.", "warn");
    if (added.length) setConditions((prev) => [...prev, ...added]);
    setShapes((prev) => [...prev, ...newShapes]);
    const ids = new Set(list.map((p) => p.id));
    if (adjustingId && ids.has(adjustingId)) setAdjustingId(null);
    setProposals((prev) => prev.filter((p) => !ids.has(p.id)));
    flash(
      `Accepted ${list.length} room${list.length === 1 ? "" : "s"} → ${newShapes.length} shapes${added.length ? `, ${added.length} new condition${added.length === 1 ? "" : "s"}` : ""}${openLf ? ` · ${fmtQty(openLf)} LF of openings left out` : ""}.`,
      "ok",
    );
  }
  const rejectProposal = (id) => {
    setProposals((prev) => prev.filter((p) => p.id !== id));
    if (selProposal === id) setSelProposal(null);
  };
  const pageProposals = proposals.filter((p) => p.page === pageNum);

  // AI: tag rooms / review ----------------------------------------------------------------------
  async function post(path, body) {
    const res = await fetch(`${API}/takeoffs3/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    // (long AI reads answer 200 straight away and report a failure as success:false in the body)
    if (!res.ok || data.success === false)
      throw new Error(data.message || data.error || `HTTP ${res.status}`);
    return data;
  }
  const sheetNotes = async (n) => {
    const eng = await ensureEngineSheet(n);
    const info = await eng.load(n);
    return [
      ...new Set(
        (info.text || []).map((x) => x.str.trim()).filter((t) => t.length > 2),
      ),
    ].slice(0, 300);
  };
  async function aiTagRooms() {
    const list = pageProposals;
    if (!list.length)
      return flash("Fill or detect some rooms on this sheet first.", "info");
    setAiBusy("tag");
    try {
      const data = await post("ai/tag-rooms", {
        sheetTitle: pageLabel(pageNum),
        sheetText: await sheetNotes(pageNum),
        rooms: list.map((p) => ({
          id: p.id,
          label: p.label,
          texts: p.texts,
          areaSf: p.area_sf,
        })),
      });
      const byId = Object.fromEntries((data.tags || []).map((t) => [t.id, t]));
      setProposals((prev) =>
        prev.map((p) => (byId[p.id] ? { ...p, tags: byId[p.id] } : p)),
      );
      flash(
        "Rooms tagged. Review the suggestions — keyword-only tags are marked unverified.",
        "ok",
      );
    } catch (e) {
      flash(`AI tagging: ${e.message}`, "warn");
    } finally {
      setAiBusy("");
    }
  }
  async function runChecks() {
    setAiBusy("review");
    try {
      const pages = [...new Set(shapes.map((s) => s.page))];
      const data = await post("ai/review", {
        conditions,
        condMetrics,
        mappings: initialData?.mappings || [],
        scales,
        pages,
        sheetText: pdfDoc ? await sheetNotes(pageNum).catch(() => []) : [],
      });
      setChecks(data);
      setRightOpen(true);
      setRightTab("checks");
    } catch (e) {
      flash(`Checks: ${e.message}`, "warn");
    } finally {
      setAiBusy("");
    }
  }

  // ═══════════════ assemblies → trace (walls on floor plans, ceilings on RCPs) ═══════════════
  // viewer geometry of ANY page (not just the one on screen): same maths as renderPage
  const geomCache = useRef(new Map());
  const sheetImgCache = useRef(new Map()); // "page|signature" → data URL
  async function pageGeom(n) {
    if (geomCache.current.has(n)) return geomCache.current.get(n);
    const page = await docPage(n);
    const base = page.getViewport({ scale: 1 });
    const rs = await sheetScale(n, page);
    const g = {
      rs,
      h: Math.round(base.height * rs),
      w: base.width,
      hh: base.height,
    };
    geomCache.current.set(n, g);
    return g;
  }

  // A picture of one sheet for the estimate's printed legend: the drawing cropped to what was traced, with every
  // line / area / count drawn on it in its assembly colour. The drawing comes from the same cached sheet picture
  // the working drawings use (no second PDF render); results are cached until the sheet's shapes or colours change.
  async function renderSheetImage(
    page,
    {
      shapes: shs = [],
      conditions: conds = [],
      crop = true,
      maxPx = 1400,
    } = {},
  ) {
    if (!pdfDoc) return null;
    const onPage = shs.filter((s) => s.page === page && s.latlngs?.length);
    if (!onPage.length) return null;
    const condById = new Map(conds.map((c) => [c.id, c]));

    // cache key: changes when anything drawn on this sheet (or its colours / tags) changes
    const sig = [
      crop ? 1 : 0,
      maxPx,
      ...onPage.map(
        (s) =>
          `${s.id}:${s.conditionId}:${s.latlngs.length}:${s.latlngs[0]?.[0]}:${s.latlngs[s.latlngs.length - 1]?.[1]}:${(s.meta?.windowSegs || []).length}:${(s.meta?.doorSegs || []).length}`,
      ),
      ...[...new Set(onPage.map((s) => s.conditionId))].map(
        (id) =>
          `${id}:${condById.get(id)?.color}:${condById.get(id)?.wallType}`,
      ),
    ].join("|");
    const key = `${page}|${sig}`;
    if (sheetImgCache.current.has(key)) return sheetImgCache.current.get(key);

    const g = await pageGeom(page);
    const pageW = g.w * g.rs; // page size in the viewer's pixel units
    const pageH = g.h;
    const toPx = ([lat, lng]) => [lng, g.h - lat]; // viewer [lat, lng] → page pixel [x, y]

    // crop box around everything traced on this sheet
    let x0 = 0,
      y0 = 0,
      x1 = pageW,
      y1 = pageH;
    if (crop) {
      let a = Infinity,
        b = Infinity,
        c = -Infinity,
        d = -Infinity;
      for (const s of onPage)
        for (const p of s.latlngs) {
          const [x, y] = toPx(p);
          if (x < a) a = x;
          if (x > c) c = x;
          if (y < b) b = y;
          if (y > d) d = y;
        }
      const pad = Math.max(40, 0.06 * Math.max(c - a, d - b));
      a -= pad;
      b -= pad;
      c += pad;
      d += pad;
      const minSide = Math.min(900, pageW, pageH);
      if (c - a < minSide) {
        const m = (a + c) / 2;
        a = m - minSide / 2;
        c = m + minSide / 2;
      }
      if (d - b < minSide) {
        const m = (b + d) / 2;
        b = m - minSide / 2;
        d = m + minSide / 2;
      }
      x0 = Math.max(0, a);
      y0 = Math.max(0, b);
      x1 = Math.min(pageW, c);
      y1 = Math.min(pageH, d);
    }
    const cropW = x1 - x0,
      cropH = y1 - y0;
    const k = maxPx / Math.max(cropW, cropH); // output pixels per viewer pixel
    const ow = Math.max(1, Math.round(cropW * k)),
      oh = Math.max(1, Math.round(cropH * k));

    // the drawing: cropped out of the cached sheet picture
    const bmp = await createImageBitmap(
      new Blob([await sheetBase(page, 0)], { type: "image/jpeg" }),
    );
    const sx = bmp.width / pageW,
      sy = bmp.height / pageH;
    const out = document.createElement("canvas");
    out.width = ow;
    out.height = oh;
    const ctx = out.getContext("2d", { alpha: false });
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, ow, oh);
    ctx.drawImage(bmp, x0 * sx, y0 * sy, cropW * sx, cropH * sy, 0, 0, ow, oh);
    bmp.close?.();

    const P = (ll) => {
      const [x, y] = toPx(ll);
      return [(x - x0) * k, (y - y0) * k];
    };
    const lw = Math.max(2.5, ow / 420);
    const path = (pts, close) => {
      ctx.beginPath();
      pts.forEach((p, i) => {
        const [x, y] = P(p);
        i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
      });
      if (close) ctx.closePath();
    };
    ctx.lineJoin = "round";
    ctx.lineCap = "round";

    // areas first (under the lines)
    for (const s of onPage) {
      if (s.kind !== "polygon") continue;
      const col = condById.get(s.conditionId)?.color || "#f97316";
      path(s.latlngs, true);
      ctx.globalAlpha = 0.28;
      ctx.fillStyle = col;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = col;
      ctx.lineWidth = lw * 0.8;
      ctx.stroke();
    }
    // wall lines — white halo underneath so they read on a busy plan
    for (const s of onPage) {
      if (s.kind !== "polyline") continue;
      const col = condById.get(s.conditionId)?.color || "#f97316";
      path(s.latlngs, false);
      ctx.globalAlpha = 0.75;
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = lw * 2.6;
      ctx.stroke();
      ctx.globalAlpha = 1;
      path(s.latlngs, false);
      ctx.strokeStyle = col;
      ctx.lineWidth = lw * 1.5;
      ctx.stroke();
    }
    // deducted windows (cyan) and doors (magenta), on top of their wall
    ctx.lineCap = "butt";
    for (const s of onPage)
      for (const [segs, col] of [
        [s.meta?.windowSegs, "#22d3ee"],
        [s.meta?.doorSegs, "#e879f9"],
      ])
        for (const seg of segs || []) {
          if (!seg?.[0] || !seg?.[1]) continue;
          path(seg, false);
          ctx.globalAlpha = 0.75;
          ctx.strokeStyle = col;
          ctx.lineWidth = lw * 2.8;
          ctx.stroke();
          ctx.globalAlpha = 1;
        }
    ctx.lineCap = "round";
    // counts
    for (const s of onPage) {
      if (s.kind !== "marker") continue;
      const col = condById.get(s.conditionId)?.color || "#f97316";
      const [x, y] = P(s.latlngs[0]);
      ctx.beginPath();
      ctx.arc(x, y, lw * 2.3, 0, Math.PI * 2);
      ctx.fillStyle = col;
      ctx.fill();
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = lw * 0.7;
      ctx.stroke();
    }
    // wall-tag labels (one per stretch of wall, not on top of each other)
    const fs = Math.round(lw * 3.8);
    ctx.font = `700 ${fs}px Arial, sans-serif`;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    const placed = [];
    for (const s of onPage) {
      if (s.kind !== "polyline") continue;
      const cond = condById.get(s.conditionId);
      const tag =
        cond?.wallType && !String(cond.wallType).startsWith("__")
          ? String(cond.wallType).replace(/·WR$/, "")
          : "";
      if (!tag) continue;
      let best = null,
        bl = 0;
      for (let i = 0; i + 1 < s.latlngs.length; i++) {
        const a = P(s.latlngs[i]),
          b = P(s.latlngs[i + 1]);
        const l = Math.hypot(b[0] - a[0], b[1] - a[1]);
        if (l > bl) {
          bl = l;
          best = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
        }
      }
      if (!best || bl < ow * 0.035) continue;
      if (
        placed.some(
          (q) =>
            q.tag === tag && Math.hypot(q.x - best[0], q.y - best[1]) < fs * 9,
        )
      )
        continue;
      placed.push({ tag, x: best[0], y: best[1] });
      const w = ctx.measureText(tag).width + fs * 0.9;
      const h = fs * 1.35;
      ctx.fillStyle = "#fff";
      ctx.fillRect(best[0] - w / 2, best[1] - h / 2, w, h);
      ctx.strokeStyle = cond.color || "#f97316";
      ctx.lineWidth = lw * 0.7;
      ctx.strokeRect(best[0] - w / 2, best[1] - h / 2, w, h);
      ctx.fillStyle = "#111";
      ctx.fillText(tag, best[0], best[1] + 1);
    }
    const url = out.toDataURL("image/jpeg", 0.75);
    out.width = 0; // free the canvas memory
    out.height = 0;
    if (sheetImgCache.current.size > 60) sheetImgCache.current.clear();
    sheetImgCache.current.set(key, url);
    return url;
  }
  const llOf =
    (g) =>
    ([x, y]) => [g.h - y * g.rs, x * g.rs];

  // ── working drawings ──────────────────────────────────────────────────────
  // One real PDF, every sheet on its own page at its own paper size — download it, print it or share it.
  // Each sheet's drawing picture is made once per quality and kept until another PDF is opened; the takeoff and
  // the legend are drawn over it as vector lines and text. The estimate page's printout uses the same pictures.
  const baseCacheRef = useRef(new Map()); // "page|dpi" → Promise<Uint8Array> (JPEG bytes)
  useEffect(() => {
    const m = baseCacheRef.current;
    return () => m.clear();
  }, [pdfDoc]);

  async function canvasJpeg(canvas) {
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.85));
    canvas.width = 0;
    canvas.height = 0;
    if (!blob)
      throw new Error(
        "couldn't make the picture (sheet too large for the browser)",
      );
    return new Uint8Array(await blob.arrayBuffer());
  }

  // the drawing of one sheet as JPEG bytes. dpi 0 = screen detail (the on-screen picture when there is one),
  // else rendered at that dpi. Always drawn straight from the PDF — never waits on the viewer.
  function sheetBase(n, dpi) {
    const key = `${n}|${dpi || "screen"}`;
    const m = baseCacheRef.current;
    if (m.has(key)) return m.get(key);
    const p = (async () => {
      const canvas = document.createElement("canvas");
      const blank = () => {
        const c = canvas.getContext("2d", { alpha: false });
        c.fillStyle = "#fff";
        c.fillRect(0, 0, canvas.width, canvas.height);
        return c;
      };
      // Fast: this sheet is already on screen (or preloaded next to it) — reuse that picture
      const onScreen = !dpi && pageCacheRef.current.get(n);
      if (onScreen) {
        try {
          const bmp = await createImageBitmap(
            await (await fetch(onScreen.url)).blob(),
          );
          canvas.width = bmp.width;
          canvas.height = bmp.height;
          blank().drawImage(bmp, 0, 0);
          bmp.close?.();
          return await canvasJpeg(canvas);
        } catch {
          /* the picture went away — render it below */
        }
      }
      const g = await pageGeom(n);
      let scale = g.rs; // screen detail
      if (dpi) {
        const sheetIn = { w: g.w / 72, h: g.hh / 72 };
        let D = dpi;
        const MAX_PX = 4.2e7; // keeps the canvas inside what browsers allow
        if (sheetIn.w * sheetIn.h * D * D > MAX_PX)
          D = Math.sqrt(MAX_PX / (sheetIn.w * sheetIn.h));
        scale = D / 72;
      }
      const pg = await docPage(n);
      const vp = pg.getViewport({ scale });
      canvas.width = Math.round(vp.width);
      canvas.height = Math.round(vp.height);
      const ctx = blank();
      const task = pg.render({ canvasContext: ctx, viewport: vp });
      const timer = setTimeout(() => task.cancel(), 90000);
      try {
        await task.promise;
      } catch {
        canvas.width = 0;
        throw new Error("took too long to draw");
      } finally {
        clearTimeout(timer);
      }
      return canvasJpeg(canvas);
    })();
    p.catch(() => m.delete(key)); // a failed render is tried again next time
    m.set(key, p);
    return p;
  }

  // the sheets with something drawn on them (what "every sheet" prints)
  const workingPages = () => {
    const st = stateRef.current;
    return [
      ...new Set(st.shapes.filter((s) => s.latlngs?.length).map((s) => s.page)),
    ]
      .filter((p) => !st.excludedPages.includes(p))
      .sort((a, b) => a - b);
  };

  // while the print dialog is open, get the first sheets' drawings ready in the background
  useEffect(() => {
    if (!wd.open || !pdfDoc) return;
    (wd.scope === "all" ? workingPages().slice(0, 2) : [pageNum]).forEach((p) =>
      sheetBase(p, wd.dpi).catch(() => {}),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wd.open, wd.scope, wd.dpi, pageNum, pdfDoc]);

  // one sheet, ready for buildWorkingPdf → null when nothing is drawn on it
  async function buildWorkingSheet(n, { legend, details, dpi }) {
    const st = stateRef.current;
    const onPage = st.shapes.filter((s) => s.page === n && s.latlngs?.length);
    if (!onPage.length) return null;
    const [g, jpeg] = await Promise.all([pageGeom(n), sheetBase(n, dpi)]);
    const condById = new Map(st.conditions.map((c) => [c.id, c]));
    const rows =
      legend === "none"
        ? []
        : buildSheetLegends({
            conditions: st.conditions,
            shapes: onPage,
            pageNames: {},
            excludedPages: [],
            pageBidAreas: {},
            mappings: [],
            metricsFor: (c, ps) =>
              computeCondMetrics(c, ps, st.scales, st.excludedPages, st.units),
          })[0]?.rows || [];
    return {
      label: pageLabel(n),
      jpeg,
      sheetPtW: g.w,
      sheetPtH: g.hh,
      shapes: onPage,
      condById,
      P: ([lat, lng]) => [lng / g.rs, (g.h - lat) / g.rs], // viewer [lat,lng] → PDF points, top-left origin
      legend: rows.length
        ? {
            rows,
            pos: legend,
            details,
            sub: `${pageLabel(n)} · ${new Date().toLocaleDateString()}`,
          }
        : null,
    };
  }

  // the sheets as one PDF (own paper size each, takeoff + legend on them), shown with Download / Print / Share.
  // Must be called from a click (it opens the window first, before anything is awaited).
  async function printWorkingDrawings({ pages, legend, details, dpi }) {
    if (!pdfDoc) return flash("Open the drawing first.", "warn");
    if (wdBusyRef.current) return;
    const win = openReportWindow();
    if (!win)
      return flash(
        "The browser blocked the new window — allow pop-ups for this site.",
        "warn",
      );
    wdBusyRef.current = true;
    pages = [...new Set(pages)].sort((a, b) => a - b);
    console.info(
      "[working drawings] sheets asked for:",
      pages.map((p) => `${p} ${pageLabel(p)}`),
    );
    const t0 = performance.now();
    const out = new Array(pages.length);
    const skipped = [];
    let next = 0,
      done = 0;
    const status = () =>
      reportWindowStatus(
        win,
        pages.length === 1
          ? "Preparing the drawing"
          : `Prepared ${done} of ${pages.length} sheets`,
        5 + (done / pages.length) * 85,
        done < pages.length ? pageLabel(pages[done]) : "",
      );
    try {
      status();
      // two sheets at a time; a sheet that fails is left out and named — the others still come out
      const worker = async () => {
        while (next < pages.length) {
          const i = next++;
          try {
            out[i] = await buildWorkingSheet(pages[i], {
              legend,
              details,
              dpi,
            });
            if (!out[i]) skipped.push(`${pageLabel(pages[i])} (nothing drawn)`);
          } catch (e) {
            console.warn("[working drawings] sheet failed", pages[i], e);
            skipped.push(`${pageLabel(pages[i])} (${e.message || "failed"})`);
          }
          done++;
          status();
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(2, pages.length) }, worker),
      );
      const sheets = out.filter(Boolean);
      if (!sheets.length) {
        win.close();
        return flash(
          skipped.length
            ? `No sheets came out — ${skipped.join(" · ")}`
            : "Nothing is drawn on those sheets yet.",
          "warn",
        );
      }
      reportWindowStatus(
        win,
        "Putting the PDF together",
        94,
        `${sheets.length} sheet${sheets.length === 1 ? "" : "s"}`,
      );
      const base = (fileNameRef.current || "drawings").replace(/\.pdf$/i, "");
      const name =
        `${base} - ${sheets.length === 1 ? sheets[0].label : "working drawings"}.pdf`
          .replace(/[\\/:*?"<>|]+/g, " ")
          .replace(/\s+/g, " ");
      const bytes = await buildWorkingPdf(sheets, { title: name });
      const url = showPdfWindow(win, bytes, name, {
        labels: sheets.map((s) => s.label),
        skipped,
      });
      setTimeout(() => URL.revokeObjectURL(url), 30 * 60 * 1000);
      flash(
        `${sheets.length} of ${pages.length} sheet${pages.length === 1 ? "" : "s"} in the PDF (${((performance.now() - t0) / 1000).toFixed(1)} s)${skipped.length ? ` — left out: ${skipped.join(" · ")}` : ""}.`,
        skipped.length ? "warn" : "ok",
      );
    } catch (e) {
      console.error("Working drawings failed", e);
      try {
        win.close();
      } catch {
        /* already closed */
      }
      flash(`Couldn't build the drawings — ${e.message}`, "warn");
    } finally {
      wdBusyRef.current = false;
    }
  }

  // the estimate page prints through these (always the newest state and options)
  const workRef = useRef({});
  workRef.current = {
    print: (pages) =>
      printWorkingDrawings({
        pages: pages?.length ? pages : workingPages(),
        legend: wd.legend === "none" ? "tl" : wd.legend,
        details: wd.details,
        dpi: wd.dpi,
      }),
    renderSheetImage: (...a) => renderSheetImage(...a),
  };

  // text of a sheet (text layer + mark-up / text-box annotations, which often hold legends)
  async function sheetItems(n) {
    const page = await docPage(n);
    const vp = page.getViewport({ scale: 1 });
    const tc = await page.getTextContent();
    const items = [];
    for (const it of tc.items || []) {
      const str = (it.str || "").trim();
      if (!str) continue;
      const t = pdfjsLib.Util.transform(vp.transform, it.transform);
      const h = Math.hypot(t[2], t[3]) || it.height || 8;
      items.push({
        s: str,
        x0: t[4],
        y0: t[5] - h,
        x1: t[4] + (it.width || str.length * h * 0.55) * vp.scale,
        y1: t[5],
      });
    }
    try {
      for (const a of await page.getAnnotations()) {
        const str = (a.contentsObj?.str || a.richText?.str || "")
          .replace(/\s+/g, " ")
          .trim();
        if (!str || !a.rect) continue;
        const [x0, y0, x1, y1] = vp.convertToViewportRectangle(a.rect);
        items.push({
          s: str,
          x0: Math.min(x0, x1),
          y0: Math.min(y0, y1),
          x1: Math.max(x0, x1),
          y1: Math.max(y0, y1),
          para: str.length > 20,
        });
      }
    } catch {
      /* optional */
    }
    return items;
  }
  async function readSheetText(n) {
    return (await sheetItems(n)).map((i) => i.s).join(" ");
  }

  // picture of a sheet in tiles, for sheets whose text is drawn as linework
  async function sheetTiles(n, cols = 2, rows = 2) {
    const page = await docPage(n);
    const base = page.getViewport({ scale: 1 });
    const sc = Math.min(4, 3400 / Math.max(base.width, base.height));
    const vp = page.getViewport({ scale: sc });
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(vp.width);
    canvas.height = Math.round(vp.height);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    const out = [],
      tw = Math.ceil(canvas.width / cols),
      th = Math.ceil(canvas.height / rows),
      ov = 60;
    for (let r = 0; r < rows; r++)
      for (let c = 0; c < cols; c++) {
        const t = document.createElement("canvas");
        const x = Math.max(0, c * tw - ov),
          y = Math.max(0, r * th - ov);
        t.width = Math.min(canvas.width - x, tw + 2 * ov);
        t.height = Math.min(canvas.height - y, th + 2 * ov);
        t.getContext("2d").drawImage(
          canvas,
          x,
          y,
          t.width,
          t.height,
          0,
          0,
          t.width,
          t.height,
        );
        out.push(t.toDataURL("image/jpeg", 0.82));
      }
    return out;
  }

  // AI wall-type check: several walls per picture (the plan around them, each wall drawn over in red and numbered)
  // → which tag applies. A wall the sheet's own text settles (one wall tag around it, sitting on it) is never sent.
  //   walls: shapes (polylines) on page n · types: wall assemblies [{tag, name}] · tagCands: the engine's tag reads
  //   → [{id, tag|null, confidence, evidence}]   (throws when the AI isn't set up — callers carry on without it)
  // Spend less on AI: a wall asked about once (same sheet + revision, same wall, same wall types) is never asked
  // again — re-running a trace or "Ask AI" reuses the answer; stubs under 2'-6" aren't sent; on a sheet whose
  // tags are real text, a wall with no wall tag anywhere in its picture isn't sent (there's nothing to read);
  // only the part of the sheet the walls are on is rendered; pictures are smaller and go 10 to a request.
  const aiWallCacheRef = useRef(new Map());
  async function aiWallTags(n, walls, types, { ftPerUnit, tagCands = [] }) {
    if (!walls.length) return [];
    const g = await pageGeom(n);
    const toSheet = ([lat, lng]) => [lng / g.rs, (g.h - lat) / g.rs];
    const pad = 9 / ftPerUnit; // 9' of drawing around the wall: the tags and arrows near it
    const near = 3 / ftPerUnit;
    const segD = (p, a, b) => {
      const dx = b[0] - a[0],
        dy = b[1] - a[1],
        L2 = dx * dx + dy * dy || 1e-12;
      const k = Math.max(
        0,
        Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2),
      );
      return Math.hypot(p[0] - a[0] - k * dx, p[1] - a[1] - k * dy);
    };
    const tagSet = new Set(
      types.filter((t) => t.tag).map((t) => t.tag.toUpperCase()),
    );
    const typeList = types
      .filter((t) => t.tag)
      .map((t) => ({ tag: t.tag, name: t.name }));
    const tkey = typeList
      .map((t) => t.tag)
      .sort()
      .join(",");
    const rk = revKeyOf(n);
    const sigOf = (sh) =>
      `${n}|${rk}|${tkey}|${sh.latlngs.map((p) => `${Math.round(p[0])},${Math.round(p[1])}`).join(";")}`;
    const textTags = tagCands.filter((tc) => tagSet.has(tc.text));
    const tagsWithin = (pts, d) => [
      ...new Set(
        textTags
          .filter((tc) =>
            pts.some((p, i) => i && segD([tc.x, tc.y], pts[i - 1], p) <= d),
          )
          .map((tc) => tc.text),
      ),
    ];
    const out = [],
      ask = [];
    for (const sh of walls) {
      const lenFt = (pathLength(sh.latlngs, false) / g.rs) * ftPerUnit;
      if (lenFt < 4) continue; // stubs aren't worth a question — fix by hand if wrong
      const hit = aiWallCacheRef.current.get(sigOf(sh));
      if (hit) {
        out.push({ ...hit, id: sh.id });
        continue;
      }
      const pts = sh.latlngs.map(toSheet);
      if (textTags.length >= 5) {
        const around = tagsWithin(pts, pad);
        if (!around.length) continue; // no tag text anywhere near it — the picture has nothing to read
        // settled from the sheet's own text, no AI: one wall type tagged around this wall, and it sits on the wall
        const onIt = tagsWithin(pts, near);
        if (around.length === 1 && onIt.length === 1 && onIt[0] === around[0]) {
          const r = {
            tag: onIt[0],
            confidence: "high",
            evidence: `the only wall tag near this wall (${onIt[0]})`,
          };
          aiWallCacheRef.current.set(sigOf(sh), r);
          out.push({ ...r, id: sh.id });
          continue;
        }
      }
      ask.push({ sh, pts, lenFt });
    }
    if (!ask.length) return out;
    const page = await docPage(n);
    const base = page.getViewport({ scale: 1 });
    const sc = Math.min(3, 5000 / Math.max(base.width, base.height));
    // several walls per picture: walls close together share one crop (≤ 8 walls, ≤ ~50' across)
    const MAXW = 8,
      SPAN = 50 / ftPerUnit;
    const boxOf = (pts) => [
      Math.min(...pts.map((p) => p[0])) - pad,
      Math.min(...pts.map((p) => p[1])) - pad,
      Math.max(...pts.map((p) => p[0])) + pad,
      Math.max(...pts.map((p) => p[1])) + pad,
    ];
    const groups = [];
    const left = ask
      .map((w) => ({ ...w, box: boxOf(w.pts) }))
      .sort((a, b) => a.box[1] - b.box[1] || a.box[0] - b.box[0]);
    while (left.length) {
      const first = left.shift();
      const grp = { walls: [first], box: [...first.box] };
      for (let i = 0; i < left.length && grp.walls.length < MAXW; ) {
        const b = left[i].box;
        const u = [
          Math.min(grp.box[0], b[0]),
          Math.min(grp.box[1], b[1]),
          Math.max(grp.box[2], b[2]),
          Math.max(grp.box[3], b[3]),
        ];
        if (u[2] - u[0] <= SPAN && u[3] - u[1] <= SPAN) {
          grp.walls.push(left.splice(i, 1)[0]);
          grp.box = u;
        } else i++;
      }
      groups.push(grp);
    }
    const tiles = [];
    for (const grp of groups) {
      const [bx0, by0] = [Math.max(0, grp.box[0]), Math.max(0, grp.box[1])];
      const [bx1, by1] = [
        Math.min(base.width, grp.box[2]),
        Math.min(base.height, grp.box[3]),
      ];
      // render just this crop, ≤ 1280 px on its long side
      const k = Math.min(sc, 1280 / Math.max(bx1 - bx0, by1 - by0, 1));
      const t = document.createElement("canvas");
      t.width = Math.max(1, Math.round((bx1 - bx0) * k));
      t.height = Math.max(1, Math.round((by1 - by0) * k));
      const c = t.getContext("2d");
      c.fillStyle = "#fff";
      c.fillRect(0, 0, t.width, t.height);
      await page.render({
        canvasContext: c,
        viewport: page.getViewport({
          scale: k,
          offsetX: -bx0 * k,
          offsetY: -by0 * k,
        }),
      }).promise;
      const P = ([x, y]) => [(x - bx0) * k, (y - by0) * k];
      const walls = grp.walls.map((w, i) => {
        c.strokeStyle = "rgba(255,0,0,0.45)";
        c.lineWidth = Math.max(8, Math.round((1.2 / ftPerUnit) * k));
        c.lineCap = "round";
        c.beginPath();
        w.pts.forEach((p, j) => (j ? c.lineTo : c.moveTo).apply(c, P(p)));
        c.stroke();
        return { ...w, n: i + 1 };
      });
      // number labels last, so no wall line covers one
      for (const w of walls) {
        // label on the middle of the wall's longest segment
        let best = 1,
          bl = -1;
        for (let j = 1; j < w.pts.length; j++) {
          const l = Math.hypot(
            w.pts[j][0] - w.pts[j - 1][0],
            w.pts[j][1] - w.pts[j - 1][1],
          );
          if (l > bl) {
            bl = l;
            best = j;
          }
        }
        const [mx, my] = P([
          (w.pts[best][0] + w.pts[best - 1][0]) / 2,
          (w.pts[best][1] + w.pts[best - 1][1]) / 2,
        ]);
        const label = String(w.n);
        c.font = "bold 18px sans-serif";
        const lw = c.measureText(label).width + 10;
        c.fillStyle = "#dc2626";
        c.fillRect(mx - lw / 2, my - 12, lw, 24);
        c.fillStyle = "#fff";
        c.fillText(label, mx - lw / 2 + 5, my + 6);
      }
      tiles.push({
        image: t.toDataURL("image/jpeg", 0.72),
        walls: walls.map((w) => ({
          n: w.n,
          id: w.sh.id,
          sig: sigOf(w.sh),
          current: w.sh.meta?.wallTag || null,
          nearby: tagsWithin(w.pts, near),
          lengthFt: w.lenFt,
        })),
      });
      t.width = t.height = 0;
    }
    const sigById = new Map(
      tiles.flatMap((t) => t.walls.map((w) => [w.id, w.sig])),
    );
    for (let i = 0; i < tiles.length; i += 4) {
      try {
        const data = await post("ai/wall-tiles", {
          types: typeList,
          tiles: tiles.slice(i, i + 4).map((t) => ({
            image: t.image,
            walls: t.walls.map(({ sig: _s, ...w }) => w),
          })),
        });
        for (const r of data.results || []) {
          out.push(r);
          const sg = sigById.get(r.id);
          // (an error answer isn't kept — it's asked again next time)
          if (sg && !/^AI error/i.test(r.evidence || "")) {
            const { id: _i, ...rest } = r;
            aiWallCacheRef.current.set(sg, rest);
          }
        }
      } catch (e) {
        if (/too.large|413/i.test(e.message))
          throw new Error(
            "request too large — the server's JSON limit for /api/takeoffs3 needs raising (50mb)",
          );
        throw e;
      }
    }
    if (aiWallCacheRef.current.size > 4000) aiWallCacheRef.current.clear();
    return out;
  }

  // AI door / window check over a whole floor plan: the plan cut into tiles, the trace's markers drawn on them
  // numbered, plus a lettered grid. → { verdict: Map(n → "door"|"double"|"window"|"none"|"unsure"),
  //   missed: [{kind, p (sheet units, centre of the grid cell), note}] }
  //   items: [{n, kind:"door"|"double"|"window"|"suspect", p:[x,y] sheet units, hint?}] · lines: wall polylines
  //   (sheet units) for the plan's extent
  // does a wall line (sheet units) cross a box?
  function lineHitsBox(l, x, y, w, h) {
    const inside = (p) =>
      p[0] >= x && p[0] <= x + w && p[1] >= y && p[1] <= y + h;
    const cross = (a, b, c, d) => {
      const o = (p, q, r) =>
        Math.sign(
          (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]),
        );
      return o(a, b, c) !== o(a, b, d) && o(c, d, a) !== o(c, d, b);
    };
    const E = [
      [
        [x, y],
        [x + w, y],
      ],
      [
        [x + w, y],
        [x + w, y + h],
      ],
      [
        [x + w, y + h],
        [x, y + h],
      ],
      [
        [x, y + h],
        [x, y],
      ],
    ];
    for (let i = 0; i < (l || []).length; i++) {
      if (inside(l[i])) return true;
      if (i && E.some(([c, d]) => cross(l[i - 1], l[i], c, d))) return true;
    }
    return false;
  }
  async function aiReviewOpenings(n, { items, lines, ftPerUnit }) {
    // nothing the trace is unsure of → nothing to ask (and no full-sheet render)
    if (!items.some((i) => i.kind === "suspect"))
      return { verdict: new Map(), missed: [], checked: new Set() };
    const page = await docPage(n);
    const base = page.getViewport({ scale: 1 });
    const sc = Math.min(2.6, 6000 / Math.max(base.width, base.height));
    const vp = page.getViewport({ scale: sc });
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(vp.width);
    canvas.height = Math.round(vp.height);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    // the plan's extent: every wall and marker, plus 4'
    const all = [...lines.flatMap((l) => l), ...items.map((i) => i.p)];
    if (!all.length)
      return { verdict: new Map(), missed: [], checked: new Set() };
    const pad = 4 / ftPerUnit;
    let X0 = Math.max(0, Math.min(...all.map((p) => p[0])) - pad),
      Y0 = Math.max(0, Math.min(...all.map((p) => p[1])) - pad);
    let X1 = Math.min(base.width, Math.max(...all.map((p) => p[0])) + pad),
      Y1 = Math.min(base.height, Math.max(...all.map((p) => p[1])) + pad);
    // tiles of ~1300 px (doors and tags readable), at most 12
    let T = 1300 / sc;
    while (Math.ceil((X1 - X0) / T) * Math.ceil((Y1 - Y0) / T) > 12) T *= 1.25;
    const cols = Math.ceil((X1 - X0) / T),
      rows = Math.ceil((Y1 - Y0) / T);
    const tw = (X1 - X0) / cols,
      th = (Y1 - Y0) / rows;
    const GC = 4,
      GR = 4; // grid cells per tile
    const COL = {
      door: "#d946ef",
      double: "#7e22ce",
      window: "#06b6d4",
      suspect: "#f97316",
    };
    const tiles = [];
    for (let r = 0; r < rows; r++)
      for (let c = 0; c < cols; c++) {
        const tx = X0 + c * tw,
          ty = Y0 + r * th;
        const mine = items.filter(
          (i) =>
            i.p[0] >= tx &&
            i.p[0] < tx + tw &&
            i.p[1] >= ty &&
            i.p[1] < ty + th,
        );
        // only the parts of the plan with a spot the trace isn't sure of (a door swing / door or window tag with
        // no marker) are sent — the trace's own markers elsewhere stand as counted (saves most of the AI cost)
        if (!mine.some((i) => i.kind === "suspect")) continue;
        const ov = 1.5 / ftPerUnit; // a little overlap so a door on the edge is whole
        const sx = Math.max(0, tx - ov) * sc,
          sy = Math.max(0, ty - ov) * sc;
        const sw = Math.min(canvas.width - sx, (tw + 2 * ov) * sc),
          sh = Math.min(canvas.height - sy, (th + 2 * ov) * sc);
        const k = Math.min(1, 1200 / Math.max(sw, sh));
        const t = document.createElement("canvas");
        t.width = Math.round(sw * k);
        t.height = Math.round(sh * k);
        const g = t.getContext("2d");
        g.drawImage(canvas, sx, sy, sw, sh, 0, 0, t.width, t.height);
        const P = ([x, y]) => [(x * sc - sx) * k, (y * sc - sy) * k];
        // grid over the tile's own area (not the overlap)
        const [gx0, gy0] = P([tx, ty]),
          [gx1, gy1] = P([tx + tw, ty + th]);
        g.strokeStyle = "rgba(37,99,235,0.25)";
        g.lineWidth = 1;
        g.fillStyle = "rgba(37,99,235,0.85)";
        g.font = "bold 18px sans-serif";
        for (let i = 0; i <= GC; i++) {
          const x = gx0 + ((gx1 - gx0) * i) / GC;
          g.beginPath();
          g.moveTo(x, gy0);
          g.lineTo(x, gy1);
          g.stroke();
          if (i < GC)
            g.fillText(
              String.fromCharCode(65 + i),
              x + (gx1 - gx0) / GC / 2 - 6,
              Math.max(18, gy0 + 18),
            );
        }
        for (let i = 0; i <= GR; i++) {
          const y = gy0 + ((gy1 - gy0) * i) / GR;
          g.beginPath();
          g.moveTo(gx0, y);
          g.lineTo(gx1, y);
          g.stroke();
          if (i < GR)
            g.fillText(
              String(i + 1),
              Math.max(2, gx0 + 4),
              y + (gy1 - gy0) / GR / 2 + 6,
            );
        }
        for (const it of mine) {
          const [x, y] = P(it.p);
          g.strokeStyle = COL[it.kind];
          g.lineWidth = 3;
          g.beginPath();
          g.arc(x, y, 13, 0, Math.PI * 2);
          g.stroke();
          const label = `#${it.n}`;
          g.font = "bold 15px sans-serif";
          const w = g.measureText(label).width + 6;
          g.fillStyle = "rgba(255,255,255,0.9)";
          g.fillRect(x + 12, y - 24, w, 18);
          g.fillStyle = COL[it.kind];
          g.fillText(label, x + 15, y - 10);
        }
        tiles.push({
          image: t.toDataURL("image/jpeg", 0.74),
          cols: GC,
          rows: GR,
          items: mine.map(({ n, kind, hint }) => ({ n, kind, hint })),
          tx,
          ty,
        });
      }
    const verdict = new Map(),
      missed = [];
    const checked = new Set(tiles.flatMap((t) => t.items.map((i) => i.n))); // markers the AI was shown
    for (let i = 0; i < tiles.length; i += 3) {
      const batchT = tiles.slice(i, i + 3);
      let data;
      try {
        data = await post("ai/openings", {
          tiles: batchT.map(({ image, cols, rows, items }) => ({
            image,
            cols,
            rows,
            items,
          })),
        });
      } catch (e) {
        if (/too.large|413/i.test(e.message))
          throw new Error(
            "request too large — the server's JSON limit for /api/takeoffs3 needs raising (50mb)",
          );
        throw e;
      }
      (data.results || []).forEach((res, j) => {
        const tl = batchT[j];
        if (!tl) return;
        for (const it of res.items || []) verdict.set(it.n, it.verdict);
        for (const m of res.missed || []) {
          const ci = m.cell.charCodeAt(0) - 65,
            ri = Number(m.cell.slice(1)) - 1;
          if (ci < 0 || ci >= GC || ri < 0 || ri >= GR) continue;
          missed.push({
            kind: m.kind,
            note: m.note,
            p: [tl.tx + (tw * (ci + 0.5)) / GC, tl.ty + (th * (ri + 0.5)) / GR],
            cellR: Math.max(tw / GC, th / GR) / 2,
          });
        }
      });
    }
    return { verdict, missed, checked };
  }

  // a legend's panels as full-resolution pictures (the tags in each + its text, for spelling)
  async function legendPanels(page, sheet, legend, items) {
    const W = sheet.width,
      H = sheet.height;
    const boxes = new Map();
    for (const a of legend) {
      let [x0, y0, x1, y1] = a.panel;
      // a panel that is most of the sheet (no dividers found): tile it round its tags instead
      if ((x1 - x0) * (y1 - y0) > W * H * 0.3) {
        const T = 640;
        const cx = (a.box[0] + a.box[2]) / 2,
          cy = (a.box[1] + a.box[3]) / 2;
        const gx = Math.floor(cx / T),
          gy = Math.floor(cy / T);
        [x0, y0, x1, y1] = [
          gx * T - 80,
          gy * T - 80,
          (gx + 1) * T + 80,
          (gy + 1) * T + 80,
        ];
      }
      const box = [
        Math.max(0, x0 - 4),
        Math.max(0, y0 - 4),
        Math.min(W, x1 + 4),
        Math.min(H, y1 + 4),
      ];
      const k = box.map(Math.round).join(",");
      if (!boxes.has(k)) boxes.set(k, { box, tags: [] });
      boxes.get(k).tags.push(a.tagRaw || a.tag);
    }
    // …and every other panel with text (a type drawn without a tag symbol — "X.6" in a box beside its
    // section — still gets read); the title block left out
    const list = [...boxes.values()];
    const ov = (a, b) =>
      Math.max(0, Math.min(a[2], b[2]) - Math.max(a[0], b[0])) *
      Math.max(0, Math.min(a[3], b[3]) - Math.max(a[1], b[1]));
    const ar = (b) => (b[2] - b[0]) * (b[3] - b[1]);
    try {
      const tb = drawingArea(sheet, {}).titleBlock;
      for (const box of legendPanelBoxes(sheet, { exclude: tb ? [tb] : [] }))
        if (!list.some((p) => ov(p.box, box) > ar(box) * 0.7))
          list.push({ box, tags: [] });
    } catch {
      /* the tag panels alone */
    }
    const out = [];
    for (const { box, tags } of list.slice(0, 30)) {
      const [x0, y0, x1, y1] = box;
      const sc = Math.min(5, 2000 / Math.max(x1 - x0, y1 - y0));
      const vp = page.getViewport({ scale: sc });
      const c = document.createElement("canvas");
      c.width = Math.ceil((x1 - x0) * sc);
      c.height = Math.ceil((y1 - y0) * sc);
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, c.width, c.height);
      await page.render({
        canvasContext: ctx,
        viewport: vp,
        transform: [1, 0, 0, 1, -x0 * sc, -y0 * sc],
      }).promise;
      out.push({
        box,
        tags,
        image: c.toDataURL("image/jpeg", 0.85),
        text: items
          .filter((t) => {
            const mx = (t.x0 + t.x1) / 2,
              my = (t.y0 + t.y1) / 2;
            return mx >= x0 && mx <= x1 && my >= y0 && my <= y1;
          })
          .map((t) => t.s),
      });
    }
    return out;
  }

  async function readAssemblies(pages, progress) {
    const all = [];
    let aiSkipped = null;
    // sheets marked "Assembly legend" (Trace settings → Sheets to scan) are the only ones read — a door
    // schedule or an elevation never feeds the assembly list by mistake
    const legendRoles = pagesWithRole(stateRef.current.traceSettings, "legend");
    if (legendRoles.length) pages = legendRoles;
    for (const n of pages) {
      progress?.(`Reading ${pageLabel(n)}…`);
      const items = await sheetItems(n);
      // every tag symbol on the legend (diamond / hexagon / box …) is a type: found from the drawing itself,
      // then each panel of the legend goes to the vision model at full resolution to read the build-ups
      let legend = [],
        panels = [];
      try {
        const page = await docPage(n);
        const sheet = await extractSheet(pdfjsLib, page);
        legend = parseLegend(sheet, { page: n });
        if (legend.length < 3) legend = []; // (a sheet with a stray tag or two is read the usual way)
        if (legend.length) {
          progress?.(
            `${pageLabel(n)}: ${legend.length} tag symbols — reading each panel at full resolution…`,
          );
          panels = await legendPanels(page, sheet, legend, items);
        }
      } catch (e) {
        console.warn("legend parse failed", e);
      }
      const textual = items.filter((i) => i.s.length > 2).length >= 40;
      const images = textual || legend.length ? [] : await sheetTiles(n);
      const data = await post("ai/assemblies", {
        pages: [
          {
            page: n,
            title: pageLabel(n),
            items: items.slice(0, 6000),
            images,
            ...(legend.length
              ? {
                  legend: legend.map((a) => ({
                    tag: a.tagRaw || a.tag,
                    kind: a.kind,
                    name: a.name,
                    description: a.description,
                    layers: a.layers,
                    fireRating: a.fireRating,
                    stc: a.stc,
                    ul: a.ul,
                    base: a.base,
                  })),
                  panels,
                }
              : {}),
          },
        ],
      });
      all.push(...(data.assemblies || []));
      if (data.aiSkipped) aiSkipped = data.aiSkipped;
    }
    // the same type can show on two sheets — keep one (the richer reading)
    const sq = (x) =>
      String(x || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");
    const out = [];
    for (const a of all) {
      const ex = out.find(
        (b) =>
          b.kind === a.kind &&
          ((a.tag && sq(a.tag) === sq(b.tag)) ||
            (!a.tag && sq(a.name) === sq(b.name))),
      );
      if (!ex) out.push(a);
      else if ((a.layers?.length || 0) > (ex.layers?.length || 0))
        Object.assign(ex, a, { tag: ex.tag || a.tag });
    }
    return { assemblies: out, aiSkipped };
  }

  // ── sheet titles read off the title block (sheets with no name show as "Sheet 14"): the biggest words in
  //    the title strip, minus what repeats on every sheet (project / firm name) → { [n]: { num, title } }
  async function scanSheetTitles(pages, progress) {
    const raw = {};
    for (const n of pages) {
      progress?.(n);
      try {
        const page = await docPage(n);
        const vp = page.getViewport({ scale: 1 });
        const items = (await sheetItems(n)).map((t) => ({
          ...t,
          h: t.y1 - t.y0,
        }));
        const strip = items.filter(
          (t) => t.x0 > vp.width * 0.72 || t.y0 > vp.height * 0.8,
        );
        const num = strip
          .filter((t) => /^[A-Z]{1,2}[-\s]?\d{1,3}(\.\d{1,3})*$/.test(t.s))
          .sort((a, b) => b.h - a.h)[0];
        const words = strip
          .filter(
            (t) =>
              t !== num &&
              /[A-Z]{3,}/.test(t.s) &&
              t.s.length <= 60 &&
              !/^\d/.test(t.s),
          )
          .sort((a, b) => b.h - a.h);
        const top = words
          .filter((t) => t.h >= (words[0]?.h || 0) * 0.55)
          .slice(0, 10)
          .map((t) => t.s.trim());
        raw[n] = { num: num?.s || "", words: top };
      } catch {
        /* unreadable sheet */
      }
    }
    const seen = new Map();
    const done = Object.values(raw);
    for (const r of done)
      for (const w of new Set(r.words)) seen.set(w, (seen.get(w) || 0) + 1);
    const common = (w) => done.length >= 3 && seen.get(w) >= done.length * 0.5;
    const out = {};
    for (const [n, r] of Object.entries(raw))
      out[n] = {
        num: r.num,
        title: r.words
          .filter((w) => !common(w))
          .slice(0, 4)
          .join(" ")
          .slice(0, 90),
      };
    return out;
  }
  // "Suggest from titles": read every sheet's title, name unnamed sheets, and set each sheet's role
  async function suggestSheetRoles(overwrite) {
    const st = stateRef.current;
    const pages = Array.from({ length: pageCount }, (_, i) => i + 1).filter(
      (n) => !st.excludedPages.includes(n),
    );
    const titles = await scanSheetTitles(pages);
    const names = {};
    setTraceSettings((prev) => {
      const roles = { ...((prev || {}).sheetRoles || {}) };
      for (const n of pages) {
        const t = titles[n];
        if (!t) continue;
        const label = [t.num, t.title].filter(Boolean).join(" ");
        if (!pageNames[n] && !pageNames[String(n)] && label) names[n] = label;
        if (!overwrite && roles[n]) continue;
        const r =
          suggestRole(`${pageNames[n] || ""} ${t.title}`) || suggestRole(label);
        if (r) roles[n] = r;
        else if (overwrite) delete roles[n];
      }
      return {
        ...(prev || {}),
        sheetRoles: roles,
        sheetTitles: titles,
        assemblyPages: pages.filter((n) => roles[n] === "legend"),
      };
    });
    if (Object.keys(names).length)
      setPageNames((prev) => ({ ...names, ...prev }));
    flash(
      `Read ${Object.keys(titles).length} sheet titles${Object.keys(names).length ? ` and named ${Object.keys(names).length} sheets` : ""} — check the roles under Sheets to scan.`,
      "ok",
    );
  }

  // scale for a sheet: the one set by the estimator, else the printed one (shown in the wizard)
  async function scaleStatus(n) {
    const f = stateRef.current.scales[n] ?? stateRef.current.scales[String(n)];
    if (f) return { ok: true, label: "set", fpu: f, source: "set" };
    const eng = await ensureEngineSheet(n);
    const c = eng.checkOf(n);
    if (c?.ok) {
      const g = await pageGeom(n);
      return {
        ok: true,
        label: `${c.label} (${c.source === "dimensions" ? "from dimensions" : "printed"})`,
        fpu: c.ftPerUnit / g.rs,
        source: "printed",
      };
    }
    const s = eng.scaleOf(n);
    if (!c && s && !s.multi) {
      const g = await pageGeom(n);
      return {
        ok: true,
        label: `${s.label} (printed)`,
        fpu: s.ftPerUnit / g.rs,
        source: "printed",
      };
    }
    return { ok: false };
  }

  // ── scale every sheet at once: printed scale checked against the sheet's own dimensions ──
  // Sheets that already have a scale are left alone (unless `force` and it came from this check).
  const scaleRunRef = useRef(0);
  async function autoScaleSheets({ force = false, quiet = false } = {}) {
    const doc = pdfDoc;
    if (!doc) return;
    const run = ++scaleRunRef.current;
    const st = stateRef.current;
    const totalSheets = Math.max(
      doc.numPages,
      ...Object.keys(revStateRef.current.sheetRevisions || {}).map(
        (k) => Number(k) || 0,
      ),
    );
    const pages = Array.from({ length: totalSheets }, (_, i) => i + 1).filter(
      (n) => !st.excludedPages.includes(n),
    );
    const auto = (n) =>
      [
        "printed+dims",
        "dimensions",
        "printed",
        "flagged",
        "suggested",
      ].includes(scaleInfo[n]?.source);
    const has = (n) => !!(st.scales[n] ?? st.scales[String(n)]);
    // quiet (on open): only sheets never checked · normal: every unscaled sheet · force: those + the auto-scaled ones
    const needsSuggest = (n) =>
      scaleInfo[n]?.source === "flagged" && !("suggest" in scaleInfo[n]);
    const todo = pages.filter((n) =>
      force
        ? !has(n) || auto(n)
        : quiet
          ? !has(n) && (!scaleInfo[n] || needsSuggest(n))
          : !has(n),
    );
    const firstLook = todo.some((n) => !scaleInfo[n]);
    if (!todo.length) {
      if (!quiet) flash("Every sheet already has a scale.", "ok");
      return;
    }
    // first look at a set: the drawing waits behind a loading screen (flipping sheets mid-read can crash the tab)
    setScaleRun({
      done: 0,
      total: todo.length,
      blocking: firstLook,
      cur: null,
    });
    const eng = getEngine();
    const setS = {},
      setI = {},
      rsOf = {},
      sheetF = {}; // sheetF: feet per sheet unit of every scaled sheet
    let ok = 0,
      warn = 0,
      bad = 0;
    for (let i = 0; i < todo.length; i++) {
      if (scaleRunRef.current !== run) return; // a newer drawing / run took over
      const n = todo[i];
      try {
        const page = await docPage(n);
        const c = await eng.checkScale(n, page);
        const g = await pageGeom(n);
        rsOf[n] = g.rs;
        if (c?.ok) {
          setS[n] = c.ftPerUnit / g.rs;
          sheetF[n] = { f: c.ftPerUnit, label: c.label };
          setI[n] = {
            source: c.source,
            label: c.label,
            reason: c.reason,
            warn: !!c.warn,
            agree: c.dims?.agree || 0,
            options: (c.options || []).map((o) => ({
              label: o.label,
              fpu: o.ftPerUnit / g.rs,
              agree: o.agree,
            })),
          };
          ok++;
          if (c.warn) warn++;
        } else {
          const options = (c?.options || []).map((o) => ({
            label: o.label,
            fpu: o.ftPerUnit / g.rs,
            agree: o.agree,
          }));
          // several printed scales: suggest the one the dimensions back most, else the one printed most often
          const suggest = options.length
            ? {
                ...options[0],
                why: options[0].agree
                  ? `${options[0].agree} dimension${options[0].agree === 1 ? "" : "s"} agree`
                  : options.length > 1
                    ? "printed most on this sheet"
                    : "printed on this sheet",
              }
            : null;
          setI[n] = {
            source: "flagged",
            reason: c?.reason || "couldn't read this sheet",
            options,
            suggest,
          };
          bad++;
        }
      } catch (err) {
        setI[n] = { source: "flagged", reason: "couldn't read this sheet" };
        bad++;
      }
      if (i % 4 === 3 || i === todo.length - 1)
        setScaleRun((prev) =>
          prev ? { ...prev, done: i + 1, total: todo.length, cur: n } : prev,
        );
      await new Promise((r) => setTimeout(r, 0)); // keep the UI responsive on big sets
    }
    if (scaleRunRef.current !== run) return;
    // sheets with nothing printed: suggest the scale most of the set uses (same set → usually the same plan scale)
    for (const n of pages) {
      if (sheetF[n] || !has(n)) continue;
      const g = await pageGeom(n).catch(() => null);
      if (!g) continue;
      const v = st.scales[n] ?? st.scales[String(n)];
      sheetF[n] = { f: v * g.rs, label: scaleInfo[n]?.label };
    }
    const groups = [];
    for (const { f, label } of Object.values(sheetF)) {
      const gr = groups.find((x) => Math.abs(x.f - f) / x.f < 0.01);
      if (gr) {
        gr.n++;
        if (!gr.label && label) gr.label = label;
      } else groups.push({ f, label, n: 1 });
    }
    groups.sort((a, b) => b.n - a.n);
    const common = groups[0] && groups[0].n >= 2 ? groups[0] : null;
    for (const [n, inf] of Object.entries(setI)) {
      if (inf.source !== "flagged" || inf.suggest || !common || !rsOf[n])
        continue;
      inf.suggest = {
        label: common.label || "set scale",
        fpu: common.f / rsOf[n],
        why: `used on ${common.n} other sheets in this set`,
      };
    }
    for (const inf of Object.values(setI))
      if (inf.source === "flagged" && !("suggest" in inf)) inf.suggest = null;
    const suggested = Object.values(setI).filter((x) => x.suggest).length;
    // a sheet the estimator scaled while this ran keeps their scale
    setScales((prev) => {
      const next = { ...prev };
      for (const [n, f] of Object.entries(setS))
        if (!(prev[n] ?? prev[String(n)]) || force) next[n] = f;
      return next;
    });
    setScaleInfo((prev) => {
      const next = { ...prev };
      for (const [n, inf] of Object.entries(setI))
        if (
          !next[n] ||
          [
            "printed+dims",
            "dimensions",
            "printed",
            "flagged",
            "suggested",
          ].includes(next[n].source)
        )
          next[n] = { ...inf, at: Date.now() };
      return next;
    });
    setScaleRun(null);
    if (quiet && !firstLook) return; // reopening: just filled in suggestions, no noise
    if (bad || warn) setScaleMenu(true);
    flash(
      bad
        ? `Scaled ${ok} of ${todo.length} sheets — ${bad} still need one${suggested ? ` (${suggested} with a suggested scale — accept under Scale)` : ""}${warn ? `, ${warn} worth a check` : ""}.`
        : warn
          ? `Scaled all ${ok} sheets — ${warn} worth a check (listed under Scale).`
          : `Scaled all ${ok} sheets — checked against their dimensions.`,
      bad ? "info" : "ok",
    );
  }
  // new drawing (or one reopened with sheets still unscaled): check the whole set straight away
  useEffect(() => {
    if (!pdfDoc) return;
    const t = setTimeout(() => autoScaleSheets({ quiet: true }), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc]);
  // accept the suggested scale on every unscaled sheet that has one (one undo step)
  function acceptSuggestedScales(only = null) {
    const st = stateRef.current;
    const list = Object.entries(scaleInfo).filter(
      ([n, inf]) =>
        inf?.source === "flagged" &&
        inf.suggest &&
        !(st.scales[n] ?? st.scales[String(n)]) &&
        !st.excludedPages.includes(Number(n)) &&
        (!only || only.includes(Number(n))),
    );
    if (!list.length) return;
    setScales((prev) => {
      const next = { ...prev };
      for (const [n, inf] of list) next[n] = inf.suggest.fpu;
      return next;
    });
    setScaleInfo((prev) => {
      const next = { ...prev };
      for (const [n, inf] of list)
        next[n] = {
          source: "suggested",
          label: inf.suggest.label,
          reason: `suggested — ${inf.suggest.why}`,
          warn: true,
          options: inf.options,
          at: Date.now(),
        };
      return next;
    });
    flash(
      `Scale set on ${list.length} sheet${list.length === 1 ? "" : "s"} from the suggestions — marked to check.`,
      "ok",
    );
  }
  function applySheetScale(n, opt) {
    setScaleValue(
      opt.fpu,
      {
        source: "printed",
        label: opt.label,
        reason: "picked from the printed scales",
      },
      n,
    );
  }
  function calibrateSheet(n) {
    setScaleMenu(false);
    if (n !== pageNum) setPageNum(n);
    setTimeout(() => startCalibrate(), n !== pageNum ? 700 : 0);
  }

  const pointIn = ([x, y], ring) => {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i],
        [xj, yj] = ring[j];
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)
        inside = !inside;
    }
    return inside;
  };
  // where a floor plan's rooms sit on the RCP of the same level: match room names printed on both
  // s: sheet units of B per sheet unit of A (1 when both sheets are drawn at the same scale)
  // tolU: how far (sheet units of B) a label may sit from where the other sheet puts it (RCP labels move
  // to clear light fixtures) — ~3 ft
  function alignSheets(textA, textB, s = 1, tolU = 3) {
    const key = (t) => t.str.trim().toUpperCase();
    // room names AND room numbers (a 1:100 suite plan repeats "BEDROOM A" — its numbers are unique)
    const ok = (t) =>
      /^[A-Z][A-Z ./&-]{3,}$/.test(key(t)) ||
      /^[A-Z]?\d{3,5}[A-Z]?$/.test(key(t));
    const mid = (t) => [(t.x0 + t.x1) / 2, (t.y0 + t.y1) / 2];
    const cnt = (list) => {
      const m = new Map();
      list.filter(ok).forEach((t) => m.set(key(t), (m.get(key(t)) || 0) + 1));
      return m;
    };
    const ca = cnt(textA),
      cb = cnt(textB);
    const d = [];
    for (const a of textA)
      if (ok(a) && ca.get(key(a)) === 1 && cb.get(key(a)) === 1) {
        const b = textB.find((t) => key(t) === key(a));
        const [ax, ay] = mid(a),
          [bx, by] = mid(b);
        d.push([bx - s * ax, by - s * ay]);
      }
    if (d.length < 3) return null;
    // the offset most matches agree on (notes and legends repeat words in other places — outliers)
    const tol = Math.max(3, tolU);
    const close = (v, w) =>
      Math.abs(w[0] - v[0]) < tol && Math.abs(w[1] - v[1]) < tol;
    let best = null;
    for (const v of d) {
      const n = d.filter((w) => close(v, w)).length;
      if (!best || n > best.n) best = { v, n };
    }
    const near = d.filter((w) => close(best.v, w));
    const dx = near.reduce((a, w) => a + w[0], 0) / near.length,
      dy = near.reduce((a, w) => a + w[1], 0) / near.length;
    const agree = near.length;
    return agree >= Math.max(4, d.length * 0.15)
      ? { dx, dy, s, n: agree }
      : null;
  }
  const squash = (x) =>
    String(x || "")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");
  function matchCeiling(strings, types) {
    const joined = strings.join(" \n ").toUpperCase();
    const flat = squash(joined);
    let best = null;
    for (const a of types) {
      let sc = 0;
      const tag = (a.tag || "").toUpperCase();
      if (
        tag &&
        (tag.length >= 2
          ? new RegExp(
              `(^|[^A-Z0-9])${tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^A-Z0-9]|$)`,
            ).test(joined)
          : strings.some((s) => s.trim().toUpperCase() === tag))
      )
        sc += 10;
      const nm = squash(a.name).replace(/CEILING|TYPE|ASSEMBLY/g, "");
      if (nm.length >= 4 && flat.includes(nm)) sc += 8;
      else {
        const toks = String(a.name || "")
          .toUpperCase()
          .split(/[^A-Z0-9']+/)
          .filter(
            (t) =>
              t.length >= 3 &&
              !["CEILING", "TYPE", "THE", "AND", "WITH", "NEW"].includes(t),
          );
        if (toks.length) {
          const hit =
            toks.filter((t) => joined.includes(t)).length / toks.length;
          if (hit >= 0.6) sc += 5 * hit;
        }
      }
      if (sc > (best?.sc || 0)) best = { a, sc };
    }
    return best && best.sc >= 4 ? best.a : null;
  }
  function ceilingHeightOf(strings) {
    for (const s of strings) {
      const t = s.toUpperCase();
      let m =
        t.match(
          /(?:CEILING|CLG|C\.?L\.?G\.?)\.?\s*(?:HT|HEIGHT|HGT)\.?\s*[:=@]?\s*(\d+\s*'\s*-?\s*\d*(?:\s*\d\/\d)?\s*"?)/,
        ) ||
        t.match(/(\d+\s*'\s*-?\s*\d+(?:\s*\d\/\d)?\s*")\s*A\.?\s*F\.?\s*F/);
      if (m) {
        const v = parseFeetInput(m[1].replace(/\s+/g, ""));
        if (v && v > 5 && v < 40) return v;
      }
      m = t.match(/(\d{4})\s*(?:MM)?\s*A\.?\s*F\.?\s*F/);
      if (m) {
        const v = Number(m[1]) / 304.8;
        if (v > 5 && v < 40) return v;
      }
    }
    return null;
  }

  // Doors / windows on one traced floor plan (called from runTrace, mutates newShapes / sum):
  //   1. suspects: door swings / door & window tags with nothing counted near them
  //   2. AI on: a verdict on every counted door / window and every suspect, plus doors / windows it sees unmarked
  //      — confirmed ones are counted (and deducted from the nearest wall), anything doubtful is added / kept as a
  //        hollow dashed marker to approve (A) or delete
  //   AI off / unavailable: suspects are added as markers to check
  async function reviewOpenings({
    fp,
    res,
    lines,
    pageMarks,
    pageShapes,
    ftPerUnit,
    toLLp,
    sum,
    ds,
    countCond,
    batch,
    newShapes,
    useAi,
    wallTags,
    progress,
    doubtfulDoors = [],
  }) {
    const texts = await sheetItems(fp.page).catch(() => []);
    const suspects = openingSuspects({
      arcs: res.doors || [],
      marks: pageMarks,
      texts,
      ftPerUnit,
      wallTags,
      gaps: doubtfulDoors,
    });
    const kindOf = (key) => (key === "door2" ? "double" : key);
    const keyOf = (kind) => (kind === "double" ? "door2" : kind);
    const op = (sum.openings = { ok: 0, flagged: 0, added: 0, toCheck: 0 });
    const placed = pageMarks.map((m) => ({ key: m.key, p: m.p })); // every door / window marker on this sheet
    // a door / window added after the walls were drawn: counted, and a door deducted from the wall it sits in
    const addMarker = (kind, p, meta) => {
      const key = keyOf(kind);
      const c = countCond(key);
      const w =
        kind === "double" ? ds.double : kind === "door" ? ds.single : null;
      newShapes.push({
        id: uid(),
        conditionId: c.id,
        page: fp.page,
        kind: "marker",
        latlngs: [toLLp(p)],
        meta: {
          source: "trace",
          batch,
          role: key,
          ...(w ? { width: Math.round(w * 100) / 100 } : {}),
          ...meta,
        },
      });
      placed.push({ key, p });
      sum.counts = sum.counts || {};
      sum.counts[key] = (sum.counts[key] || 0) + 1;
      if (!w || meta.needsCheck) return; // only a confirmed door changes a wall
      let best = null,
        bd = 2.5 / ftPerUnit;
      for (const { shape } of pageShapes) {
        const pts = lines.find((l) => l.__shapeId === shape.id)?.pts;
        if (!pts) continue;
        for (let i = 0; i + 1 < pts.length; i++) {
          const a = pts[i],
            b = pts[i + 1],
            dx = b[0] - a[0],
            dy = b[1] - a[1],
            L2 = dx * dx + dy * dy || 1e-12;
          const t = Math.max(
            0,
            Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2),
          );
          const d = Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy);
          if (d < bd) {
            bd = d;
            best = shape;
          }
        }
      }
      if (best) {
        const m = best.meta;
        best.meta = {
          ...m,
          doors: [...(m.doors || []), Math.round(w * 100) / 100],
          doorKinds: [
            ...(m.doorKinds || []),
            kind === "double" ? "double" : "single",
          ],
          doorLf: Math.round(((Number(m.doorLf) || 0) + w) * 100) / 100,
          doorHeight: m.doorHeight || ds.height,
        };
        sum.doorsLf += w;
      }
    };
    if (!useAi) {
      for (const x of suspects) {
        addMarker(x.kind, x.p, { needsCheck: `${x.hint} — check` });
        op.toCheck++;
      }
      return;
    }
    const items = [
      ...pageMarks.map((m, i) => ({
        n: i + 1,
        kind: kindOf(m.key),
        p: m.p,
        mark: m,
      })),
      ...suspects.map((x, i) => ({
        n: pageMarks.length + i + 1,
        kind: "suspect",
        p: x.p,
        hint: x.hint,
        sus: x,
      })),
    ];
    progress?.(`AI checking doors & windows · ${pageLabel(fp.page)}`);
    let rv;
    try {
      rv = await aiReviewOpenings(fp.page, {
        items,
        lines: lines.map((l) => l.pts),
        ftPerUnit,
      });
    } catch (e) {
      sum.aiNote = [sum.aiNote, `AI door/window check skipped: ${e.message}`]
        .filter(Boolean)
        .join(" · ");
      for (const x of suspects) {
        addMarker(x.kind, x.p, { needsCheck: `${x.hint} — check` });
        op.toCheck++;
      }
      return;
    }
    for (const it of items) {
      if (rv.checked && !rv.checked.has(it.n)) continue; // not shown to the AI (no doubt in that part): stands
      const v = rv.verdict.get(it.n);
      if (it.mark) {
        const sh = it.mark.shape;
        if (v === it.kind) {
          sh.meta = { ...sh.meta, aiOk: true };
          op.ok++;
        } else {
          sh.meta = {
            ...sh.meta,
            needsCheck:
              v === "none"
                ? "AI sees no door / window here"
                : v === "unsure" || !v
                  ? "AI couldn't confirm this one"
                  : `AI thinks this is a ${v === "double" ? "double door" : v}`,
          };
          op.flagged++;
        }
      } else if (["door", "double", "window"].includes(v)) {
        addMarker(v, it.sus.p, {
          aiOk: true,
          aiAdded: true,
          note: it.sus.hint,
        });
        op.added++;
      } else if (v === "unsure") {
        addMarker(it.sus.kind, it.sus.p, {
          needsCheck: `${it.sus.hint} — AI unsure`,
        });
        op.toCheck++;
      }
    }
    // doors / windows the AI saw with no marker: at the centre of the grid cell it named — move it onto the opening
    // (snapped to an uncounted door swing in that cell when there is one)
    for (const m of rv.missed) {
      const arc = (res.doors || [])
        .map((a) => [(a.hx + a.cx) / 2, (a.hy + a.cy) / 2])
        .filter(
          (q) =>
            Math.abs(q[0] - m.p[0]) <= m.cellR &&
            Math.abs(q[1] - m.p[1]) <= m.cellR,
        )
        .sort(
          (a, b) =>
            Math.hypot(a[0] - m.p[0], a[1] - m.p[1]) -
            Math.hypot(b[0] - m.p[0], b[1] - m.p[1]),
        )[0];
      // windows are only counted where the plan tags them (W1, CW4 …) — an untagged one the AI "sees" is skipped
      if (
        m.kind === "window" &&
        stateRef.current.traceSettings?.windowTags !== false &&
        !texts.some((t) => {
          const s2 = String(t.s || "").replace(/\s+/g, "");
          return (
            WINDOW_TAG_RE.test(s2) &&
            !wallTags.has(s2.toUpperCase()) &&
            Math.hypot(
              (t.x0 + t.x1) / 2 - m.p[0],
              (t.y0 + t.y1) / 2 - m.p[1],
            ) <=
              m.cellR + 10 / ftPerUnit
          );
        })
      )
        continue;
      const p = m.kind !== "window" && arc ? arc : m.p;
      // a marker of that kind already there → the AI just didn't see ours. With a door swing to go on, "there" is
      // within 3' of it; without one (only the cell is known), anywhere in that cell
      const isWin = m.kind === "window";
      const same = placed.filter((q) => (q.key === "window") === isWin);
      if (
        p !== m.p
          ? same.some(
              (q) => Math.hypot(q.p[0] - p[0], q.p[1] - p[1]) < 3 / ftPerUnit,
            )
          : same.some(
              (q) =>
                Math.abs(q.p[0] - m.p[0]) <= m.cellR &&
                Math.abs(q.p[1] - m.p[1]) <= m.cellR,
            )
      )
        continue;
      addMarker(m.kind, p, {
        aiAdded: true,
        needsCheck: `AI found a ${m.kind === "double" ? "double door" : m.kind} near here${m.note ? ` (${m.note})` : ""} — move it onto the opening and approve`,
      });
      op.toCheck++;
    }
  }

  async function runTrace(plan, progress) {
    // Sheets marked in Trace settings → Sheets to scan ARE the sheet list: every "Floor plan" sheet is traced
    // for walls, every "Ceiling plan" sheet for ceilings, nothing else. (Heights and options the wizard set for
    // a sheet are kept; a marked sheet the wizard didn't list takes the wizard's first floor settings.)
    {
      const ts = stateRef.current.traceSettings || {};
      const floorPages = pagesWithRole(ts, "floor"),
        rcpPages = pagesWithRole(ts, "rcp");
      if (floorPages.length || rcpPages.length) {
        const base = plan.floor[0] || {
          walls: true,
          areas: false,
          height: parseFeetInput(ts.defaultHeight || "") || null,
        };
        plan = {
          ...plan,
          floor: floorPages.map(
            (n) => plan.floor.find((x) => x.page === n) || { ...base, page: n },
          ),
          rcp: rcpPages.map(
            (n) => plan.rcp.find((x) => x.page === n) || { page: n },
          ),
        };
      }
    }
    const batch = uid();
    const added = [];
    const asmCond = new Map(); // assembly id → condition
    const have = () => [...stateRef.current.conditions, ...added];
    const condFor = (a, wet = false) => {
      if (wet && a.kind === "wall") {
        // the same wall type in a washroom: its own assembly (moisture-resistant board, other finishes)
        const k = `${a.id}|wet`;
        if (asmCond.has(k)) return asmCond.get(k);
        const wt = `${a.tag || a.name}·WR`;
        let c = have().find(
          (x) =>
            x.type === "linear" &&
            x.wet &&
            (x.assemblyId === a.id || x.wallType === wt),
        );
        if (!c) {
          c = {
            id: uid(),
            name: `${a.tag ? `${a.tag} — ` : ""}${a.name || "Wall"} · Washroom`.slice(
              0,
              120,
            ),
            type: "linear",
            wallType: wt,
            height: null,
            color: tagColor(`wet${a.tag || a.name}`),
            description: `${descOf(a)}${descOf(a) ? "\n" : ""}Washroom walls — moisture-resistant board / wet-area finishes`,
            assemblyId: a.id,
            wet: true,
          };
          added.push(c);
        }
        asmCond.set(k, c);
        return c;
      }
      if (asmCond.has(a.id)) return asmCond.get(a.id);
      let c = a.conditionId && have().find((x) => x.id === a.conditionId);
      if (!c && a.kind === "wall" && a.tag)
        c = have().find(
          (x) =>
            x.type === "linear" &&
            !x.wet &&
            (x.wallType || "").toUpperCase() === a.tag,
        );
      if (!c) {
        c = {
          id: uid(),
          name: `${a.tag ? `${a.tag} — ` : ""}${a.name || (a.kind === "wall" ? "Wall" : "Ceiling")}`.slice(
            0,
            120,
          ),
          type: a.kind === "wall" ? "linear" : "area",
          wallType: a.kind === "wall" ? a.tag || "" : "",
          height: null,
          color: tagColor(`${a.kind}${a.tag || a.name}`),
          description: descOf(a),
          assemblyId: a.id,
        };
        added.push(c);
      }
      asmCond.set(a.id, c);
      return c;
    };
    // count assemblies (doors, double doors, windows, corner bead)
    const countCond = (key) => {
      const spec = COUNT_KINDS[key],
        wt = `__count:${key}`;
      let c = have().find((x) => x.type === "count" && x.wallType === wt);
      if (!c) {
        c = {
          id: uid(),
          name: spec.name,
          type: "count",
          wallType: wt,
          height: null,
          color: spec.color,
          description: spec.description,
        };
        added.push(c);
      }
      return c;
    };
    const floorAreaCond = () => {
      if (plan.areaTarget) {
        const a = assemblies.find((x) => x.id === plan.areaTarget);
        if (a) return condFor(a);
      }
      let c = have().find(
        (x) => x.type === "area" && x.name === "Floor area (by room)",
      );
      if (!c) {
        c = {
          id: uid(),
          name: "Floor area (by room)",
          type: "area",
          wallType: "",
          height: null,
          color: "#a3a3a3",
        };
        added.push(c);
      }
      return c;
    };
    const byTag = new Map(
      plan.wallTypes.filter((a) => a.tag).map((a) => [a.tag.toUpperCase(), a]),
    );
    // walls no traced type claims: left out (default) — only the walls of the types picked to trace are drawn.
    //   "__guess": typed from the wall thickness when one traced type clearly owns it, else left out
    //   "__untagged": drawn as "Untagged wall" to assign later · <assembly id>: drawn as that type
    const untaggedMode =
      plan.untaggedWall === "__untagged"
        ? "draw"
        : plan.untaggedWall === "__guess"
          ? "guess"
          : plan.untaggedWall
            ? "type"
            : "skip";
    const untagged =
      untaggedMode === "type"
        ? assemblies.find((a) => a.id === plan.untaggedWall) || null
        : null;
    // walls the engine can't type go to one "Untagged wall" condition — click a line to give it a type
    const untaggedCond = (wet = false) => {
      const wt = wet ? `${UNTAGGED}:wet` : UNTAGGED;
      let c = have().find((x) => x.type === "linear" && x.wallType === wt);
      if (!c) {
        c = {
          id: uid(),
          name: wet ? "Untagged wall · Washroom" : "Untagged wall",
          type: "linear",
          wallType: wt,
          height: null,
          color: wet ? "#7dd3fc" : "#94a3b8",
          ...(wet
            ? {
                wet: true,
                description:
                  "Washroom walls with no type tag — assign a washroom wall type",
              }
            : {}),
        };
        added.push(c);
      }
      return c;
    };
    // ceilings with no tag: typed by the grid drawn in the room, else one "Untagged ceiling" per grid
    const gridAsm = (kind) => {
      const id = (plan.ceilGrid || {})[kind];
      return id ? assemblies.find((a) => a.id === id) || null : null;
    };
    const UNTAGGED_CEIL_NAMES = {
      "2x4": "Untagged ceiling — 2'×4' grid",
      "2x2": "Untagged ceiling — 2'×2' grid",
      none: "Untagged ceiling — drywall (no grid)",
      grid: "Untagged ceiling — other grid",
    };
    const untaggedCeilCond = (kind) => {
      const wt = `${UNTAGGED}:ceil:${kind}`;
      let c = have().find((x) => x.type === "area" && x.wallType === wt);
      if (!c) {
        c = {
          id: uid(),
          name: UNTAGGED_CEIL_NAMES[kind] || UNTAGGED_CEIL_NAMES.grid,
          type: "area",
          wallType: wt,
          height: null,
          color:
            { "2x4": "#38bdf8", "2x2": "#2dd4bf", none: "#f59e0b" }[kind] ||
            "#a78bfa",
        };
        added.push(c);
      }
      return c;
    };
    const newShapes = [],
      summary = [],
      scaleSet = {},
      floorRooms = new Map();
    const total = plan.floor.length + plan.rcp.length;
    let done = 0;

    const prepare = async (n) => {
      const st = await scaleStatus(n);
      if (!st.ok)
        throw new Error(`${pageLabel(n)} has no scale — calibrate it first.`);
      if (st.source === "printed") scaleSet[n] = st.fpu;
      const g = await pageGeom(n);
      const eng = await ensureEngineSheet(n);
      return { eng, g, f: st.fpu, ftPerUnit: st.fpu * g.rs };
    };

    for (const fp of plan.floor) {
      progress?.({ done, total, msg: `Walls · ${pageLabel(fp.page)}` });
      const { eng, g, ftPerUnit } = await prepare(fp.page);
      const res = await eng.rooms(
        fp.page,
        ftPerUnit,
        null,
        engineOpts(fp.page),
        {
          faces: fp.walls && plan.openFaces,
          exterior: fp.walls && plan.exterior !== false,
        },
      );
      rememberGeo(fp.page, res);
      const roomEdges = [];
      floorRooms.set(fp.page, res.rooms);
      const toLLp = llOf(g);
      const enabled = new Set(byTag.keys());
      const sum = {
        page: fp.page,
        kind: "floor",
        height: fp.height,
        rooms: res.rooms.length,
        walls: {},
        ceilings: {},
        untaggedLf: 0,
        doorsLf: 0,
        engine: res.stats?.engine || "old engine — update lib/takeoff/engine",
      };
      // the same rooms Detect rooms finds
      const wetSplit = plan.wetSeparate !== false;
      const perRoom = res.rooms.map((r) => {
        const wet = wetSplit && isWetRoom(r.label, r.texts);
        const edges = fp.walls
          ? assignTags({
              edges: r.edges,
              runs: res.runs,
              tagCands: res.tagCands,
              enabled,
              sideAware: false,
            }).map((e) => (wet ? { ...e, wet } : e))
          : [];
        roomEdges.push(...edges);
        return { r, edges, wet };
      });
      // tagged walls with no closed room beside them (open offices that leak, corridors, lobbies)
      const extra =
        fp.walls && plan.openFaces && res.faces?.length
          ? // …kept only when they join the traced walls and aren't part of a dimension string
            filterOpenFaces(
              uncoveredTaggedFaces({
                faces: res.faces,
                roomEdges,
                runs: res.runs,
                tagCands: res.tagCands,
                enabled,
                minLen: 1.5 / ftPerUnit,
              }).filter((f) => byTag.get(f.tag)),
              { roomEdges, dims: res.dims || [], ftPerUnit },
            )
          : [];
      console.info(
        `[takeoff] ${pageLabel(fp.page)}: engine ${res.stats?.engine || "OLD"} · area ${res.area?.source || "?"} · ${res.rooms.length} rooms · ${res.stats?.gridLines ?? "?"} grid pieces left out`,
      );
      const winGap = plan.windowMode === "gap",
        doorGap = plan.doorMode === "gap";
      const faceMode = plan.wallLines === "faces";
      const doubtfulDoors = []; // door-sized gaps the engine left out as "not a door" — checked below
      // one line per wall (centreline; a wall shared by two rooms is drawn once, through its doors and
      // windows) — or, by choice, every room's own faces
      const rawLines = !fp.walls
        ? []
        : faceMode
          ? [
              ...perRoom.flatMap(({ r, edges, wet }) =>
                chainEdges(edges, { windowsBreak: winGap }).map((ch) => ({
                  ...ch,
                  wet,
                  doors: [],
                  doorSegs: [],
                  doorKinds: [],
                  room: r.label || null,
                })),
              ),
              ...extra.map((f) => ({
                tag: f.tag,
                pts: [f.a, f.b],
                lf: f.len * ftPerUnit,
                run: f.run,
                windows: [],
                windowSegs: [],
                doors: [],
                doorSegs: [],
                openArea: true,
              })),
            ]
          : // one line per wall: drop lines lying inside another line's wall (dashed line in a wall, window sills)
            dropNestedLines(
              centerlineChains({
                edges: [
                  ...roomEdges.filter((e) => !(winGap && e.kind === "window")),
                  ...extra.map((f) => ({
                    a: f.a,
                    b: f.b,
                    kind: "wall",
                    run: f.run,
                    tag: f.tag,
                    lf: f.len * ftPerUnit,
                  })),
                ],
                runs: res.runs,
                ftPerUnit,
                doorMode: doorGap ? "gap" : "deduct",
                swings: res.doors || [],
                doubtful: doubtfulDoors,
              }),
              { ftPerUnit },
            );
      // every straight wall typed by the tags pointing at it (arrows first); split where the type changes
      // the exterior: one continuous line through the whole (wide, noisy) outside wall, typed by the exterior tags
      // printed outside it, windows and doors carried onto it — instead of the parallel lines found inside it
      const extNames =
        /EXTERIOR|VENEER|CLADDING|CURTAIN\s*WALL|SIDING|EIFS|STUCCO|RAINSCREEN/i;
      const extTags = new Set(
        (asmRef.current || [])
          .filter(
            (a) =>
              a &&
              a.kind === "wall" &&
              a.tag &&
              extNames.test(`${a.name || ""} ${descOf(a) || ""}`),
          )
          .map((a) => String(a.tag).toUpperCase()),
      );
      const lines = applyExterior(
        retagLinesByEvidence(rawLines, {
          tagCands: res.tagCands || [],
          runs: res.runs,
          ftPerUnit,
          has: (t) => byTag.has(t),
        }),
        {
          bands: plan.exterior !== false ? res.exterior || [] : [],
          tagCands: res.tagCands || [],
          runs: res.runs,
          ftPerUnit,
          has: (t) => byTag.has(t),
          isExterior: extTags.size ? (t) => extTags.has(t) : null,
        },
      );
      const guess = thicknessGuesser(lines, res.runs, ftPerUnit * 12); // inches per sheet unit
      const ds = doorSizes(plan); // door width / double width / height from the wizard
      const pageShapes = []; // [{shape, key, lf}] walls drawn on this sheet (for the AI check)
      for (const ch of lines) {
        let a = null,
          tagSource = null,
          share = null,
          review = null;
        // a tag on the wall: typed by the tags pointing at it (dashed when they disagree or it's carried far)
        if (ch.tag && byTag.get(ch.tag)) {
          review = ch.tagEv || { src: "tag", reasons: [], alt: null };
          a = byTag.get(ch.tag);
          tagSource = review.src;
        }
        // the wall's colour (fire-rating band / pre-coloured assembly) when the estimator mapped that colour
        const runInfo = ch.run >= 0 ? res.runs?.[ch.run] : null;
        if (!a && runInfo?.colorFamily) {
          const id = (stateRef.current.traceSettings?.colorMap || {})[
            runInfo.colorFamily
          ];
          const ca = id ? assemblies.find((x) => x.id === id) : null;
          if (ca) {
            a = ca;
            tagSource = "color";
          }
        }
        if (!a && (untaggedMode === "guess" || untaggedMode === "draw")) {
          const gss = guess(ch);
          if (gss && byTag.get(gss.tag)) {
            a = byTag.get(gss.tag);
            tagSource = "thickness";
            share = Math.round(gss.share * 100) / 100;
          }
        }
        if (!a && untagged) {
          a = untagged;
          tagSource = "default";
        }
        const c = a ? condFor(a, ch.wet) : untaggedCond(ch.wet);
        if (ch.wet) sum.wetLf = (sum.wetLf || 0) + ch.lf;
        // doors at the wizard's sizes (3'-2" / 6'-2" × 7'-0" unless changed), not the drawn opening
        if (ch.doors?.length) ch.doors = sizedDoors(ch, ds);
        const winLf = ch.windows.reduce((x, y) => x + y, 0),
          doorLf = (ch.doors || []).reduce((x, y) => x + y, 0);
        const r2 = (v) => Math.round(v * 100) / 100;
        newShapes.push({
          id: uid(),
          conditionId: c.id,
          page: fp.page,
          kind: "polyline",
          latlngs: ch.pts.map(toLLp),
          label: ch.room || undefined,
          meta: {
            source: "trace",
            batch,
            role: "wall",
            line: faceMode ? "face" : "centre",
            room: ch.room || null,
            wallTag: ch.tag || null,
            tagSource: tagSource || "none",
            tagShare: share,
            ...(runInfo?.color
              ? { lineColor: runInfo.color, rating: runInfo.rating || null }
              : {}),
            ...(review?.src === "check"
              ? { tagCheck: review.reasons, tagAlt: review.alt || null }
              : {}),
            height: fp.height,
            ...(ch.openArea ? { openArea: true } : {}),
            ...(ch.exterior ? { exterior: true } : {}),
            windows: ch.windows.map(r2),
            ...(winGap
              ? {}
              : {
                  windowSegs: ch.windowSegs.map(([p, q]) => [
                    toLLp(p),
                    toLLp(q),
                  ]),
                  deductLf: r2(winLf),
                  windowHeight: plan.windowHeight || null,
                }),
            ...(ch.wet ? { wet: true } : {}),
            ...(doorLf > 0
              ? {
                  doors: ch.doors.map(r2),
                  doorKinds: ch.doorKinds || [],
                  doorSegs: ch.doorSegs.map(([p, q]) => [toLLp(p), toLLp(q)]),
                  doorLf: r2(doorLf),
                  doorHeight: ds.height,
                }
              : {}),
          },
        });
        const k = a ? a.tag || a.name : "Untagged";
        pageShapes.push({
          shape: newShapes[newShapes.length - 1],
          key: k,
          lf: ch.lf,
          wet: !!ch.wet,
        });
        ch.__shapeId = newShapes[newShapes.length - 1].id;
        if (tagSource === "check") sum.checkLf = (sum.checkLf || 0) + ch.lf;
        if (tagSource === "color") sum.colorLf = (sum.colorLf || 0) + ch.lf;
        if (runInfo?.rating)
          (sum.ratings ||= {})[runInfo.rating] =
            (sum.ratings[runInfo.rating] || 0) + ch.lf;
        sum.walls[k] = (sum.walls[k] || 0) + ch.lf;
        if (!a) sum.untaggedLf += ch.lf;
        if (tagSource === "thickness")
          sum.guessedLf = (sum.guessedLf || 0) + ch.lf;
        if (ch.openArea) sum.openLf = (sum.openLf || 0) + ch.lf;
        sum.doorsLf += doorLf;
      }
      // AI: read the drawing around every wall the trace isn't sure of (two tags pointing at it, a tag carried
      // far, typed from thickness, or no tag at all) — confident answers are applied, the rest become suggestions
      if (fp.walls && plan.aiWalls !== false) {
        const unsure = pageShapes
          .filter(({ shape }) =>
            ["check", "thickness", "default", "none"].includes(
              shape.meta.tagSource,
            ),
          )
          .sort((x, y) => y.lf - x.lf)
          .slice(0, 40);
        if (unsure.length) {
          progress?.({
            done,
            total,
            msg: `AI checking ${unsure.length} wall${unsure.length === 1 ? "" : "s"} · ${pageLabel(fp.page)}`,
          });
          try {
            const results = await aiWallTags(
              fp.page,
              unsure.map((u) => u.shape),
              plan.wallTypes,
              { ftPerUnit, tagCands: res.tagCands || [] },
            );
            for (const r of results) {
              const u = unsure.find((x) => x.shape.id === r.id);
              if (!u || !r.tag || !byTag.get(r.tag)) continue;
              const m = u.shape.meta;
              if (r.confidence === "high") {
                const a2 = byTag.get(r.tag);
                const c2 = condFor(a2, u.wet);
                const k2 = a2.tag || a2.name;
                if (m.tagSource === "check") sum.checkLf -= u.lf;
                if (m.tagSource === "thickness") sum.guessedLf -= u.lf;
                if (u.key === "Untagged") sum.untaggedLf -= u.lf;
                sum.walls[u.key] -= u.lf;
                if (sum.walls[u.key] < 0.01) delete sum.walls[u.key];
                sum.walls[k2] = (sum.walls[k2] || 0) + u.lf;
                sum.aiLf = (sum.aiLf || 0) + u.lf;
                u.key = k2; // (typed now — kept when untyped walls are left out)
                u.shape.conditionId = c2.id;
                u.shape.meta = {
                  ...m,
                  wallTag: r.tag,
                  tagSource: "ai",
                  aiEvidence: r.evidence || "",
                  tagCheck: undefined,
                  tagAlt:
                    m.wallTag && m.wallTag !== r.tag
                      ? m.wallTag
                      : m.tagAlt || null,
                };
              } else {
                u.shape.meta = {
                  ...m,
                  aiSuggest: {
                    tag: r.tag,
                    confidence: r.confidence,
                    evidence: r.evidence || "",
                  },
                  ...(r.tag !== m.wallTag ? { tagAlt: r.tag } : {}),
                };
              }
            }
            sum.aiChecked = (sum.aiChecked || 0) + results.length;
          } catch (e) {
            sum.aiNote = `AI check skipped: ${e.message}`;
          }
        }
      }
      // only walls a traced type claims are drawn (by its tag, its colour, the AI reading the tags, or — when
      // asked — its thickness); the rest are left out instead of cluttering the takeoff with "Untagged wall"
      let keptLines = lines;
      if (fp.walls && untaggedMode !== "draw") {
        const drop = new Set();
        for (const ps of pageShapes) {
          const src = ps.shape.meta?.tagSource;
          if (
            ps.key === "Untagged" ||
            src === "none" ||
            (src === "default" && !untagged)
          ) {
            drop.add(ps.shape.id);
            sum.walls[ps.key] = (sum.walls[ps.key] || 0) - ps.lf;
            if (sum.walls[ps.key] < 0.01) delete sum.walls[ps.key];
            sum.untaggedLf = Math.max(0, (sum.untaggedLf || 0) - ps.lf);
            sum.skippedLf = (sum.skippedLf || 0) + ps.lf;
            if (ps.shape.meta?.openArea)
              sum.openLf = Math.max(0, (sum.openLf || 0) - ps.lf);
          }
        }
        if (drop.size) {
          for (let i = newShapes.length - 1; i >= 0; i--)
            if (drop.has(newShapes[i].id)) newShapes.splice(i, 1);
          for (let i = pageShapes.length - 1; i >= 0; i--)
            if (drop.has(pageShapes[i].shape.id)) pageShapes.splice(i, 1);
          keptLines = lines.filter((l) => !drop.has(l.__shapeId));
        }
      }
      sum.lines = keptLines.length;
      sum.points = keptLines.reduce((n, l) => n + l.pts.length, 0);
      // counts: a marker on every door (single / double), window and outside corner
      if (fp.walls && plan.counts !== false) {
        // (with door gaps the drawn lines carry no doors — count from the through-door chains so doors are
        //  still counted and no corner bead lands on their jambs)
        const src =
          faceMode || doorGap
            ? centerlineChains({
                edges: roomEdges,
                runs: res.runs,
                ftPerUnit,
                doorMode: "deduct",
                swings: res.doors || [],
              }).map((c) => ({ ...c, doors: sizedDoors(c, ds) }))
            : lines;
        const marks = countMarks({
          chains: src,
          rooms: res.rooms,
          ftPerUnit,
          corners: plan.cornerBead !== false,
          doors: res.doors || [],
          // corner bead only where a traced wall is drawn
          cornerLines: keptLines.map((c) => ({ pts: c.pts, th: c.th })),
        });
        const pageMarks = [];
        for (const m of marks) {
          const c = countCond(m.key);
          newShapes.push({
            id: uid(),
            conditionId: c.id,
            page: fp.page,
            kind: "marker",
            latlngs: [toLLp(m.p)],
            meta: {
              source: "trace",
              batch,
              role: m.key,
              ...(m.lf ? { width: Math.round(m.lf * 100) / 100 } : {}),
              ...(m.key === "bead" && fp.height ? { height: fp.height } : {}),
              ...(m.noSwing
                ? {
                    needsCheck:
                      "door-sized opening with no door swing drawn — is it a door?",
                  }
                : {}),
            },
          });
          if (m.key !== "bead")
            pageMarks.push({
              shape: newShapes[newShapes.length - 1],
              key: m.key,
              p: m.p,
            });
          sum.counts = sum.counts || {};
          sum.counts[m.key] = (sum.counts[m.key] || 0) + 1;
        }
        // doors & windows double-check: cross-check against door swings and door / window tags, then (AI on)
        // the AI confirms every one and looks for any the trace missed
        await reviewOpenings({
          fp,
          res,
          lines,
          pageMarks,
          pageShapes,
          ftPerUnit,
          toLLp,
          sum,
          ds,
          countCond,
          batch,
          newShapes,
          useAi: plan.aiOpenings !== false,
          wallTags: new Set(byTag.keys()),
          progress: (msg) => progress?.({ done, total, msg }),
          doubtfulDoors,
        });
      }
      if (fp.areas) {
        for (const { r } of perRoom) {
          const c = floorAreaCond();
          newShapes.push({
            id: uid(),
            conditionId: c.id,
            page: fp.page,
            kind: "polygon",
            latlngs: simplifyRing(r.ring, AREA_TOL_IN / 12 / ftPerUnit).map(
              toLLp,
            ),
            label: r.label || undefined,
            meta: {
              source: "trace",
              batch,
              room: r.label || null,
              confidence: r.confidence,
              role: "floor-area",
            },
          });
          sum.ceilings[c.name] = (sum.ceilings[c.name] || 0) + r.areaSf;
        }
      }
      summary.push(sum);
      done++;
    }

    for (const rp of plan.rcp) {
      progress?.({ done, total, msg: `Ceilings · ${pageLabel(rp.page)}` });
      const { eng, g, ftPerUnit } = await prepare(rp.page);
      const res = await eng.rooms(
        rp.page,
        ftPerUnit,
        null,
        engineOpts(rp.page, { gridMaxIn: 64 }), // RCP: ceiling grids are pattern, not walls
      );
      const info = await eng.load(rp.page);
      const toLLp = llOf(g);
      let rooms = res.rooms.map((r) => ({
        ring: r.ring,
        areaSf: r.areaSf,
        label: r.label,
        texts: r.texts || [],
        callouts: r.callouts || [],
      }));
      let note = "";
      // The ceiling of a room is the whole room between its walls. An RCP's own linework cuts rooms up
      // (bulkheads, soffits, light coves, grid edges) — so its rooms come from the floor plan of the same
      // level, lined up by the room names / numbers both sheets carry (any scale). The wizard's "from" sheet
      // first; otherwise every floor plan in this trace is tried and the best match wins.
      {
        const tries =
          rp.from === "self"
            ? []
            : rp.from
              ? [rp.from]
              : plan.rcpFromFloor === false
                ? []
                : [
                    ...new Set([
                      ...plan.floor.map((x) => x.page),
                      ...pagesWithRole(stateRef.current.traceSettings, "floor"),
                    ]),
                  ].filter((n) => n !== rp.page);
        let best = null;
        for (const from of tries) {
          let srcSt;
          try {
            srcSt = await prepare(from);
          } catch {
            continue;
          }
          const srcInfo = await srcSt.eng.load(from);
          const sc = srcSt.ftPerUnit / ftPerUnit; // RCP units per floor-plan unit
          const off = alignSheets(
            srcInfo.text || [],
            info.text || [],
            sc,
            3 / ftPerUnit,
          );
          if (off && (!best || off.n > best.off.n)) best = { from, off, srcSt };
        }
        if (best) {
          let src = floorRooms.get(best.from);
          if (!src) {
            src = (
              await best.srcSt.eng.rooms(
                best.from,
                best.srcSt.ftPerUnit,
                null,
                engineOpts(best.from),
              )
            ).rooms;
            floorRooms.set(best.from, src);
          }
          const { dx, dy, s: k } = best.off;
          rooms = src.map((r) => ({
            ring: r.ring.map(([x, y]) => [x * k + dx, y * k + dy]),
            areaSf: r.areaSf,
            label: r.label,
            texts: [],
            callouts: [],
          }));
          note = `rooms from ${pageLabel(best.from)} (${best.off.n} rooms lined up)`;
        } else if (tries.length)
          note = `couldn't line up with ${tries.map(pageLabel).join(" / ")} — used the RCP's own rooms`;
      }
      const sum = {
        page: rp.page,
        kind: "rcp",
        rooms: rooms.length,
        walls: {},
        ceilings: {},
        untypedSf: 0,
        gridSf: 0,
        note,
      };
      // the grid drawn in every room (read on the RCP itself, also when the rooms came from a floor plan)
      progress?.({
        done,
        total,
        msg: `Reading ceiling grids · ${pageLabel(rp.page)}`,
      });
      let grids = [];
      try {
        grids = await eng.grids(
          rp.page,
          ftPerUnit,
          rooms.map((r) => r.ring),
        );
      } catch (e) {
        console.warn("ceiling grid read failed", e);
      }
      // a small room that shows only one direction of grid follows this sheet's usual grid
      const byKind = {};
      grids.forEach((gd, i) => {
        if (gd && !gd.guessed && gd.kind !== "none")
          byKind[gd.kind] = (byKind[gd.kind] || 0) + rooms[i].areaSf;
      });
      const usual =
        Object.entries(byKind).sort((p, q) => q[1] - p[1])[0]?.[0] || null;
      const GRID_LABEL = { "2x4": "2'×4' grid", "2x2": "2'×2' grid" };
      rooms.forEach((r, i) => {
        let gd = grids[i] || { kind: "none", label: "no grid" };
        if (gd.guessed && usual && GRID_LABEL[usual])
          gd = {
            ...gd,
            kind: usual,
            label: `${GRID_LABEL[usual]} (sheet's usual — small room)`,
          };
        const inside = (info.text || [])
          .filter((t) =>
            pointIn([(t.x0 + t.x1) / 2, (t.y0 + t.y1) / 2], r.ring),
          )
          .map((t) => t.str);
        const calls = (res.calloutPts || [])
          .filter((c) => pointIn([c.x, c.y], r.ring))
          .map((c) => c.text);
        const strings = [...new Set([...inside, ...r.callouts, ...calls])];
        let a = matchCeiling(strings, plan.ceilingTypes),
          tagSource = a ? "tag" : "none";
        if (!a) {
          a = gridAsm(gd.kind === "grid" ? "" : gd.kind);
          if (a) tagSource = "grid";
        }
        const c = a ? condFor(a) : untaggedCeilCond(gd.kind);
        const ch = ceilingHeightOf(strings);
        newShapes.push({
          id: uid(),
          conditionId: c.id,
          page: rp.page,
          kind: "polygon",
          latlngs: simplifyRing(r.ring, AREA_TOL_IN / 12 / ftPerUnit).map(
            toLLp,
          ),
          label: r.label || undefined,
          meta: {
            source: "trace",
            batch,
            role: "ceiling",
            room: r.label || null,
            ceilingHeight: ch,
            grid: gd.kind,
            gridLabel: gd.label,
            tagSource,
            matchedFrom: strings.slice(0, 6),
          },
        });
        const k = a ? a.tag || a.name : c.name;
        sum.ceilings[k] = (sum.ceilings[k] || 0) + r.areaSf;
        if (!a) sum.untypedSf += r.areaSf;
        if (tagSource === "grid") sum.gridSf += r.areaSf;
      });
      summary.push(sum);
      done++;
    }
    progress?.({ done, total, msg: "Drawing…" });

    if (Object.keys(scaleSet).length)
      setScales((prev) => ({ ...prev, ...scaleSet }));
    // every assembly set up in the wizard gets its card in Assemblies — even one this trace drew nothing with
    for (const a of assemblies)
      if (
        a &&
        (a.kind === "wall" || a.kind === "ceiling") &&
        !asmCond.has(a.id)
      )
        condFor(a);
    // each wall condition's default height = the height most of its traced walls got (hand-drawn walls use it)
    const hByCond = new Map();
    for (const x of newShapes)
      if (x.meta?.height) {
        const m = hByCond.get(x.conditionId) || new Map();
        m.set(x.meta.height, (m.get(x.meta.height) || 0) + 1);
        hByCond.set(x.conditionId, m);
      }
    const topH = (id) => {
      const m = hByCond.get(id);
      return m ? [...m.entries()].sort((p, q) => q[1] - p[1])[0][0] : null;
    };
    for (const c of added)
      if (c.type === "linear" && !c.height && topH(c.id)) c.height = topH(c.id);
    setConditions((prev) => [
      ...prev.map((c) =>
        c.type === "linear" && !c.height && topH(c.id)
          ? { ...c, height: topH(c.id) }
          : c,
      ),
      ...added,
    ]);
    setShapes((prev) => [...prev, ...newShapes]);
    setAssemblies((prev) =>
      prev.map((a) =>
        asmCond.has(a.id) ? { ...a, conditionId: asmCond.get(a.id).id } : a,
      ),
    );
    setTraceSettings((prev) => ({
      ...(prev || {}),
      batches: [
        ...((prev || {}).batches || []),
        {
          id: batch,
          at: new Date().toISOString(),
          pages: summary.map((x) => x.page),
          shapes: newShapes.length,
        },
      ],
    }));
    return {
      batch,
      shapes: newShapes.length,
      pages: summary,
      newConditions: added.length,
    };
  }

  // the wizard closed: give every assembly in it a card in Assemblies (made once; linked by assemblyId / tag)
  function syncAsmConditions() {
    const conds = stateRef.current.conditions;
    const add = [],
      link = new Map();
    for (const a of assemblies) {
      if (!a || !(a.kind === "wall" || a.kind === "ceiling")) continue;
      let c =
        (a.conditionId && conds.find((x) => x.id === a.conditionId)) ||
        conds.find((x) => x.assemblyId === a.id && !x.wet) ||
        (a.kind === "wall" &&
          a.tag &&
          conds.find(
            (x) =>
              x.type === "linear" &&
              !x.wet &&
              (x.wallType || "").toUpperCase() === String(a.tag).toUpperCase(),
          ));
      if (!c) {
        c = {
          id: uid(),
          name: `${a.tag ? `${a.tag} — ` : ""}${a.name || (a.kind === "wall" ? "Wall" : "Ceiling")}`.slice(
            0,
            120,
          ),
          type: a.kind === "wall" ? "linear" : "area",
          wallType: a.kind === "wall" ? a.tag || "" : "",
          height: null,
          color: tagColor(`${a.kind}${a.tag || a.name}`),
          description: descOf(a),
          assemblyId: a.id,
        };
        add.push(c);
      }
      if (a.conditionId !== c.id) link.set(a.id, c.id);
    }
    if (add.length) setConditions((prev) => [...prev, ...add]);
    if (link.size)
      setAssemblies((prev) =>
        prev.map((a) =>
          link.has(a.id) ? { ...a, conditionId: link.get(a.id) } : a,
        ),
      );
  }

  function undoBatch(id) {
    setShapes((prev) => prev.filter((s) => s.meta?.batch !== id));
    setTraceSettings((prev) => ({
      ...(prev || {}),
      batches: ((prev || {}).batches || []).filter((b) => b.id !== id),
    }));
    flash("Trace removed.", "ok");
  }

  // per-wall height (walls start at their floor's height; the estimator lowers the odd one)
  const selShapes = shapes.filter(
    (x) => selIds.includes(x.id) && x.page === pageNum,
  );
  const selShape = selShapes.length === 1 ? selShapes[0] : null;
  const selCond =
    selShape && conditions.find((c) => c.id === selShape.conditionId);
  const [heightDraft, setHeightDraft] = useState("");
  useEffect(() => {
    if (!selShape || !selCond) return;
    const h = Number(selShape.meta?.height) || Number(selCond.height) || null;
    setHeightDraft(h ? fmtFeetIn(h) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selShapeId, selShape?.meta?.height, selCond?.height]);

  function setFloorHeight(page, text) {
    setTraceSettings((prev) => ({
      ...(prev || {}),
      floor: {
        ...((prev || {}).floor || {}),
        [page]: {
          ...((prev || {}).floor?.[page] || { on: true, walls: true }),
          height: text,
        },
      },
    }));
    const h = parseFeetInput(text);
    if (!h) return;
    const lin = new Set(
      conditions.filter((c) => c.type === "linear").map((c) => c.id),
    );
    setShapes((prev) =>
      prev.map((x) =>
        x.page === Number(page) &&
        lin.has(x.conditionId) &&
        !x.meta?.heightManual
          ? { ...x, meta: { ...(x.meta || {}), height: h } }
          : x,
      ),
    );
  }
  // a count assembly (doors / double doors / windows / corner bead), made the first time it's needed
  function ensureCountCondition(key) {
    const wt = `__count:${key}`;
    const ex = stateRef.current.conditions.find(
      (c) => c.type === "count" && c.wallType === wt,
    );
    if (ex) return ex;
    const spec = COUNT_KINDS[key];
    const c = {
      id: uid(),
      name: spec.name,
      type: "count",
      wallType: wt,
      height: null,
      color: spec.color,
      description: spec.description,
    };
    setConditions((prev) =>
      prev.some((x) => x.wallType === wt) ? prev : [...prev, c],
    );
    stateRef.current.conditions = [...stateRef.current.conditions, c];
    return c;
  }
  // ── cut a section out of a line: click its start and its end ON the line — that stretch becomes its own line
  //    (selected, ready to take another assembly); the rest stays as it was. Windows / doors go with their piece.
  const spliceRef = useRef(null); // { shapeId, s0: distance along | null, p0, layers: [] }
  const [splicing, setSplicing] = useState(null); // { step: 1 | 2, name }
  function clearSpliceLayers() {
    const map = mapRef.current;
    for (const l of spliceRef.current?.layers || []) map?.removeLayer(l);
    if (spliceRef.current) spliceRef.current.layers = [];
  }
  function cancelSplice() {
    clearSpliceLayers();
    spliceRef.current = null;
    setSplicing(null);
    if (baseToolRef.current === "pan") mapRef.current?.dragging.enable();
    setMapCursor(baseToolRef.current === "pan" ? "grab" : "default");
  }
  handlersRef.current.cancelSplice = cancelSplice;
  function startSplice(shapeId) {
    const sh = stateRef.current.shapes.find((x) => x.id === shapeId);
    if (!sh || sh.kind !== "polyline") return;
    cancelPlaceDoor();
    disableAllPm();
    spliceRef.current = { shapeId, s0: null, p0: null, layers: [] };
    const cond = stateRef.current.conditions.find(
      (c) => c.id === sh.conditionId,
    );
    setSplicing({ step: 1, name: cond?.name || "this line" });
    mapRef.current?.dragging.disable();
    setMapCursor("crosshair");
  }
  // where a click lands on the line being cut (snapped to a corner of it when close), or null when off it
  function spliceSnap(sh, p) {
    const ll = sh.latlngs;
    const tol = pxTol(14);
    for (let i = 0; i < ll.length; i++)
      if (Math.hypot(ll[i][0] - p[0], ll[i][1] - p[1]) <= tol) {
        const proj =
          i < ll.length - 1
            ? { i, t: 0, p: ll[i] }
            : { i: i - 1, t: 1, p: ll[i] };
        return { s: alongLine(ll, proj), p: ll[i] };
      }
    const proj = projectOnLine(ll, p);
    if (!proj || proj.d > pxTol(40)) return null;
    return { s: alongLine(ll, proj), p: proj.p };
  }
  function spliceClick(p) {
    const sp = spliceRef.current;
    const st = stateRef.current;
    const sh = st.shapes.find((x) => x.id === sp?.shapeId);
    if (!sh) return cancelSplice();
    const hit = spliceSnap(sh, p);
    if (!hit) return flash("Click on the line itself.", "info");
    const map = mapRef.current;
    if (sp.s0 == null) {
      sp.s0 = hit.s;
      sp.p0 = hit.p;
      sp.layers.push(
        L.circleMarker(hit.p, {
          radius: 6,
          color: "#fff",
          weight: 2,
          fillColor: "#f97316",
          fillOpacity: 1,
          interactive: false,
        }).addTo(map),
      );
      setSplicing((v) => (v ? { ...v, step: 2 } : v));
      return;
    }
    const a = Math.min(sp.s0, hit.s),
      b = Math.max(sp.s0, hit.s);
    const f = st.scales[sh.page] ?? st.scales[String(sh.page)];
    const minLen = f ? 3 / 12 / f : pxTol(6); // a section at least 3"
    if (b - a < minLen)
      return flash("Pick an end a little further along the line.", "info");
    cancelSplice();
    const parts = splitLineMeta(sh, [a, b], f);
    // the cut-out section: the piece that starts at `a`
    const mid = parts.find((x) => Math.abs(x.s0 - a) < 1e-9) || parts[0];
    const pieces = parts.map((x, i) => ({
      ...sh,
      id: i === 0 ? sh.id : uid(),
      latlngs: x.latlngs,
      meta: { ...x.meta, ...(x === mid ? { spliced: true } : {}) },
      _mid: x === mid,
    }));
    const midShape = pieces.find((x) => x._mid);
    pieces.forEach((x) => delete x._mid);
    setShapes((prev) => {
      const at = prev.findIndex((x) => x.id === sh.id);
      if (at < 0) return prev;
      return [...prev.slice(0, at), ...pieces, ...prev.slice(at + 1)];
    });
    layerMapRef.current.get(sh.id)?.setLatLngs(pieces[0].latlngs);
    setTimeout(() => setSel([midShape.id]), 0);
    const lf = f
      ? fmtFeetIn(
          midShape.latlngs.reduce(
            (t, q, i) =>
              i
                ? t +
                  Math.hypot(
                    q[0] - midShape.latlngs[i - 1][0],
                    q[1] - midShape.latlngs[i - 1][1],
                  )
                : 0,
            0,
          ) * f,
        )
      : "";
    flash(
      `Section cut${lf ? ` (${lf})` : ""} — it's selected: pick its assembly in the panel. The rest of the line is unchanged.`,
      "ok",
    );
  }
  handlersRef.current.spliceMove = (p) => {
    const sp = spliceRef.current;
    if (!sp) return;
    const sh = stateRef.current.shapes.find((x) => x.id === sp.shapeId);
    if (!sh) return;
    const hit = spliceSnap(sh, p);
    const map = mapRef.current;
    // the cursor's spot on the line, and once the start is set, the section that will be cut
    if (sp.hover) map.removeLayer(sp.hover);
    if (sp.preview) map.removeLayer(sp.preview);
    sp.hover = sp.preview = null;
    if (!hit) return;
    sp.hover = L.circleMarker(hit.p, {
      radius: 5,
      color: "#f97316",
      weight: 2,
      fillColor: "#fff",
      fillOpacity: 1,
      interactive: false,
    }).addTo(map);
    if (sp.s0 != null) {
      const seg = lineBetween(
        sh.latlngs,
        Math.min(sp.s0, hit.s),
        Math.max(sp.s0, hit.s),
      );
      if (seg.length >= 2)
        sp.preview = L.polyline(seg, {
          color: "#f97316",
          weight: 9,
          opacity: 0.55,
          interactive: false,
          dashArray: "10 6",
        }).addTo(map);
    }
    sp.layers = sp.layers.filter((l) => l !== sp.hover && l !== sp.preview);
    sp.layers.push(...[sp.hover, sp.preview].filter(Boolean));
  };
  // another sheet / another tool: a half-done cut is dropped
  useEffect(() => {
    if (spliceRef.current) cancelSplice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum, mode]);
  // ── doors / windows on a wall: placed where you click, dragged to line up with the opening ──
  const placeDoorRef = useRef(null); // { shapeId, kind, w (ft) }
  const [placingDoor, setPlacingDoor] = useState(null);
  function cancelPlaceDoor() {
    placeDoorRef.current = null;
    setPlacingDoor(null);
    if (baseToolRef.current === "pan") mapRef.current?.dragging.enable();
    setMapCursor(baseToolRef.current === "pan" ? "grab" : "default");
  }
  handlersRef.current.cancelPlaceDoor = cancelPlaceDoor;
  function placeDoorAt(p) {
    const pd = placeDoorRef.current;
    cancelPlaceDoor();
    const sh = stateRef.current.shapes.find((x) => x.id === pd?.shapeId);
    if (!sh) return;
    const f =
      stateRef.current.scales[sh.page] ??
      stateRef.current.scales[String(sh.page)];
    const proj = projectOnLine(sh.latlngs, p);
    if (!proj || !f) return;
    const seg = segOnLine(sh.latlngs, proj, pd.w / f);
    const center = [(seg[0][0] + seg[1][0]) / 2, (seg[0][1] + seg[1][1]) / 2];
    const ds = doorSizes(stateRef.current.traceSettings);
    setShapes((prev) =>
      prev.map((x) => {
        if (x.id !== sh.id) return x;
        const m = x.meta || {};
        return {
          ...x,
          meta: {
            ...m,
            doors: [...(m.doors || []), pd.w],
            doorKinds: [
              ...(m.doorKinds || (m.doors || []).map(() => "single")),
              pd.kind,
            ],
            doorSegs: [...(m.doorSegs || []), seg],
            doorLf: Math.round(((Number(m.doorLf) || 0) + pd.w) * 100) / 100,
            doorHeight: m.doorHeight || ds.height,
          },
        };
      }),
    );
    addCountMarker(pd.kind === "double" ? "door2" : "door", center, sh.page);
    flash(
      `${pd.kind === "double" ? "Double door" : "Door"} ${fmtFeetIn(pd.w)} deducted and counted — drag it along the wall to adjust.`,
      "ok",
    );
  }
  // a door / window deduction dragged along its wall: save it, and bring its count marker along
  function moveOpening(shapeId, field, index, from, to) {
    const c0 = [(from[0][0] + from[1][0]) / 2, (from[0][1] + from[1][1]) / 2];
    const c1 = [(to[0][0] + to[1][0]) / 2, (to[0][1] + to[1][1]) / 2];
    const roles = field === "doorSegs" ? ["door", "door2"] : ["window"];
    const reach = Math.max(
      Math.hypot(from[1][0] - from[0][0], from[1][1] - from[0][1]),
      pxTol(30),
    );
    setShapes((prev) => {
      const sh = prev.find((x) => x.id === shapeId);
      let marker = null;
      let best = Infinity;
      for (const x of prev)
        if (
          x.kind === "marker" &&
          x.page === sh?.page &&
          roles.includes(x.meta?.role)
        ) {
          const q = x.latlngs?.[0];
          const d = q ? Math.hypot(q[0] - c0[0], q[1] - c0[1]) : Infinity;
          if (d < best && d <= reach) {
            best = d;
            marker = x.id;
          }
        }
      return prev.map((x) => {
        if (x.id === shapeId) {
          const segs = [...(x.meta?.[field] || [])];
          segs[index] = to;
          return { ...x, meta: { ...x.meta, [field]: segs } };
        }
        if (x.id === marker) return { ...x, latlngs: [c1] };
        return x;
      });
    });
  }
  function addCountMarker(key, ll, page) {
    const c = ensureCountCondition(key);
    const h = key === "bead" ? floorHeightOf(page) : null;
    setShapes((prev) => [
      ...prev,
      {
        id: uid(),
        conditionId: c.id,
        page,
        kind: "marker",
        latlngs: [ll],
        meta: { source: "manual", role: key, ...(h ? { height: h } : {}) },
      },
    ]);
  }
  // ── typical units: a group of takeoff (one unit's walls, ceilings, counts) that the building repeats.
  //    Every shape in the unit counts `qty` times in the quantities and the estimate.
  const UNIT_COLORS = [
    "#fb923c",
    "#38bdf8",
    "#a78bfa",
    "#34d399",
    "#f472b6",
    "#facc15",
  ];
  function createUnit({ ids, name, qty }) {
    const q = Math.max(0, Math.round(Number(qty) || 0));
    if (!ids?.length || !q)
      return flash(
        "Give the unit a quantity (how many of them the building has).",
        "warn",
      );
    const id = uid();
    const nm =
      (name || "").trim() ||
      `Unit ${String.fromCharCode(65 + (units.length % 26))}`;
    const color = UNIT_COLORS[units.length % UNIT_COLORS.length];
    const moved = shapes.filter(
      (x) => ids.includes(x.id) && x.meta?.unitId,
    ).length;
    setUnits((prev) => [...prev, { id, name: nm, qty: q, color }]);
    setShapes((prev) =>
      prev.map((x) =>
        ids.includes(x.id)
          ? { ...x, meta: { ...(x.meta || {}), unitId: id } }
          : x,
      ),
    );
    setUnitDraft(null);
    flash(
      `“${nm}” × ${q}: ${ids.length} item${ids.length === 1 ? "" : "s"} now count ${q} times${moved ? ` (${moved} moved from another unit)` : ""}.`,
      "ok",
    );
  }
  const updateUnit = (id, patch) =>
    setUnits((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  function removeUnit(id) {
    setUnits((prev) => prev.filter((u) => u.id !== id));
    setShapes((prev) =>
      prev.map((x) =>
        x.meta?.unitId === id
          ? { ...x, meta: { ...x.meta, unitId: undefined } }
          : x,
      ),
    );
  }
  function addToUnit(id, ids) {
    setShapes((prev) =>
      prev.map((x) =>
        ids.includes(x.id)
          ? { ...x, meta: { ...(x.meta || {}), unitId: id || undefined } }
          : x,
      ),
    );
  }
  function selectUnit(id) {
    const list = shapes.filter((x) => x.meta?.unitId === id);
    if (!list.length) return;
    if (!list.some((x) => x.page === pageNum)) setPageNum(list[0].page);
    setSel(
      list
        .filter(
          (x) =>
            x.page ===
            (list.some((y) => y.page === pageNum) ? pageNum : list[0].page),
        )
        .map((x) => x.id),
    );
  }
  // a condition's description; one made from an assembly edits the assembly too (they stay in step)
  function setCondDescription(cond, text) {
    updateCondition(cond.id, { description: text });
    const aid =
      cond.assemblyId || assemblies.find((a) => a.conditionId === cond.id)?.id;
    if (aid)
      setAssemblies((prev) =>
        prev.map((a) =>
          a.id === aid
            ? {
                ...a,
                description: text,
                layers: text
                  .split("\n")
                  .map((x) => x.trim())
                  .filter(Boolean),
              }
            : a,
        ),
      );
  }
  // a wall the trace wasn't sure of (dashed): the estimator checked it — keep its type, draw it solid
  const isUnsureWall = (s) =>
    (s?.kind === "polyline" &&
      ["thickness", "check"].includes(s.meta?.tagSource) &&
      !s.meta?.tagApproved) ||
    (s?.kind === "marker" && !!s.meta?.needsCheck);
  // approve: a wall keeps its type (solid); a door / window marker is confirmed (filled)
  function approveWalls(ids) {
    const set = new Set(ids);
    setShapes((prev) =>
      prev.map((s) => {
        if (!set.has(s.id) || !isUnsureWall(s)) return s;
        if (s.kind === "marker")
          return {
            ...s,
            meta: { ...(s.meta || {}), needsCheck: undefined, approved: true },
          };
        return { ...s, meta: { ...(s.meta || {}), tagApproved: true } };
      }),
    );
    if (ids.length > 1) flash(`Approved ${ids.length}.`, "ok");
  }
  // ask the AI about selected walls (same check the trace runs): a confident answer is applied, else suggested
  const [aiAsking, setAiAsking] = useState(false);
  async function askAiForWalls(ids) {
    const list = shapes.filter(
      (s) => ids.includes(s.id) && s.kind === "polyline",
    );
    if (!list.length) return;
    const page = list[0].page;
    const fpu = scales[page] ?? scales[String(page)];
    if (!fpu) return flash("Set this sheet's scale first.", "warn");
    const types = assemblies.filter((a) => a.kind === "wall" && a.tag);
    if (!types.length)
      return flash(
        "No wall types with tags — read the assembly sheets first.",
        "warn",
      );
    setAiAsking(true);
    try {
      const g = await pageGeom(page);
      const results = await aiWallTags(
        page,
        list.filter((s) => s.page === page).slice(0, 40),
        types,
        {
          ftPerUnit: fpu * g.rs,
          tagCands: sheetGeo[page]?.tagCands || [],
        },
      );
      let fixed = 0,
        suggested = 0;
      const byId = new Map(results.map((r) => [r.id, r]));
      setShapes((prev) =>
        prev.map((s) => {
          const r = byId.get(s.id);
          if (!r || !r.tag) return s;
          const m = s.meta || {};
          const c = wallCondForTag(r.tag, m.wet);
          if (r.confidence === "high" && c) {
            fixed++;
            return {
              ...s,
              conditionId: c.id,
              meta: {
                ...m,
                wallTag: r.tag,
                tagSource: "ai",
                aiEvidence: r.evidence || "",
                tagCheck: undefined,
                tagApproved: undefined,
                aiSuggest: undefined,
              },
            };
          }
          suggested++;
          return {
            ...s,
            meta: {
              ...m,
              aiSuggest: {
                tag: r.tag,
                confidence: r.confidence,
                evidence: r.evidence || "",
              },
              ...(r.tag !== m.wallTag ? { tagAlt: r.tag } : {}),
            },
          };
        }),
      );
      flash(
        fixed || suggested
          ? `AI: ${fixed} wall${fixed === 1 ? "" : "s"} set${suggested ? `, ${suggested} suggestion${suggested === 1 ? "" : "s"} to check` : ""}.`
          : "AI couldn't tell from the drawing — set it by hand.",
        fixed || suggested ? "ok" : "warn",
      );
    } catch (e) {
      flash(`AI check failed: ${e.message}`, "warn");
    } finally {
      setAiAsking(false);
    }
  }
  // the condition a wall of this tag goes on (dry or washroom) — for "switch to the other likely type"
  function wallCondForTag(tag, wet) {
    const T = String(tag || "").toUpperCase();
    if (!T) return null;
    const a = assemblies.find(
      (x) => x.kind === "wall" && String(x.tag || "").toUpperCase() === T,
    );
    const find = (w) =>
      conditions.find(
        (c) =>
          c.type === "linear" &&
          !!c.wet === !!w &&
          ((a && (c.assemblyId === a.id || (!w && c.id === a.conditionId))) ||
            String(c.wallType || "").toUpperCase() === (w ? `${T}·WR` : T)),
      );
    // (a washroom wall with no washroom version of that type yet goes on the plain type)
    return find(wet) || (wet ? find(false) : null) || null;
  }
  function reassignShapes(ids, condId) {
    const set = new Set(ids);
    setShapes((prev) =>
      prev.map((s) =>
        set.has(s.id)
          ? {
              ...s,
              conditionId: condId,
              meta: { ...(s.meta || {}), tagSource: "manual" },
            }
          : s,
      ),
    );
    const c = conditions.find((x) => x.id === condId);
    if (ids.length > 1) flash(`${ids.length} → ${c?.name || "type"}.`, "ok");
  }
  useEffect(() => {
    if (selIds.length !== 1) return;
    const sh = shapes.find((x) => x.id === selIds[0]);
    if (!sh) return;
    setActiveCondId(sh.conditionId);
    // (the left panel stays as it is — hidden means hidden; the details show in the right panel)
    setRightOpen(true);
    requestAnimationFrame(() =>
      document
        .querySelector("[data-selection-panel]")
        ?.scrollIntoView({ block: "nearest" }),
    );
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        (
          document.querySelector(`[data-shape-row="${sh.id}"]`) ||
          document.querySelector(`[data-cond-card="${sh.conditionId}"]`)
        )?.scrollIntoView({ block: "nearest", behavior: "smooth" }),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selIds]);
  // a single selected area / line shows its corner handles straight away — drag a corner to resize, drag a
  // midpoint to add a corner, right-click a corner to remove it (no separate Edit step)
  const autoEditRef = useRef(null);
  useEffect(() => {
    const want = isNeutral(mode) && selIds.length === 1 ? selIds[0] : null;
    const prevId = autoEditRef.current;
    if (prevId && prevId !== want) {
      const l = layerMapRef.current.get(prevId);
      if (l?.pm && editingShapeId !== prevId) l.pm.disable();
      autoEditRef.current = null;
    }
    if (!want || want === autoEditRef.current || editingShapeId === want)
      return;
    const sh = stateRef.current.shapes.find((x) => x.id === want);
    if (!sh || sh.page !== pageNum || sh.kind === "marker" || sh.meta?.headerOf)
      return;
    const l = layerMapRef.current.get(want);
    if (!l?.pm) return;
    l.pm.enable({
      allowSelfIntersection: false,
      hideMiddleMarkers: false,
      preventMarkerRemoval: false,
      snappable: false,
    });
    autoEditRef.current = want;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selIds, mode, pageNum, editingShapeId]);

  // windows deducted from a wall: drawn on top of the line so you can see what was taken off
  const winLayersRef = useRef([]);
  useEffect(() => {
    const map = mapRef.current;
    winLayersRef.current.forEach((l) => map?.removeLayer(l));
    winLayersRef.current = [];
    if (!map || !mapReady) return;
    const f = scales[pageNum] ?? scales[String(pageNum)];
    // windows cyan, doors magenta — both drawn on the line, both deducted
    for (const sh of shapes) {
      if (sh.page !== pageNum || hiddenConds.has(sh.conditionId)) continue;
      const sets = [
        ...(sh.meta?.windowSegs?.length && sh.meta.deductLf > 0
          ? [["Window", "#22d3ee", sh.meta.windowSegs, sh.meta.windowHeight]]
          : []),
        ...(sh.meta?.doorSegs?.length && sh.meta.doorLf > 0
          ? [
              [
                "Door",
                "#e879f9",
                sh.meta.doorSegs,
                sh.meta.doorHeight || DEFAULT_DOOR_H,
              ],
            ]
          : []),
      ];
      const cond = conditions.find((c) => c.id === sh.conditionId);
      const bandPx = cond ? inchesToPx(condLineIn(cond), sh.page) : null;
      for (const [what, color, segs, hh] of sets)
        for (const seg of segs) {
          const isDoor = what === "Door";
          const di = segs.indexOf(seg);
          // a door: a swinging leaf + arc (single or double) drawn on the opening — never a dot, so a
          // deduction doesn't look like a door count. Double-click a door to swing it the other way.
          if (isDoor) {
            const kind = sh.meta?.doorKinds?.[di] || "single";
            const flip = Number(sh.meta?.doorFlips?.[di]) || 0;
            for (const pl of doorSwingLines(seg, kind, flip)) {
              const sw = L.polyline(pl, {
                color,
                weight: 2,
                opacity: 0.95,
                lineCap: "round",
                interactive: false,
              }).addTo(map);
              winLayersRef.current.push(sw);
            }
          }
          const l = L.polyline(seg, {
            color,
            weight: Math.max(isDoor ? 5 : 7, Math.min(60, (bandPx || 0) + 2)),
            opacity: isDoor ? 0.4 : 0.55,
            lineCap: "butt",
            interactive: true,
            bubblingMouseEvents: true,
          });
          l._band = {
            inches: cond ? condLineIn(cond) : null,
            page: sh.page,
            min: isDoor ? 5 : 7,
          };
          if (isDoor)
            l.on("dblclick", (ev) => {
              L.DomEvent.stop(ev);
              flipDoorSwing(sh.id, di);
            });
          const len = f
            ? Math.hypot(seg[1][0] - seg[0][0], seg[1][1] - seg[0][1]) * f
            : null;
          l.bindTooltip(
            `${what} — deducted${len ? ` ${fmtFeetIn(len)}` : ""}${hh ? ` × ${fmtFeetIn(Number(hh))}` : ""} · drag along the wall to line it up${isDoor ? " · double-click to swing it the other way" : ""}`,
            { sticky: true },
          );
          l.on("click", (ev) => {
            if (!isNeutral(stateRef.current.mode)) return;
            L.DomEvent.stop(ev);
            lastShapeClickRef.current = Date.now();
            setSel([sh.id]);
          });
          // drag a door / window along its wall to line it up with the opening on the drawing
          const segIndex = segs.indexOf(seg);
          l.on("mousedown", (ev) => {
            if (
              !isNeutral(stateRef.current.mode) ||
              ev.originalEvent?.button !== 0
            )
              return;
            L.DomEvent.stop(ev);
            ev.originalEvent?.preventDefault?.();
            const segLen = Math.hypot(
              seg[1][0] - seg[0][0],
              seg[1][1] - seg[0][1],
            );
            const ll = sh.latlngs;
            let cur = null;
            map.dragging.disable();
            map.getContainer().style.cursor = "grabbing";
            const move = (e2) => {
              const proj = projectOnLine(ll, [e2.latlng.lat, e2.latlng.lng]);
              if (!proj) return;
              cur = segOnLine(ll, proj, segLen);
              l.setLatLngs(cur);
            };
            const up = () => {
              map.off("mousemove", move);
              map.off("mouseup", up);
              if (baseToolRef.current === "pan") map.dragging.enable();
              map.getContainer().style.cursor = "";
              if (cur)
                moveOpening(
                  sh.id,
                  what === "Door" ? "doorSegs" : "windowSegs",
                  segIndex,
                  seg,
                  cur,
                );
            };
            map.on("mousemove", move);
            map.on("mouseup", up);
          });
          l.addTo(map);
          winLayersRef.current.push(l);
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapes, pageNum, mapReady, scales, conditions, hiddenConds]);
  // a door's swing: other side → other jamb → … (it's only a picture — the deduction is the same)
  function flipDoorSwing(shapeId, i) {
    setShapes((prev) =>
      prev.map((x) => {
        if (x.id !== shapeId) return x;
        const m = x.meta || {};
        const fl = [...(m.doorFlips || [])];
        while (fl.length <= i) fl.push(0);
        fl[i] = ((Number(fl[i]) || 0) + 1) % 4;
        return { ...x, meta: { ...m, doorFlips: fl } };
      }),
    );
  }

  // door headers: kept in step with every door deduction (see planDoorHeaders) — a door added, moved, resized or
  // deleted, a wall's height or assembly changed → its header follows
  useEffect(() => {
    const plan = planDoorHeaders(shapes, conditions, scales);
    if (plan.condsChanged) setConditions(plan.conditions);
    if (plan.shapesChanged) setShapes(plan.shapes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapes, conditions, scales]);

  // typical units: a dashed box around each unit's takeoff on this sheet, labelled "name × qty"
  const unitLayersRef = useRef([]);
  useEffect(() => {
    const map = mapRef.current;
    unitLayersRef.current.forEach((l) => map?.removeLayer(l));
    unitLayersRef.current = [];
    if (!map || !mapReady) return;
    for (const u of units) {
      const list = shapes.filter(
        (x) => x.page === pageNum && x.meta?.unitId === u.id,
      );
      if (!list.length) continue;
      const b = L.latLngBounds(list.flatMap((x) => x.latlngs));
      const pad = pxTol(14);
      const bb = L.latLngBounds(
        [b.getSouth() - pad, b.getWest() - pad],
        [b.getNorth() + pad, b.getEast() + pad],
      );
      const r = L.rectangle(bb, {
        color: u.color || "#fb923c",
        weight: 1.5,
        dashArray: "8 6",
        fill: false,
        interactive: false,
      }).addTo(map);
      const lab = L.marker([bb.getNorth(), bb.getWest()], {
        interactive: true,
        icon: L.divIcon({
          className: "",
          iconSize: [0, 0],
          html: `<div style="transform:translate(0,-100%);width:max-content;background:${u.color || "#fb923c"};color:#111827;font:700 11px system-ui;padding:2px 7px;border-radius:5px 5px 5px 0;cursor:pointer">${u.name.replace(/[<>&]/g, "")} × ${u.qty}</div>`,
        }),
      }).addTo(map);
      lab.on("click", (ev) => {
        L.DomEvent.stop(ev);
        lastShapeClickRef.current = Date.now();
        handlersRef.current.selectUnit?.(u.id);
      });
      unitLayersRef.current.push(r, lab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, shapes, pageNum, mapReady]);
  handlersRef.current.selectUnit = selectUnit;

  // a selected line shows a grip at each end — click one to keep drawing from that end
  const gripLayersRef = useRef([]);
  useEffect(() => {
    const map = mapRef.current;
    gripLayersRef.current.forEach((l) => map?.removeLayer(l));
    gripLayersRef.current = [];
    if (!map || !mapReady || !isNeutral(mode) || selIds.length !== 1) return;
    const sh = shapes.find((x) => x.id === selIds[0]);
    if (
      !sh ||
      sh.kind !== "polyline" ||
      sh.page !== pageNum ||
      sh.latlngs.length < 2
    )
      return;
    const ends = [
      ["start", sh.latlngs[0]],
      ["end", sh.latlngs[sh.latlngs.length - 1]],
    ];
    for (const [which, ll] of ends) {
      const g = L.circleMarker(ll, {
        radius: 8,
        color: "#fff",
        weight: 2.5,
        fillColor: "#f97316",
        fillOpacity: 1,
        bubblingMouseEvents: false,
        className: "takeoff-grip",
      }).addTo(map);
      g.bindTooltip(`Keep drawing from this ${which}`, {
        direction: "top",
        offset: [0, -8],
      });
      g.on("click", (ev) => {
        L.DomEvent.stop(ev);
        lastShapeClickRef.current = Date.now();
        startExtendShape(sh, ll);
      });
      g.on("mousedown", (ev) => L.DomEvent.stop(ev));
      gripLayersRef.current.push(g);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selIds, mode, shapes, pageNum, mapReady]);

  function setShapeHeight(ids, h) {
    const set = new Set(ids);
    // the assemblies these walls belong to follow too, so the left panel's height field matches
    const condIds = new Set(
      stateRef.current.shapes
        .filter((s) => set.has(s.id))
        .map((s) => s.conditionId),
    );
    setShapes((prev) =>
      prev.map((s) =>
        set.has(s.id)
          ? { ...s, meta: { ...(s.meta || {}), height: h, heightManual: true } }
          : s,
      ),
    );
    setConditions((prev) =>
      prev.map((c) =>
        condIds.has(c.id) && c.type === "linear" ? { ...c, height: h } : c,
      ),
    );
  }
  // tooltips follow height / quantity changes
  useEffect(() => {
    layerMapRef.current.forEach((layer, id) => {
      const sh = shapes.find((x) => x.id === id);
      if (sh && layer.setTooltipContent)
        layer.setTooltipContent(tooltipHtml(sh));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapes, conditions, scales]);

  // fullscreen + shortcuts + autosave ------------------------------------------------------------
  // Full screen = (1) lift the workspace out of the app shell (sidebar, header, tab bar) and
  // (2) ask the browser for real fullscreen. If the browser refuses or the user presses Esc,
  // the workspace still covers the whole window until "Exit full screen" is clicked.
  useEffect(() => {
    onImmersiveChange?.(immersive || isFullscreen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immersive, isFullscreen]);
  function toggleFullscreen() {
    const on = isFullscreen || immersive;
    if (!on) {
      if (!inShell) setImmersive(true);
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else {
      setImmersive(false);
      if (document.fullscreenElement)
        document.exitFullscreen?.().catch(() => {});
    }
  }
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  useEffect(() => {
    const t = setTimeout(
      () => mapRef.current?.invalidateSize({ animate: false }),
      220,
    );
    return () => clearTimeout(t);
  }, [leftOpen, rightOpen, isFullscreen, immersive]);
  handlersRef.current.mode = () => stateRef.current.mode;
  handlersRef.current.extend = startExtendShape;
  handlersRef.current.drawWith = (condId) => {
    const c = stateRef.current.conditions.find((x) => x.id === condId);
    if (!c) return;
    startDraw(c);
    flash(
      `Drawing ${c.name} — click to place points, Enter or double-click to finish.`,
      "info",
    );
  };
  handlersRef.current.finishDraft = finishDraft;
  handlersRef.current.undo = undo;
  handlersRef.current.smartUndo = smartUndo;
  handlersRef.current.redo = redo;
  handlersRef.current.popPoint = popPoint;
  handlersRef.current.rightClick = () => {
    const m = stateRef.current.mode;
    if ((m === "draw" || m === "extend") && drawRef.current) finishDraft();
  };
  handlersRef.current.boxSelect = (bounds, add) => {
    const st = stateRef.current;
    const x0 = bounds.getWest(),
      x1 = bounds.getEast(),
      y0 = bounds.getSouth(),
      y1 = bounds.getNorth();
    const inside = ([y, x]) => x >= x0 && x <= x1 && y >= y0 && y <= y1;
    const cross = (a, b) => {
      // segment a-b touches the box
      if (inside(a) || inside(b)) return true;
      const edges = [
        [
          [y0, x0],
          [y0, x1],
        ],
        [
          [y0, x1],
          [y1, x1],
        ],
        [
          [y1, x1],
          [y1, x0],
        ],
        [
          [y1, x0],
          [y0, x0],
        ],
      ];
      const o = (p, q, r) =>
        Math.sign(
          (q[1] - p[1]) * (r[0] - p[0]) - (q[0] - p[0]) * (r[1] - p[1]),
        );
      return edges.some(
        ([c, d]) => o(a, b, c) !== o(a, b, d) && o(c, d, a) !== o(c, d, b),
      );
    };
    const hit = st.shapes
      .filter((sh) => {
        if (sh.page !== st.pageNum) return false;
        if (hiddenRef.current?.has(sh.conditionId)) return false; // hidden: not on the drawing, not selectable
        const ll = sh.latlngs;
        if (sh.kind === "marker") return inside(ll[0]);
        for (let i = 0; i + 1 < ll.length; i++)
          if (cross(ll[i], ll[i + 1])) return true;
        return (
          sh.kind === "polygon" &&
          ll.length > 2 &&
          cross(ll[ll.length - 1], ll[0])
        );
      })
      .map((sh) => sh.id);
    setSel(add ? [...selRef.current, ...hit] : hit);
    if (hit.length)
      flash(
        `${hit.length} selected — Delete removes them, or change their assembly below.`,
        "info",
      );
  };
  handlersRef.current.kb = {
    selected: () => selRef.current.size > 0,
    deleteSelected: () => {
      const all = [...selRef.current];
      // door headers follow their doors — they aren't deleted on their own
      const ids = all.filter(
        (id) =>
          !stateRef.current.shapes.find((s) => s.id === id)?.meta?.headerOf,
      );
      ids.forEach((id) => deleteShape(id));
      setSel([]);
      if (ids.length < all.length)
        flash(
          "Door headers follow their doors — delete the door, or turn “Door headers” off on the wall's assembly.",
          "info",
        );
      else if (ids.length > 1) flash(`Deleted ${ids.length} shapes.`, "ok");
    },
    clearSelected: () => setSel([]),
    approveSelected: () => {
      const ids = [...selRef.current].filter((id) =>
        isUnsureWall(stateRef.current.shapes.find((s) => s.id === id)),
      );
      if (ids.length) approveWalls(ids);
      return ids.length > 0;
    },
    startCalibrate,
    startMeasure,
    startFill: () => askRooms("fill"),
    toggleEditMode,
    toggleDeleteMode,
    toggleFullscreen,
    setLeftOpen,
    setRightOpen,
  };
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (
        t?.tagName === "INPUT" ||
        t?.tagName === "TEXTAREA" ||
        t?.tagName === "SELECT" ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey
      )
        return;
      const k = handlersRef.current.kb;
      const key = e.key.toLowerCase();
      if ((key === "delete" || key === "backspace") && k.selected?.()) {
        e.preventDefault();
        k.deleteSelected();
        return;
      }
      if (key === "escape" && k.selected?.()) {
        k.clearSelected();
        return;
      }
      if (key === "a" && k.selected?.() && k.approveSelected?.()) {
        e.preventDefault();
        return;
      }
      if (key === " " || key === "v" || key === "h") return;
      if (key === "c") k.startCalibrate();
      else if (key === "m") k.startMeasure();
      else if (key === "r") k.startFill();
      else if (key === "e") k.toggleEditMode();
      else if (key === "x") k.toggleDeleteMode();
      else if (key === "f") k.toggleFullscreen();
      else if (key === "[") k.setLeftOpen((v) => !v);
      else if (key === "]") k.setRightOpen((v) => !v);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  // Always saving: 1.5 s after a change, and at least every 10 s while changes keep coming (a long stretch
  // of drawing never waits for a pause). Saves queue one at a time, each with the newest state.
  const dirtySinceRef = useRef(0);
  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    if (!dirtyRef.current) dirtySinceRef.current = Date.now();
    dirtyRef.current = true;
    if (!onSave) return;
    const wait = Math.max(
      0,
      Math.min(1500, 10000 - (Date.now() - dirtySinceRef.current)),
    );
    const t = setTimeout(() => {
      if (dirtyRef.current) handleSave({ quiet: true }).catch(() => {});
    }, wait);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    conditions,
    shapes,
    scales,
    pageNames,
    pageBidAreas, // (sheet bid areas weren't saving)
    coPins,
    excludedPages,
    proposals,
    enabledTags,
    tagSideAware,
    tagCondMap,
    assemblies,
    traceSettings,
    units,
    scaleInfo,
    sheetRevisions,
    fileName,
    gcsPath,
    pageCount,
    status,
  ]);
  // switching tabs / apps, or the page going away: save right then
  useEffect(() => {
    const flush = () => {
      if (dirtyRef.current) saveRef.current?.({ quiet: true }).catch(() => {});
    };
    const onVis = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", flush);
    };
  }, []);
  // a failed save (network blip) retries on its own every 15 s until it goes through
  useEffect(() => {
    if (!saveError || !onSave) return;
    const t = setInterval(() => {
      if (dirtyRef.current) handleSave({ quiet: true }).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveError]);
  // leaving the takeoff (Back, closing the launcher, switching takeoffs): save what hasn't been saved yet
  useEffect(
    () => () => {
      if (dirtyRef.current) saveRef.current?.({ quiet: true }).catch(() => {});
    },
    [],
  );
  // closing the browser tab with unsaved work: ask first
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (dirtyRef.current || saving) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [saving]);

  return (
    <MovableHost fullscreen={immersive && !inShell}>
      <div
        ref={rootRef}
        className={`relative flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-gray-950 text-gray-200 ${className}`}
      >
        {scaleRun?.blocking && (
          <ScaleLoadOverlay
            run={scaleRun}
            label={scaleRun.cur ? pageLabel(scaleRun.cur) : ""}
            onSkip={() => {
              scaleRunRef.current++; // the run stops at its next sheet
              setScaleRun(null);
            }}
          />
        )}
        {/* ═══════════════ HEADER ═══════════════ */}
        <header className="relative shrink-0 border-b border-white/5 bg-gray-900/95">
          <div className="flex h-14 items-center gap-3 px-3">
            {/* left: drawing + scale (the left panel is only assemblies) */}
            <div className="flex min-w-0 items-center gap-1.5">
              <IconBtn
                title="Assemblies panel  ( [ )"
                active={leftOpen}
                onClick={() => setLeftOpen((v) => !v)}
              >
                <Icon name="panelLeft" />
              </IconBtn>
              {(drawingMenu || scaleMenu) && (
                <div
                  className="fixed inset-0 z-[1290]"
                  onClick={() => {
                    setDrawingMenu(false);
                    setScaleMenu(false);
                  }}
                />
              )}

              {/* drawing */}
              <div className="relative">
                <button
                  onClick={() => {
                    setDrawingMenu((v) => !v);
                    setScaleMenu(false);
                  }}
                  title={fileName || "Open a drawing"}
                  className={`flex h-10 max-w-[12rem] items-center gap-2 rounded-lg px-2 text-left xl:max-w-[16rem] 2xl:max-w-[22rem] ${drawingMenu ? "bg-white/10" : "hover:bg-white/5"}`}
                >
                  <Icon
                    name="file"
                    size={15}
                    className="shrink-0 text-gray-500"
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-[12.5px] font-semibold text-white">
                        {fileName || "No drawing open"}
                      </span>
                      <span
                        className={`hidden shrink-0 rounded-full px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide 2xl:inline ${status === "published" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}
                      >
                        {status}
                      </span>
                      {peers.length > 0 && (
                        <span
                          className="flex shrink-0 -space-x-1.5"
                          title={`Also working on this takeoff: ${peers.map((p) => `${p.user?.name || "Someone"}${p.page ? ` (${pageLabel(p.page)})` : ""}`).join(", ")}`}
                        >
                          {peers.slice(0, 4).map((p) => (
                            <Avatar
                              key={p.clientId}
                              name={p.user?.name}
                              size={18}
                              ring
                            />
                          ))}
                          {peers.length > 4 && (
                            <span className="ml-2 text-[10px] text-gray-400">
                              +{peers.length - 4}
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                    <div className="truncate text-[10.5px] text-gray-500">
                      {pageCount
                        ? `${pageCount} sheet${pageCount === 1 ? "" : "s"}`
                        : "Open a PDF"}
                      {uploadingPdf ? " · uploading drawing…" : ""}
                      {onSave &&
                        (fileName || takeoffId) &&
                        (saving ? (
                          " · saving…"
                        ) : saveError ? (
                          <span className="text-red-400"> · not saved</span>
                        ) : savedAt ? (
                          ` · saved ${savedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
                        ) : (
                          <span className="text-amber-400">
                            {" "}
                            · not saved yet
                          </span>
                        ))}
                    </div>
                  </div>
                  <Icon
                    name="chevD"
                    size={13}
                    className="shrink-0 text-gray-500"
                  />
                </button>
                {drawingMenu && (
                  <div className="absolute left-0 top-11 z-[1300] w-[36rem] max-w-[calc(100vw-2rem)] rounded-xl border border-white/10 bg-gray-900 p-3 shadow-2xl">
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        {renameOpen ? (
                          <input
                            autoFocus
                            value={renameVal}
                            onChange={(e) => setRenameVal(e.target.value)}
                            onBlur={renameDrawing}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") renameDrawing();
                              if (e.key === "Escape") {
                                setRenameOpen(false);
                                setRenameErr("");
                              }
                            }}
                            className="w-full rounded-md border border-orange-500 bg-gray-950 px-2 py-1 text-[12px] text-white focus:outline-none"
                          />
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <span className="truncate text-[12px] font-medium text-gray-100">
                              {fileName || "No drawing open"}
                            </span>
                            {fileName && (
                              <button
                                onClick={() => {
                                  setRenameVal(fileName);
                                  setRenameOpen(true);
                                }}
                                title="Rename drawing"
                                className="shrink-0 rounded bg-white/5 px-1.5 py-px text-[10px] text-gray-400 hover:bg-white/10 hover:text-white"
                              >
                                Rename
                              </button>
                            )}
                          </div>
                        )}
                        {renameErr && (
                          <div className="text-[10.5px] text-red-300">
                            {renameErr}
                          </div>
                        )}
                        <div className="text-[10.5px] text-gray-500">
                          {pageCount ? `${pageCount} sheets` : "—"}
                          {gcsPath ? " · stored with the takeoff" : ""}
                        </div>
                      </div>
                      <label
                        title="Open a PDF"
                        className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg bg-orange-500 px-2.5 text-[12px] font-semibold text-white hover:bg-orange-400"
                      >
                        <Icon name="plus" size={13} />
                        {pdfDoc ? "Replace PDF" : "Open PDF"}
                        <input
                          type="file"
                          accept="application/pdf"
                          className="hidden"
                          onChange={(e) => {
                            setDrawingMenu(false);
                            handleFilePick(e);
                          }}
                        />
                      </label>
                    </div>
                    {pdfDoc && onUploadDrawing && (
                      <div className="mt-2 flex items-center gap-1.5 text-[11px]">
                        <span className="text-gray-500">Revisions</span>
                        <button
                          disabled={!!revBusy}
                          onClick={() => {
                            setDrawingMenu(false);
                            revFileRef.current?.click();
                          }}
                          className="rounded-md bg-white/5 px-2 py-1 text-gray-200 hover:bg-white/10 disabled:opacity-40"
                        >
                          + This sheet
                        </button>
                        <button
                          disabled={!!revBusy}
                          onClick={() => {
                            setDrawingMenu(false);
                            revBulkRef.current?.click();
                          }}
                          className="rounded-md bg-white/5 px-2 py-1 text-gray-200 hover:bg-white/10 disabled:opacity-40"
                        >
                          Update from a new set…
                        </button>
                        {Object.keys(sheetRevisions).length > 0 && (
                          <span className="ml-auto text-gray-500">
                            {Object.keys(sheetRevisions).length} sheet
                            {Object.keys(sheetRevisions).length === 1
                              ? ""
                              : "s"}{" "}
                            revised
                          </span>
                        )}
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-1.5 text-[11px]">
                      <span className="text-gray-500">
                        {saving
                          ? "Saving…"
                          : saveError
                            ? "Not saved"
                            : savedAt
                              ? `Saved ${savedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · saves as you work`
                              : "Saves as you work"}
                      </span>
                      <button
                        onClick={() => {
                          setDrawingMenu(false);
                          exportCsv();
                        }}
                        className="ml-auto inline-flex items-center gap-1 rounded-md bg-white/5 px-2 py-1 text-gray-200 hover:bg-white/10"
                      >
                        <Icon name="download" size={12} />
                        Export CSV
                      </button>
                    </div>
                    {pageCount > 0 && (
                      <div className="mt-3">
                        <div className="mb-1 flex items-center gap-1">
                          <span className="text-[10px] font-medium uppercase tracking-wider text-gray-500">
                            Sheets
                          </span>
                          <button
                            onClick={autoNameSheets}
                            disabled={autoNaming || !pdfDoc}
                            className="ml-auto rounded-md px-2 py-0.5 text-[11px] text-gray-400 hover:bg-white/5 hover:text-white disabled:opacity-40"
                          >
                            {autoNaming
                              ? `Naming ${autoNameProgress.done}/${autoNameProgress.total}…`
                              : "Auto-name"}
                          </button>
                        </div>
                        <datalist id="sheet-bid-areas">
                          {[
                            "Basement",
                            "Level 1",
                            "Level 2",
                            "Level 3",
                            "Level 4",
                            "Roof",
                          ].map((a) => (
                            <option key={a} value={a} />
                          ))}
                        </datalist>
                        <div className="max-h-[50vh] space-y-0.5 overflow-y-auto rounded-lg bg-black/20 p-1">
                          {Array.from(
                            { length: pageCount },
                            (_, i) => i + 1,
                          ).map((n) => {
                            const excluded = excludedPages.includes(n);
                            const scaled = !!(scales[n] ?? scales[String(n)]);
                            return (
                              <div
                                key={n}
                                className={`group flex items-center gap-1.5 rounded-md px-1.5 py-1 ${n === pageNum ? "bg-orange-500/10" : "hover:bg-white/5"} ${excluded ? "opacity-45" : ""}`}
                              >
                                <button
                                  onClick={() => !excluded && setPageNum(n)}
                                  disabled={excluded}
                                  className="w-6 shrink-0 text-right font-mono text-[11px] text-gray-500"
                                >
                                  {n}
                                </button>
                                <input
                                  title={
                                    pageNames[n] ?? pageNames[String(n)] ?? ""
                                  }
                                  value={
                                    pageNames[n] ?? pageNames[String(n)] ?? ""
                                  }
                                  onChange={(e) =>
                                    setPageNames((prev) => ({
                                      ...prev,
                                      [n]: e.target.value,
                                    }))
                                  }
                                  placeholder={`Sheet ${n}`}
                                  className="min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 text-[13px] text-gray-100 placeholder-gray-600 focus:border-white/10 focus:bg-gray-950 focus:outline-none"
                                />
                                <BidAreaCombobox
                                  className="w-28 shrink-0 rounded bg-white/5 px-1.5 py-0.5"
                                  value={
                                    pageBidAreas[n] ??
                                    pageBidAreas[String(n)] ??
                                    ""
                                  }
                                  onChange={(v) =>
                                    setPageBidAreas((prev) => ({
                                      ...prev,
                                      [n]: v,
                                    }))
                                  }
                                  options={bidAreaOptions}
                                />
                                {!excluded && (
                                  <span
                                    title={scaled ? "Scale set" : "No scale"}
                                    className={`h-1.5 w-1.5 shrink-0 rounded-full ${scaled ? "bg-emerald-400" : "bg-red-400"}`}
                                  />
                                )}
                                <button
                                  onClick={() =>
                                    setExcludedPages((prev) =>
                                      prev.includes(n)
                                        ? prev.filter((p) => p !== n)
                                        : [...prev, n],
                                    )
                                  }
                                  className="shrink-0 rounded px-1.5 py-0.5 text-[10px] text-gray-500 opacity-0 hover:bg-white/10 hover:text-gray-200 group-hover:opacity-100"
                                  title={
                                    excluded
                                      ? "Include sheet"
                                      : "Exclude sheet from takeoff"
                                  }
                                >
                                  {excluded ? "Include" : "Exclude"}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* scale: this sheet's, plus the whole set's check */}
              {(() => {
                const curInfo =
                  scaleInfo[pageNum] || scaleInfo[String(pageNum)];
                const incl = Array.from(
                  { length: pageCount },
                  (_, i) => i + 1,
                ).filter((n) => !excludedPages.includes(n));
                const hasS = (n) => !!(scales[n] ?? scales[String(n)]);
                const infoOf = (n) => scaleInfo[n] || scaleInfo[String(n)];
                const flagged = incl.filter((n) => !hasS(n));
                const warned = incl.filter((n) => hasS(n) && infoOf(n)?.warn);
                const srcText = (inf) =>
                  !inf
                    ? "set"
                    : inf.source === "printed+dims"
                      ? "printed · checked against dimensions"
                      : inf.source === "dimensions"
                        ? "read from the dimensions"
                        : inf.source === "printed"
                          ? "printed scale"
                          : inf.source === "preset"
                            ? "preset"
                            : inf.source === "calibrated"
                              ? "calibrated"
                              : inf.source === "suggested"
                                ? "suggested — check it"
                                : "set";
                // sheets with nothing to scale (covers, schedules, specs) go last and can be excluded in one go
                const blank = flagged.filter((n) =>
                  /not a plan sheet/.test(infoOf(n)?.reason || ""),
                );
                const actionable = flagged.filter((n) => !blank.includes(n));
                const withSuggest = flagged.filter((n) => infoOf(n)?.suggest);
                const rows = [
                  ...actionable,
                  ...warned,
                  ...blank,
                  ...incl.filter((n) => hasS(n) && !infoOf(n)?.warn),
                ];
                return (
                  <div className="relative">
                    <button
                      onClick={() => {
                        setScaleMenu((v) => !v);
                        setDrawingMenu(false);
                      }}
                      disabled={!pdfDoc}
                      title={
                        fpu
                          ? `${curInfo?.label || "Scale set"} — ${srcText(curInfo)}`
                          : "This sheet has no scale"
                      }
                      className={`flex h-10 items-center gap-2 rounded-lg px-2.5 text-left ring-1 disabled:opacity-40 ${scaleMenu ? "bg-white/10 ring-white/15" : !pdfDoc || fpu ? "ring-white/10 hover:bg-white/5" : "bg-red-500/10 ring-red-500/40 hover:bg-red-500/15"}`}
                    >
                      <Icon
                        name="ruler"
                        size={15}
                        className={fpu ? "text-gray-400" : "text-red-300"}
                      />
                      <div className="min-w-0">
                        <div className="text-[9.5px] font-medium uppercase tracking-wider text-gray-500">
                          Scale
                        </div>
                        <div
                          className={`max-w-[9rem] truncate text-[12px] font-medium ${scaleRun ? "text-orange-200" : fpu ? (curInfo?.warn ? "text-amber-200" : "text-gray-100") : "text-red-200"}`}
                        >
                          {scaleRun
                            ? `Checking ${scaleRun.done}/${scaleRun.total}…`
                            : !pdfDoc
                              ? "—"
                              : fpu
                                ? curInfo?.label || "set"
                                : "not set"}
                        </div>
                      </div>
                      {flagged.length > 0 && !scaleRun && (
                        <span
                          title={`${flagged.length} sheet(s) need a scale`}
                          className="rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white"
                        >
                          {flagged.length}
                        </span>
                      )}
                      {flagged.length === 0 &&
                        warned.length > 0 &&
                        !scaleRun && (
                          <span
                            title={`${warned.length} sheet(s) worth a check`}
                            className="rounded-full bg-amber-500/80 px-1.5 text-[10px] font-semibold text-gray-950"
                          >
                            {warned.length}
                          </span>
                        )}
                      <Icon name="chevD" size={13} className="text-gray-500" />
                    </button>
                    {scaleMenu && (
                      <div className="absolute left-0 top-11 z-[1300] flex max-h-[78vh] w-[27rem] flex-col rounded-xl border border-white/10 bg-gray-900 shadow-2xl">
                        {/* this sheet */}
                        <div className="border-b border-white/5 p-3">
                          <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-gray-500">
                            This sheet · {pageLabel(pageNum)}
                          </div>
                          <div className="mb-2 text-[12px]">
                            {fpu ? (
                              <>
                                <b className="text-gray-100">
                                  {curInfo?.label || "Scale set"}
                                </b>{" "}
                                <span className="text-gray-500">
                                  · {srcText(curInfo)}
                                </span>
                              </>
                            ) : (
                              <b className="text-red-300">No scale</b>
                            )}
                            {curInfo?.reason && (curInfo.warn || !fpu) && (
                              <div
                                className={`mt-0.5 text-[11px] ${fpu ? "text-amber-300/90" : "text-gray-400"}`}
                              >
                                {curInfo.reason}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => {
                                setScaleMenu(false);
                                startCalibrate();
                              }}
                              disabled={!pageImage}
                              className={`flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg text-[12px] font-medium disabled:opacity-40 ${mode === "calibrate" ? "bg-orange-500 text-white" : "bg-white/5 text-gray-200 hover:bg-white/10"}`}
                            >
                              <Icon name="ruler" size={14} />
                              Calibrate
                            </button>
                            <select
                              onChange={(e) =>
                                e.target.value &&
                                applyPresetScale(parseFloat(e.target.value))
                              }
                              value=""
                              disabled={!pageImage}
                              className="h-8 flex-1 rounded-lg border border-white/5 bg-white/5 px-2 text-[12px] text-gray-200 disabled:opacity-40"
                            >
                              <option value="">Scale preset…</option>
                              {PRESET_SCALES.map((sc) => (
                                <option
                                  key={sc.label}
                                  value={sc.ftPerPaperInch}
                                >
                                  {sc.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <label className="mt-2 flex cursor-pointer items-center gap-2 text-[11px] text-gray-400">
                            <input
                              type="checkbox"
                              checked={scaleAllPages}
                              onChange={(e) =>
                                setScaleAllPages(e.target.checked)
                              }
                              className="h-3.5 w-3.5 accent-orange-500"
                            />
                            Apply a calibration / preset to every sheet
                          </label>
                        </div>
                        {/* every sheet */}
                        <div className="flex items-center gap-2 px-3 pb-1.5 pt-2.5">
                          <div className="min-w-0 flex-1">
                            <div className="text-[10px] font-medium uppercase tracking-wider text-gray-500">
                              All sheets
                            </div>
                            <div className="text-[11px] text-gray-400">
                              <span className="text-emerald-300">
                                {incl.length - flagged.length} scaled
                              </span>
                              {flagged.length > 0 && (
                                <>
                                  {" "}
                                  ·{" "}
                                  <span className="text-red-300">
                                    {flagged.length} need a scale
                                  </span>
                                </>
                              )}
                              {warned.length > 0 && (
                                <>
                                  {" "}
                                  ·{" "}
                                  <span className="text-amber-300">
                                    {warned.length} to check
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => autoScaleSheets()}
                            disabled={!!scaleRun}
                            title="Check every sheet that has no scale yet"
                            className="rounded-md bg-white/5 px-2 py-1 text-[11px] text-gray-200 hover:bg-white/10 disabled:opacity-40"
                          >
                            Check unscaled
                          </button>
                          <button
                            onClick={() => autoScaleSheets({ force: true })}
                            disabled={!!scaleRun}
                            title="Re-check every sheet (keeps calibrated / preset scales)"
                            className="rounded-md px-2 py-1 text-[11px] text-gray-400 hover:bg-white/5 hover:text-white disabled:opacity-40"
                          >
                            Re-check all
                          </button>
                        </div>
                        {withSuggest.length > 0 && !scaleRun && (
                          <div className="mx-3 mb-2 flex items-center gap-2 rounded-lg border border-orange-500/30 bg-orange-500/[0.07] px-2 py-1.5 text-[11px] text-orange-100">
                            <span className="min-w-0 flex-1">
                              {withSuggest.length} unscaled sheet
                              {withSuggest.length === 1 ? " has" : "s have"} a
                              suggested scale
                            </span>
                            <button
                              onClick={() => acceptSuggestedScales()}
                              className="shrink-0 rounded-md bg-orange-500 px-2 py-0.5 font-semibold text-white hover:bg-orange-400"
                            >
                              Accept all
                            </button>
                          </div>
                        )}
                        {blank.length > 0 && !scaleRun && (
                          <div className="mx-3 mb-2 flex items-center gap-2 rounded-lg bg-white/[0.03] px-2 py-1.5 text-[11px] text-gray-400">
                            <span className="min-w-0 flex-1">
                              {blank.length} sheet
                              {blank.length === 1 ? " has" : "s have"} no scale
                              or dimensions (cover, schedules, notes?)
                            </span>
                            <button
                              onClick={() =>
                                setExcludedPages((prev) => [
                                  ...new Set([...prev, ...blank]),
                                ])
                              }
                              className="shrink-0 rounded-md bg-white/5 px-2 py-0.5 text-gray-200 hover:bg-white/10"
                            >
                              Exclude {blank.length === 1 ? "it" : "them"}
                            </button>
                          </div>
                        )}
                        {scaleRun && (
                          <div className="mx-3 mb-2 h-1 overflow-hidden rounded-full bg-white/5">
                            <div
                              className="h-full bg-orange-500 transition-all"
                              style={{
                                width: `${(100 * scaleRun.done) / Math.max(1, scaleRun.total)}%`,
                              }}
                            />
                          </div>
                        )}
                        <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 pb-2">
                          {rows.map((n) => {
                            const inf = infoOf(n),
                              ok = hasS(n);
                            return (
                              <div
                                key={n}
                                data-scale-row={n}
                                className={`rounded-md px-1.5 py-1 ${n === pageNum ? "bg-orange-500/10" : "hover:bg-white/[0.04]"}`}
                              >
                                <div className="flex items-center gap-1.5 text-[12px]">
                                  <span
                                    className={`h-2 w-2 shrink-0 rounded-full ${!ok ? "bg-red-400" : inf?.warn ? "bg-amber-400" : "bg-emerald-400"}`}
                                  />
                                  <button
                                    onClick={() => setPageNum(n)}
                                    className="min-w-0 flex-1 truncate text-left text-gray-200 hover:text-orange-200"
                                    title={`Go to ${pageLabel(n)}`}
                                  >
                                    <span className="mr-1 font-mono text-[10.5px] text-gray-500">
                                      {n}
                                    </span>
                                    {pageLabel(n)}
                                  </button>
                                  <span
                                    className={`shrink-0 text-[11px] ${!ok ? (inf?.suggest ? "text-orange-200" : "text-red-300") : inf?.warn ? "text-amber-200" : "text-gray-400"}`}
                                  >
                                    {ok
                                      ? inf?.label || "set"
                                      : inf?.suggest
                                        ? `suggest ${inf.suggest.label}`
                                        : "no scale"}
                                  </span>
                                </div>
                                {(!ok || inf?.warn) && (
                                  <div className="ml-3.5 mt-0.5">
                                    {inf?.reason && (
                                      <div className="text-[10.5px] leading-snug text-gray-500">
                                        {inf.reason}
                                      </div>
                                    )}
                                    <div className="mt-1 flex flex-wrap items-center gap-1">
                                      {!ok && inf?.suggest && (
                                        <button
                                          onClick={() =>
                                            acceptSuggestedScales([n])
                                          }
                                          title={inf.suggest.why}
                                          className="rounded-md bg-orange-500 px-1.5 py-0.5 text-[10.5px] font-semibold text-white hover:bg-orange-400"
                                        >
                                          Use {inf.suggest.label} · suggested
                                        </button>
                                      )}
                                      {!ok &&
                                        (inf?.options || [])
                                          .filter(
                                            (o) =>
                                              o.label !== inf?.suggest?.label,
                                          )
                                          .map((o) => (
                                            <button
                                              key={o.label}
                                              onClick={() =>
                                                applySheetScale(n, o)
                                              }
                                              title={
                                                o.agree
                                                  ? `${o.agree} dimension(s) agree`
                                                  : "Printed on the sheet"
                                              }
                                              className="rounded-md bg-orange-500/15 px-1.5 py-0.5 text-[10.5px] text-orange-200 hover:bg-orange-500/25"
                                            >
                                              Use {o.label}
                                            </button>
                                          ))}
                                      <button
                                        onClick={() => calibrateSheet(n)}
                                        className="rounded-md bg-white/5 px-1.5 py-0.5 text-[10.5px] text-gray-300 hover:bg-white/10"
                                      >
                                        Calibrate
                                      </button>
                                      {!ok && (
                                        <button
                                          onClick={() =>
                                            setExcludedPages((prev) => [
                                              ...prev,
                                              n,
                                            ])
                                          }
                                          title="Leave this sheet out of the takeoff"
                                          className="rounded-md px-1.5 py-0.5 text-[10.5px] text-gray-500 hover:bg-white/5 hover:text-gray-200"
                                        >
                                          Exclude
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* sheet switcher */}
            <div className="mx-auto flex min-w-0 shrink items-center gap-1 rounded-xl bg-black/30 p-1 ring-1 ring-white/5">
              <IconBtn
                title="Previous sheet"
                disabled={!pageCount || !nextIncluded(pageNum, -1)}
                onClick={() => {
                  const p = nextIncluded(pageNum, -1);
                  if (p) setPageNum(p);
                }}
              >
                <Icon name="chevL" />
              </IconBtn>
              <div className="relative">
                <button
                  onClick={() => setSheetMenu((v) => !v)}
                  disabled={!pageCount}
                  className="flex h-8 min-w-[8rem] max-w-[14rem] items-center gap-2 rounded-lg px-3 text-left hover:bg-white/5 disabled:opacity-40 lg:max-w-[20rem] 2xl:max-w-[26rem]"
                >
                  <Icon
                    name="file"
                    size={14}
                    className="shrink-0 text-gray-500"
                  />
                  <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-gray-100">
                    {pageCount ? pageLabel(pageNum) : "No drawing"}
                  </span>
                  {pageCount > 0 && (
                    <span className="text-[11px] tabular-nums text-gray-500">
                      {pageNum}/{pageCount}
                    </span>
                  )}
                  {shapesPerPage.get(pageNum) > 0 && (
                    <span
                      className="flex items-center gap-1 rounded-md bg-orange-500/15 px-1.5 py-px text-[10px] font-semibold text-orange-300"
                      title={`${shapesPerPage.get(pageNum)} takeoff items on this sheet`}
                    >
                      <Icon name="layers" size={10} />
                      {shapesPerPage.get(pageNum)}
                    </span>
                  )}
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${fpu ? "bg-emerald-400" : "bg-red-400"}`}
                    title={fpu ? "Scale set" : "No scale on this sheet"}
                  />
                  <Icon name="chevD" size={14} className="text-gray-500" />
                </button>
                {sheetMenu &&
                  pageCount > 0 &&
                  (() => {
                    const sq = sheetQuery.trim().toLowerCase();
                    const incl = Array.from(
                      { length: pageCount },
                      (_, i) => i + 1,
                    ).filter((n) => !excludedPages.includes(n));
                    const nDone = incl.filter((n) =>
                      shapesPerPage.get(n),
                    ).length;
                    const list = incl.filter(
                      (n) =>
                        (sheetListFilter === "all" ||
                          (sheetListFilter === "done"
                            ? shapesPerPage.get(n)
                            : !shapesPerPage.get(n))) &&
                        (!sq ||
                          `${n} ${pageLabel(n)}`.toLowerCase().includes(sq)),
                    );
                    const fbtn = (key, label, count) => (
                      <button
                        key={key}
                        onClick={() => setSheetListFilter(key)}
                        className={`flex-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${sheetListFilter === key ? "bg-orange-500 text-white" : "text-gray-400 hover:text-gray-200"}`}
                      >
                        {label} <span className="opacity-70">{count}</span>
                      </button>
                    );
                    return (
                      <>
                        <div
                          className="fixed inset-0 z-[1290]"
                          onClick={() => setSheetMenu(false)}
                        />
                        <div className="absolute left-0 top-10 z-[1300] flex max-h-[70vh] w-[22rem] flex-col rounded-xl border border-white/10 bg-gray-900 shadow-2xl">
                          <div className="space-y-2 border-b border-white/5 p-2">
                            <input
                              autoFocus
                              value={sheetQuery}
                              onChange={(e) => setSheetQuery(e.target.value)}
                              placeholder="Find a sheet…"
                              style={{ fontSize: 12 }}
                              className="h-8 w-full rounded-lg border border-white/10 bg-gray-950/60 px-2.5 text-gray-100 placeholder:text-gray-600 focus:border-orange-500 focus:outline-none"
                            />
                            <div className="flex rounded-lg bg-black/30 p-0.5 ring-1 ring-white/5">
                              {fbtn("all", "All", incl.length)}
                              {fbtn("done", "With takeoff", nDone)}
                              {fbtn("todo", "No takeoff", incl.length - nDone)}
                            </div>
                          </div>
                          <div className="min-h-0 flex-1 overflow-y-auto p-1">
                            {list.map((n) => {
                              const cnt = shapesPerPage.get(n) || 0;
                              return (
                                <button
                                  key={n}
                                  onClick={() => {
                                    setPageNum(n);
                                    setSheetMenu(false);
                                  }}
                                  className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[12px] ${n === pageNum ? "bg-orange-500/15 text-orange-200" : "text-gray-300 hover:bg-white/5"}`}
                                >
                                  <span className="w-6 text-right font-mono text-[11px] text-gray-500">
                                    {n}
                                  </span>
                                  <span className="min-w-0 flex-1 truncate">
                                    {pageLabel(n)}
                                  </span>
                                  {revsOf(n).length > 0 && (
                                    <span
                                      className={`rounded px-1 text-[9.5px] font-semibold ${shownRevNo(n) ? "bg-sky-500/15 text-sky-300" : "bg-amber-500/15 text-amber-300"}`}
                                      title={
                                        shownRevNo(n)
                                          ? `Showing revision ${shownRevNo(n)}`
                                          : "Showing the original"
                                      }
                                    >
                                      {shownRevNo(n)
                                        ? `R${shownRevNo(n)}`
                                        : "Orig"}
                                    </span>
                                  )}
                                  {cnt > 0 && (
                                    <span
                                      className="flex items-center gap-1 rounded-md bg-orange-500/15 px-1.5 py-px text-[10px] font-semibold text-orange-300"
                                      title={`${cnt} takeoff item${cnt === 1 ? "" : "s"} on this sheet`}
                                    >
                                      <Icon name="layers" size={10} />
                                      {cnt}
                                    </span>
                                  )}
                                  <span
                                    className={`h-1.5 w-1.5 shrink-0 rounded-full ${(scales[n] ?? scales[String(n)]) ? "bg-emerald-400" : "bg-red-400/70"}`}
                                    title={
                                      (scales[n] ?? scales[String(n)])
                                        ? "Scale set"
                                        : "No scale"
                                    }
                                  />
                                </button>
                              );
                            })}
                            {!list.length && (
                              <div className="px-3 py-6 text-center text-[12px] text-gray-500">
                                No sheets match.
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    );
                  })()}
              </div>
              <IconBtn
                title="Next sheet"
                disabled={!pageCount || !nextIncluded(pageNum, 1)}
                onClick={() => {
                  const p = nextIncluded(pageNum, 1);
                  if (p) setPageNum(p);
                }}
              >
                <Icon name="chevR" />
              </IconBtn>

              {/* revisions of this sheet: newest shown; the original / earlier ones to compare */}
              {pageCount > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setRevMenu((v) => !v)}
                    title="Sheet revisions — add an updated sheet, or show the original to compare"
                    className={`flex h-8 items-center gap-1 rounded-lg px-2 text-[11.5px] font-semibold ${revsOf(pageNum).length ? (shownRevNo(pageNum) ? "bg-sky-500/15 text-sky-200 ring-1 ring-sky-500/30 hover:bg-sky-500/25" : "bg-amber-500/15 text-amber-200 ring-1 ring-amber-500/30 hover:bg-amber-500/25") : "text-gray-400 hover:bg-white/5 hover:text-gray-200"}`}
                  >
                    {revBusy ? (
                      <span className="max-w-[9rem] truncate font-medium">
                        {revBusy}
                      </span>
                    ) : revsOf(pageNum).length ? (
                      shownRevNo(pageNum) ? (
                        `Rev ${shownRevNo(pageNum)}`
                      ) : (
                        "Original"
                      )
                    ) : (
                      "Rev"
                    )}
                    <Icon name="chevD" size={12} className="opacity-70" />
                  </button>
                  {revMenu && (
                    <div
                      className="absolute left-0 top-10 z-[1300] w-80 rounded-xl border border-white/10 bg-gray-900 p-1 shadow-2xl"
                      onMouseLeave={() => setRevMenu(false)}
                    >
                      <div className="px-2.5 pb-1 pt-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-500">
                        {pageLabel(pageNum)} — show
                        {isAddedSheet(pageNum) && (
                          <span className="ml-1.5 rounded bg-emerald-500/15 px-1 normal-case tracking-normal text-emerald-300">
                            added sheet
                          </span>
                        )}
                      </div>
                      {[...revsOf(pageNum)].reverse().map((r, i) => (
                        <div
                          key={r.no}
                          className={`group flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[12px] ${shownRevNo(pageNum) === r.no ? "bg-sky-500/15 text-sky-100" : "text-gray-300 hover:bg-white/5"}`}
                        >
                          <button
                            onClick={() => {
                              setRevView((v) => ({ ...v, [pageNum]: r.no }));
                              setRevMenu(false);
                            }}
                            className="flex min-w-0 flex-1 items-center gap-2 text-left"
                          >
                            <span className="w-12 shrink-0 font-semibold">
                              Rev {r.no}
                            </span>
                            <span className="min-w-0 flex-1 truncate text-[11px] text-gray-400">
                              {r.fileName}
                              {r.srcPage > 1 ? ` · p${r.srcPage}` : ""}
                              {r.at
                                ? ` · ${new Date(r.at).toLocaleDateString()}`
                                : ""}
                              {i === 0 ? " · latest" : ""}
                            </span>
                          </button>
                          <button
                            onClick={() => {
                              setRevMenu(false);
                              removeRevision(pageNum, r.no);
                            }}
                            title="Remove this revision (the takeoff isn't touched)"
                            className="hidden rounded p-0.5 text-gray-500 hover:bg-red-500/20 hover:text-red-300 group-hover:block"
                          >
                            <Icon name="x" size={12} />
                          </button>
                        </div>
                      ))}
                      {!isAddedSheet(pageNum) && (
                        <button
                          onClick={() => {
                            setRevView((v) => ({ ...v, [pageNum]: 0 }));
                            setRevMenu(false);
                          }}
                          className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[12px] ${revsOf(pageNum).length && !shownRevNo(pageNum) ? "bg-amber-500/15 text-amber-100" : "text-gray-300 hover:bg-white/5"}`}
                        >
                          <span className="w-12 shrink-0 font-semibold">
                            Original
                          </span>
                          <span className="min-w-0 flex-1 truncate text-[11px] text-gray-400">
                            {fileName || "the drawing set"}
                          </span>
                        </button>
                      )}
                      {revsOf(pageNum).length > 0 && !isAddedSheet(pageNum) && (
                        <button
                          onClick={() => {
                            const latest =
                              revsOf(pageNum)[revsOf(pageNum).length - 1].no;
                            setRevView((v) => ({
                              ...v,
                              [pageNum]: shownRevNo(pageNum) ? 0 : latest,
                            }));
                          }}
                          className="mt-0.5 flex w-full items-center justify-center gap-1.5 rounded-lg bg-white/[0.04] px-2.5 py-1.5 text-[11.5px] text-gray-200 hover:bg-white/10"
                        >
                          ⇄ Flip original / latest
                        </button>
                      )}
                      <div className="my-1 border-t border-white/10" />
                      <button
                        disabled={!!revBusy || !onUploadDrawing}
                        onClick={() => revFileRef.current?.click()}
                        className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[12px] text-orange-200 hover:bg-orange-500/10 disabled:opacity-40"
                      >
                        <Icon name="plus" size={13} />
                        Add revision to this sheet
                        <span className="ml-auto text-[10.5px] text-gray-500">
                          Rev{" "}
                          {revsOf(pageNum).reduce(
                            (m, r) => Math.max(m, Number(r.no) || 0),
                            0,
                          ) + 1}
                        </span>
                      </button>
                      <button
                        disabled={!!revBusy || !onUploadDrawing}
                        onClick={() => revBulkRef.current?.click()}
                        className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[12px] text-gray-300 hover:bg-white/5 disabled:opacity-40"
                      >
                        <Icon name="layers" size={13} />
                        Update many sheets from a new set…
                      </button>
                      <p className="px-2.5 pb-1.5 pt-0.5 text-[10.5px] leading-snug text-gray-500">
                        The takeoff stays as it is on the new sheet. A whole set
                        is matched to your sheets by their names first, and
                        pages with no match can be added as new sheets.
                      </p>
                    </div>
                  )}
                  <input
                    ref={revFileRef}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      setRevMenu(false);
                      openRevisionFile(f, { page: pageNum });
                    }}
                  />
                  <input
                    ref={revBulkRef}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      setRevMenu(false);
                      openRevisionFile(f);
                    }}
                  />
                </div>
              )}
              {pageCount > 0 &&
                (pageBidAreas[pageNum] ?? pageBidAreas[String(pageNum)]) && (
                  <span className="hidden h-8 max-w-[5.5rem] shrink items-center truncate rounded-lg bg-orange-500/10 px-2 text-[11px] font-medium text-orange-200 ring-1 ring-orange-500/25 xl:flex 2xl:max-w-[10rem] 2xl:text-[12px]">
                    <span className="truncate">
                      {pageBidAreas[pageNum] ?? pageBidAreas[String(pageNum)]}
                    </span>
                  </span>
                )}
            </div>

            {/* right: automation + output */}
            <div className="flex shrink-0 items-center gap-1">
              <HeaderBtn
                icon="layers"
                active={showWizard}
                disabled={!pdfDoc}
                onClick={() => setShowWizard(true)}
                title="Read the wall & ceiling types off the assembly sheets, then trace walls (with floor heights) and ceilings"
              >
                <span className="hidden xl:inline">Assemblies &amp; trace</span>
              </HeaderBtn>
              <HeaderBtn
                icon="shield"
                disabled={aiBusy === "review"}
                onClick={runChecks}
                title="What to look out for: rated walls, missing items, heights, spec notes"
              >
                <span className="hidden xl:inline">
                  {aiBusy === "review" ? "Checking…" : "Checks"}
                </span>
                {checks?.items?.filter((i) => i.severity === "high").length >
                  0 && (
                  <span className="rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white">
                    {checks.items.filter((i) => i.severity === "high").length}
                  </span>
                )}
              </HeaderBtn>
              <div className="mx-1 h-6 w-px bg-white/10" />
              <IconBtn
                title={
                  isFullscreen || immersive
                    ? "Exit full screen  (F)"
                    : "Full screen  (F)"
                }
                active={isFullscreen || immersive}
                onClick={toggleFullscreen}
              >
                <Icon
                  name={isFullscreen || immersive ? "minimize" : "maximize"}
                />
              </IconBtn>
              <IconBtn
                title="Types, rooms & checks  ( ] )"
                active={rightOpen}
                onClick={() => setRightOpen((v) => !v)}
              >
                <Icon name="panelRight" />
              </IconBtn>
              <div className="mx-1 h-6 w-px bg-white/10" />
              <HeaderBtn
                primary
                disabled={conditions.length === 0}
                onClick={() =>
                  onGoToEstimate?.({
                    ...buildPayload(),
                    condMetrics,
                    condMetricsByArea,
                    // draws a sheet + its assemblies for the printed legend
                    // the estimate prints through the same cached sheet pictures as the working drawings
                    renderSheetImage: (...a) =>
                      workRef.current.renderSheetImage(...a),
                    printDrawings: (pages) => workRef.current.print(pages),
                    // (so the estimate page saves only its own fields — see TakeoffPage saveDraft)
                    rev: syncRef.current?.rev ?? null,
                    supportsOps: supportsOpsRef.current && !!syncRef.current,
                  })
                }
                title="Estimate"
              >
                <span className="hidden sm:inline">Estimate</span>{" "}
                <Icon name="arrowR" size={14} />
              </HeaderBtn>
            </div>
          </div>
          <div className="flex min-h-[46px] flex-wrap items-center gap-2 border-t border-white/5 bg-gray-900/80 px-3 py-1">
            {/* drawing tools — their own row, so every tool has room (no sideways scrolling) */}
            <div className="shrink-0">
              <AssemblyPicker
                conditions={conditions}
                hidden={hiddenConds}
                activeId={activeCondId}
                drawing={mode === "draw"}
                disabled={!pageImage}
                condMetrics={condMetrics}
                onPick={(c) => {
                  setActiveCondId(c.id);
                  startDraw(c);
                }}
                onStop={() => setPanMode()}
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-0.5">
              <RailBtn
                icon="cursor"
                label="Select — click, Shift+click, or drag a box (V flips Select ⇄ Pan)"
                kbd="V"
                active={mode === "select"}
                onClick={() => setTool("select")}
              />
              <RailBtn
                icon="hand"
                label="Pan — drag the drawing (or hold Ctrl / ⌘ and drag, or right-drag, with any tool)"
                kbd="V / H"
                active={mode === "pan"}
                onClick={() => setTool("pan")}
              />
              <RailBtn
                icon="pencil"
                label="Edit points"
                kbd="E"
                active={mode === "edit"}
                disabled={!pageImage}
                onClick={toggleEditMode}
              />
              <RailBtn
                icon="trash"
                label="Delete tool"
                kbd="X"
                danger
                active={mode === "delete"}
                disabled={!pageImage}
                onClick={toggleDeleteMode}
              />
              <RailBtn
                icon="measure"
                label="Measure"
                kbd="M"
                active={mode === "measure"}
                disabled={!pageImage}
                onClick={startMeasure}
              />
              <RailBtn
                icon="legend"
                label="Legend on the drawing — this sheet's assemblies"
                active={showLegend}
                disabled={!pageImage}
                onClick={() => setShowLegend((v) => !v)}
              />
              <RailBtn
                icon="printer"
                label="Print working drawings — sheets at their own size, with the legend"
                disabled={!pdfDoc || !shapes.length}
                onClick={() => setWd((w) => ({ ...w, open: true }))}
              />
              {projectId && (
                <RailBtn
                  icon="flag"
                  label={
                    coMode
                      ? "Click the drawing where the change order applies (Esc to stop)"
                      : "Pin a change order on the drawing"
                  }
                  active={coMode}
                  disabled={!pageImage}
                  onClick={() => setCoMode(!coMode)}
                />
              )}
              {onSnip && (
                <RailBtn
                  icon="scissors"
                  label={
                    snipMode
                      ? "Drag a box round the area to cut (Esc to stop)"
                      : "Cut a picture of the drawing for an RFI"
                  }
                  active={snipMode}
                  disabled={!pageImage}
                  onClick={() => onSnipModeChange?.(!snipMode)}
                />
              )}
              {onRfiPinPlace && (
                <RailBtn
                  icon="pin"
                  label={
                    pinMode
                      ? "Click the drawing to pin the RFI (Esc to stop)"
                      : "Pin an RFI on the drawing"
                  }
                  active={pinMode}
                  disabled={!pageImage}
                  onClick={() => onPinModeChange?.(!pinMode)}
                />
              )}
              <div className="mx-0.5 h-6 w-px bg-white/10" />
              <RailBtn
                icon="undo"
                label={
                  mode === "draw" || mode === "extend"
                    ? "Undo the last point / change"
                    : histN.past
                      ? "Undo"
                      : "Nothing to undo"
                }
                kbd="Ctrl+Z"
                disabled={!histN.past && mode !== "draw" && mode !== "extend"}
                onClick={smartUndo}
              />
              <RailBtn
                icon="redo"
                label={histN.future ? "Redo" : "Nothing to redo"}
                kbd="Ctrl+Shift+Z"
                disabled={!histN.future}
                onClick={redo}
              />
            </div>
          </div>
        </header>

        {remoteReload && (
          <div className="flex shrink-0 items-center gap-3 border-b border-sky-500/30 bg-sky-500/10 px-4 py-1.5 text-[12px] text-sky-100">
            <span className="min-w-0 flex-1">
              <b>Lots changed while this was open</b> — others saved more than
              this screen can catch up on. Reload to get the latest (your
              changes are saved first).
            </span>
            <button
              onClick={async () => {
                await handleSave({ quiet: true }).catch(() => {});
                window.location.reload();
              }}
              className="shrink-0 rounded-md bg-sky-500/80 px-2.5 py-0.5 font-semibold text-white hover:bg-sky-500"
            >
              Reload
            </button>
          </div>
        )}
        {saveError && (
          <div className="flex shrink-0 items-center gap-3 border-b border-red-500/30 bg-red-500/10 px-4 py-1.5 text-[12px] text-red-200">
            <span className="min-w-0 flex-1">
              <b>Takeoff not saved</b> — {saveError}. It won't show in the
              project until it saves.
            </span>
            <button
              onClick={() => handleSave().catch(() => {})}
              disabled={saving}
              className="shrink-0 rounded-md bg-red-500/80 px-2.5 py-0.5 font-semibold text-white hover:bg-red-500 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Retry"}
            </button>
          </div>
        )}
        <div className="flex min-h-0 flex-1">
          {/* ═══════════════ LEFT PANEL ═══════════════ */}
          {leftOpen && (
            <aside
              className="relative flex shrink-0 flex-col overflow-hidden border-r border-white/5 bg-gray-900"
              style={{ width: leftW }}
            >
              {/* resize the panel by dragging its edge */}
              <div
                title="Drag to resize · double-click to reset"
                onDoubleClick={() => setLeftW(320)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  const x0 = e.clientX,
                    w0 = leftW;
                  const mv = (ev) =>
                    setLeftW(
                      Math.max(260, Math.min(560, w0 + ev.clientX - x0)),
                    );
                  const up = () => {
                    window.removeEventListener("mousemove", mv);
                    window.removeEventListener("mouseup", up);
                  };
                  window.addEventListener("mousemove", mv);
                  window.addEventListener("mouseup", up);
                }}
                className="absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize hover:bg-orange-500/40"
              />

              {/* conditions */}
              <section className="flex min-h-0 flex-1 flex-col">
                <div className="flex items-center justify-between px-3 pb-1.5 pt-3">
                  <h4 className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    Assemblies{" "}
                    <span className="font-normal normal-case tracking-normal text-gray-600">
                      · {conditions.length}
                    </span>
                  </h4>
                  <div className="flex items-center gap-1">
                    {hiddenConds.size > 0 && (
                      <button
                        onClick={showAllConds}
                        title={`Hidden: ${conditions
                          .filter((c) => hiddenConds.has(c.id))
                          .map((c) => c.name)
                          .join(", ")}\nClick to show them all`}
                        className="inline-flex h-7 items-center gap-1 whitespace-nowrap rounded-md bg-amber-500/15 px-2 text-[11px] font-semibold text-amber-200 ring-1 ring-amber-500/30 hover:bg-amber-500/25"
                      >
                        <Icon name="eyeOff" size={13} />
                        {hiddenConds.size} hidden
                      </button>
                    )}
                    <IconBtn
                      title={
                        allGroupsShut
                          ? "Expand all groups"
                          : "Collapse all groups"
                      }
                      className="h-7 w-7"
                      onClick={() =>
                        setCollapsedGroups(
                          allGroupsShut
                            ? []
                            : ["linear", "area", "count", "units"],
                        )
                      }
                    >
                      <Icon
                        name={allGroupsShut ? "expandAll" : "collapseAll"}
                        size={14}
                      />
                    </IconBtn>

                    <button
                      onClick={() => setShowAddCond((v) => !v)}
                      className="inline-flex h-7 items-center gap-1 rounded-md bg-orange-500 px-2 text-[11px] font-semibold text-white hover:bg-orange-400"
                    >
                      <Icon name="plus" size={13} />
                      New
                    </button>
                  </div>
                </div>

                {/* scope: this sheet (default) or the whole takeoff */}
                <div className="mx-3 mb-2 flex items-center gap-1 rounded-lg bg-black/25 p-0.5 text-[11px]">
                  {[
                    ["sheet", pageLabel(pageNum) || `Sheet ${pageNum}`],
                    ["all", "All sheets"],
                  ].map(([k, label]) => (
                    <button
                      key={k}
                      onClick={() => setAsmScope(k)}
                      title={
                        k === "sheet"
                          ? "Assemblies drawn on this sheet, with this sheet's quantities"
                          : "Every assembly, whole-takeoff totals and a breakdown by sheet"
                      }
                      className={`min-w-0 flex-1 truncate rounded-md px-2 py-1 font-medium transition-colors ${asmScope === k ? "bg-white/10 text-white shadow-sm" : "text-gray-400 hover:text-gray-200"}`}
                    >
                      {k === "sheet" ? (
                        <>
                          <span className="text-gray-500">This sheet · </span>
                          {label}
                        </>
                      ) : (
                        label
                      )}
                    </button>
                  ))}
                </div>
                {conditions.length > 5 && (
                  <div className="relative mx-3 mb-2">
                    <input
                      value={asmQuery}
                      onChange={(e) => setAsmQuery(e.target.value)}
                      placeholder="Find an assembly…"
                      className="h-7 w-full rounded-md border border-white/5 bg-black/20 pl-7 pr-6 text-[11.5px] text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
                    />
                    <Icon
                      name="search"
                      size={13}
                      className="pointer-events-none absolute left-2 top-1.5 text-gray-600"
                    />
                    {asmQuery && (
                      <button
                        onClick={() => setAsmQuery("")}
                        className="absolute right-1.5 top-1.5 text-gray-500 hover:text-gray-200"
                      >
                        <Icon name="x" size={12} />
                      </button>
                    )}
                  </div>
                )}
                {showAddCond && (
                  <div
                    className="mx-3 mb-3 space-y-3 rounded-xl border border-orange-500/30 bg-gray-950/70 p-3 shadow-lg shadow-black/30"
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] font-semibold text-white">
                        New assembly
                      </span>
                      <button
                        onClick={() => setShowAddCond(false)}
                        className="rounded p-0.5 text-gray-500 hover:bg-white/10 hover:text-gray-200"
                        title="Close"
                      >
                        <Icon name="x" size={14} />
                      </button>
                    </div>
                    {/* what it measures */}
                    <div className="grid grid-cols-3 gap-1 rounded-lg bg-black/30 p-0.5">
                      {["linear", "area", "count"].map((t) => (
                        <button
                          key={t}
                          onClick={() => setNewCond({ ...newCond, type: t })}
                          className={`rounded-md py-1.5 text-[11px] font-medium transition-colors ${newCond.type === t ? "bg-white/10 text-white shadow-sm" : "text-gray-400 hover:text-gray-200"}`}
                        >
                          {TYPE_META[t].label}
                        </button>
                      ))}
                    </div>
                    <label className="block">
                      <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-gray-500">
                        Name
                      </span>
                      <input
                        autoFocus
                        value={newCond.name}
                        onChange={(e) =>
                          setNewCond({ ...newCond, name: e.target.value })
                        }
                        onKeyDown={(e) => e.key === "Enter" && addCondition()}
                        placeholder={
                          newCond.type === "linear"
                            ? "e.g. W1 partition"
                            : newCond.type === "area"
                              ? "e.g. Drywall ceiling"
                              : "e.g. Access panels"
                        }
                        className="h-9 w-full rounded-md border border-white/10 bg-gray-950 px-2.5 text-[12.5px] text-gray-100 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
                      />
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block">
                        <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-gray-500">
                          Tag on drawings
                        </span>
                        <input
                          value={newCond.wallType}
                          onChange={(e) =>
                            setNewCond({ ...newCond, wallType: e.target.value })
                          }
                          placeholder="e.g. W3"
                          className="h-9 w-full rounded-md border border-white/10 bg-gray-950 px-2.5 font-mono text-[12px] text-gray-100 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
                        />
                      </label>
                      {newCond.type === "linear" ? (
                        <label className="block">
                          <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-gray-500">
                            Height
                          </span>
                          <FtInInput
                            value={newCond.height || null}
                            onCommit={(v) =>
                              setNewCond((n) => ({ ...n, height: v || "" }))
                            }
                            placeholder={`9'-0"`}
                            className="h-9 w-full"
                          />
                        </label>
                      ) : (
                        <div />
                      )}
                    </div>
                    <div>
                      <span className="mb-1.5 block text-[10px] font-medium uppercase tracking-wider text-gray-500">
                        Colour
                      </span>
                      <Swatches
                        value={
                          newCond.color ||
                          PALETTE[conditions.length % PALETTE.length]
                        }
                        onChange={(c) => setNewCond({ ...newCond, color: c })}
                      />
                    </div>
                    <label className="block">
                      <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-gray-500">
                        Items{" "}
                        <span className="normal-case tracking-normal text-gray-600">
                          — optional, one per line
                        </span>
                      </span>
                      <textarea
                        value={newCond.description}
                        onChange={(e) =>
                          setNewCond({
                            ...newCond,
                            description: e.target.value,
                          })
                        }
                        rows={3}
                        placeholder={`5/8" Type X GWB each side\n3-5/8" studs @ 16" O.C.`}
                        className="w-full resize-y rounded-md border border-white/10 bg-gray-950 px-2.5 py-1.5 text-[11.5px] text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
                      />
                    </label>
                    <button
                      onClick={addCondition}
                      disabled={!newCond.name.trim()}
                      className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-lg bg-orange-500 text-[12px] font-semibold text-white hover:bg-orange-400 disabled:opacity-40"
                    >
                      <Icon name="pencil" size={13} /> Add &amp; start drawing
                    </button>
                  </div>
                )}

                <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 pb-3">
                  {[
                    ["linear", "Walls · LF"],
                    ["area", "Areas · SF"],
                    ["count", "Counts · EA"],
                  ].map(([type, title]) => {
                    const q = asmQuery.trim().toLowerCase();
                    const M = asmScope === "sheet" ? pageMetrics : condMetrics;
                    const list = conditions.filter(
                      (c) =>
                        c.type === type &&
                        (!q ||
                          `${c.name} ${c.description || ""}`
                            .toLowerCase()
                            .includes(q)) &&
                        (asmScope !== "sheet" ||
                          (pageMetrics[c.id]?.shapes || 0) > 0 ||
                          c.id === activeCondId),
                    );
                    if (!list.length) return null;
                    const shut =
                      collapsedGroups.includes(type) &&
                      !q &&
                      !list.some(
                        (c) => c.id === activeCondId && mode === "draw",
                      );
                    const tot = list.reduce(
                      (t, c) =>
                        t +
                        ((type === "area"
                          ? M[c.id]?.sf
                          : type === "linear"
                            ? M[c.id]?.lf
                            : M[c.id]?.count) || 0),
                      0,
                    );
                    const groupHidden = list.every((c) =>
                      hiddenConds.has(c.id),
                    );
                    const groupSomeHidden = list.some((c) =>
                      hiddenConds.has(c.id),
                    );
                    return (
                      <div key={type}>
                        <div className="mb-1 flex items-center gap-1">
                          <button
                            onClick={() =>
                              setHidden(
                                list.map((c) => c.id),
                                !groupHidden,
                              )
                            }
                            title={
                              groupHidden
                                ? `Show every ${title.toLowerCase()} assembly`
                                : `Hide every ${title.toLowerCase()} assembly (review the rest)`
                            }
                            className={`shrink-0 rounded p-0.5 ${groupSomeHidden ? "text-amber-300 hover:bg-amber-500/15" : "text-gray-600 hover:bg-white/10 hover:text-gray-300"}`}
                          >
                            <Icon
                              name={groupHidden ? "eyeOff" : "eye"}
                              size={13}
                            />
                          </button>
                          <button
                            onClick={() =>
                              setCollapsedGroups((g) =>
                                g.includes(type)
                                  ? g.filter((x) => x !== type)
                                  : [...g, type],
                              )
                            }
                            className="flex min-w-0 flex-1 items-center gap-1 rounded px-1 py-0.5 text-[10px] font-medium uppercase tracking-wider text-gray-500 hover:bg-white/5 hover:text-gray-300"
                          >
                            <Icon name={shut ? "chevR" : "chevD"} size={12} />
                            {title}
                            <span className="normal-case tracking-normal text-gray-600">
                              · {list.length}
                            </span>
                            <span className="ml-auto normal-case tracking-normal tabular-nums text-gray-500">
                              {fmtQty(tot, 0)}{" "}
                              {type === "area"
                                ? "SF"
                                : type === "linear"
                                  ? "LF"
                                  : "EA"}
                            </span>
                          </button>
                        </div>
                        {shut ? (
                          // collapsed: one chip per assembly — still pick what to draw with
                          <div className="flex flex-wrap gap-1 px-1 pb-1">
                            {list.map((c) => (
                              <button
                                key={c.id}
                                onClick={() => {
                                  setActiveCondId(c.id);
                                  startDraw(c);
                                }}
                                title={`Draw: ${c.name}${hiddenConds.has(c.id) ? " (hidden — drawing shows it again)" : ""}${c.description ? `\n${c.description}` : ""}`}
                                className={`inline-flex max-w-full items-center gap-1 truncate rounded-full border px-2 py-0.5 text-[10.5px] ${activeCondId === c.id ? "border-orange-500/70 bg-orange-500/15 text-orange-100" : hiddenConds.has(c.id) ? "border-dashed border-amber-500/40 text-gray-500" : "border-white/10 text-gray-300 hover:border-white/25"}`}
                              >
                                {hiddenConds.has(c.id) && (
                                  <Icon
                                    name="eyeOff"
                                    size={10}
                                    className="shrink-0 text-amber-300"
                                  />
                                )}
                                <span
                                  className="h-2 w-2 shrink-0 rounded-full"
                                  style={{ background: c.color }}
                                />
                                <span className="truncate">
                                  {c.no ? (
                                    <span className="mr-1 font-mono text-[10px] text-orange-300/80">
                                      #{c.no}
                                    </span>
                                  ) : null}
                                  {c.name}
                                </span>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="space-y-1">
                            {list.map((cond) => {
                              const m = M[cond.id] || { count: 0 };
                              const isActive = activeCondId === cond.id;
                              const isOpen = openConds.includes(cond.id); // items shown — only when asked for
                              const condShapes = shapes.filter(
                                (x) =>
                                  x.conditionId === cond.id &&
                                  (asmScope !== "sheet" || x.page === pageNum),
                              );
                              // whole takeoff: where it is, sheet by sheet
                              const bySheet =
                                asmScope === "all" && isOpen
                                  ? [...new Set(condShapes.map((x) => x.page))]
                                      .sort((a, b) => a - b)
                                      .map((pg) => {
                                        const mm = computeCondMetrics(
                                          cond,
                                          shapes.filter((x) => x.page === pg),
                                          scales,
                                          excludedPages,
                                          units,
                                        );
                                        return {
                                          pg,
                                          n: mm.shapes,
                                          q:
                                            cond.type === "area"
                                              ? `${fmtQty(mm.sf ?? 0, 0)} SF`
                                              : cond.type === "count"
                                                ? `${mm.count} EA`
                                                : `${fmtQty(mm.lf ?? 0, 0)} LF`,
                                        };
                                      })
                                  : [];
                              const untaggedC = isUntagged(cond);
                              const qty =
                                cond.type === "count"
                                  ? `${m.count} EA${m.lf ? ` · ${fmtQty(m.lf, 0)} LF` : ""}`
                                  : m.uncal && m.count > 0
                                    ? "set scale"
                                    : cond.type === "area"
                                      ? `${fmtQty(m.sf ?? 0, 0)} SF`
                                      : `${fmtQty(m.lf ?? 0, 0)} LF`;
                              return (
                                <div
                                  key={cond.id}
                                  className={`group rounded-xl border transition-colors ${hiddenConds.has(cond.id) ? "border-dashed border-amber-500/30 bg-black/20 opacity-60 hover:opacity-90" : isActive ? "border-orange-500/60 bg-orange-500/[0.07] shadow-sm shadow-orange-950/40" : untaggedC && m.count ? "border-amber-500/30 bg-amber-500/[0.04]" : "border-white/[0.04] bg-white/[0.03] hover:border-white/10 hover:bg-white/[0.06]"}`}
                                >
                                  <div
                                    data-cond-card={cond.id}
                                    className="flex cursor-pointer items-center gap-2 py-2 pl-1.5 pr-2.5"
                                    onClick={() =>
                                      setActiveCondId(isActive ? null : cond.id)
                                    }
                                    title={
                                      isActive
                                        ? "Drawing with this assembly — click to deselect"
                                        : "Click to draw with this assembly"
                                    }
                                  >
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleOpenCond(cond.id);
                                      }}
                                      title={
                                        isOpen ? "Hide items" : "Show items"
                                      }
                                      className="shrink-0 rounded p-0.5 text-gray-500 hover:bg-white/10 hover:text-gray-200"
                                    >
                                      <Icon
                                        name={isOpen ? "chevD" : "chevR"}
                                        size={13}
                                      />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (e.altKey) isolateCond(cond.id);
                                        else toggleHidden(cond.id);
                                      }}
                                      title={
                                        hiddenConds.has(cond.id)
                                          ? "Hidden on the drawing — click to show"
                                          : "Hide on the drawing (Alt+click: show only this one)"
                                      }
                                      className={`shrink-0 rounded p-0.5 ${hiddenConds.has(cond.id) ? "text-amber-300 hover:bg-amber-500/15" : "text-gray-500 opacity-0 hover:bg-white/10 hover:text-gray-200 group-hover:opacity-100"}`}
                                    >
                                      <Icon
                                        name={
                                          hiddenConds.has(cond.id)
                                            ? "eyeOff"
                                            : "eye"
                                        }
                                        size={13}
                                      />
                                    </button>
                                    <input
                                      type="color"
                                      value={cond.color}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) =>
                                        updateCondition(cond.id, {
                                          color: e.target.value,
                                        })
                                      }
                                      title="Colour"
                                      className="h-3.5 w-3.5 shrink-0 cursor-pointer appearance-none rounded-full border-0 bg-transparent p-0 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-0"
                                      style={{ backgroundColor: cond.color }}
                                    />
                                    {cond.type === "count" && (
                                      <span
                                        className="shrink-0"
                                        title={`Drawn as a ${condSymbol(cond)}`}
                                      >
                                        {symbolSvg(
                                          condSymbol(cond),
                                          cond.color,
                                          13,
                                        )}
                                      </span>
                                    )}
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-1.5">
                                        {editCondId === cond.id ? (
                                          <input
                                            autoFocus
                                            defaultValue={cond.name}
                                            onClick={(e) => e.stopPropagation()}
                                            onBlur={(e) => {
                                              const v = e.target.value.trim();
                                              if (v && v !== cond.name)
                                                updateCondition(cond.id, {
                                                  name: v,
                                                });
                                              setEditCondId(null);
                                            }}
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter")
                                                e.currentTarget.blur();
                                              if (e.key === "Escape") {
                                                e.currentTarget.value =
                                                  cond.name; // discard the edit
                                                setEditCondId(null);
                                              }
                                            }}
                                            className="min-w-0 flex-1 rounded !border !border-orange-500/60 !bg-gray-950 !px-1 !py-0 text-[12px] font-medium text-gray-100 !shadow-none !outline-none !ring-0"
                                          />
                                        ) : (
                                          <>
                                            {cond.wallType &&
                                              !String(cond.wallType).startsWith(
                                                "__",
                                              ) && (
                                                <span className="shrink-0 rounded bg-white/[0.08] px-1 font-mono text-[9.5px] font-semibold text-gray-300">
                                                  {String(
                                                    cond.wallType,
                                                  ).replace(/·WR$/, "")}
                                                </span>
                                              )}
                                            <span
                                              className="truncate text-[12.5px] font-medium text-gray-100"
                                              title={
                                                cond.description || cond.name
                                              }
                                              onDoubleClick={(e) => {
                                                e.stopPropagation();
                                                setEditCondId(cond.id);
                                              }}
                                            >
                                              {cond.no ? (
                                                <span className="mr-1 font-mono text-[10px] text-orange-300/80">
                                                  #{cond.no}
                                                </span>
                                              ) : null}
                                              {cond.name}
                                            </span>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setEditCondId(cond.id);
                                              }}
                                              title="Rename assembly"
                                              className="hidden shrink-0 rounded px-1 text-[11px] text-gray-500 hover:bg-white/10 hover:text-white group-hover:block"
                                            >
                                              ✎
                                            </button>
                                          </>
                                        )}
                                        {(() => {
                                          const asm = assemblies.find(
                                            (x) => x.conditionId === cond.id,
                                          );
                                          return asm ? (
                                            <>
                                              {asm.fireRating && (
                                                <span className="shrink-0 rounded bg-red-500/15 px-1 text-[9px] font-semibold text-red-300">
                                                  {asm.fireRating}
                                                </span>
                                              )}
                                              {!asm.verified && (
                                                <span className="shrink-0 rounded bg-amber-500/15 px-1 text-[9px] text-amber-300">
                                                  check
                                                </span>
                                              )}
                                            </>
                                          ) : null;
                                        })()}
                                        {selIds.length > 0 &&
                                          condShapes.some((x) =>
                                            selIds.includes(x.id),
                                          ) && (
                                            <span className="shrink-0 rounded-full bg-orange-500 px-1.5 text-[9px] font-semibold text-white">
                                              {
                                                condShapes.filter((x) =>
                                                  selIds.includes(x.id),
                                                ).length
                                              }{" "}
                                              selected
                                            </span>
                                          )}
                                      </div>
                                      {isOpen && editingDescId === cond.id ? (
                                        <textarea
                                          autoFocus
                                          value={cond.description || ""}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) =>
                                            setCondDescription(
                                              cond,
                                              e.target.value,
                                            )
                                          }
                                          onBlur={() => setEditingDescId(null)}
                                          rows={Math.min(
                                            10,
                                            Math.max(
                                              3,
                                              (cond.description || "").split(
                                                "\n",
                                              ).length,
                                            ),
                                          )}
                                          placeholder="Description — what's in this assembly, one item per line"
                                          className="my-1 w-full resize-y rounded-md border border-white/10 bg-black/30 px-1.5 py-1 text-[10.5px] leading-snug text-gray-300 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
                                        />
                                      ) : isOpen ? (
                                        <div
                                          className="my-1 rounded-md bg-black/20 px-1.5 py-1"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {(cond.description || "")
                                            .split("\n")
                                            .map((x) => x.trim())
                                            .filter(Boolean)
                                            .map((line, i) => (
                                              <div
                                                key={i}
                                                className="flex gap-1.5 text-[10.5px] leading-snug text-gray-300"
                                              >
                                                <span className="text-gray-600">
                                                  •
                                                </span>
                                                <span className="min-w-0">
                                                  {line}
                                                </span>
                                              </div>
                                            ))}
                                          {!cond.description && (
                                            <div className="text-[10.5px] text-gray-500">
                                              No items listed.
                                            </div>
                                          )}
                                          <button
                                            onClick={() =>
                                              setEditingDescId(cond.id)
                                            }
                                            className="mt-0.5 text-[10px] text-gray-500 hover:text-orange-300"
                                          >
                                            Edit items
                                          </button>
                                        </div>
                                      ) : cond.description ? (
                                        <div
                                          title={cond.description}
                                          className="truncate text-[10.5px] leading-snug text-gray-400"
                                        >
                                          {cond.description
                                            .split("\n")
                                            .join(" · ")}
                                        </div>
                                      ) : null}
                                      <div className="text-[10px] text-gray-500">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleOpenCond(cond.id);
                                          }}
                                          className="hover:text-gray-300 hover:underline"
                                          title={
                                            isOpen ? "Hide items" : "Show items"
                                          }
                                        >
                                          {m.shapes ?? m.count}{" "}
                                          {cond.type === "area"
                                            ? "area"
                                            : cond.type === "count"
                                              ? "marker"
                                              : "line"}
                                          {(m.shapes ?? m.count) === 1
                                            ? ""
                                            : "s"}
                                        </button>
                                        {m.count !== (m.shapes ?? m.count) && (
                                          <span className="text-orange-300/80">
                                            {" "}
                                            ({fmtQty(m.count, 0)} with units)
                                          </span>
                                        )}
                                        {cond.type === "linear" &&
                                          !m.uncal &&
                                          m.sf != null && (
                                            <>
                                              {" "}
                                              · {fmtQty(m.sf, 0)} SF @{" "}
                                              {(() => {
                                                const hs = [
                                                  ...new Set(
                                                    condShapes.map(
                                                      (x) =>
                                                        Number(
                                                          x.meta?.height,
                                                        ) ||
                                                        Number(cond.height) ||
                                                        0,
                                                    ),
                                                  ),
                                                ];
                                                return hs.length === 1
                                                  ? fmtFeetIn(hs[0])
                                                  : `${hs.length} heights`;
                                              })()}
                                            </>
                                          )}
                                        {untaggedC && m.count > 0 && (
                                          <span className="text-amber-300">
                                            {" "}
                                            · click{" "}
                                            {cond.type === "area"
                                              ? "an area"
                                              : "a line"}{" "}
                                            to assign
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    {hiddenConds.has(cond.id) && (
                                      <span className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-amber-300">
                                        Hidden
                                      </span>
                                    )}
                                    <span className="shrink-0 rounded-lg bg-orange-500/10 px-2 py-1 text-[12px] font-semibold tabular-nums text-orange-200">
                                      {qty}
                                    </span>
                                  </div>
                                  {/* actions: draw · default height · edit · duplicate · delete */}
                                  <div
                                    className={`flex items-center gap-1 px-2 pb-2 ${isActive || editAsmId === cond.id ? "" : "hidden group-hover:flex"}`}
                                  >
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        startDraw(cond);
                                      }}
                                      disabled={!pageImage}
                                      className={`inline-flex h-8 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-lg text-[11.5px] font-semibold transition-colors disabled:opacity-40 ${mode === "draw" && activeCondId === cond.id ? "bg-orange-500 text-white shadow-sm shadow-orange-900/40" : "bg-white/[0.07] text-gray-100 hover:bg-white/[0.12]"}`}
                                    >
                                      <Icon
                                        name="pencil"
                                        size={13}
                                        className="shrink-0"
                                      />
                                      {mode === "draw" &&
                                      activeCondId === cond.id
                                        ? "Drawing…"
                                        : "Draw"}
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const only =
                                          hiddenConds.size > 0 &&
                                          conditions.every(
                                            (c) =>
                                              c.id === cond.id ||
                                              hiddenConds.has(c.id) ||
                                              !shapes.some(
                                                (x) => x.conditionId === c.id,
                                              ),
                                          );
                                        if (only) showAllConds();
                                        else isolateCond(cond.id);
                                      }}
                                      title="Show only this assembly — hide every other one (click again to show all)"
                                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/[0.07] text-gray-200 hover:bg-white/[0.12]"
                                    >
                                      <Icon name="eye" size={14} />
                                    </button>
                                    {cond.type === "linear" && (
                                      <FtInInput
                                        value={cond.height}
                                        onCommit={(v) =>
                                          setConditionHeight(cond, v)
                                        }
                                        placeholder={`Ht 9'-0"`}
                                        title="Height for this assembly's walls — changes every wall already drawn with it (walls you set by hand keep theirs)"
                                        className="h-8 w-[4.6rem] text-center"
                                      />
                                    )}
                                    <IconBtn
                                      title="Edit name, tag, height, colour and items"
                                      active={editAsmId === cond.id}
                                      className="h-8 w-8"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditAsmId(
                                          editAsmId === cond.id
                                            ? null
                                            : cond.id,
                                        );
                                      }}
                                    >
                                      <Icon name="settings" size={14} />
                                    </IconBtn>
                                    <IconBtn
                                      title="Duplicate assembly (same type, tag, height and items — no lines)"
                                      className="h-8 w-8"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        duplicateCondition(cond);
                                      }}
                                    >
                                      <Icon name="duplicate" size={14} />
                                    </IconBtn>
                                    <IconBtn
                                      title="Delete assembly and its shapes"
                                      danger
                                      className="h-8 w-8"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (
                                          window.confirm(
                                            `Delete "${cond.name}" and its ${condMetrics[cond.id]?.shapes ?? 0} shape(s) on all sheets?`,
                                          )
                                        )
                                          deleteCondition(cond.id);
                                      }}
                                    >
                                      <Icon name="trash" size={14} />
                                    </IconBtn>
                                  </div>
                                  {/* edit panel: everything about the assembly, with room to type */}
                                  {editAsmId === cond.id && (
                                    <div
                                      className="mx-2 mb-2 space-y-2.5 rounded-xl border border-white/10 bg-gray-950/70 p-3"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <label className="block">
                                        <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-gray-500">
                                          Name
                                        </span>
                                        <input
                                          defaultValue={cond.name}
                                          key={`n-${cond.id}-${cond.name}`}
                                          onBlur={(e) => {
                                            const v = e.target.value.trim();
                                            if (v && v !== cond.name)
                                              updateCondition(cond.id, {
                                                name: v,
                                                ...(isHeaderCond(cond)
                                                  ? { headerAuto: false }
                                                  : {}),
                                              });
                                          }}
                                          onKeyDown={(e) => {
                                            e.stopPropagation();
                                            if (e.key === "Enter")
                                              e.currentTarget.blur();
                                          }}
                                          className="h-8 w-full rounded-md border border-white/10 bg-gray-950 px-2 text-[12.5px] font-medium text-gray-100 focus:border-orange-500 focus:outline-none"
                                        />
                                      </label>
                                      <div className="grid grid-cols-2 gap-2">
                                        <label className="block">
                                          <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-gray-500">
                                            Tag on drawings
                                          </span>
                                          <input
                                            defaultValue={cond.wallType || ""}
                                            key={`t-${cond.id}-${cond.wallType}`}
                                            placeholder="e.g. W3"
                                            onBlur={(e) => {
                                              const v = e.target.value
                                                .trim()
                                                .toUpperCase();
                                              if (v !== (cond.wallType || ""))
                                                updateCondition(cond.id, {
                                                  wallType: v,
                                                });
                                            }}
                                            onKeyDown={(e) => {
                                              e.stopPropagation();
                                              if (e.key === "Enter")
                                                e.currentTarget.blur();
                                            }}
                                            className="h-8 w-full rounded-md border border-white/10 bg-gray-950 px-2 font-mono text-[12px] text-gray-100 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
                                          />
                                        </label>
                                        {cond.type === "linear" ? (
                                          <label className="block">
                                            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-gray-500">
                                              Height
                                            </span>
                                            <FtInInput
                                              value={cond.height}
                                              onCommit={(v) =>
                                                setConditionHeight(cond, v)
                                              }
                                              placeholder={`9'-0"`}
                                              className="h-8 w-full"
                                            />
                                          </label>
                                        ) : (
                                          <div>
                                            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-gray-500">
                                              Measured as
                                            </span>
                                            <TypeSwitch
                                              cond={cond}
                                              used={shapes.some(
                                                (x) =>
                                                  x.conditionId === cond.id,
                                              )}
                                              onChange={(t) =>
                                                changeConditionType(cond, t)
                                              }
                                            />
                                          </div>
                                        )}
                                      </div>
                                      {cond.type === "linear" && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-[10px] font-medium uppercase tracking-wider text-gray-500">
                                            Measured as
                                          </span>
                                          <TypeSwitch
                                            cond={cond}
                                            used={shapes.some(
                                              (x) => x.conditionId === cond.id,
                                            )}
                                            onChange={(t) =>
                                              changeConditionType(cond, t)
                                            }
                                          />
                                        </div>
                                      )}
                                      {cond.type === "linear" &&
                                        cond.height > 0 &&
                                        (() => {
                                          const lines = shapes.filter(
                                            (x) =>
                                              x.conditionId === cond.id &&
                                              x.kind === "polyline",
                                          );
                                          const other = lines.filter(
                                            (x) =>
                                              Math.abs(
                                                (Number(x.meta?.height) || 0) -
                                                  cond.height,
                                              ) > 1e-6,
                                          );
                                          return other.length ? (
                                            <button
                                              onClick={() =>
                                                applyHeightToLines(
                                                  cond,
                                                  cond.height,
                                                )
                                              }
                                              className="flex w-full items-center justify-between rounded-md bg-orange-500/10 px-2 py-1.5 text-left text-[11px] text-orange-200 hover:bg-orange-500/20"
                                            >
                                              <span>
                                                {other.length} of {lines.length}{" "}
                                                line
                                                {lines.length === 1
                                                  ? ""
                                                  : "s"}{" "}
                                                at another height
                                              </span>
                                              <b>
                                                Set all to{" "}
                                                {fmtFeetIn(cond.height)}
                                              </b>
                                            </button>
                                          ) : null;
                                        })()}
                                      <div>
                                        <span className="mb-1.5 block text-[10px] font-medium uppercase tracking-wider text-gray-500">
                                          Colour
                                        </span>
                                        <Swatches
                                          value={cond.color}
                                          onChange={(c) =>
                                            updateCondition(cond.id, {
                                              color: c,
                                            })
                                          }
                                        />
                                      </div>
                                      <LookOnDrawing
                                        cond={cond}
                                        onChange={(patch) =>
                                          updateCondition(cond.id, patch)
                                        }
                                        headerCount={
                                          cond.type === "linear" &&
                                          !isHeaderCond(cond)
                                            ? shapes.filter(
                                                (x) =>
                                                  x.meta?.hdrFor === cond.id,
                                              ).length
                                            : null
                                        }
                                      />
                                      <label className="block">
                                        <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-gray-500">
                                          Items{" "}
                                          <span className="normal-case tracking-normal text-gray-600">
                                            — one per line
                                          </span>
                                        </span>
                                        <textarea
                                          value={cond.description || ""}
                                          onChange={(e) =>
                                            setCondDescription(
                                              cond,
                                              e.target.value,
                                            )
                                          }
                                          onKeyDown={(e) => e.stopPropagation()}
                                          rows={Math.min(
                                            8,
                                            Math.max(
                                              3,
                                              (cond.description || "").split(
                                                "\n",
                                              ).length + 1,
                                            ),
                                          )}
                                          placeholder={`5/8" Type X GWB each side\n3-5/8" studs @ 16" O.C.`}
                                          className="w-full resize-y rounded-md border border-white/10 bg-gray-950 px-2 py-1.5 text-[11.5px] leading-snug text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
                                        />
                                      </label>
                                      <div className="flex items-center gap-2 pt-0.5">
                                        <button
                                          onClick={() =>
                                            duplicateCondition(cond)
                                          }
                                          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-white/[0.06] px-2.5 text-[11.5px] text-gray-200 hover:bg-white/10"
                                        >
                                          <Icon name="duplicate" size={13} />{" "}
                                          Duplicate
                                        </button>
                                        <button
                                          onClick={() => setEditAsmId(null)}
                                          className="ml-auto h-8 rounded-md bg-orange-500 px-3.5 text-[11.5px] font-semibold text-white hover:bg-orange-400"
                                        >
                                          Done
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                  {bySheet.length > 0 && (
                                    <div className="flex flex-wrap gap-1 border-t border-white/5 px-2 py-1.5">
                                      {bySheet.map((b) => (
                                        <button
                                          key={b.pg}
                                          onClick={() => setPageNum(b.pg)}
                                          title={`Go to ${pageLabel(b.pg)}`}
                                          className={`rounded-md px-1.5 py-0.5 text-[10.5px] ${b.pg === pageNum ? "bg-orange-500/20 text-orange-100" : "bg-white/5 text-gray-300 hover:bg-white/10"}`}
                                        >
                                          {pageLabel(b.pg)} ·{" "}
                                          <b className="font-semibold">{b.q}</b>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                  {isOpen && condShapes.length === 0 && (
                                    <div className="border-t border-white/5 px-3 py-1.5 text-[10.5px] text-gray-500">
                                      Nothing drawn
                                      {asmScope === "sheet"
                                        ? " on this sheet"
                                        : ""}{" "}
                                      yet — pick it and draw.
                                    </div>
                                  )}
                                  {isOpen && condShapes.length > 0 && (
                                    <div className="max-h-56 overflow-y-auto border-t border-white/5 px-2 py-1">
                                      {condShapes.map((sh, i) => {
                                        const sFpu =
                                          scales[String(sh.page)] ??
                                          scales[sh.page] ??
                                          null;
                                        const { qty: q, unit } =
                                          shapePrimaryQty(sh, cond, sFpu);
                                        const isEditing =
                                          editingShapeId === sh.id;
                                        const isExtending =
                                          mode === "extend" &&
                                          drawRef.current?.extendId === sh.id;
                                        return (
                                          <div
                                            key={sh.id}
                                            data-shape-row={sh.id}
                                            className={`group/row flex items-center gap-1.5 rounded-md px-1 py-0.5 text-[11px] ${selIds.includes(sh.id) ? "bg-orange-500/20 ring-1 ring-orange-500/50" : "hover:bg-white/5"}`}
                                          >
                                            <span className="w-6 shrink-0 font-mono text-gray-600">
                                              {i + 1}
                                            </span>
                                            <button
                                              onClick={(e) => {
                                                focusShape(sh);
                                                if (e.shiftKey)
                                                  setSel(
                                                    selIds.includes(sh.id)
                                                      ? selIds.filter(
                                                          (x) => x !== sh.id,
                                                        )
                                                      : [...selIds, sh.id],
                                                  );
                                                else setSel([sh.id]);
                                              }}
                                              className="min-w-0 flex-1 truncate text-left text-gray-300 hover:text-orange-300"
                                              title={`Go to shape on ${pageLabel(sh.page)}`}
                                            >
                                              {q === null
                                                ? "set scale"
                                                : `${fmtQty(q)} ${unit}`}
                                              {sh.meta?.height &&
                                              cond.type === "linear" ? (
                                                <span className="ml-1 text-gray-500">
                                                  @ {fmtFeetIn(sh.meta.height)}
                                                </span>
                                              ) : null}
                                              <span className="ml-1 text-gray-600">
                                                ·{" "}
                                                {sh.label || pageLabel(sh.page)}
                                              </span>
                                            </button>
                                            <div className="hidden items-center gap-0.5 group-hover/row:flex">
                                              {sh.kind === "polyline" && (
                                                <button
                                                  onClick={() =>
                                                    isExtending
                                                      ? setPanMode()
                                                      : startExtendShape(sh)
                                                  }
                                                  className={`rounded px-1.5 py-0.5 ${isExtending ? "bg-orange-500 text-white" : "text-gray-400 hover:bg-white/10"}`}
                                                  title="Keep drawing this line (or select it and press Shift+Space)"
                                                >
                                                  {isExtending
                                                    ? "Stop"
                                                    : "Extend"}
                                                </button>
                                              )}
                                              <button
                                                onClick={() =>
                                                  toggleEditShape(sh)
                                                }
                                                className={`rounded px-1.5 py-0.5 ${isEditing ? "bg-orange-500 text-white" : "text-gray-400 hover:bg-white/10"}`}
                                                title="Edit vertices"
                                              >
                                                {isEditing ? "Done" : "Edit"}
                                              </button>
                                              <button
                                                onClick={() =>
                                                  deleteShape(sh.id)
                                                }
                                                className="rounded px-1 py-0.5 text-gray-500 hover:bg-red-500/15 hover:text-red-300"
                                                title="Delete shape"
                                              >
                                                <Icon name="x" size={12} />
                                              </button>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* this sheet: every other assembly, one chip each — pick one to draw it here */}
                  {asmScope === "sheet" &&
                    (() => {
                      const q = asmQuery.trim().toLowerCase();
                      const rest = conditions.filter(
                        (c) =>
                          !(
                            (pageMetrics[c.id]?.shapes || 0) > 0 ||
                            c.id === activeCondId
                          ) &&
                          (!q ||
                            `${c.name} ${c.description || ""}`
                              .toLowerCase()
                              .includes(q)),
                      );
                      if (!rest.length) return null;
                      const shut = !collapsedGroups.includes("others-open");
                      return (
                        <div>
                          <button
                            onClick={() =>
                              setCollapsedGroups((g) =>
                                g.includes("others-open")
                                  ? g.filter((x) => x !== "others-open")
                                  : [...g, "others-open"],
                              )
                            }
                            className="mb-1 flex w-full items-center gap-1 rounded px-1 py-0.5 text-[10px] font-medium uppercase tracking-wider text-gray-500 hover:bg-white/5 hover:text-gray-300"
                          >
                            <Icon name={shut ? "chevR" : "chevD"} size={12} />
                            Not on this sheet
                            <span className="normal-case tracking-normal text-gray-600">
                              · {rest.length}
                            </span>
                            <span className="ml-auto normal-case tracking-normal text-gray-600">
                              click one to draw it here
                            </span>
                          </button>
                          {!shut && (
                            <div className="flex flex-wrap gap-1 px-1 pb-1">
                              {rest.map((c) => (
                                <button
                                  key={c.id}
                                  onClick={() => {
                                    setActiveCondId(c.id);
                                    startDraw(c);
                                  }}
                                  title={`Draw: ${c.name}${c.description ? `\n${c.description}` : ""}${condMetrics[c.id]?.shapes || 0 ? `\n(${condMetrics[c.id].shapes} on other sheets)` : ""}`}
                                  className="inline-flex max-w-full items-center gap-1 truncate rounded-full border border-white/10 px-2 py-0.5 text-[10.5px] text-gray-400 hover:border-white/25 hover:text-gray-200"
                                >
                                  <span
                                    className="h-2 w-2 shrink-0 rounded-full"
                                    style={{ background: c.color }}
                                  />
                                  <span className="truncate">
                                    {c.no ? (
                                      <span className="mr-1 font-mono text-[10px] text-orange-300/80">
                                        #{c.no}
                                      </span>
                                    ) : null}
                                    {c.name}
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  {asmScope === "sheet" &&
                    conditions.length > 0 &&
                    !conditions.some(
                      (c) => (pageMetrics[c.id]?.shapes || 0) > 0,
                    ) && (
                      <p className="px-1 text-[11px] leading-snug text-gray-500">
                        Nothing taken off on this sheet yet. Pick an assembly to
                        draw, trace the sheet, or switch to{" "}
                        <button
                          onClick={() => setAsmScope("all")}
                          className="text-orange-300 underline decoration-dotted"
                        >
                          All sheets
                        </button>
                        .
                      </p>
                    )}
                  {/* typical units */}
                  {(units.length > 0 || conditions.length > 0) && (
                    <div>
                      <button
                        onClick={() =>
                          setCollapsedGroups((g) =>
                            g.includes("units")
                              ? g.filter((x) => x !== "units")
                              : [...g, "units"],
                          )
                        }
                        className="mb-1 flex w-full items-center gap-1 rounded px-1 py-0.5 text-[10px] font-medium uppercase tracking-wider text-gray-500 hover:bg-white/5 hover:text-gray-300"
                      >
                        <Icon
                          name={
                            collapsedGroups.includes("units")
                              ? "chevR"
                              : "chevD"
                          }
                          size={12}
                        />
                        Typical units
                        <span className="normal-case tracking-normal text-gray-600">
                          · {units.length}
                        </span>
                      </button>
                      {collapsedGroups.includes(
                        "units",
                      ) ? null : units.length === 0 ? (
                        <p className="px-1 text-[11px] leading-snug text-gray-600">
                          Select one unit's lines and areas (drag a box), then{" "}
                          <b className="text-gray-400">Make typical unit</b> —
                          name it and set how many the building has.
                        </p>
                      ) : (
                        <div className="space-y-1">
                          {units.map((u) => {
                            const n = shapes.filter(
                              (x) => x.meta?.unitId === u.id,
                            ).length;
                            return (
                              <div
                                key={u.id}
                                className="flex items-center gap-1.5 rounded-lg bg-white/[0.03] px-2 py-1.5"
                              >
                                <span
                                  className="h-2.5 w-2.5 shrink-0 rounded-sm"
                                  style={{ background: u.color || "#fb923c" }}
                                />
                                <input
                                  value={u.name}
                                  onChange={(e) =>
                                    updateUnit(u.id, { name: e.target.value })
                                  }
                                  className="min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 text-[12px] font-medium text-gray-100 hover:border-white/10 focus:border-orange-500 focus:bg-gray-950 focus:outline-none"
                                />
                                <span className="text-[11px] text-gray-500">
                                  ×
                                </span>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={u.qty}
                                  onChange={(e) =>
                                    updateUnit(u.id, {
                                      qty: Math.max(
                                        0,
                                        Math.round(Number(e.target.value) || 0),
                                      ),
                                    })
                                  }
                                  title="How many of this unit"
                                  className="h-6 w-12 rounded border border-white/10 bg-gray-950 px-1 text-right text-[12px] font-semibold text-orange-200 focus:border-orange-500 focus:outline-none"
                                />
                                <button
                                  onClick={() => selectUnit(u.id)}
                                  title="Select this unit's items"
                                  className="rounded px-1 text-[10.5px] text-gray-400 hover:bg-white/10 hover:text-white"
                                >
                                  {n} item{n === 1 ? "" : "s"}
                                </button>
                                <button
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        `Ungroup “${u.name}”? Its items stay, counted once.`,
                                      )
                                    )
                                      removeUnit(u.id);
                                  }}
                                  title="Ungroup (items stay, counted once)"
                                  className="rounded p-0.5 text-gray-500 hover:bg-red-500/15 hover:text-red-300"
                                >
                                  <Icon name="x" size={12} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  {conditions.length === 0 && !showAddCond && (
                    <div className="rounded-lg border border-dashed border-white/10 p-4 text-center text-[12px] text-gray-500">
                      No conditions yet. Use{" "}
                      <b className="text-gray-300">Assemblies &amp; trace</b> to
                      read your wall and ceiling types, or add one with{" "}
                      <b className="text-gray-300">New</b>.
                    </div>
                  )}
                </div>
              </section>
            </aside>
          )}

          {/* ═══════════════ MAP ═══════════════ */}
          <div className="relative min-h-0 min-w-0 flex-1 bg-gray-800">
            <div
              ref={mapElRef}
              className="h-full w-full min-h-0"
              style={{ background: "#1f2937" }}
            />

            {splicing && (
              <div className="pointer-events-none absolute inset-x-0 bottom-16 z-[1000] flex justify-center px-3">
                <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-orange-500/50 bg-gray-950/90 py-1.5 pl-3 pr-1.5 text-[12px] text-orange-100 shadow-lg backdrop-blur">
                  <Icon name="scissors" size={14} className="text-orange-400" />
                  <span>
                    <b>Cut a section</b> of {splicing.name}:{" "}
                    {splicing.step === 1
                      ? "click where it STARTS on the line"
                      : "now click where it ENDS"}
                    <span className="text-orange-200/60">
                      {" "}
                      · snaps to the line's corners · Esc to cancel
                    </span>
                  </span>
                  <button
                    onClick={cancelSplice}
                    className="rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-semibold text-gray-100 hover:bg-white/20"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* hidden assemblies: always said on the drawing, so nothing is redrawn thinking it's missing */}
            {hiddenConds.size > 0 &&
              pageImage &&
              (() => {
                const hid = conditions.filter((c) => hiddenConds.has(c.id));
                const nHere = shapes.filter(
                  (x) => x.page === pageNum && hiddenConds.has(x.conditionId),
                ).length;
                return (
                  <div className="pointer-events-none absolute inset-x-0 top-3 z-[1000] flex justify-center px-3">
                    <div className="pointer-events-auto flex max-w-full items-center gap-2 rounded-full border border-amber-500/40 bg-gray-950/90 py-1 pl-2.5 pr-1 text-[11.5px] text-amber-100 shadow-lg backdrop-blur">
                      <Icon
                        name="eyeOff"
                        size={14}
                        className="shrink-0 text-amber-300"
                      />
                      <span className="min-w-0 truncate">
                        <b>{hid.length}</b> assembl
                        {hid.length === 1 ? "y" : "ies"} hidden
                        {nHere ? (
                          <span className="text-amber-200/70">
                            {" "}
                            · {nHere} item{nHere === 1 ? "" : "s"} on this sheet
                            not shown
                          </span>
                        ) : null}
                      </span>
                      <span className="hidden max-w-[16rem] items-center gap-1 truncate md:flex">
                        {hid.slice(0, 4).map((c) => (
                          <button
                            key={c.id}
                            onClick={() => toggleHidden(c.id)}
                            title={`Show ${c.name}`}
                            className="inline-flex max-w-[7rem] items-center gap-1 truncate rounded-full bg-white/5 px-1.5 py-0.5 text-[10.5px] text-gray-300 hover:bg-white/10"
                          >
                            <span
                              className="h-2 w-2 shrink-0 rounded-full"
                              style={{ background: c.color }}
                            />
                            <span className="truncate">
                              {c.no ? `#${c.no} ` : ""}
                              {c.name.split(" — ")[0]}
                            </span>
                          </button>
                        ))}
                        {hid.length > 4 && (
                          <span className="text-gray-500">
                            +{hid.length - 4}
                          </span>
                        )}
                      </span>
                      <button
                        onClick={showAllConds}
                        className="shrink-0 rounded-full bg-amber-500 px-2.5 py-0.5 text-[11px] font-semibold text-gray-950 hover:bg-amber-400"
                      >
                        Show all
                      </button>
                    </div>
                  </div>
                );
              })()}

            {!fpu &&
              pageImage &&
              !scaleRun &&
              (scaleInfo[pageNum] || scaleInfo[String(pageNum)])?.source ===
                "flagged" &&
              !(scaleHint && scaleHint.page === pageNum && !scaleHint.multi) &&
              (() => {
                const inf = scaleInfo[pageNum] || scaleInfo[String(pageNum)];
                return (
                  <div className="absolute bottom-6 left-1/2 z-[1000] flex max-w-[44rem] -translate-x-1/2 flex-wrap items-center gap-2 rounded-lg border border-red-500/40 bg-gray-900/95 px-3 py-2 text-xs shadow-xl">
                    <span className="text-red-200">
                      <b>No scale on this sheet</b> — {inf.reason}
                    </span>
                    {inf.suggest && (
                      <button
                        onClick={() => acceptSuggestedScales([pageNum])}
                        title={inf.suggest.why}
                        className="rounded bg-orange-600 px-2 py-1 font-semibold text-white hover:bg-orange-500"
                      >
                        Use {inf.suggest.label} · suggested ({inf.suggest.why})
                      </button>
                    )}
                    {(inf.options || [])
                      .filter((o) => o.label !== inf.suggest?.label)
                      .map((o) => (
                        <button
                          key={o.label}
                          onClick={() => applySheetScale(pageNum, o)}
                          className="rounded bg-white/10 px-2 py-1 text-gray-100 hover:bg-white/15"
                        >
                          Use {o.label}
                        </button>
                      ))}
                    <button
                      onClick={startCalibrate}
                      className="rounded bg-white/10 px-2 py-1 text-gray-100 hover:bg-white/15"
                    >
                      Calibrate
                    </button>
                  </div>
                );
              })()}
            {scaleHint &&
              !fpu &&
              scaleHint.page === pageNum &&
              !scaleHint.multi && (
                <div className="absolute left-1/2 top-[4.5rem] z-[1000] flex -translate-x-1/2 items-center gap-3 rounded-lg border border-orange-600/50 bg-gray-900/95 px-3 py-2 text-xs shadow-xl">
                  {scaleHint.multi ? (
                    <span className="text-yellow-300">
                      This sheet prints several scales ({scaleHint.label}) —
                      calibrate on a known dimension.
                    </span>
                  ) : (
                    <>
                      <span>
                        Sheet says{" "}
                        <b className="text-orange-300">{scaleHint.label}</b>
                      </span>
                      <button
                        onClick={() => {
                          setScaleValue(scaleHint.fpu, {
                            source: "printed",
                            label: scaleHint.label,
                          });
                          setScaleHint(null);
                        }}
                        className="rounded bg-orange-600 px-2 py-1 font-semibold text-white hover:bg-orange-500"
                      >
                        Use this scale
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setScaleHint(null)}
                    className="text-gray-400 hover:text-white"
                  >
                    ✕
                  </button>
                </div>
              )}

            {toast && (
              <div
                className={`absolute left-1/2 top-3 z-[1200] max-w-lg -translate-x-1/2 rounded-lg px-4 py-2 text-xs font-medium shadow-xl ${toast.kind === "error" ? "bg-red-900/95 text-red-100" : toast.kind === "warn" ? "bg-yellow-900/95 text-yellow-100" : toast.kind === "ok" ? "bg-green-900/95 text-green-100" : "bg-gray-900/95 text-gray-100"}`}
              >
                {toast.msg}
              </div>
            )}

            {showLegend && (
              <div className="absolute right-3 top-3 z-[1000] flex max-h-[72%] w-72 flex-col overflow-hidden rounded-2xl border border-white/10 bg-gray-900/95 shadow-2xl backdrop-blur">
                <div className="flex items-center gap-2 border-b border-white/5 px-3 py-2">
                  <Icon name="legend" size={14} className="text-orange-400" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-semibold text-white">
                      Legend
                    </div>
                    <div className="truncate text-[10.5px] text-gray-500">
                      {pageLabel(pageNum)}
                    </div>
                  </div>
                  <button
                    onClick={() => setWd((w) => ({ ...w, open: true }))}
                    title="Print working drawings"
                    className="inline-flex h-7 items-center gap-1 rounded-md bg-orange-500 px-2 text-[11px] font-semibold text-white hover:bg-orange-400"
                  >
                    <Icon name="printer" size={13} />
                    Print
                  </button>
                  <button
                    onClick={() => setShowLegend(false)}
                    className="rounded p-1 text-gray-500 hover:bg-white/10 hover:text-gray-200"
                    title="Hide legend"
                  >
                    <Icon name="x" size={13} />
                  </button>
                </div>
                <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-2">
                  {legendRows.length === 0 && (
                    <p className="p-2 text-[11px] text-gray-500">
                      Nothing drawn on this sheet yet.
                    </p>
                  )}
                  {legendRows.map((r) => {
                    const tag =
                      r.cond.wallType &&
                      !String(r.cond.wallType).startsWith("__")
                        ? String(r.cond.wallType).replace(/·WR$/, "")
                        : "";
                    return (
                      <button
                        key={r.cond.id}
                        onClick={() => setActiveCondId(r.cond.id)}
                        title={r.desc.join("\n")}
                        className={`flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left ${activeCondId === r.cond.id ? "bg-orange-500/15" : "hover:bg-white/5"}`}
                      >
                        <span
                          className="mt-1 h-3 w-3 shrink-0 rounded-full ring-1 ring-white/20"
                          style={{ background: r.cond.color }}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[12px] font-medium text-gray-100">
                            {tag && (
                              <span className="mr-1 rounded bg-white/10 px-1 font-mono text-[10px]">
                                {tag}
                              </span>
                            )}
                            {r.cond.name}
                          </span>
                          {r.desc[0] && (
                            <span className="block truncate text-[10.5px] text-gray-500">
                              {r.desc.join(" · ")}
                            </span>
                          )}
                        </span>
                        <span className="shrink-0 text-right text-[11px] leading-tight tabular-nums text-orange-200">
                          {r.qty}
                          {r.height && (
                            <span className="block text-gray-500">
                              {r.height}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-3 border-t border-white/5 px-3 py-1.5 text-[10px] text-gray-500">
                  <span>
                    <span className="mr-1 inline-block h-1.5 w-4 rounded-sm bg-cyan-400/70 align-middle" />
                    window
                  </span>
                  <span>
                    <span className="mr-1 inline-block h-1.5 w-4 rounded-sm bg-fuchsia-400/70 align-middle" />
                    door
                  </span>
                </div>
              </div>
            )}

            {wd.open && (
              <div
                className="absolute inset-0 z-[1150] flex items-start justify-center bg-black/40 pt-[10vh]"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget)
                    setWd((w) => ({ ...w, open: false }));
                }}
              >
                <div
                  className="w-[28rem] max-w-[calc(100%-2rem)] rounded-2xl border border-white/10 bg-gray-900 p-4 text-xs shadow-2xl"
                  role="dialog"
                  aria-label="Print working drawings"
                >
                  <div className="text-[14px] font-semibold text-white">
                    Print working drawings
                  </div>
                  <p className="mb-3 mt-0.5 text-[11px] text-gray-400">
                    Each sheet prints at its own paper size, with the takeoff
                    drawn on it and the legend placed on the sheet.
                  </p>
                  {(() => {
                    const allPages = [
                      ...new Set(
                        shapes
                          .filter((s) => s.latlngs?.length)
                          .map((s) => s.page),
                      ),
                    ]
                      .filter((p) => !excludedPages.includes(p))
                      .sort((a, b) => a - b);
                    const lab =
                      "mb-1 block text-[10px] font-medium uppercase tracking-wider text-gray-500";
                    const sel =
                      "h-9 w-full rounded-lg border border-gray-700 bg-gray-800 px-2 text-gray-100 focus:border-orange-500 focus:outline-none";
                    return (
                      <div className="space-y-3">
                        <div>
                          <span className={lab}>Sheets</span>
                          <div className="space-y-1">
                            {[
                              ["this", `This sheet — ${pageLabel(pageNum)}`],
                              [
                                "all",
                                `Every sheet with takeoff (${allPages.length})`,
                              ],
                            ].map(([k, l]) => (
                              <label
                                key={k}
                                className="flex cursor-pointer items-center gap-2 text-[12.5px] text-gray-200"
                              >
                                <input
                                  type="radio"
                                  name="wd-scope"
                                  checked={wd.scope === k}
                                  onChange={() =>
                                    setWd((w) => ({ ...w, scope: k }))
                                  }
                                  className="accent-orange-500"
                                />
                                {l}
                              </label>
                            ))}
                          </div>
                          {wd.scope === "all" && (
                            <div className="mt-1.5 max-h-28 overflow-y-auto rounded-lg bg-black/25 px-2 py-1.5 text-[11px] text-gray-300">
                              {allPages.length ? (
                                allPages.map((p) => (
                                  <div key={p} className="truncate">
                                    <span className="mr-1.5 font-mono text-gray-500">
                                      {p}
                                    </span>
                                    {pageLabel(p)}
                                  </div>
                                ))
                              ) : (
                                <span className="text-gray-500">
                                  Nothing drawn yet.
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <label>
                            <span className={lab}>Legend</span>
                            <select
                              value={wd.legend}
                              onChange={(e) =>
                                setWd((w) => ({ ...w, legend: e.target.value }))
                              }
                              className={sel}
                            >
                              <option value="tl">Top-left of sheet</option>
                              <option value="tr">Top-right of sheet</option>
                              <option value="bl">Bottom-left of sheet</option>
                              <option value="br">Bottom-right of sheet</option>
                              <option value="side">
                                Beside the sheet (wider page)
                              </option>
                              <option value="none">No legend</option>
                            </select>
                          </label>
                          <label>
                            <span className={lab}>Quality</span>
                            <select
                              value={wd.dpi}
                              onChange={(e) =>
                                setWd((w) => ({
                                  ...w,
                                  dpi: Number(e.target.value),
                                }))
                              }
                              className={sel}
                            >
                              <option value={0}>
                                Fast — on-screen drawing (quickest)
                              </option>
                              <option value={150}>Standard (150 dpi)</option>
                              <option value={200}>High (200 dpi, slow)</option>
                            </select>
                          </label>
                        </div>
                        <label className="flex cursor-pointer items-center gap-2 text-[12.5px] text-gray-200">
                          <input
                            type="checkbox"
                            checked={wd.details}
                            onChange={(e) =>
                              setWd((w) => ({
                                ...w,
                                details: e.target.checked,
                              }))
                            }
                            className="accent-orange-500"
                          />
                          Include each assembly's description lines
                        </label>
                        <p className="rounded-lg bg-white/[0.04] px-2.5 py-2 text-[11px] leading-relaxed text-gray-400">
                          Opens one PDF with every sheet at its own paper size —{" "}
                          <b className="text-gray-300">Download</b> it to send
                          to site, <b className="text-gray-300">Print</b> it, or{" "}
                          <b className="text-gray-300">Share</b> it from a phone
                          or tablet. Fast reuses the drawing already on screen.
                        </p>
                        <div className="flex justify-end gap-2 pt-1">
                          <button
                            onClick={() =>
                              setWd((w) => ({ ...w, open: false }))
                            }
                            className="h-8 rounded-lg px-3 text-gray-300 hover:bg-white/10"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              const pages =
                                wd.scope === "all" ? allPages : [pageNum];
                              setWd((w) => ({ ...w, open: false }));
                              printWorkingDrawings({
                                pages,
                                legend: wd.legend,
                                details: wd.details,
                                dpi: wd.dpi,
                              });
                            }}
                            disabled={wd.scope === "all" && !allPages.length}
                            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-orange-500 px-3 font-semibold text-white hover:bg-orange-400 disabled:opacity-40"
                          >
                            <Icon name="printer" size={13} />
                            {wd.scope === "all"
                              ? `Make ${allPages.length} drawing${allPages.length === 1 ? "" : "s"}`
                              : "Make drawing"}
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {unitDraft && (
              <div
                className="absolute inset-0 z-[1150] flex items-start justify-center bg-black/30 pt-[14vh]"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) setUnitDraft(null);
                }}
              >
                <div
                  className="w-[26rem] max-w-[calc(100%-2rem)] rounded-2xl border border-white/10 bg-gray-900/95 p-4 text-xs shadow-2xl backdrop-blur"
                  role="dialog"
                  aria-label="Typical unit"
                >
                  <div className="mb-1 text-[13px] font-semibold text-white">
                    Make a typical unit
                  </div>
                  <p className="mb-3 text-[11px] text-gray-400">
                    The {unitDraft.ids.length} selected item
                    {unitDraft.ids.length === 1 ? "" : "s"} will count once per
                    unit — set how many of this unit the building has.
                  </p>
                  <div className="grid grid-cols-[1fr_6rem] gap-2">
                    <label className="space-y-1">
                      <span className="text-gray-400">Unit name</span>
                      <input
                        autoFocus
                        value={unitDraft.name}
                        onChange={(e) =>
                          setUnitDraft({ ...unitDraft, name: e.target.value })
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") createUnit(unitDraft);
                          if (e.key === "Escape") setUnitDraft(null);
                        }}
                        placeholder={`e.g. Unit A — 1 bed`}
                        className="h-9 w-full rounded-lg border border-gray-700 bg-gray-800 px-2 text-[13px] text-gray-100 focus:border-orange-500 focus:outline-none"
                      />
                    </label>
                    <label className="space-y-1">
                      <span className="text-gray-400">Quantity</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={unitDraft.qty}
                        onChange={(e) =>
                          setUnitDraft({ ...unitDraft, qty: e.target.value })
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") createUnit(unitDraft);
                          if (e.key === "Escape") setUnitDraft(null);
                        }}
                        className="h-9 w-full rounded-lg border border-gray-700 bg-gray-800 px-2 text-right text-[13px] font-semibold text-orange-200 focus:border-orange-500 focus:outline-none"
                      />
                    </label>
                  </div>
                  {units.length > 0 && (
                    <div className="mt-3 flex items-center gap-2 text-gray-400">
                      or add to
                      <select
                        defaultValue=""
                        onChange={(e) => {
                          if (!e.target.value) return;
                          const u = units.find((x) => x.id === e.target.value);
                          addToUnit(u.id, unitDraft.ids);
                          setUnitDraft(null);
                          flash(
                            `${unitDraft.ids.length} item(s) added to “${u.name}” × ${u.qty}.`,
                            "ok",
                          );
                        }}
                        className="h-8 flex-1 rounded-lg border border-gray-700 bg-gray-800 px-2 text-gray-100"
                      >
                        <option value="">an existing unit…</option>
                        {units.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name} × {u.qty}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      onClick={() => setUnitDraft(null)}
                      className="h-8 rounded-lg px-3 text-gray-300 hover:bg-white/10"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => createUnit(unitDraft)}
                      className="h-8 rounded-lg bg-orange-500 px-3 font-semibold text-white hover:bg-orange-400"
                    >
                      Create unit
                    </button>
                  </div>
                </div>
              </div>
            )}

            {revModal && (
              <div
                className="fixed inset-0 z-[2000] flex items-start justify-center bg-black/60 p-4 pt-[7vh]"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget && !revBusy)
                    setRevModal(null);
                }}
              >
                <div className="flex max-h-[84vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-gray-900 shadow-2xl">
                  <div className="border-b border-white/10 px-4 py-3">
                    <div className="text-sm font-semibold text-white">
                      {revModal.mode === "one"
                        ? `New revision of ${pageLabel(revModal.page)}`
                        : "Update sheets from a new drawing set"}
                    </div>
                    <div className="truncate text-[11px] text-gray-500">
                      {revModal.file?.name} · {revModal.numPages} page
                      {revModal.numPages === 1 ? "" : "s"} · the takeoff stays
                      as it is; matched pages update their sheet, unmatched
                      pages can be added as new sheets
                    </div>
                  </div>
                  {revModal.mode === "bulk" && revModal.unnamed > 0 && (
                    <div className="border-b border-amber-900/40 bg-amber-950/30 px-4 py-2 text-[11.5px] text-amber-200">
                      {revModal.unnamed} sheet
                      {revModal.unnamed === 1 ? " has" : "s have"} no name —
                      pages are matched by sheet name / number, so unnamed
                      sheets can only be picked by hand below. Name them
                      (Drawing ▸ Sheets ▸ Auto-name, or type them) for a safe
                      bulk update.
                    </div>
                  )}
                  <div className="min-h-0 flex-1 overflow-y-auto p-2">
                    <table className="w-full text-[12px]">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500">
                          <th className="px-2 py-1.5">New page</th>
                          <th className="px-2 py-1.5">Updates sheet</th>
                          <th className="px-2 py-1.5">Matched by</th>
                        </tr>
                      </thead>
                      <tbody>
                        {revModal.rows.map((r, ri) => (
                          <tr
                            key={`${r.src}-${ri}`}
                            className="border-t border-white/5"
                          >
                            <td className="px-2 py-1.5">
                              {revModal.mode === "one" &&
                              revModal.numPages > 1 ? (
                                <select
                                  value={r.src}
                                  onChange={(e) =>
                                    setRevModal((m) => ({
                                      ...m,
                                      rows: m.rows.map((x, k) =>
                                        k === ri
                                          ? {
                                              ...x,
                                              src: Number(e.target.value),
                                              why: "picked by hand",
                                            }
                                          : x,
                                      ),
                                    }))
                                  }
                                  className="h-7 rounded-md border border-white/10 bg-gray-950 px-1.5 text-[12px] text-gray-100"
                                >
                                  {Array.from(
                                    { length: revModal.numPages },
                                    (_, k) => k + 1,
                                  ).map((k) => (
                                    <option key={k} value={k}>
                                      Page {k}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span className="font-mono text-gray-300">
                                  Page {r.src}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-1.5">
                              {revModal.mode === "one" ? (
                                <span className="text-gray-100">
                                  {pageLabel(r.target)}
                                </span>
                              ) : (
                                <select
                                  value={r.target ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setRevModal((m) => ({
                                      ...m,
                                      rows: m.rows.map((x, k) =>
                                        k === ri
                                          ? {
                                              ...x,
                                              target:
                                                v === ""
                                                  ? null
                                                  : v === "new"
                                                    ? "new"
                                                    : Number(v),
                                              why:
                                                v === ""
                                                  ? "skipped"
                                                  : v === "new"
                                                    ? "added as a new sheet"
                                                    : "picked by hand",
                                            }
                                          : x,
                                      ),
                                    }));
                                  }}
                                  className={`h-10 w-full max-w-[20rem] rounded-md border bg-gray-950 px-1.5 text-[12px] ${r.target == null ? "border-white/10 text-gray-500" : r.target === "new" ? "border-emerald-500/40 text-emerald-200" : "border-sky-500/40 text-gray-100"}`}
                                >
                                  <option value="">— skip this page —</option>
                                  <option value="new">
                                    ＋ Add as a new sheet
                                  </option>
                                  {Array.from(
                                    { length: pageCount },
                                    (_, k) => k + 1,
                                  ).map((k) => (
                                    <option key={k} value={k}>
                                      {k} · {pageLabel(k)}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </td>
                            <td
                              className={`px-2 py-1.5 text-[11px] ${r.target == null ? "text-gray-500" : r.target === "new" ? "text-emerald-300" : /AI|hand/.test(r.why) ? "text-amber-300" : "text-emerald-300"}`}
                            >
                              {r.why}
                              {r.target != null &&
                                r.target !== "new" &&
                                revModal.rows.filter(
                                  (x) => x.target === r.target,
                                ).length > 1 && (
                                  <span className="ml-1 text-red-300">
                                    · same sheet twice
                                  </span>
                                )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 border-t border-white/10 px-4 py-3">
                    <span className="text-[11.5px] text-gray-400">
                      {revBusy ||
                        (() => {
                          const add = revModal.rows.filter(
                            (r) => r.target === "new",
                          ).length;
                          const upd = revModal.rows.filter(
                            (r) => r.target != null && r.target !== "new",
                          ).length;
                          return `${upd} sheet${upd === 1 ? "" : "s"} updated${add ? ` · ${add} new sheet${add === 1 ? "" : "s"} added` : ""}`;
                        })()}
                    </span>
                    {revModal.mode === "bulk" &&
                      revModal.rows.some((r) => r.target == null) && (
                        <button
                          disabled={!!revBusy}
                          onClick={() =>
                            setRevModal((m) => ({
                              ...m,
                              rows: m.rows.map((x) =>
                                x.target == null
                                  ? {
                                      ...x,
                                      target: "new",
                                      why: "added as a new sheet",
                                    }
                                  : x,
                              ),
                            }))
                          }
                          className="rounded-lg bg-emerald-500/15 px-2.5 py-1.5 text-[11.5px] text-emerald-200 hover:bg-emerald-500/25 disabled:opacity-40"
                        >
                          Add{" "}
                          {revModal.rows.filter((r) => r.target == null).length}{" "}
                          unmatched as new sheets
                        </button>
                      )}
                    {revModal.mode === "bulk" &&
                      revModal.rows.some((r) => r.target == null) && (
                        <button
                          disabled={!!revBusy}
                          onClick={aiNameRevisionPages}
                          title="Read the title blocks of the pages that didn't match (uses AI — only those pages)"
                          className="rounded-lg bg-white/5 px-2.5 py-1.5 text-[11.5px] text-gray-300 hover:bg-white/10 disabled:opacity-40"
                        >
                          Read unmatched title blocks (AI)
                        </button>
                      )}
                    <div className="ml-auto flex gap-2">
                      <button
                        disabled={!!revBusy}
                        onClick={() => setRevModal(null)}
                        className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15 disabled:opacity-40"
                      >
                        Cancel
                      </button>
                      <button
                        disabled={
                          !!revBusy ||
                          !revModal.rows.some((r) => r.target != null)
                        }
                        onClick={applyRevisions}
                        className="rounded-lg bg-orange-500 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-orange-400 disabled:opacity-40"
                      >
                        {revBusy ? "Working…" : "Apply"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {roomAsk && (
              <div
                className="absolute inset-0 z-[1150] flex items-start justify-center bg-black/30 pt-[12vh]"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) setRoomAsk(null);
                }}
              >
                <RoomMakePrompt
                  action={roomAsk}
                  current={roomMake}
                  onPick={chooseRoomMake}
                  onCancel={() => setRoomAsk(null)}
                />
              </div>
            )}

            {modeLabel && (
              <div className="pointer-events-none absolute bottom-4 left-1/2 z-[1000] max-w-[70%] -translate-x-1/2 rounded-full border border-orange-500/30 bg-gray-900/95 px-4 py-1.5 text-xs font-medium text-orange-200 shadow-lg backdrop-blur">
                {modeLabel}
              </div>
            )}

            {/* Measure panel */}
            {mode === "measure" &&
              (() => {
                const last = measures[measures.length - 1];
                const onPage = measures.filter((m) => m.page === pageNum);
                const ftOf = (m) => {
                  const f = scales[m.page] ?? scales[String(m.page)];
                  return f
                    ? Math.hypot(m.p2[0] - m.p1[0], m.p2[1] - m.p1[1]) * f
                    : null;
                };
                const lf = last ? ftOf(last) : null;
                const f = last
                  ? (scales[last.page] ?? scales[String(last.page)])
                  : null;
                const dx =
                  last && f ? Math.abs(last.p2[1] - last.p1[1]) * f : null;
                const dy =
                  last && f ? Math.abs(last.p2[0] - last.p1[0]) * f : null;
                const ang = last
                  ? Math.round(
                      ((Math.atan2(
                        Math.abs(last.p2[0] - last.p1[0]),
                        Math.abs(last.p2[1] - last.p1[1]),
                      ) *
                        180) /
                        Math.PI) *
                        10,
                    ) / 10
                  : null;
                const total = onPage.reduce((t, m) => t + (ftOf(m) || 0), 0);
                return (
                  <div className="absolute bottom-4 left-3 z-[1000] w-72 overflow-hidden rounded-2xl border border-white/10 bg-gray-900/95 shadow-2xl backdrop-blur">
                    <div className="flex items-center gap-2 border-b border-white/5 px-3 py-2">
                      <Icon
                        name="ruler"
                        size={15}
                        className="text-orange-400"
                      />
                      <span className="text-[12px] font-semibold text-white">
                        Measure
                      </span>
                      <span className="ml-auto text-[10.5px] text-gray-500">
                        {measurePts === 1
                          ? "click the second point"
                          : "click a first point"}
                      </span>
                    </div>
                    <div className="px-3 py-2.5">
                      {last ? (
                        <>
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-semibold tabular-nums text-orange-300">
                              {lf != null
                                ? fmtFeetIn(lf)
                                : `${fmtQty(measureUnits, 0)} px`}
                            </span>
                            {lf != null && (
                              <span className="text-[11px] tabular-nums text-gray-500">
                                {fmtQty(lf, 2)} ft
                              </span>
                            )}
                          </div>
                          {lf != null && (
                            <div className="mt-1.5 grid grid-cols-3 gap-1.5 text-[10.5px]">
                              {[
                                ["Across", fmtFeetIn(dx)],
                                ["Up / down", fmtFeetIn(dy)],
                                ["Angle", `${ang}°`],
                              ].map(([k, v]) => (
                                <div
                                  key={k}
                                  className="rounded-md bg-white/[0.04] px-1.5 py-1"
                                >
                                  <div className="text-gray-500">{k}</div>
                                  <div className="font-medium tabular-nums text-gray-200">
                                    {v}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {lf == null && (
                            <div className="mt-1 text-[11px] text-amber-300">
                              This sheet has no scale yet — set it to read feet.
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-[11.5px] leading-relaxed text-gray-400">
                          Click two points. Points snap to the drawing's corners
                          and lines
                          <span className="text-sky-300"> (blue ring)</span> and
                          lock straight — hold{" "}
                          <kbd className="rounded bg-white/10 px-1">Shift</kbd>{" "}
                          for any angle.
                        </div>
                      )}
                      {onPage.length > 1 && (
                        <div className="mt-2.5 max-h-32 space-y-0.5 overflow-y-auto border-t border-white/5 pt-2">
                          {onPage.map((m, i) => (
                            <div
                              key={m.id}
                              className="group/m flex items-center gap-2 rounded px-1 text-[11px] hover:bg-white/5"
                            >
                              <span className="w-5 font-mono text-gray-600">
                                #{measures.indexOf(m) + 1}
                              </span>
                              <span className="flex-1 tabular-nums text-gray-200">
                                {ftOf(m) != null ? fmtFeetIn(ftOf(m)) : "—"}
                              </span>
                              <button
                                onClick={() => {
                                  setMeasures(
                                    measuresRef.current.filter(
                                      (x) => x.id !== m.id,
                                    ),
                                  );
                                  drawMeasures();
                                }}
                                className="hidden rounded px-1 text-gray-500 hover:text-red-300 group-hover/m:block"
                                title="Remove"
                              >
                                <Icon name="x" size={11} />
                              </button>
                            </div>
                          ))}
                          <div className="flex items-center gap-2 border-t border-white/5 px-1 pt-1 text-[11px]">
                            <span className="flex-1 text-gray-500">Total</span>
                            <b className="tabular-nums text-orange-200">
                              {fmtFeetIn(total)}
                            </b>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 border-t border-white/5 bg-black/20 px-3 py-2">
                      <label
                        className="flex cursor-pointer items-center gap-1.5 text-[11px] text-gray-300"
                        title="Each measurement starts where the last one ended — walk a room's perimeter"
                      >
                        <input
                          type="checkbox"
                          checked={measureChain}
                          onChange={(e) => {
                            setMeasureChain(e.target.checked);
                            const lastM =
                              measuresRef.current[
                                measuresRef.current.length - 1
                              ];
                            if (
                              e.target.checked &&
                              lastM &&
                              !tempPtsRef.current.length
                            ) {
                              tempPtsRef.current = [lastM.p2];
                              setMeasurePts(1);
                            }
                          }}
                          className="accent-orange-500"
                        />
                        Chain
                      </label>
                      {measures.length > 0 && (
                        <button
                          onClick={clearMeasures}
                          className="rounded-md px-2 py-1 text-[11px] text-gray-400 hover:bg-white/10 hover:text-white"
                        >
                          Clear
                        </button>
                      )}
                      {last && (
                        <button
                          onClick={calibrateFromMeasure}
                          className="rounded-md px-2 py-1 text-[11px] text-gray-400 hover:bg-white/10 hover:text-white"
                          title="Use the last measurement to set this sheet's scale"
                        >
                          Set scale
                        </button>
                      )}
                      <button
                        onClick={setPanMode}
                        className="ml-auto rounded-md bg-orange-500 px-3 py-1 text-[11px] font-semibold text-white hover:bg-orange-400"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                );
              })()}
            {(loading || (pdfDoc && !mapReady)) && (
              <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-gray-900/60">
                <span className="text-sm text-gray-300">Rendering sheet…</span>
              </div>
            )}

            {!pdfDoc && !loading && (
              <div className="absolute inset-0 z-[900] flex items-center justify-center overflow-y-auto p-6">
                <div className="w-full max-w-md space-y-4 rounded-2xl border border-white/10 bg-gray-900/95 p-5 text-center shadow-2xl">
                  {fileName && (shapes.length > 0 || conditions.length > 0) ? (
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-white">
                        {fileName}
                      </p>
                      <p className="text-[12px] text-gray-400">
                        {gcsPath
                          ? "The saved drawing couldn't be loaded"
                          : "This takeoff's drawing isn't stored yet"}{" "}
                        — open the same PDF to carry on. Your takeoff (
                        {shapes.length} shape{shapes.length === 1 ? "" : "s"},{" "}
                        {conditions.length} assembl
                        {conditions.length === 1 ? "y" : "ies"}) is kept.
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-300">
                      Open a drawing set to get started
                    </p>
                  )}
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-400">
                    <Icon name="file" size={15} />
                    Open PDF
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={handleFilePick}
                    />
                  </label>
                  {savedList?.length > 0 && (
                    <div className="border-t border-white/5 pt-3 text-left">
                      <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-500">
                        Saved takeoffs on this project
                      </div>
                      <div className="max-h-64 space-y-1 overflow-y-auto">
                        {savedList.map((t) => (
                          <button
                            key={t._id}
                            onClick={() => onOpenTakeoff?.(t._id)}
                            disabled={t._id === takeoffId}
                            className="flex w-full items-center gap-2 rounded-lg bg-white/[0.03] px-2.5 py-2 text-left hover:bg-white/[0.07] disabled:opacity-50"
                          >
                            <Icon
                              name="file"
                              size={14}
                              className="shrink-0 text-gray-500"
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-[12px] font-medium text-gray-100">
                                {t.fileName || "Untitled drawing"}
                              </span>
                              <span className="block text-[10.5px] text-gray-500">
                                {t.pageCount || 0} sheets ·{" "}
                                {(t.conditions || []).length} assemblies
                                {t.updatedAt
                                  ? ` · saved ${new Date(t.updatedAt).toLocaleDateString()}`
                                  : ""}
                                {t.gcsPath ? "" : " · drawing not stored"}
                              </span>
                            </span>
                            {t._id === takeoffId ? (
                              <span className="text-[10px] text-gray-500">
                                open
                              </span>
                            ) : (
                              <Icon
                                name="arrowR"
                                size={14}
                                className="text-gray-500"
                              />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Calibration modal */}
            {calibDialog && (
              <div className="absolute inset-0 z-[1100] flex items-center justify-center bg-black/50">
                <div className="w-72 rounded-lg border border-gray-700 bg-gray-900 p-4 shadow-xl">
                  <h4 className="mb-1 text-sm font-semibold text-white">
                    Set scale — {pageLabel(pageNum)}
                  </h4>
                  <p className="mb-3 text-xs text-gray-400">
                    Enter the real-world length of the measured line.
                  </p>
                  <input
                    autoFocus
                    value={calibInput}
                    onChange={(e) => setCalibInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && applyCalibration()}
                    placeholder={`e.g. 10'-6" or 10.5`}
                    className="mb-2 w-full rounded border border-gray-600 bg-gray-800 px-2 py-1.5 text-sm text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                  />
                  <label className="mb-3 flex cursor-pointer items-center gap-1.5 text-[11px] text-gray-400">
                    <input
                      type="checkbox"
                      checked={scaleAllPages}
                      onChange={(e) => setScaleAllPages(e.target.checked)}
                      className="h-3 w-3 accent-orange-600"
                    />
                    Apply to all sheets
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={applyCalibration}
                      disabled={!parseFeetInput(calibInput)}
                      className="flex-1 rounded bg-orange-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-500 disabled:opacity-40"
                    >
                      Apply
                    </button>
                    <button
                      onClick={setPanMode}
                      className="flex-1 rounded bg-gray-700 px-3 py-1.5 text-xs text-gray-200 hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ═══════════════ RIGHT PANEL ═══════════════ */}
          {rightOpen && (
            <aside className="flex w-[22rem] shrink-0 flex-col border-l border-white/5 bg-gray-900">
              <div className="m-3 mb-0 flex rounded-xl bg-black/30 p-1 text-xs ring-1 ring-white/5">
                {[
                  [
                    "takeoff",
                    `Takeoff${pageProposals.length ? ` · ${pageProposals.length} to review` : ""}`,
                  ],
                  [
                    "checks",
                    `Checks${checks?.items?.length ? ` (${checks.items.length})` : ""}`,
                  ],
                  ["history", "History"],
                ].map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setRightTab(k)}
                    className={`flex-1 rounded-lg px-2 py-1.5 font-medium transition-colors ${rightTab === k ? "bg-white/10 text-white shadow-sm" : "text-gray-400 hover:text-gray-200"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3 text-xs">
                {/* ── the selected line(s) / area(s): details here, never over the drawing ── */}
                {selShapes.length > 1 &&
                  isNeutral(mode) &&
                  (() => {
                    const types = [
                      ...new Set(
                        selShapes.map(
                          (x) =>
                            conditions.find((c) => c.id === x.conditionId)
                              ?.type,
                        ),
                      ),
                    ];
                    const oneType = types.length === 1 ? types[0] : null;
                    const same = oneType
                      ? conditions.filter((c) => c.type === oneType)
                      : [];
                    const lf = fpu
                      ? selShapes
                          .filter((x) => x.kind !== "marker")
                          .reduce(
                            (t, x) =>
                              t +
                              pathLength(x.latlngs, x.kind === "polygon") * fpu,
                            0,
                          )
                      : null;
                    const condIds = [
                      ...new Set(selShapes.map((x) => x.conditionId)),
                    ];
                    return (
                      <section data-selection-panel>
                        <div className="rounded-xl border border-orange-500/30 bg-gray-900/95 p-3 text-xs ">
                          <div className="mb-2.5 flex items-center gap-2">
                            <div className="flex -space-x-1">
                              {condIds.slice(0, 5).map((id) => (
                                <span
                                  key={id}
                                  className="h-3 w-3 rounded-full ring-2 ring-gray-900"
                                  style={{
                                    background: conditions.find(
                                      (c) => c.id === id,
                                    )?.color,
                                  }}
                                />
                              ))}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-[13px] font-semibold text-white">
                                {selShapes.length} selected
                              </div>
                              <div className="text-[11px] text-gray-400">
                                {lf !== null && <>{fmtQty(lf, 0)} LF</>} ·{" "}
                                {condIds.length} assembl
                                {condIds.length === 1 ? "y" : "ies"} ·
                                Shift+click to add / remove
                              </div>
                            </div>
                            <IconBtn
                              title="Delete all selected  (Del)"
                              danger
                              onClick={() =>
                                handlersRef.current.kb.deleteSelected()
                              }
                            >
                              <Icon name="trash" />
                            </IconBtn>
                            <IconBtn
                              title="Clear selection  (Esc)"
                              onClick={() => setSel([])}
                            >
                              <Icon name="x" />
                            </IconBtn>
                          </div>
                          {oneType ? (
                            <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2">
                              <span className="text-gray-400">Assembly</span>
                              <AssemblySelect
                                size="sm"
                                options={same}
                                value={condIds.length === 1 ? condIds[0] : ""}
                                placeholder="Mixed — set all to…"
                                recent={recentCondIds}
                                onChange={(id) => {
                                  reassignShapes(
                                    selShapes.map((x) => x.id),
                                    id,
                                  );
                                  noteRecentCond(id);
                                }}
                              />
                              {oneType === "linear" && (
                                <>
                                  <span className="text-gray-400">Height</span>
                                  <input
                                    defaultValue=""
                                    placeholder="set all, e.g. 9'-0&quot;"
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        const h = parseFeetInput(
                                          e.currentTarget.value,
                                        );
                                        if (h) {
                                          setShapeHeight(
                                            selShapes.map((x) => x.id),
                                            h,
                                          );
                                          flash(
                                            `${fmtFeetIn(h)} on ${selShapes.length} walls.`,
                                            "ok",
                                          );
                                          e.currentTarget.blur();
                                        }
                                      }
                                    }}
                                    className="h-8 w-40 rounded-lg border border-gray-700 bg-gray-800 px-2 text-gray-100 focus:border-orange-500 focus:outline-none"
                                  />
                                </>
                              )}
                            </div>
                          ) : (
                            <p className="text-gray-500">
                              Walls and areas are both selected — pick one kind
                              to change the assembly, or delete them all.
                            </p>
                          )}
                          <div className="mt-2.5 flex flex-wrap items-center gap-1.5 border-t border-gray-800 pt-2.5">
                            <button
                              onClick={() => {
                                setHidden(condIds, true);
                                setSel([]);
                                flash(
                                  `${condIds.length} assembl${condIds.length === 1 ? "y" : "ies"} hidden.`,
                                  "info",
                                );
                              }}
                              title="Take these assemblies off the drawing while you review (nothing is deleted)"
                              className="inline-flex h-7 items-center gap-1 rounded-lg bg-white/5 px-2 text-gray-200 hover:bg-white/10"
                            >
                              <Icon name="eyeOff" size={13} />
                              Hide{" "}
                              {condIds.length === 1
                                ? "assembly"
                                : `${condIds.length} assemblies`}
                            </button>
                            {oneType === "linear" &&
                              condIds.length === 1 &&
                              selShapes.every((x) => x.kind === "polyline") && (
                                <button
                                  onClick={() =>
                                    mergeSelectedLines(
                                      selShapes.map((x) => x.id),
                                    )
                                  }
                                  title="Join the lines whose ends meet into one line and drop extra points"
                                  className="inline-flex h-7 items-center gap-1 rounded-lg bg-white/5 px-2 text-gray-200 hover:bg-white/10"
                                >
                                  <Icon name="merge" size={13} />
                                  Merge into one line
                                </button>
                              )}
                            {selShapes.some(
                              (x) =>
                                x.kind === "polyline" || x.kind === "polygon",
                            ) && (
                              <button
                                onClick={() =>
                                  simplifyShapes(selShapes.map((x) => x.id))
                                }
                                className="inline-flex h-7 items-center gap-1 rounded-lg bg-white/5 px-2 text-gray-200 hover:bg-white/10"
                              >
                                Remove extra points
                              </button>
                            )}
                            <button
                              onClick={() =>
                                setUnitDraft({
                                  ids: selShapes.map((x) => x.id),
                                  name: "",
                                  qty: "1",
                                })
                              }
                              title="Group these as a typical unit and set how many of them the building has"
                              className="ml-auto inline-flex h-7 items-center gap-1 rounded-lg bg-orange-500/15 px-2 font-medium text-orange-200 hover:bg-orange-500/25"
                            >
                              <Icon name="copy" size={13} />
                              Make typical unit…
                            </button>
                          </div>
                        </div>
                      </section>
                    );
                  })()}
                {selShape &&
                  selCond &&
                  isNeutral(mode) &&
                  (() => {
                    const same = conditions.filter(
                      (c) => c.type === selCond.type,
                    );
                    const isWall = selCond.type === "linear";
                    const lf = fpu
                      ? pathLength(
                          selShape.latlngs,
                          selShape.kind === "polygon",
                        ) * fpu
                      : null;
                    const sf =
                      fpu && selShape.kind === "polygon"
                        ? ringArea(selShape.latlngs) * fpu * fpu
                        : null;
                    const untaggedOnSheet = isUntagged(selCond)
                      ? shapes.filter(
                          (s) =>
                            s.page === pageNum && s.conditionId === selCond.id,
                        )
                      : [];
                    const src = selShape.meta?.tagSource;
                    return (
                      <section data-selection-panel>
                        <div className="rounded-xl border border-gray-700/80 bg-gray-900/95 p-3 text-xs ">
                          <div className="mb-3 flex items-start gap-2">
                            <span
                              className="mt-1 h-3 w-3 shrink-0 rounded-full ring-2 ring-white/10"
                              style={{ background: selCond.color }}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-[13px] font-semibold text-white">
                                {isWall
                                  ? "Wall"
                                  : selShape.kind === "marker"
                                    ? selCond.name
                                    : "Area"}
                                {selShape.label ? ` · ${selShape.label}` : ""}
                              </div>
                              <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-400">
                                {lf !== null && <>{fmtQty(lf, 1)} LF</>}
                                {sf !== null && <> · {fmtQty(sf, 0)} SF</>}
                                {src === "thickness" && (
                                  <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-amber-300">
                                    guessed from wall thickness
                                    {selShape.meta?.tagApproved
                                      ? " · approved"
                                      : ""}
                                  </span>
                                )}
                                {src === "ai" && (
                                  <span
                                    className="rounded bg-violet-500/15 px-1.5 py-0.5 text-violet-300"
                                    title={selShape.meta?.aiEvidence || ""}
                                  >
                                    AI read tag {selShape.meta?.wallTag}
                                  </span>
                                )}
                                {src === "check" && (
                                  <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-amber-300">
                                    {selShape.meta?.tagApproved
                                      ? "tag checked · approved"
                                      : "tag needs a check"}
                                  </span>
                                )}
                                {src === "tag" && selShape.meta?.wallTag && (
                                  <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-emerald-300">
                                    tag {selShape.meta.wallTag} on drawing
                                  </span>
                                )}
                                {isUntagged(selCond) && (
                                  <span className="rounded bg-slate-500/20 px-1.5 py-0.5 text-slate-300">
                                    no tag found
                                  </span>
                                )}
                                {selShape.meta?.gridLabel && (
                                  <span
                                    className={`rounded px-1.5 py-0.5 ${src === "grid" ? "bg-sky-500/15 text-sky-300" : "bg-white/5 text-gray-300"}`}
                                  >
                                    {selShape.meta.gridLabel}
                                    {src === "grid" ? " → typed from grid" : ""}
                                  </span>
                                )}
                                {src === "tag" &&
                                  selShape.meta?.role === "ceiling" && (
                                    <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-emerald-300">
                                      tag / callout on drawing
                                    </span>
                                  )}
                              </div>
                            </div>
                            <IconBtn
                              title="Delete  (Del)"
                              danger
                              onClick={() => {
                                deleteShape(selShape.id);
                                setSelShapeId(null);
                              }}
                            >
                              <Icon name="trash" />
                            </IconBtn>
                            <IconBtn
                              title="Close  (Esc)"
                              onClick={() => setSelShapeId(null)}
                            >
                              <Icon name="x" />
                            </IconBtn>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <div className="mb-1 flex items-center text-[11px] font-medium text-gray-400">
                                {isWall ? "Assembly" : "Type"}
                                <span className="ml-auto text-[10px] text-gray-500">
                                  {same.length} to choose from
                                </span>
                              </div>
                              <AssemblySelect
                                options={same}
                                value={selShape.conditionId}
                                recent={recentCondIds}
                                suggested={(() => {
                                  const m = selShape.meta || {};
                                  const ids = [];
                                  const alt =
                                    m.tagAlt && wallCondForTag(m.tagAlt, m.wet);
                                  if (alt) ids.push(alt.id);
                                  const ai =
                                    m.aiSuggest?.tag &&
                                    wallCondForTag(m.aiSuggest.tag, m.wet);
                                  if (ai) ids.push(ai.id);
                                  return ids;
                                })()}
                                onChange={(id) => {
                                  reassignShapes([selShape.id], id);
                                  noteRecentCond(id);
                                }}
                              />
                              {selCond.description && (
                                <details className="group mt-1.5 rounded-lg bg-white/[0.03] px-2 py-1">
                                  <summary className="cursor-pointer list-none text-[11px] text-gray-400 hover:text-gray-200">
                                    <span className="group-open:hidden">
                                      Show build-up ▾
                                    </span>
                                    <span className="hidden group-open:inline">
                                      Hide build-up ▴
                                    </span>
                                  </summary>
                                  <div className="mt-1 max-h-32 overflow-y-auto whitespace-pre-line text-[11px] leading-snug text-gray-400">
                                    {selCond.description}
                                  </div>
                                </details>
                              )}
                            </div>
                            {isWall && (
                              <div>
                                <div className="mb-1 text-[11px] font-medium text-gray-400">
                                  Height
                                </div>
                                <div className="flex items-center gap-2">
                                  <input
                                    value={heightDraft}
                                    onChange={(e) =>
                                      setHeightDraft(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        const h = parseFeetInput(heightDraft);
                                        if (h) setShapeHeight([selShape.id], h);
                                      }
                                    }}
                                    onBlur={() => {
                                      const h = parseFeetInput(heightDraft);
                                      if (h) setShapeHeight([selShape.id], h);
                                    }}
                                    className="h-8 w-24 rounded-lg border border-gray-700 bg-gray-950/60 px-2 text-gray-100 focus:border-orange-500 focus:outline-none"
                                    placeholder={`9'-0"`}
                                  />
                                  <button
                                    onClick={() => {
                                      const h = parseFeetInput(heightDraft);
                                      if (!h) return;
                                      setShapeHeight(
                                        shapes
                                          .filter(
                                            (s) =>
                                              s.page === pageNum &&
                                              s.conditionId ===
                                                selShape.conditionId,
                                          )
                                          .map((s) => s.id),
                                        h,
                                      );
                                      flash(
                                        `${fmtFeetIn(h)} on every ${selCond.name} wall on this sheet.`,
                                        "ok",
                                      );
                                    }}
                                    className="h-8 rounded-lg px-2 text-[11px] text-gray-400 hover:bg-white/5 hover:text-gray-200"
                                  >
                                    Apply to this type on the sheet
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                          {selShape.kind === "polyline" &&
                            isUnsureWall(selShape) &&
                            !isUntagged(selCond) &&
                            (() => {
                              const m = selShape.meta || {};
                              const altCond =
                                m.tagAlt && wallCondForTag(m.tagAlt, m.wet);
                              const dashedHere = shapes.filter(
                                (s) =>
                                  s.page === pageNum &&
                                  s.conditionId === selShape.conditionId &&
                                  isUnsureWall(s),
                              );
                              const reasons = m.tagCheck?.length
                                ? m.tagCheck
                                : [
                                    "no tag on this line — typed from the wall's thickness",
                                  ];
                              return (
                                <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-[11px] text-amber-100">
                                  <div className="mb-1 font-semibold text-amber-300">
                                    Check this wall's type
                                  </div>
                                  <ul className="mb-1.5 space-y-0.5 text-amber-100/80">
                                    {reasons.map((r) => (
                                      <li key={r}>• {r}</li>
                                    ))}
                                    {m.aiSuggest && (
                                      <li className="text-violet-200">
                                        • AI thinks {m.aiSuggest.tag} (
                                        {m.aiSuggest.confidence})
                                        {m.aiSuggest.evidence
                                          ? ` — ${m.aiSuggest.evidence}`
                                          : ""}
                                      </li>
                                    )}
                                  </ul>
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <button
                                      onClick={() =>
                                        approveWalls([selShape.id])
                                      }
                                      title="Keep this type and draw the line solid  (A)"
                                      className="inline-flex h-7 items-center gap-1 rounded-md bg-emerald-600 px-2 font-semibold text-white hover:bg-emerald-500"
                                    >
                                      ✓ Approve
                                      <kbd className="rounded bg-white/20 px-1 text-[10px] font-medium">
                                        A
                                      </kbd>
                                    </button>
                                    {!m.aiSuggest && (
                                      <button
                                        onClick={() =>
                                          askAiForWalls([selShape.id])
                                        }
                                        disabled={aiAsking}
                                        title="Let the AI read the tags and arrows around this wall"
                                        className="inline-flex h-7 items-center rounded-md bg-violet-600/80 px-2 text-white hover:bg-violet-500 disabled:opacity-50"
                                      >
                                        {aiAsking ? "Asking AI…" : "Ask AI"}
                                      </button>
                                    )}
                                    {altCond &&
                                      altCond.id !== selShape.conditionId && (
                                        <button
                                          onClick={() =>
                                            reassignShapes(
                                              [selShape.id],
                                              altCond.id,
                                            )
                                          }
                                          className="inline-flex h-7 items-center rounded-md bg-white/10 px-2 text-gray-100 hover:bg-white/15"
                                        >
                                          Use {m.tagAlt}
                                        </button>
                                      )}
                                    {dashedHere.length > 1 && (
                                      <button
                                        onClick={() =>
                                          askAiForWalls(
                                            dashedHere
                                              .filter((s) => !s.meta?.aiSuggest)
                                              .map((s) => s.id),
                                          )
                                        }
                                        disabled={
                                          aiAsking ||
                                          dashedHere.every(
                                            (s) => s.meta?.aiSuggest,
                                          )
                                        }
                                        className="inline-flex h-7 items-center rounded-md px-2 text-violet-200 hover:bg-white/10 disabled:opacity-40"
                                      >
                                        Ask AI for all {dashedHere.length}
                                      </button>
                                    )}
                                    {dashedHere.length > 1 && (
                                      <button
                                        onClick={() =>
                                          approveWalls(
                                            dashedHere.map((s) => s.id),
                                          )
                                        }
                                        className="ml-auto inline-flex h-7 items-center rounded-md px-2 text-amber-200 hover:bg-white/10"
                                      >
                                        Approve all {dashedHere.length} on this
                                        sheet
                                      </button>
                                    )}
                                  </div>
                                  <div className="mt-1 text-[10px] text-amber-100/60">
                                    Or pick another assembly above.
                                  </div>
                                </div>
                              );
                            })()}
                          {selShape.kind === "marker" &&
                            (selShape.meta?.needsCheck ||
                              selShape.meta?.aiOk) &&
                            (() => {
                              const m = selShape.meta || {};
                              const toCheck = shapes.filter(
                                (s) =>
                                  s.page === pageNum &&
                                  s.kind === "marker" &&
                                  s.meta?.needsCheck,
                              );
                              if (!m.needsCheck)
                                return (
                                  <div className="mt-3 rounded-lg bg-violet-500/10 px-2 py-1.5 text-[11px] text-violet-200">
                                    ✓ AI confirmed this{" "}
                                    {m.role === "window"
                                      ? "window"
                                      : m.role === "door2"
                                        ? "double door"
                                        : "door"}
                                    {m.aiAdded
                                      ? " — the trace had missed it"
                                      : ""}
                                  </div>
                                );
                              return (
                                <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-[11px] text-amber-100">
                                  <div className="mb-1 font-semibold text-amber-300">
                                    Check this{" "}
                                    {m.role === "window" ? "window" : "door"}
                                  </div>
                                  <div className="mb-1.5 text-amber-100/80">
                                    {m.needsCheck}
                                  </div>
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <button
                                      onClick={() =>
                                        approveWalls([selShape.id])
                                      }
                                      title="It's right — count it  (A)"
                                      className="inline-flex h-7 items-center gap-1 rounded-md bg-emerald-600 px-2 font-semibold text-white hover:bg-emerald-500"
                                    >
                                      ✓ Approve
                                      <kbd className="rounded bg-white/20 px-1 text-[10px] font-medium">
                                        A
                                      </kbd>
                                    </button>
                                    <button
                                      onClick={() => {
                                        deleteShape(selShape.id);
                                        setSelShapeId(null);
                                      }}
                                      className="inline-flex h-7 items-center rounded-md bg-white/10 px-2 text-gray-100 hover:bg-white/15"
                                    >
                                      Not one — delete
                                    </button>
                                    {toCheck.length > 1 && (
                                      <span className="ml-auto text-amber-200/70">
                                        {toCheck.length} to check on this sheet
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-1 text-[10px] text-amber-100/60">
                                    Drag the marker onto the opening if it's
                                    off.
                                  </div>
                                </div>
                              );
                            })()}
                          {isWall &&
                            (() => {
                              const n = wallNet(selShape, selCond, lf || 0);
                              const m = selShape.meta || {};
                              const nWin =
                                  m.windows?.length || (n.winLf > 0 ? 1 : 0),
                                nDoor = m.doors?.length || 0;
                              const nDouble = (m.doorKinds || []).filter(
                                (k) => k === "double",
                              ).length;
                              const patch = (p) =>
                                setShapes((prev) =>
                                  prev.map((x) =>
                                    x.id === selShape.id
                                      ? {
                                          ...x,
                                          meta: { ...(x.meta || {}), ...p },
                                        }
                                      : x,
                                  ),
                                );
                              // add a door by hand: deducted from this wall and counted on the door assembly (marker on the line)
                              const addDoor = (kind) => {
                                const ds = doorSizes(traceSettings);
                                const w =
                                  kind === "double" ? ds.double : ds.single;
                                // with a scale: click where the door is on the wall (it's placed there, and can be dragged after)
                                const f =
                                  scales[selShape.page] ??
                                  scales[String(selShape.page)];
                                if (f) {
                                  placeDoorRef.current = {
                                    shapeId: selShape.id,
                                    kind,
                                    w,
                                  };
                                  setPlacingDoor(kind);
                                  mapRef.current?.dragging.disable();
                                  setMapCursor("crosshair");
                                  flash(
                                    `Click the wall where the ${kind === "double" ? "double door" : "door"} is (Esc to cancel).`,
                                    "info",
                                  );
                                  return;
                                }
                                const kinds = [
                                  ...(m.doorKinds ||
                                    (m.doors || []).map(() => "single")),
                                  kind,
                                ];
                                patch({
                                  doors: [...(m.doors || []), w],
                                  doorKinds: kinds,
                                  doorLf:
                                    Math.round(
                                      ((Number(m.doorLf) || 0) + w) * 100,
                                    ) / 100,
                                  doorHeight: m.doorHeight || ds.height,
                                });
                                addCountMarker(
                                  kind === "double" ? "door2" : "door",
                                  lineMidpoint(selShape.latlngs),
                                  selShape.page,
                                );
                                flash(
                                  `${kind === "double" ? "Double door" : "Door"} ${fmtFeetIn(w)} × ${fmtFeetIn(Number(m.doorHeight) || ds.height)} deducted and counted.`,
                                  "ok",
                                );
                              };
                              return (
                                <div className="mt-3 space-y-1.5 border-t border-gray-800 pt-3">
                                  {nWin > 0 && (
                                    <div className="flex items-center gap-2">
                                      <span className="h-2 w-4 rounded-sm bg-cyan-400/60" />
                                      <span className="text-gray-300">
                                        {nWin} window{nWin === 1 ? "" : "s"}:{" "}
                                        <b className="text-cyan-300">
                                          −{fmtQty(n.winLf, 1)} LF
                                        </b>
                                        {n.winSf > 0 && (
                                          <> · −{fmtQty(n.winSf, 0)} SF</>
                                        )}
                                      </span>
                                      <button
                                        onClick={() =>
                                          patch({
                                            deductLf: 0,
                                            deductSf: 0,
                                            windowSegs: [],
                                            windows: [],
                                          })
                                        }
                                        className="ml-auto rounded-md px-1.5 py-0.5 text-gray-400 hover:bg-white/10 hover:text-white"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  )}
                                  {nDoor > 0 && (
                                    <div className="flex items-center gap-2">
                                      <span className="h-2 w-4 rounded-sm bg-fuchsia-400/60" />
                                      <span className="text-gray-300">
                                        {nDoor} door{nDoor === 1 ? "" : "s"}
                                        {nDouble
                                          ? ` (${nDouble} double)`
                                          : ""}:{" "}
                                        <b className="text-fuchsia-300">
                                          −{fmtQty(n.doorLf, 1)} LF
                                        </b>{" "}
                                        · −{fmtQty(n.doorSf, 0)} SF
                                      </span>
                                      <span className="text-gray-500">@</span>
                                      <input
                                        defaultValue={fmtFeetIn(
                                          Number(m.doorHeight) ||
                                            DEFAULT_DOOR_H,
                                        )}
                                        key={`dh-${selShape.id}-${m.doorHeight}`}
                                        title="Door height"
                                        onBlur={(e) => {
                                          const v = parseFeetInput(
                                            e.target.value,
                                          );
                                          if (v) patch({ doorHeight: v });
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter")
                                            e.currentTarget.blur();
                                        }}
                                        className="h-6 w-14 rounded border border-gray-700 bg-gray-800 px-1 text-[11px] text-gray-100"
                                      />
                                      <button
                                        onClick={() =>
                                          patch({
                                            doorLf: 0,
                                            doors: [],
                                            doorSegs: [],
                                            doorKinds: [],
                                            doorFlips: [],
                                          })
                                        }
                                        className="ml-auto rounded-md px-1.5 py-0.5 text-gray-400 hover:bg-white/10 hover:text-white"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2 text-gray-500">
                                    {(nWin > 0 || nDoor > 0) && (
                                      <span>
                                        Net {fmtQty(n.lf, 1)} LF
                                        {n.h ? ` · ${fmtQty(n.sf, 0)} SF` : ""}
                                      </span>
                                    )}
                                    <button
                                      onClick={() => addDoor("single")}
                                      className="ml-auto inline-flex items-center gap-1 rounded-md bg-white/5 px-2 py-0.5 text-gray-300 hover:bg-white/10"
                                    >
                                      <Icon name="plus" size={12} />
                                      Door{" "}
                                      {fmtFeetIn(
                                        doorSizes(traceSettings).single,
                                      )}
                                    </button>
                                    <button
                                      onClick={() => addDoor("double")}
                                      className="inline-flex items-center gap-1 rounded-md bg-white/5 px-2 py-0.5 text-gray-300 hover:bg-white/10"
                                    >
                                      <Icon name="plus" size={12} />
                                      Double{" "}
                                      {fmtFeetIn(
                                        doorSizes(traceSettings).double,
                                      )}
                                    </button>
                                  </div>
                                </div>
                              );
                            })()}
                          {untaggedOnSheet.length > 1 && (
                            <div className="mt-3 flex items-center gap-2 border-t border-gray-800 pt-3 text-gray-400">
                              <span>
                                {untaggedOnSheet.length}{" "}
                                {isWall
                                  ? "untagged walls"
                                  : `like this (${selCond.name.replace(/^Untagged ceiling — /, "")})`}{" "}
                                on this sheet →
                              </span>
                              <div className="min-w-0 flex-1">
                                <AssemblySelect
                                  size="sm"
                                  options={same.filter((c) => !isUntagged(c))}
                                  value=""
                                  placeholder="Assign all to…"
                                  recent={recentCondIds}
                                  onChange={(id) => {
                                    reassignShapes(
                                      untaggedOnSheet.map((s) => s.id),
                                      id,
                                    );
                                    noteRecentCond(id);
                                  }}
                                />
                              </div>
                            </div>
                          )}
                          <div className="mt-3 space-y-2 border-t border-gray-800 pt-3">
                            <button
                              onClick={() =>
                                handlersRef.current.drawWith?.(
                                  selShape.conditionId,
                                )
                              }
                              title="Draw more with this assembly  (Space)"
                              className="flex h-9 w-full items-center justify-center gap-1.5 rounded-lg bg-orange-500 px-2 text-[12px] font-semibold text-white hover:bg-orange-400"
                            >
                              <Icon
                                name="pencil"
                                size={13}
                                className="shrink-0"
                              />
                              <span className="min-w-0 truncate">
                                Draw with {selCond.name}
                              </span>
                              <kbd className="ml-1 shrink-0 rounded bg-white/20 px-1 text-[10px] font-medium">
                                Space
                              </kbd>
                            </button>
                            {selShape.kind === "polyline" && (
                              <button
                                onClick={() => startSplice(selShape.id)}
                                disabled={!!splicing}
                                title="Click the start and the end of a stretch on this line — it becomes its own line (then pick its assembly); the rest stays as it is"
                                className="flex h-8 w-full items-center justify-center gap-1.5 rounded-lg bg-white/[0.07] px-2 text-[12px] font-medium text-gray-100 hover:bg-white/[0.12] disabled:opacity-50"
                              >
                                <Icon name="scissors" size={13} />
                                {splicing
                                  ? "Cutting — click the line…"
                                  : "Cut a section into another assembly"}
                              </button>
                            )}
                            <div className="flex items-center gap-1.5 text-[11px]">
                              <button
                                onClick={() => {
                                  setHidden([selShape.conditionId], true);
                                  setSelShapeId(null);
                                  flash(
                                    `${selCond.name} hidden — the banner on the drawing shows it again.`,
                                    "info",
                                  );
                                }}
                                title="Take this assembly off the drawing while you review (nothing is deleted)"
                                className="inline-flex h-7 items-center gap-1 rounded-lg bg-white/5 px-2 text-gray-200 hover:bg-white/10"
                              >
                                <Icon name="eyeOff" size={13} />
                                Hide assembly
                              </button>
                              <button
                                onClick={() =>
                                  isolateCond(selShape.conditionId)
                                }
                                title="Hide every other assembly"
                                className="inline-flex h-7 items-center gap-1 rounded-lg bg-white/5 px-2 text-gray-200 hover:bg-white/10"
                              >
                                <Icon name="eye" size={13} />
                                Show only this
                              </button>
                            </div>
                            {selShape.kind === "polyline" && (
                              <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                                <span className="text-gray-400">Extend</span>
                                <button
                                  title="Keep drawing from the start of this line"
                                  onClick={() =>
                                    startExtendShape(
                                      selShape,
                                      selShape.latlngs[0],
                                    )
                                  }
                                  className="inline-flex h-7 items-center gap-1 whitespace-nowrap rounded-lg bg-white/5 px-2 text-gray-200 hover:bg-white/10"
                                >
                                  <Icon name="chevL" size={13} />
                                  from start
                                </button>
                                <button
                                  title="Keep drawing from the end of this line"
                                  onClick={() =>
                                    startExtendShape(
                                      selShape,
                                      selShape.latlngs[
                                        selShape.latlngs.length - 1
                                      ],
                                    )
                                  }
                                  className="inline-flex h-7 items-center gap-1 whitespace-nowrap rounded-lg bg-white/5 px-2 text-gray-200 hover:bg-white/10"
                                >
                                  from end
                                  <Icon name="chevR" size={13} />
                                </button>
                                <span className="ml-auto whitespace-nowrap">
                                  <kbd className="rounded bg-white/10 px-1 text-gray-300">
                                    Shift+Space
                                  </kbd>
                                </span>
                              </div>
                            )}
                            {(() => {
                              const u =
                                selShape.meta?.unitId &&
                                units.find(
                                  (x) => x.id === selShape.meta.unitId,
                                );
                              const extra =
                                selShape.kind === "polyline"
                                  ? selShape.latlngs.length -
                                    simplifyPts(selShape.latlngs).length
                                  : selShape.kind === "polygon"
                                    ? selShape.latlngs.length -
                                      slimArea(selShape.latlngs, selShape.page)
                                        .length
                                    : 0;
                              return (
                                <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                                  {u ? (
                                    <span
                                      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium text-gray-900"
                                      style={{
                                        background: u.color || "#fb923c",
                                      }}
                                    >
                                      {u.name} × {u.qty}
                                    </span>
                                  ) : null}
                                  {u ? (
                                    <button
                                      onClick={() =>
                                        addToUnit(null, [selShape.id])
                                      }
                                      className="rounded-md px-1.5 py-0.5 text-gray-400 hover:bg-white/10 hover:text-white"
                                    >
                                      Remove from unit
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() =>
                                        setUnitDraft({
                                          ids: [selShape.id],
                                          name: "",
                                          qty: "1",
                                        })
                                      }
                                      className="rounded-md px-1.5 py-0.5 text-gray-400 hover:bg-white/10 hover:text-white"
                                    >
                                      Typical unit…
                                    </button>
                                  )}
                                  {extra > 0 && (
                                    <button
                                      onClick={() =>
                                        simplifyShapes([selShape.id])
                                      }
                                      className="ml-auto rounded-md bg-white/5 px-1.5 py-0.5 text-gray-300 hover:bg-white/10"
                                    >
                                      Remove {extra} extra point
                                      {extra === 1 ? "" : "s"}
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </section>
                    );
                  })()}
                {rightTab === "takeoff" && (
                  <>
                    {/* automate */}
                    <section className="space-y-2">
                      <button
                        onClick={() => setShowWizard(true)}
                        disabled={!pdfDoc}
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-3 py-2.5 text-[13px] font-semibold text-white shadow-sm shadow-orange-900/40 hover:bg-orange-400 disabled:opacity-40"
                      >
                        <Icon name="layers" />
                        Assemblies &amp; trace
                      </button>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => askRooms("detect")}
                          disabled={!pageImage || detecting}
                          title={`Detect rooms in view (${ROOM_MAKE_LABEL[roomMake]})`}
                          className="flex items-center justify-center gap-1.5 rounded-lg bg-white/5 px-2 py-2 text-gray-200 hover:bg-white/10 disabled:opacity-40"
                        >
                          <Icon name="scan" size={14} />
                          {detecting ? "Tracing…" : "Detect rooms"}
                        </button>
                        <button
                          onClick={() => askRooms("fill")}
                          disabled={!pageImage}
                          title={`Fill room — click inside a room (${ROOM_MAKE_LABEL[roomMake]})  R`}
                          className={`flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 disabled:opacity-40 ${mode === "fill" ? "bg-orange-500 text-white" : "bg-white/5 text-gray-200 hover:bg-white/10"}`}
                        >
                          <Icon name="wand" size={14} />
                          Fill one room
                        </button>
                      </div>
                      {pageProposals.length > 0 && (
                        <button
                          onClick={aiTagRooms}
                          disabled={aiBusy === "tag"}
                          className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-white/5 px-2 py-2 text-gray-200 hover:bg-white/10 disabled:opacity-40"
                        >
                          <Icon name="tag" size={14} />
                          {aiBusy === "tag"
                            ? "Tagging…"
                            : `AI tag the ${pageProposals.length} room${pageProposals.length === 1 ? "" : "s"} to review`}
                        </button>
                      )}
                      <p className="text-[11px] leading-relaxed text-gray-500">
                        Trace draws every room's walls and ceilings straight
                        onto your assemblies. Detect / Fill give room outlines
                        to review first — accept them below.
                      </p>
                    </section>

                    {/* floor heights */}
                    {Object.keys(traceSettings.floor || {}).length > 0 && (
                      <section>
                        <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                          Wall heights by floor
                        </h4>
                        <div className="space-y-1 rounded-xl bg-black/20 p-1.5">
                          {Object.entries(traceSettings.floor)
                            .filter(([, v]) => v?.on !== false)
                            .map(([n, v]) => {
                              const cnt = shapes.filter(
                                (x) =>
                                  x.page === Number(n) &&
                                  conditions.find((c) => c.id === x.conditionId)
                                    ?.type === "linear",
                              ).length;
                              const manual = shapes.filter(
                                (x) =>
                                  x.page === Number(n) && x.meta?.heightManual,
                              ).length;
                              return (
                                <div
                                  key={n}
                                  className={`flex items-center gap-2 rounded-lg px-2 py-1 ${Number(n) === pageNum ? "bg-white/5" : ""}`}
                                >
                                  <button
                                    onClick={() => setPageNum(Number(n))}
                                    className="min-w-0 flex-1 truncate text-left text-gray-300 hover:text-white"
                                    title={pageLabel(Number(n))}
                                  >
                                    {pageLabel(Number(n))}
                                  </button>
                                  <span
                                    className="text-[10px] text-gray-500"
                                    title={
                                      manual
                                        ? `${manual} wall(s) set by hand keep their height`
                                        : ""
                                    }
                                  >
                                    {cnt} walls
                                    {manual ? ` · ${manual} custom` : ""}
                                  </span>
                                  <input
                                    defaultValue={
                                      v.height ||
                                      traceSettings.defaultHeight ||
                                      ""
                                    }
                                    key={`${n}-${v.height}`}
                                    placeholder={`9'-0"`}
                                    onBlur={(e) => {
                                      if (e.target.value !== (v.height || "")) {
                                        setFloorHeight(n, e.target.value);
                                        flash(
                                          `${pageLabel(Number(n))}: walls now ${e.target.value}.`,
                                          "ok",
                                        );
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter")
                                        e.currentTarget.blur();
                                    }}
                                    className="h-7 w-16 rounded-md border border-white/10 bg-gray-950 px-1.5 text-center text-[12px] text-gray-100 focus:border-orange-500 focus:outline-none"
                                  />
                                </div>
                              );
                            })}
                        </div>
                        <p className="mt-1 text-[10px] text-gray-500">
                          Changing a floor updates every wall on that sheet
                          except the ones you've set by hand.
                        </p>
                      </section>
                    )}

                    {/* rooms to review */}
                    {(pageProposals.length > 0 || detectInfo) && (
                      <section className="space-y-2">
                        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                          Rooms to review · this sheet
                        </h4>
                        {detectInfo?.found !== undefined && (
                          <div className="rounded bg-gray-800 px-2 py-1.5 text-[11px] text-gray-400">
                            Found {detectInfo.found} room
                            {detectInfo.found === 1 ? "" : "s"}{" "}
                            {detectInfo.inView ? "in view" : "on the sheet"} (
                            {(detectInfo.ms / 1000).toFixed(1)} s). Zoom to one
                            plan and run again to limit it.
                          </div>
                        )}

                        {pageProposals.length > 0 && (
                          <div className="flex gap-2">
                            <button
                              onClick={() =>
                                acceptProposals(
                                  pageProposals.filter(
                                    (p) => (p.confidence ?? 0) >= 0.85,
                                  ),
                                )
                              }
                              className="flex-1 rounded bg-orange-600 px-2 py-1.5 font-semibold text-white hover:bg-orange-500"
                            >
                              Accept ≥85% (
                              {
                                pageProposals.filter(
                                  (p) => (p.confidence ?? 0) >= 0.85,
                                ).length
                              }
                              )
                            </button>
                            <button
                              onClick={() =>
                                setProposals((prev) =>
                                  prev.filter((p) => p.page !== pageNum),
                                )
                              }
                              className="rounded bg-gray-700 px-2 py-1.5 text-gray-300 hover:bg-red-900/60"
                            >
                              Clear
                            </button>
                          </div>
                        )}

                        <div className="space-y-1.5">
                          {pageProposals.map((p) => {
                            const sel = p.id === selProposal;
                            const by = perimSummary(p);
                            return (
                              <div
                                key={p.id}
                                className={`rounded border p-2 ${sel ? "border-yellow-500/70 bg-gray-800" : "border-gray-700 bg-gray-800/50"}`}
                              >
                                <div className="flex items-center gap-2">
                                  <button
                                    className="flex-1 truncate text-left font-semibold text-gray-100 hover:text-yellow-300"
                                    onClick={() => {
                                      setSelProposal(p.id);
                                      const l = proposalLayersRef.current.get(
                                        p.id,
                                      )?._poly;
                                      if (l) zoomToLayer(l);
                                    }}
                                  >
                                    {p.label ||
                                      (p.texts || []).find((t) =>
                                        /[A-Za-z]{3,}/.test(t),
                                      ) ||
                                      "Room"}
                                  </button>
                                  <span
                                    className={`rounded px-1.5 py-0.5 text-[10px] ${confColor(p.confidence)}`}
                                  >
                                    {p.edited
                                      ? "edited"
                                      : p.confidence != null
                                        ? `${Math.round(p.confidence * 100)}%`
                                        : "—"}
                                  </span>
                                </div>
                                <div className="mt-0.5 text-gray-400">
                                  <span className="text-yellow-200">
                                    {fmtQty(p.area_sf)} SF
                                  </span>{" "}
                                  ceiling ·{" "}
                                  <span className="text-orange-200">
                                    {fmtQty(p.wall_lf ?? p.perimeter_lf)} LF
                                  </span>{" "}
                                  wall
                                  {p.opening_lf > 0.05
                                    ? ` · ${fmtQty(p.opening_lf)} LF openings`
                                    : ""}
                                </div>
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {Object.entries(by)
                                    .filter(([k]) => k !== "__open")
                                    .map(([k, v]) => (
                                      <span
                                        key={k}
                                        className="flex items-center gap-1 rounded bg-gray-900/70 px-1.5 py-0.5 text-[10px] text-gray-200"
                                      >
                                        <span
                                          className="h-2 w-2 rounded-full"
                                          style={{
                                            background:
                                              k === "__none"
                                                ? "#f97316"
                                                : tagColor(k),
                                          }}
                                        />
                                        {k === "__none" ? "untagged" : k}{" "}
                                        {fmtQty(v)} LF
                                      </span>
                                    ))}
                                </div>
                                {p.edited && (
                                  <div className="mt-1 text-[10px] text-yellow-300/80">
                                    Outline edited by hand — wall tags cleared;
                                    perimeter goes to the untagged condition.
                                  </div>
                                )}
                                {p.tags && (
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {p.tags.roomType && (
                                      <span className="rounded bg-gray-700 px-1.5 py-0.5 text-[10px] text-gray-200">
                                        {p.tags.roomType}
                                      </span>
                                    )}
                                    {p.tags.wallType && (
                                      <span className="rounded bg-orange-900/50 px-1.5 py-0.5 text-[10px] text-orange-200">
                                        {p.tags.wallType}
                                      </span>
                                    )}
                                    {p.tags.ceiling && (
                                      <span className="rounded bg-blue-900/50 px-1.5 py-0.5 text-[10px] text-blue-200">
                                        {p.tags.ceiling}
                                      </span>
                                    )}
                                    {p.tags.finish && (
                                      <span className="rounded bg-gray-700 px-1.5 py-0.5 text-[10px] text-gray-200">
                                        {p.tags.finish}
                                      </span>
                                    )}
                                    <span
                                      className={`rounded px-1.5 py-0.5 text-[10px] ${p.tags.verified ? "bg-green-900/50 text-green-300" : "bg-yellow-900/40 text-yellow-300"}`}
                                      title={
                                        p.tags.evidence
                                          ? `Evidence: “${p.tags.evidence}”`
                                          : ""
                                      }
                                    >
                                      {p.tags.verified
                                        ? "verified from sheet"
                                        : "suggested"}
                                    </span>
                                  </div>
                                )}
                                {p.tags?.flags?.length > 0 && (
                                  <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[10px] text-yellow-200/80">
                                    {p.tags.flags.slice(0, 3).map((f, i) => (
                                      <li key={i}>{f}</li>
                                    ))}
                                  </ul>
                                )}
                                <div className="mt-1.5 flex gap-1.5">
                                  <button
                                    onClick={() => acceptProposals([p])}
                                    className="flex-1 rounded bg-orange-600 px-2 py-1 font-semibold text-white hover:bg-orange-500"
                                  >
                                    Accept
                                  </button>
                                  <button
                                    onClick={() => toggleAdjust(p)}
                                    className={`rounded px-2 py-1 ${adjustingId === p.id ? "bg-orange-500 text-white" : "bg-gray-700 text-gray-200 hover:bg-gray-600"}`}
                                  >
                                    {adjustingId === p.id ? "Done" : "Adjust"}
                                  </button>
                                  <button
                                    onClick={() => rejectProposal(p.id)}
                                    className="rounded bg-gray-700 px-2 py-1 text-gray-400 hover:bg-red-900/60 hover:text-red-300"
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                          {pageProposals.length === 0 && (
                            <p className="py-6 text-center text-gray-500">
                              No room proposals on this sheet.
                              <br />
                              Press{" "}
                              <kbd className="rounded bg-gray-800 px-1">
                                R
                              </kbd>{" "}
                              and click inside a room, or run{" "}
                              <b>Detect rooms</b> (zoom to one plan first on
                              busy sheets).
                            </p>
                          )}
                        </div>
                      </section>
                    )}

                    {/* trace history */}
                    {(traceSettings.batches || []).length > 0 && (
                      <section>
                        <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                          Traces
                        </h4>
                        {traceSettings.batches.map((b) => (
                          <div
                            key={b.id}
                            className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-white/5"
                          >
                            <span className="flex-1 text-gray-400">
                              {new Date(b.at).toLocaleString([], {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}{" "}
                              · {b.shapes} shapes · {b.pages.length} sheet
                              {b.pages.length === 1 ? "" : "s"}
                            </span>
                            <button
                              onClick={() => undoBatch(b.id)}
                              title="Remove every shape this trace drew"
                              className="rounded-md px-1.5 py-0.5 text-red-300 hover:bg-red-500/15"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </section>
                    )}

                    {/* settings */}
                    <details className="rounded-xl bg-black/20 p-2.5">
                      <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                        Settings
                      </summary>
                      <div className="mt-2 space-y-3">
                        <div className="rounded border border-gray-700 p-2">
                          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                            When accepted, create
                          </div>
                          <label className="mb-1.5 flex items-center gap-2 text-gray-300">
                            <input
                              type="checkbox"
                              checked={!traceSettings?.roomAskOff}
                              onChange={(e) =>
                                setTraceSettings((prev) => ({
                                  ...(prev || {}),
                                  roomAskOff: !e.target.checked,
                                }))
                              }
                              className="accent-orange-600"
                            />
                            Ask walls / ceilings / both each time I detect or
                            fill rooms
                          </label>
                          <label className="mb-1 flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={acceptArea}
                              onChange={(e) => setAcceptArea(e.target.checked)}
                              className="accent-orange-600"
                            />
                            <span className="w-24 text-gray-300">
                              Ceiling area →
                            </span>
                            <select
                              value={areaCondId}
                              onChange={(e) => setAreaCondId(e.target.value)}
                              className="min-w-0 flex-1 rounded border border-gray-600 bg-gray-800 px-1 py-1 text-[11px]"
                            >
                              <option value="auto">auto (AI tag / GWB)</option>
                              {conditions
                                .filter((c) => c.type === "area")
                                .map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.no ? `#${c.no} · ` : ""}
                                    {c.name}
                                  </option>
                                ))}
                            </select>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={acceptPerimeter}
                              onChange={(e) =>
                                setAcceptPerimeter(e.target.checked)
                              }
                              className="accent-orange-600"
                            />
                            <span className="w-24 text-gray-300">
                              Untagged wall →
                            </span>
                            <select
                              value={perimCondId}
                              onChange={(e) => setPerimCondId(e.target.value)}
                              className="min-w-0 flex-1 rounded border border-gray-600 bg-gray-800 px-1 py-1 text-[11px]"
                            >
                              <option value="auto">auto (AI tag / P1)</option>
                              {conditions
                                .filter((c) => c.type === "linear")
                                .map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.no ? `#${c.no} · ` : ""}
                                    {c.name}
                                  </option>
                                ))}
                            </select>
                          </label>
                          <div className="mt-2 space-y-1.5 border-t border-gray-700/60 pt-2">
                            <label className="flex items-center gap-2 text-gray-300">
                              Wall lines
                              <select
                                value={traceSettings.wallLines || "centre"}
                                onChange={(e) =>
                                  setTraceSettings((prev) => ({
                                    ...(prev || {}),
                                    wallLines: e.target.value,
                                  }))
                                }
                                className="ml-auto rounded border border-gray-600 bg-gray-800 px-1 py-0.5 text-[11px]"
                              >
                                <option value="centre">
                                  one per wall (centreline)
                                </option>
                                <option value="faces">each room's faces</option>
                              </select>
                            </label>
                            <label className="flex items-center gap-2 text-gray-300">
                              <input
                                type="checkbox"
                                checked={traceSettings.wetSeparate ?? true}
                                onChange={(e) =>
                                  setTraceSettings((prev) => ({
                                    ...(prev || {}),
                                    wetSeparate: e.target.checked,
                                  }))
                                }
                                className="accent-orange-600"
                              />
                              Washroom walls as their own assembly
                            </label>
                            <label className="flex items-center gap-2 text-gray-300">
                              <input
                                type="checkbox"
                                checked={traceSettings.counts ?? true}
                                onChange={(e) =>
                                  setTraceSettings((prev) => ({
                                    ...(prev || {}),
                                    counts: e.target.checked,
                                  }))
                                }
                                className="accent-orange-600"
                              />
                              Count doors / double doors / windows
                            </label>
                            <label className="flex items-center gap-2 text-gray-300">
                              <input
                                type="checkbox"
                                checked={traceSettings.cornerBead ?? true}
                                onChange={(e) =>
                                  setTraceSettings((prev) => ({
                                    ...(prev || {}),
                                    cornerBead: e.target.checked,
                                  }))
                                }
                                className="accent-orange-600"
                              />
                              Count outside corners (corner bead)
                            </label>
                            <label className="flex items-center gap-2 text-gray-300">
                              Doors
                              <select
                                value={traceSettings.doorMode || "deduct"}
                                onChange={(e) =>
                                  setTraceSettings((prev) => ({
                                    ...(prev || {}),
                                    doorMode: e.target.value,
                                  }))
                                }
                                className="ml-auto rounded border border-gray-600 bg-gray-800 px-1 py-0.5 text-[11px]"
                              >
                                <option value="deduct">
                                  line crosses · deducted
                                </option>
                                <option value="gap">gap in the line</option>
                              </select>
                              {(traceSettings.doorMode || "deduct") ===
                                "deduct" && (
                                <input
                                  defaultValue={traceSettings.doorHeight || ""}
                                  onBlur={(e) =>
                                    setTraceSettings((prev) => ({
                                      ...(prev || {}),
                                      doorHeight: e.target.value,
                                    }))
                                  }
                                  placeholder={`7'-0"`}
                                  title="Door height for the SF deduction"
                                  className="w-14 rounded border border-gray-600 bg-gray-800 px-1 py-0.5 text-[11px]"
                                />
                              )}
                            </label>
                          </div>
                          <p className="mt-1.5 text-[10px] text-gray-500">
                            Tagged walls go to their tag's condition. One line
                            per wall draws a wall shared by two rooms once —
                            right for studs, track and insulation; add board
                            layers per side in the assembly items.
                          </p>
                        </div>

                        {/* what the trace reads, and where: sheets, tag symbol, trace area, line colours */}
                        <TraceSetupPanel
                          pageCount={pageCount}
                          pageNum={pageNum}
                          pageLabel={pageLabel}
                          excludedPages={excludedPages}
                          settings={traceSettings}
                          setSettings={setTraceSettings}
                          info={sheetEngInfo[pageNum]}
                          wallTypes={assemblies.filter(
                            (a) => a && a.kind === "wall",
                          )}
                          onUseView={useViewAsArea}
                          onGoToPage={(n) => setPageNum(n)}
                          onSuggest={suggestSheetRoles}
                          onOpenWizard={() => setShowWizard(true)}
                        />

                        {/* wall tags */}
                        <div className="rounded border border-gray-700 p-2">
                          <button
                            onClick={() => setShowTagPanel((v) => !v)}
                            className="flex w-full items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-gray-500"
                          >
                            <span>
                              Wall tags{" "}
                              {allTagList.length
                                ? `(${enabledSet.size} of ${allTagList.length} counted)`
                                : ""}
                            </span>
                            <span>{showTagPanel ? "▾" : "▸"}</span>
                          </button>
                          {showTagPanel && (
                            <div className="mt-2 space-y-1">
                              {allTagList.length === 0 && (
                                <p className="text-[11px] text-gray-500">
                                  No tags found on walls yet. Run Fill room or
                                  Detect rooms first.
                                </p>
                              )}
                              {allTagList.length > 0 && (
                                <p className="text-[10px] text-gray-500">
                                  A tag anywhere along a straight wall line
                                  applies to that whole line. Untick anything
                                  that isn't a wall type (door, window or
                                  keynote tags).
                                </p>
                              )}
                              {allTagList.slice(0, 40).map((t) => {
                                const on = enabledSet.has(t.text);
                                return (
                                  <div
                                    key={t.text}
                                    className="flex items-center gap-2"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={on}
                                      className="accent-orange-600"
                                      onChange={() => {
                                        const next = new Set(enabledSet);
                                        on
                                          ? next.delete(t.text)
                                          : next.add(t.text);
                                        setEnabledTags([...next]);
                                      }}
                                    />
                                    <span
                                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                                      style={{
                                        background: on
                                          ? tagColor(t.text)
                                          : "#4b5563",
                                      }}
                                    />
                                    <span className="w-14 truncate font-mono text-gray-200">
                                      {t.text}
                                    </span>
                                    <span className="w-8 text-right text-[10px] text-gray-500">
                                      ×{t.count}
                                    </span>
                                    {on && (
                                      <select
                                        value={tagCondMap[t.text] || "auto"}
                                        onChange={(e) =>
                                          setTagCondMap((m) => ({
                                            ...m,
                                            [t.text]:
                                              e.target.value === "auto"
                                                ? undefined
                                                : e.target.value,
                                          }))
                                        }
                                        className="min-w-0 flex-1 rounded border border-gray-600 bg-gray-800 px-1 py-0.5 text-[11px]"
                                      >
                                        <option value="auto">
                                          →{" "}
                                          {wallPreset(t.text)
                                            ? `${t.text} preset`
                                            : `new "Wall type ${t.text}"`}
                                        </option>
                                        {conditions
                                          .filter((c) => c.type === "linear")
                                          .map((c) => (
                                            <option key={c.id} value={c.id}>
                                              → {c.name}
                                            </option>
                                          ))}
                                      </select>
                                    )}
                                  </div>
                                );
                              })}
                              <label className="mt-1 flex items-center gap-2 text-[11px] text-gray-400">
                                <input
                                  type="checkbox"
                                  checked={tagSideAware}
                                  onChange={(e) =>
                                    setTagSideAware(e.target.checked)
                                  }
                                  className="accent-orange-600"
                                />{" "}
                                Finish tags (paint etc.) — apply to the tagged
                                face only
                              </label>
                              {enabledTags && (
                                <button
                                  onClick={() => setEnabledTags(null)}
                                  className="text-[10px] text-orange-400 hover:underline"
                                >
                                  reset to automatic
                                </button>
                              )}
                            </div>
                          )}
                        </div>

                        <p className="text-gray-500">
                          The takeoff saves itself as you work — every change,
                          within a couple of seconds.
                        </p>
                      </div>
                    </details>
                  </>
                )}

                {/* ───────── HISTORY: the change log ───────── */}
                {rightTab === "history" && (
                  <ChangeLog
                    api={API}
                    takeoffId={takeoffId}
                    authHeaders={authHeaders}
                    pageLabel={pageLabel}
                    rev={syncRev}
                    onGoToPage={(p) => setPageNum(p)}
                    onShow={(ids, page) => {
                      const have = new Set(shapes.map((x) => x.id));
                      const on = shapes.filter(
                        (x) => ids.includes(x.id) && (!page || x.page === page),
                      );
                      if (page) setPageNum(page);
                      if (!on.length) {
                        flash(
                          ids.some((id) => have.has(id))
                            ? "Those shapes are on another sheet now."
                            : "Those shapes have been deleted since.",
                          "info",
                        );
                        return;
                      }
                      setSel(on.map((x) => x.id));
                    }}
                  />
                )}

                {/* ───────── CHECKS ───────── */}
                {rightTab === "checks" && (
                  <div className="space-y-2">
                    <button
                      onClick={runChecks}
                      disabled={aiBusy === "review"}
                      className="w-full rounded bg-orange-600 px-2 py-1.5 font-semibold text-white hover:bg-orange-500 disabled:opacity-50"
                    >
                      {aiBusy === "review"
                        ? "Checking…"
                        : checks
                          ? "Re-run checks"
                          : "Run checks"}
                    </button>
                    <p className="text-[11px] text-gray-500">
                      Rule-based checks always run. AI-read notes appear only
                      when they quote text from your sheets.
                    </p>
                    {checks?.aiSkipped && (
                      <p className="rounded bg-gray-800 px-2 py-1 text-[11px] text-gray-400">
                        AI notes skipped: {checks.aiSkipped}
                      </p>
                    )}
                    {checks && checks.items.length === 0 && (
                      <p className="py-4 text-center text-green-400">
                        Nothing to flag. 👍
                      </p>
                    )}
                    {(checks?.items || []).map((it, i) => (
                      <button
                        key={i}
                        onClick={() =>
                          it.conditionId && setActiveCondId(it.conditionId)
                        }
                        className={`block w-full rounded border p-2 text-left ${sevStyle[it.severity] || sevStyle.info}`}
                      >
                        <div className="flex items-center gap-1.5 font-semibold">
                          <span className="text-[9px] uppercase tracking-wide opacity-70">
                            {it.severity}
                          </span>
                          <span className="flex-1">{it.title}</span>
                          {it.source === "ai" && (
                            <span className="rounded bg-black/30 px-1 text-[9px]">
                              sheet note
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 text-[11px] opacity-90">
                          {it.detail}
                        </div>
                        {it.quote && (
                          <div className="mt-1 border-l-2 border-current pl-2 text-[10px] italic opacity-70">
                            “{it.quote}”
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </aside>
          )}
        </div>

        <AssemblyWizard
          open={showWizard}
          onClose={() => {
            setShowWizard(false);
            syncAsmConditions();
          }}
          pageCount={pageCount}
          pageLabel={pageLabel}
          readSheetText={readSheetText}
          readAssemblies={readAssemblies}
          scaleStatus={scaleStatus}
          assemblies={assemblies}
          setAssemblies={setAssemblies}
          settings={traceSettings}
          setSettings={setTraceSettings}
          onRun={runTrace}
          onGoToPage={(n) => setPageNum(n)}
          parseFeetInput={parseFeetInput}
          fmtFeetIn={fmtFeetIn}
          onSuggestRoles={suggestSheetRoles}
        />

        {/* ═══════════════ STATUS BAR ═══════════════ */}
        <div className="flex h-7 shrink-0 items-center gap-4 border-t border-white/5 bg-gray-900 px-3 text-[11px] text-gray-500">
          <span>
            {conditions.length} conditions · {shapes.length} shapes
          </span>
          {pageProposals.length > 0 && (
            <span className="text-yellow-400">
              {pageProposals.length} proposal
              {pageProposals.length === 1 ? "" : "s"} awaiting review
            </span>
          )}
          {uploadingPdf && (
            <span className="text-orange-400">uploading drawing…</span>
          )}
          {error && <span className="text-red-400">{error}</span>}
          <span className="ml-auto hidden md:inline">
            Ctrl/⌘-drag pan · Space draw with selected · V select ⇄ pan · C
            calibrate · M measure · R fill room · E edit · X delete tool · Del
            delete selected · F full screen · [ ] panels
          </span>
        </div>
      </div>
      {coPick && (
        <div
          className="fixed inset-0 z-[1400] flex items-start justify-center bg-black/60 p-4 pt-[12vh]"
          onMouseDown={() => setCoPick(null)}
        >
          <CoPicker
            list={coList}
            err={coErr}
            pinned={new Set(coPins.map((p) => p.coId))}
            onPick={addCoPin}
            onClose={() => setCoPick(null)}
          />
        </div>
      )}
      {coOpen && (
        <div
          className="fixed inset-0 z-[1400] flex items-start justify-center bg-black/50 p-4 pt-[16vh]"
          onMouseDown={() => setCoOpen(null)}
        >
          <div
            className="w-full max-w-sm overflow-hidden rounded-2xl border border-white/10 bg-gray-900 shadow-2xl"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {(() => {
              const co =
                (coList || []).find(
                  (c) => String(c._id) === String(coOpen.coId),
                ) || {};
              const money = (v) =>
                v == null || v === "" || isNaN(Number(v))
                  ? ""
                  : Number(v).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    });
              return (
                <>
                  <div className="border-b border-white/10 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="rounded bg-teal-600 px-1.5 py-0.5 text-[11px] font-bold text-white">
                        CO {co.number ?? coOpen.number}
                      </span>
                      {(co.status || coOpen.status) && (
                        <span className="text-[11px] text-gray-400">
                          {co.status || coOpen.status}
                        </span>
                      )}
                      {money(co.total) && (
                        <span className="ml-auto text-sm font-semibold text-white">
                          {money(co.total)}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-[13px] text-gray-100">
                      {co.title || coOpen.title || "Change order"}
                    </div>
                    {coOpen.note && (
                      <div className="mt-1 text-[12px] text-gray-400">
                        {coOpen.note}
                      </div>
                    )}
                    <div className="mt-1 text-[10.5px] text-gray-500">
                      Pinned on page {coOpen.page}
                      {coOpen.at
                        ? ` · ${new Date(coOpen.at).toLocaleDateString()}`
                        : ""}
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 px-4 py-3">
                    <button
                      onClick={() => {
                        setCoPins((prev) =>
                          prev.filter((x) => x.id !== coOpen.id),
                        );
                        setCoOpen(null);
                      }}
                      className="rounded-lg px-3 py-1.5 text-xs text-gray-400 hover:bg-red-500/10 hover:text-red-300"
                    >
                      Remove pin
                    </button>
                    <button
                      onClick={() =>
                        window.open(
                          `/projects/${projectId}/change-orders/${coOpen.coId}`,
                          "_blank",
                        )
                      }
                      className="rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-500"
                    >
                      Open change order ↗
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </MovableHost>
  );
}
